import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  bg:       "#070c16",
  surface:  "#0e1625",
  surf2:    "#131f33",
  surf3:    "#1a2840",
  border:   "#1e2d45",
  borderHi: "#263550",
  accent:   "#2563eb",
  accentLt: "#3b82f6",
  accentGl: "rgba(37,99,235,0.12)",
  success:  "#10b981",
  succGl:   "rgba(16,185,129,0.12)",
  warn:     "#f59e0b",
  warnGl:   "rgba(245,158,11,0.12)",
  danger:   "#ef4444",
  dangGl:   "rgba(239,68,68,0.1)",
  teal:     "#14b8a6",
  purple:   "#8b5cf6",
  text:     "#e2e8f0",
  textMd:   "#94a3b8",
  textDm:   "#475569",
  mono:     "'JetBrains Mono', 'IBM Plex Mono', monospace",
  sans:     "'Inter', system-ui, sans-serif",
};


const API_BASE = import.meta.env?.VITE_API_URL || (window.location.hostname === "localhost" ? "http://localhost:4000/api" : "https://freightflow-tms.onrender.com/api");

// ─── Auth Context ─────────────────────────────────────────────────────────────
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(() => localStorage.getItem("ff_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000); // 5s timeout
    fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setUser(d.user))
      .catch(() => { localStorage.removeItem("ff_token"); setToken(null); })
      .finally(() => { clearTimeout(timer); setLoading(false); });
  }, [token]);

  const login = async (email, password) => {
    const res  = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Login failed");
    localStorage.setItem("ff_token", data.accessToken);
    setToken(data.accessToken);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    const userId = user?.id;
    // Clear all FreightFlow session data
    Object.keys(localStorage)
      .filter(k => k.startsWith("ff_"))
      .forEach(k => localStorage.removeItem(k));
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Login Page ───────────────────────────────────────────────────────────────
const CC = C; // alias — LoginPage uses CC to avoid conflict with inner C usages

function LoginPage() {
  const { login } = useAuth();
  const [tab,      setTab]      = useState("login");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const [warming,  setWarming]  = useState(false);

  // Warm up the backend on page load to reduce cold start delay
  useEffect(() => {
    const warm = async () => {
      try {
        setWarming(true);
        await fetch(`${API_BASE.replace("/api","")}/health`, { method:"GET" });
      } catch(_) {}
      finally { setWarming(false); }
    };
    warm();
  }, []); // eslint-disable-line
  const [reqForm,  setReqForm]  = useState({ company:"", email:"", phone:"", dot:"", role:"" });
  const [reqError, setReqError] = useState(null);
  const [reqLoading, setReqLoading] = useState(false);
  const setReq = k => e => setReqForm(f => ({...f, [k]: e.target.value}));

  const handleRequest = async () => {
    if (!reqForm.company || !reqForm.email || !reqForm.role) {
      setReqError("Please fill in all required fields");
      return;
    }
    if (!reqForm.dot || !/^\d{7}$/.test(reqForm.dot.trim())) {
      setReqError("DOT number must be exactly 7 digits");
      return;
    }
    setReqLoading(true);
    setReqError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: reqForm.email,
          password: "TempPass123!",
          role: reqForm.role,
          companyName: reqForm.company,
          phone: reqForm.phone,
          dotNumber: reqForm.dot,
          status: "PENDING",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      setSent(true);
    } catch (err) {
      setReqError(err.message);
    } finally {
      setReqLoading(false);
    }
  };

  const DEMO = [
    { label:"Shipper",  email:"shipper@midwestgoods.com", password:"Password123!" },
    { label:"Carrier",  email:"ops@arrowlogistics.com",   password:"Password123!" },
    { label:"Broker",   email:"broker@midwestbroker.com", password:"Password123!" },
    { label:"Admin",    email:"admin@freightflow.com",    password:"Password123!" },
    { label:"Driver",   email:"driver@arrowlogistics.com", password:"Password123!" },
  ];

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError(null); setLoading(true);
    try { await login(email, password); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const inp = (value, onChange, type="text", placeholder="") => ({
    type, value, onChange, placeholder,
    style: { width:"100%", background:CC.bg, border:`1px solid ${CC.border}`, borderRadius:8,
      padding:"10px 14px", color:CC.text, fontSize:14, outline:"none", fontFamily:CC.sans, boxSizing:"border-box" }
  });

  return (
    <div style={{ minHeight:"100vh", background:CC.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:CC.sans }}>
      <style>{`* { box-sizing:border-box; }`}</style>
      <div style={{ width:"100%", maxWidth:420, padding:"0 20px" }}>
        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ width:52, height:52, borderRadius:14, background:`linear-gradient(135deg,${CC.accent},${CC.purple})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, margin:"0 auto 12px" }}>🚛</div>
          <div style={{ fontSize:24, fontWeight:700, color:CC.text }}>FreightFlow</div>
          <div style={{ fontSize:13, color:CC.textMd, marginTop:3 }}>Transportation Management Platform</div>
        </div>

        <div style={{ background:CC.surface, border:`1px solid ${CC.borderHi}`, borderRadius:16, overflow:"hidden" }}>
          {/* Tabs */}
          <div style={{ display:"flex", borderBottom:`1px solid ${CC.border}` }}>
            {[["login","Sign In"],["request","Request Access"]].map(([id,label]) => (
              <button key={id} onClick={() => { setTab(id); setError(null); setSent(false); }} style={{
                flex:1, background:"transparent", border:"none",
                borderBottom:`2px solid ${tab===id ? CC.accent : "transparent"}`,
                color: tab===id ? CC.accentLt : CC.textMd,
                padding:"14px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:CC.sans,
              }}>{label}</button>
            ))}
          </div>

          <div style={{ padding:28 }}>
            {tab === "login" && (
              <form onSubmit={handleLogin}>
                <div style={{ marginBottom:14 }}>
                  <label style={{ fontSize:11, color:CC.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:5 }}>Email</label>
                  <input {...inp(email, e => setEmail(e.target.value), "email", "you@company.com")} autoFocus required />
                </div>
                <div style={{ marginBottom:20 }}>
                  <label style={{ fontSize:11, color:CC.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:5 }}>Password</label>
                  <input {...inp(password, e => setPassword(e.target.value), "password", "••••••••")} required />
                </div>
                {error && (
                  <div style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", borderRadius:8, padding:"10px 14px", fontSize:13, color:CC.danger, marginBottom:14 }}>
                    {error}
                  </div>
                )}
                <button type="submit" disabled={loading} style={{
                  width:"100%", background:CC.accent, color:"#fff", border:"none",
                  borderRadius:8, padding:"11px", fontSize:14, fontWeight:600,
                  cursor:loading?"not-allowed":"pointer", opacity:loading?0.7:1, fontFamily:CC.sans,
                }}>{loading ? "Signing in…" : "Sign In"}</button>
              </form>
            )}

            {tab === "request" && !sent && (
              <div>
                <div style={{ fontSize:13, color:CC.textMd, marginBottom:16, lineHeight:1.7 }}>
                  FreightFlow is currently <strong style={{ color:CC.text }}>invite-only</strong>. Submit your details and we'll review within 24 hours.
                </div>
                {[
                  { label:"Company Name", type:"text",  placeholder:"Acme Freight LLC",   key:"company" },
                  { label:"Work Email",   type:"email", placeholder:"you@company.com",     key:"email"   },
                  { label:"Phone",        type:"tel",   placeholder:"+1 (555) 000-0000",   key:"phone"   },
                ].map(f => (
                  <div key={f.label} style={{ marginBottom:12 }}>
                    <label style={{ fontSize:11, color:CC.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:5 }}>{f.label}</label>
                    <input type={f.type} placeholder={f.placeholder} value={reqForm[f.key]} onChange={setReq(f.key)}
                      style={{ width:"100%", background:CC.bg, border:`1px solid ${CC.border}`, borderRadius:8, padding:"10px 14px", color:CC.text, fontSize:14, outline:"none", boxSizing:"border-box" }} />
                  </div>
                ))}
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:11, color:CC.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:5 }}>
                    DOT Number <span style={{color:CC.danger}}>*</span>
                    <span style={{color:CC.textDm, textTransform:"none", letterSpacing:"normal", marginLeft:6, fontSize:10}}>7-digit FMCSA number</span>
                  </label>
                  <input type="text" maxLength={7} placeholder="1234567" value={reqForm.dot} onChange={e => setReqForm(f=>({...f, dot:e.target.value.replace(/\D/g,"").slice(0,7)}))}
                    style={{ width:"100%", background:CC.bg, border:`1px solid ${reqForm.dot && !/^\d{7}$/.test(reqForm.dot)?CC.danger:CC.border}`, borderRadius:8, padding:"10px 14px", color:CC.text, fontSize:14, fontFamily:"monospace", outline:"none", boxSizing:"border-box", letterSpacing:"0.15em" }} />
                  {reqForm.dot && !/^\d{7}$/.test(reqForm.dot) && (
                    <div style={{ fontSize:11, color:CC.danger, marginTop:4 }}>Must be exactly 7 digits ({reqForm.dot.length}/7)</div>
                  )}
                </div>
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:11, color:CC.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:5 }}>I am a <span style={{color:CC.danger}}>*</span></label>
                  <select value={reqForm.role} onChange={setReq("role")}
                    style={{ width:"100%", background:CC.bg, border:`1px solid ${CC.border}`, borderRadius:8, padding:"10px 14px", color:CC.text, fontSize:14, outline:"none" }}>
                    <option value="">Select…</option>
                    <option value="SHIPPER">Shipper — I move freight</option>
                    <option value="CARRIER">Carrier — I haul freight</option>
                    <option value="BROKER">Broker — I arrange freight between shippers and carriers</option>
                  </select>
                </div>
                {reqError && (
                  <div style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", borderRadius:8, padding:"10px 14px", fontSize:13, color:CC.danger, marginBottom:12 }}>
                    {reqError}
                  </div>
                )}
                <button onClick={handleRequest} disabled={reqLoading} style={{
                  width:"100%", background:CC.accent, color:"#fff", border:"none",
                  borderRadius:8, padding:"11px", fontSize:14, fontWeight:600,
                  cursor:reqLoading?"not-allowed":"pointer", opacity:reqLoading?0.7:1, fontFamily:CC.sans,
                }}>{reqLoading ? "Submitting…" : "Request Access"}</button>
              </div>
            )}

            {tab === "request" && sent && (
              <div style={{ textAlign:"center", padding:"12px 0" }}>
                <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
                <div style={{ fontSize:16, fontWeight:700, color:CC.text, marginBottom:8 }}>Request received!</div>
                <div style={{ fontSize:13, color:CC.textMd, lineHeight:1.7 }}>We'll review and send credentials within 24 hours.</div>
              </div>
            )}
          </div>
        </div>
        
      </div>
    </div>
  );
}

// ─── Access Gate ──────────────────────────────────────────────────────────────
function AccessGate({ children }) {
  const { user, loading, logout } = useAuth();

  // Show login page while loading — avoids black screen if backend is slow
  if (loading) return <LoginPage />;

  if (!user) return <LoginPage />;

  if (user.status === "PENDING") return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:C.sans }}>
      <div style={{ textAlign:"center", maxWidth:400, padding:24 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>⏳</div>
        <div style={{ fontSize:20, fontWeight:700, color:C.text, marginBottom:8 }}>Application under review</div>
        <div style={{ fontSize:14, color:C.textMd, lineHeight:1.7, marginBottom:24 }}>
          Your account is pending approval. You'll receive an email at <strong style={{ color:C.text }}>{user.email}</strong> once access is granted.
        </div>
        <button onClick={logout} style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.textMd, borderRadius:8, padding:"8px 20px", fontSize:13, cursor:"pointer", fontFamily:C.sans }}>Sign Out</button>
      </div>
    </div>
  );

  if (user.status === "SUSPENDED") return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:C.sans }}>
      <div style={{ textAlign:"center", maxWidth:400, padding:24 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>⛔</div>
        <div style={{ fontSize:20, fontWeight:700, color:C.text, marginBottom:8 }}>Account suspended</div>
        <div style={{ fontSize:14, color:C.textMd, lineHeight:1.7, marginBottom:24 }}>
          Contact <a href="mailto:support@freightflow.com" style={{ color:C.accentLt }}>support@freightflow.com</a> for assistance.
        </div>
        <button onClick={logout} style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.textMd, borderRadius:8, padding:"8px 20px", fontSize:13, cursor:"pointer", fontFamily:C.sans }}>Sign Out</button>
      </div>
    </div>
  );

  return <>{children}</>;
}



// ─── Utilities ────────────────────────────────────────────────────────────────
const fmt$ = (n) => `$${Number(n).toLocaleString()}`;
const fmtMi = (n) => `${Number(n).toLocaleString()} mi`;
const fmtLb = (n) => `${Number(n).toLocaleString()} lbs`;
const cpm = (rate, miles) => miles ? `$${(rate / miles).toFixed(2)}/mi` : "";

const STATUS_META = {
  POSTED:      { label: "Posted",      color: C.accentLt, bg: C.accentGl },
  BIDDING:     { label: "Bidding",     color: C.warn,     bg: C.warnGl   },
  BOOKED:      { label: "Booked",      color: C.purple,   bg: "rgba(139,92,246,.12)" },
  IN_TRANSIT:  { label: "In Transit",  color: C.success,  bg: C.succGl   },
  AT_PICKUP:   { label: "At Pickup",   color: C.warn,     bg: C.warnGl   },
  PICKED_UP:   { label: "Picked Up",   color: C.success,  bg: C.succGl   },
  AT_DELIVERY: { label: "At Delivery", color: C.teal,     bg: "rgba(20,184,166,.12)" },
  DELIVERED:   { label: "Delivered",   color: C.success,  bg: C.succGl   },
  POD_RECEIVED:{ label: "POD Done",    color: C.teal,     bg: "rgba(20,184,166,.12)" },
  INVOICED:    { label: "Invoiced",    color: C.warn,     bg: C.warnGl   },
  PAID:        { label: "Paid",        color: C.success,  bg: C.succGl   },
  CANCELLED:   { label: "Cancelled",   color: C.danger,   bg: C.dangGl   },
};

const EQUIP_META = {
  DRY_VAN:   { label: "Dry Van",    color: C.accentLt },
  FLATBED:   { label: "Flatbed",    color: C.warn     },
  REEFER:    { label: "Reefer",     color: C.teal     },
  TANKER:    { label: "Tanker",     color: C.purple   },
  STEP_DECK: { label: "Step Deck",  color: "#f97316"  },
  LOWBOY:    { label: "Lowboy",     color: "#ec4899"  },
  BOX_TRUCK: { label: "Box Truck",  color: C.textMd   },
  SPRINTER:  { label: "Sprinter",   color: C.textMd   },
};

// ─── Sample Data ──────────────────────────────────────────────────────────────

// ─── FMCSA API helpers ────────────────────────────────────────────────────────
// ── FMCSA Lookup ─────────────────────────────────────────────────────────────
// All real lookups go through the backend (/api/fmcsa/lookup/dot/:dot).
// The backend calls SaferWebAPI.com (needs SAFER_WEB_API_KEY env var) or
// falls back to scraping FMCSA SAFER directly — no CORS issues server-side.
//
// CORS proxies cannot be used from the browser for fmcsa.dot.gov (it is on
// their blocklists). Run the backend and set SAFER_WEB_API_KEY for live data.
// Free key at: https://saferwebapi.com (15-day trial, no card required)
//
// Demo DOT numbers for UI testing (no backend needed):
//   3791729  ACME FREIGHT LLC              Satisfactory
//   2529862  SUNBELT CARRIERS INC          Satisfactory  (DBA: Sunbelt Trucking)
//   1234567  EAGLE TRANSPORT SOLUTIONS LLC Conditional
//   9999999  OUT OF SERVICE HAULING INC    Unsatisfactory / OOS

const FMCSA_DEMO = {
  "3791729": {
    legalName:"ACME FREIGHT LLC", dbaName:null,
    safetyRating:"Satisfactory", outOfService:false,
    operatingStatus:"Interstate", powerUnits:24, drivers:31,
    mcNumber:"MC-812345", address:"4800 S Halsted St, Chicago, IL 60609",
    phone:"(312) 555-0100", ratingDate:"Jan 15, 2025",
    commonAuthority:"ACTIVE", contractAuthority:"ACTIVE", brokerAuthority:null,
    insuranceRequired:"Yes", insuranceOnFile:"Yes",
    crashes:{fatal:0,injury:1,tow:2,total:3},
    inspections:{vehicle:48,vehicleOOS:4,driver:48,driverOOS:2,hazmat:0,hazmatOOS:0},
  },
  "2529862": {
    legalName:"SUNBELT CARRIERS INC", dbaName:"Sunbelt Trucking",
    safetyRating:"Satisfactory", outOfService:false,
    operatingStatus:"Interstate", powerUnits:18, drivers:22,
    mcNumber:"MC-556677", address:"1200 Logistics Way, Atlanta, GA 30301",
    phone:"(404) 555-0200", ratingDate:"Mar 3, 2025",
    commonAuthority:"ACTIVE", contractAuthority:"ACTIVE", brokerAuthority:null,
    insuranceRequired:"Yes", insuranceOnFile:"Yes",
    crashes:{fatal:0,injury:0,tow:1,total:1},
    inspections:{vehicle:32,vehicleOOS:2,driver:32,driverOOS:1,hazmat:0,hazmatOOS:0},
  },
  "1234567": {
    legalName:"EAGLE TRANSPORT SOLUTIONS LLC", dbaName:"Eagle Transport",
    safetyRating:"Conditional", outOfService:false,
    operatingStatus:"Interstate", powerUnits:8, drivers:9,
    mcNumber:"MC-334455", address:"990 Commerce Dr, Dallas, TX 75207",
    phone:"(214) 555-0300", ratingDate:"Sep 10, 2024",
    commonAuthority:"ACTIVE", contractAuthority:null, brokerAuthority:null,
    insuranceRequired:"Yes", insuranceOnFile:"Yes",
    crashes:{fatal:0,injury:2,tow:3,total:5},
    inspections:{vehicle:24,vehicleOOS:6,driver:24,driverOOS:4,hazmat:0,hazmatOOS:0},
  },
  "9999999": {
    legalName:"OUT OF SERVICE HAULING INC", dbaName:null,
    safetyRating:"Unsatisfactory", outOfService:true,
    operatingStatus:"Out of Service", powerUnits:3, drivers:3,
    mcNumber:null, address:"123 Revoked Rd, Phoenix, AZ 85001",
    phone:null, ratingDate:"Feb 1, 2024",
    commonAuthority:"REVOKED", contractAuthority:null, brokerAuthority:null,
    insuranceRequired:"Yes", insuranceOnFile:"No",
    crashes:{fatal:1,injury:4,tow:6,total:11},
    inspections:{vehicle:18,vehicleOOS:12,driver:18,driverOOS:9,hazmat:0,hazmatOOS:0},
  },
};


const fmcsaLookup = async (dotNumber, mode = "dot") => {
  const dot = String(dotNumber).replace(/\D/g, "");

  // ── 1. Backend — scrapes FMCSA SAFER directly, no API key needed ───────────
  try {
    const endpoint = mode === "mc"
      ? `${API_BASE}/fmcsa/lookup/mc/${dot}`
      : `${API_BASE}/fmcsa/lookup/dot/${dot}`;
    const res = await fetch(endpoint, { signal: AbortSignal.timeout(15000) });
    const data = await res.json();
    if (res.ok && data.profile?.legalName) return data.profile;
    // Surface the real error from the backend (e.g. parse failed, DOT not found)
    if (data.error) throw new Error(data.error);
  } catch (err) {
    // Re-throw real errors; swallow only network/offline errors
    if (err.name !== "AbortError"
        && !err.message.includes("Failed to fetch")
        && !err.message.includes("NetworkError")
        && !err.message.includes("Load failed")) {
      throw err;
    }
    // Backend unreachable — fall through to demo data
  }

  // ── 2. Demo data — works without backend running ──────────────────────────
  const demo = FMCSA_DEMO[dot];
  if (demo) {
    return {
      source: "Demo data (backend offline — run: cd backend && npm run dev)",
      dotNumber: dot, ratingDate: demo.ratingDate, ...demo,
      verifiedAt: new Date().toISOString(),
    };
  }

  // ── 3. Backend offline + DOT not in demo set ─────────────────────────────
  throw new Error(
    `Backend not running. Start it: cd backend && npm run dev\n` +
    `Demo DOTs (offline): 3791729 · 2529862 · 1234567 · 9999999`
  );
};

const fmcsaVerify = async (carrierId) => {
  const token = localStorage.getItem("accessToken");
  const res = await fetch(`http://localhost:4000/api/fmcsa/verify/${carrierId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`Verification failed (${res.status})`);
  return (await res.json()).profile;
};

const LOADS = [
  // FF-000001 — POSTED: Shipper just posted, broker can see and book it
  { id:"L001", number:"FF-000001",
    shipperId:"S1", shipper:"Midwest Goods Co.", shipperEmail:"dispatch@midwestgoods.com", shipperPhone:"(312) 555-0101",
    carrierId:null, carrier:null,
    origin:"Chicago, IL", originLat:41.8781, originLng:-87.6298,
    dest:"Dallas, TX",   destLat:32.7767,   destLng:-96.7970,
    pickupDate:"Jun 16", deliveryDate:"Jun 17", miles:917, weight:24000,
    commodity:"Auto Parts", equipmentType:"DRY_VAN", rate:3200, brokerRate:null,
    status:"POSTED", urgency:"STANDARD", bids:2, postedAt:"2h ago",
  },

  // FF-000002 — BOOKED: Broker booked to Arrow Logistics, needs driver & truck assigned
  { id:"L002", number:"FF-000002",
    shipperId:"S1", shipper:"Midwest Goods Co.", shipperEmail:"dispatch@midwestgoods.com", shipperPhone:"(312) 555-0101",
    carrierId:"C1", carrier:"Arrow Logistics Inc.", carrierPhone:"(773) 555-0200",
    driver:"", truck:"",
    origin:"Atlanta, GA",  originLat:33.7490, originLng:-84.3880,
    dest:"Miami, FL",      destLat:25.7617,   destLng:-80.1918,
    pickupDate:"Jun 17", deliveryDate:"Jun 18", miles:662, weight:18000,
    commodity:"Fresh Produce", equipmentType:"REEFER", rate:2800, brokerRate:2100,
    status:"BOOKED", urgency:"EXPEDITED", bids:0, postedAt:"1d ago",
  },

  // FF-000003 — IN_TRANSIT: Fully dispatched, driver Marcus Johnson on the road
  { id:"L003", number:"FF-000003",
    shipperId:"S2", shipper:"Pacific Freight LLC", shipperEmail:"loads@pacificfreight.com", shipperPhone:"(415) 555-0202",
    carrierId:"C1", carrier:"Arrow Logistics Inc.", carrierPhone:"(773) 555-0200",
    driver:"Marcus Johnson", driverPhone:"(312) 555-9001", truck:"ARR-002",
    origin:"Atlanta, GA",  originLat:33.7490, originLng:-84.3880,
    dest:"Miami, FL",      destLat:25.7617,   destLng:-80.1918,
    pickupDate:"Jun 14", deliveryDate:"Jun 15", miles:662, weight:25000,
    commodity:"Retail Goods", equipmentType:"DRY_VAN", rate:2100, brokerRate:1650,
    status:"IN_TRANSIT", urgency:"STANDARD", bids:0, postedAt:"2d ago", progress:55,
  },

  // FF-000004 — DELIVERED: Complete. Carrier can invoice, shipper can pay, broker sees margin
  { id:"L004", number:"FF-000004",
    shipperId:"S1", shipper:"Midwest Goods Co.", shipperEmail:"dispatch@midwestgoods.com", shipperPhone:"(312) 555-0101",
    carrierId:"C1", carrier:"Arrow Logistics Inc.", carrierPhone:"(773) 555-0200",
    driver:"Kevin Park", driverPhone:"(206) 555-9003", truck:"ARR-001",
    origin:"Seattle, WA",  originLat:47.6062, originLng:-122.3321,
    dest:"Portland, OR",   destLat:45.5051,   destLng:-122.6750,
    pickupDate:"Jun 12", deliveryDate:"Jun 12", miles:175, weight:12000,
    commodity:"Electronics", equipmentType:"DRY_VAN", rate:1800, brokerRate:1400,
    status:"DELIVERED", urgency:"STANDARD", bids:0, postedAt:"4d ago", progress:100,
  },

  // FF-000005 — POSTED: Another open load for broker to pick up
  { id:"L005", number:"FF-000005",
    shipperId:"S2", shipper:"Pacific Freight LLC", shipperEmail:"loads@pacificfreight.com", shipperPhone:"(415) 555-0202",
    carrierId:null, carrier:null,
    origin:"Los Angeles, CA", originLat:34.0522, originLng:-118.2437,
    dest:"Phoenix, AZ",       destLat:33.4484,   destLng:-112.0740,
    pickupDate:"Jun 17", deliveryDate:"Jun 18", miles:372, weight:15000,
    commodity:"Clothing", equipmentType:"DRY_VAN", rate:1600, brokerRate:null,
    status:"POSTED", urgency:"STANDARD", bids:1, postedAt:"5h ago",
  },

  // FF-000006 — AT_PICKUP: Driver Sandra Torres just arrived at pickup location
  { id:"L006", number:"FF-000006",
    shipperId:"S1", shipper:"Midwest Goods Co.", shipperEmail:"dispatch@midwestgoods.com", shipperPhone:"(312) 555-0101",
    carrierId:"C1", carrier:"Arrow Logistics Inc.", carrierPhone:"(773) 555-0200",
    driver:"Sandra Torres", driverPhone:"(404) 555-9002", truck:"ARR-003",
    origin:"Kansas City, MO", originLat:39.0997, originLng:-94.5786,
    dest:"Memphis, TN",       destLat:35.1495,   destLng:-90.0490,
    pickupDate:"Jun 14", deliveryDate:"Jun 15", miles:441, weight:38000,
    commodity:"Steel Coils", equipmentType:"FLATBED", rate:2400, brokerRate:1900,
    status:"AT_PICKUP", urgency:"STANDARD", bids:0, postedAt:"1d ago", progress:10,
  },
];

const BIDS = [
  { id:"B001", loadId:"L001", carrier:"Arrow Logistics Inc.", rating:4.9, deliveries:312, amount:2750, note:"Available early pickup, real-time GPS.", status:"PENDING", submitted:"1h ago" },
  { id:"B002", loadId:"L001", carrier:"Sunbelt Trucking Co.", rating:4.7, deliveries:198, amount:2800, note:"Experienced team, dedicated lane.", status:"PENDING", submitted:"45m ago" },
  { id:"B003", loadId:"L001", carrier:"Eagle Transport LLC", rating:4.5, deliveries:94,  amount:2720, note:"Available immediately.", status:"PENDING", submitted:"2h ago" },
  { id:"B004", loadId:"L002", carrier:"Arrow Logistics Inc.", rating:4.9, deliveries:312, amount:1200, note:"Flatbed specialist, OSHA certified.", status:"PENDING", submitted:"30m ago" },
];

const TRACKING_EVENTS = [
  { id:1, loadId:"L003", type:"PICKED_UP",  message:"Load picked up at origin",       location:"Atlanta, GA",     time:"Jun 8, 8:42 AM",  lat:33.7490, lng:-84.3880 },
  { id:2, loadId:"L003", type:"IN_TRANSIT", message:"En route to destination",        location:"Albany, GA",      time:"Jun 8, 12:15 PM", lat:31.5601, lng:-84.1557 },
  { id:3, loadId:"L003", type:"IN_TRANSIT", message:"Passed through Tallahassee",     location:"Tallahassee, FL", time:"Jun 8, 3:30 PM",  lat:30.4518, lng:-84.2807 },
  { id:4, loadId:"L003", type:"IN_TRANSIT", message:"Approx 200 miles from delivery", location:"Gainesville, FL", time:"Jun 8, 6:10 PM",  lat:29.6516, lng:-82.3248 },
  { id:5, loadId:"L007", type:"PICKED_UP",  message:"Load picked up at origin",       location:"Seattle, WA",     time:"Jun 9, 7:15 AM",  lat:47.6062, lng:-122.3321 },
  { id:6, loadId:"L007", type:"IN_TRANSIT", message:"En route to Portland",           location:"Tacoma, WA",      time:"Jun 9, 8:00 AM",  lat:47.2529, lng:-122.4443 },
  { id:7, loadId:"L007", type:"IN_TRANSIT", message:"Passing through Olympia",        location:"Olympia, WA",     time:"Jun 9, 8:45 AM",  lat:47.0379, lng:-122.9007 },
];

const EARNINGS = [
  { id:"E1", load:"FF-000003", route:"ATL → MIA", gross:2100, fee:73.5,  net:2026.5, date:"Jun 8" },
  { id:"E2", load:"FF-000006", route:"CHI → DAL", gross:2650, fee:92.75, net:2557.25, date:"Jun 5" },
  { id:"E3", load:"FF-000007", route:"LAX → PHX", gross:1200, fee:42,    net:1158,   date:"Jun 2" },
];

// Carrier invoices — bills the carrier generates and sends to shippers
// ─── Primitive Components ─────────────────────────────────────────────────────

const Badge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, color: C.textMd, bg: C.surf3 };
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:4, background:m.bg, color:m.color, border:`1px solid ${m.color}44`, textTransform:"uppercase", letterSpacing:"0.07em", whiteSpace:"nowrap" }}>
      {m.label}
    </span>
  );
};

const EquipBadge = ({ type }) => {
  const m = EQUIP_META[type] || { label: type, color: C.textMd };
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:"2px 6px", borderRadius:3, background:`${m.color}18`, color:m.color, border:`1px solid ${m.color}33`, textTransform:"uppercase", letterSpacing:"0.07em" }}>
      {m.label}
    </span>
  );
};

const UrgencyDot = ({ urgency }) => urgency === "URGENT"
  ? <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:3, background:C.dangGl, color:C.danger, border:`1px solid ${C.danger}44` }}>URGENT</span>
  : urgency === "EXPEDITED"
  ? <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:3, background:C.warnGl, color:C.warn, border:`1px solid ${C.warn}44` }}>EXPEDITED</span>
  : null;

const ProgressBar = ({ pct, color = C.success, height = 4 }) => (
  <div style={{ height, background:C.border, borderRadius:height, overflow:"hidden" }}>
    <div style={{ height:"100%", width:`${Math.min(pct,100)}%`, background:color, borderRadius:height, transition:"width 0.6s ease" }} />
  </div>
);

const StatCard = ({ label, value, sub, color, icon }) => (
  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px", flex:1, minWidth:0 }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
      <span style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</span>
      {icon && <span style={{ fontSize:16 }}>{icon}</span>}
    </div>
    <div style={{ fontSize:24, fontWeight:700, color: color || C.text, fontFamily:C.mono, lineHeight:1 }}>{value}</div>
    {sub && <div style={{ fontSize:11, color:C.textMd, marginTop:4 }}>{sub}</div>}
  </div>
);

const Btn = ({ children, variant="primary", size="md", onClick, disabled, style={} }) => {
  const base = { border:"none", borderRadius:7, cursor:disabled?"not-allowed":"pointer", fontWeight:600, fontFamily:C.sans, transition:"all 0.15s", opacity:disabled?0.5:1 };
  const sizes = { sm:{ padding:"5px 12px", fontSize:12 }, md:{ padding:"8px 16px", fontSize:13 }, lg:{ padding:"11px 22px", fontSize:14 } };
  const variants = {
    primary: { background:C.accent, color:"#fff" },
    success: { background:C.success, color:"#fff" },
    danger:  { background:C.danger,  color:"#fff" },
    ghost:   { background:"transparent", color:C.textMd, border:`1px solid ${C.border}` },
    outline: { background:"transparent", color:C.accentLt, border:`1px solid ${C.accentLt}` },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...sizes[size], ...variants[variant], ...style }}>{children}</button>;
};

const Input = ({ label, value, onChange, type="text", placeholder, small }) => (
  <div style={{ marginBottom: small ? 8 : 12 }}>
    {label && <label style={{ display:"block", fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>{label}</label>}
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding: small?"6px 10px":"8px 12px", color:C.text, fontSize:13, outline:"none", fontFamily:C.sans, boxSizing:"border-box" }} />
  </div>
);

const Select = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom:12 }}>
    {label && <label style={{ display:"block", fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>{label}</label>}
    <select value={value} onChange={onChange}
      style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 12px", color:C.text, fontSize:13, outline:"none", fontFamily:C.sans, boxSizing:"border-box" }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

// ─── Load Card ────────────────────────────────────────────────────────────────

const LoadCard = ({ load, selected, onClick, role }) => (
  <div onClick={onClick} style={{
    background: selected ? C.surf3 : C.surface,
    border:`1px solid ${selected ? C.accentLt : C.border}`,
    borderRadius:10, padding:"13px 14px", cursor:"pointer",
    boxShadow: selected ? `0 0 0 1px ${C.accentLt}22` : "none",
    transition:"all 0.14s", position:"relative",
  }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
      <div>
        <div style={{ fontSize:10, color:C.textDm, fontFamily:C.mono }}>{load.number}</div>
        <div style={{ fontSize:13, fontWeight:600, color:C.text, marginTop:2, lineHeight:1.3 }}>
          {load.origin.split(",")[0]} → {load.dest.split(",")[0]}
        </div>
        <div style={{ fontSize:11, color:C.textMd, marginTop:1 }}>{load.origin.split(",")[1]?.trim()} → {load.dest.split(",")[1]?.trim()}</div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
        <Badge status={load.status} />
        <UrgencyDot urgency={load.urgency} />
      </div>
    </div>
    <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
      <EquipBadge type={load.equipmentType} />
      <span style={{ fontSize:11, color:C.textMd }}>{fmtMi(load.miles)}</span>
      <span style={{ fontSize:11, color:C.textDm }}>·</span>
      <span style={{ fontSize:11, color:C.textMd }}>{fmtLb(load.weight)}</span>
    </div>
    {load.status === "IN_TRANSIT" && (
      <div style={{ marginBottom:6 }}>
        <ProgressBar pct={load.progress} />
        <div style={{ fontSize:10, color:C.textMd, marginTop:3 }}>{load.progress}% complete</div>
      </div>
    )}
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
      <div style={{ fontSize:11, color:C.textMd }}>Pickup {load.pickupDate} {load.bids > 0 && load.status !== "IN_TRANSIT" && <span style={{ color:C.warn }}>· {load.bids} bid{load.bids>1?"s":""}</span>}</div>
      <div style={{ fontSize:17, fontWeight:700, color:C.success, fontFamily:C.mono }}>{fmt$(load.rate)}</div>
    </div>
  </div>
);

// ─── Sidebar Nav ──────────────────────────────────────────────────────────────

const NAV_SHIPPER = [
  { id:"dashboard",  label:"Dashboard",   icon:"⬡" },
  { id:"loads",      label:"My Loads",    icon:"📦" },
  { id:"post",       label:"Post Load",   icon:"+" },
  { id:"tracking",   label:"Live Track",  icon:"📡" },
  { id:"invoices",   label:"Invoices",    icon:"🧾" },
  { id:"analytics",  label:"Analytics",   icon:"📊" },
  { id:"carriers",   label:"Carriers",    icon:"🚚" },
  { id:"fmcsa",      label:"FMCSA Lookup", icon:"🛡" },
];

const NAV_ADMIN = [
  { id:"admin_dashboard", label:"Dashboard",      icon:"⬡" },
  { id:"admin_users",     label:"All Users",      icon:"👥" },
  { id:"admin_shippers",  label:"Shippers",       icon:"📦" },
  { id:"admin_carriers",  label:"Carriers",       icon:"🚚" },
  { id:"admin_brokers",   label:"Brokers",        icon:"🤝" },
  { id:"admin_loads",     label:"All Loads",      icon:"📋" },
  { id:"admin_billing",   label:"Billing",        icon:"💳" },
  { id:"fmcsa",           label:"FMCSA Lookup",   icon:"🛡" },
  { id:"admin_access",    label:"Access Control", icon:"🔐" },
];

const NAV_CARRIER = [
  { id:"dashboard",  label:"Dashboard",   icon:"⬡"  },
  { id:"loadboard",  label:"Load Board",  icon:"📋" },
  { id:"mybids",     label:"My Bids",     icon:"🏷" },
  { id:"dispatch",   label:"Dispatch",    icon:"🎯" },
  { id:"tracking",   label:"Tracking",    icon:"📡" },
  { id:"invoicing",  label:"Invoicing",   icon:"🧾" },
  { id:"fleet",      label:"Fleet",       icon:"🔧" },
  { id:"fmcsa",      label:"FMCSA Lookup", icon:"🛡" },
];

const NAV_DRIVER = [
  { id:"dashboard", label:"My Load",    icon:"📋" },
  { id:"checkin",   label:"Check In",   icon:"📍" },
  { id:"messages",  label:"Messages",   icon:"💬" },
  { id:"documents", label:"Documents",  icon:"📄" },
];

const NAV_BROKER = [
  { id:"dashboard",  label:"Dashboard",    icon:"⬡"  },
  { id:"dispatch",   label:"Dispatch",     icon:"🎯" },
  { id:"loadboard",  label:"All Loads",    icon:"📋" },
  { id:"post",       label:"Post Load",    icon:"+"  },
  { id:"mybids",     label:"My Loads",     icon:"📦" },
  { id:"shippers",   label:"Shippers",     icon:"📦" },
  { id:"directory",  label:"Find Shippers",icon:"🔎" },
  { id:"carriers",   label:"Carriers",     icon:"🚚" },
  { id:"tracking",   label:"Tracking",     icon:"📡" },
  { id:"invoices",   label:"Invoices",     icon:"🧾" },
  { id:"analytics",  label:"Analytics",    icon:"📊" },
  { id:"fmcsa",      label:"FMCSA Lookup", icon:"🛡" },
];

const Sidebar = ({ role, page, setPage }) => {
  const { user: authUser } = useAuth();
  const nav = role === "ADMIN" ? NAV_ADMIN : role === "SHIPPER" ? NAV_SHIPPER : role === "BROKER" ? NAV_BROKER : role === "DRIVER" ? NAV_DRIVER : NAV_CARRIER;
  return (
    <div style={{ width:200, background:C.surface, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", flexShrink:0 }}>
      <div style={{ padding:"16px 14px 12px", borderBottom:`1px solid ${C.border}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:28, height:28, borderRadius:7, background:`linear-gradient(135deg,${C.accent},${C.purple})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>🚛</div>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:C.text }}>FreightFlow</div>
            <div style={{ fontSize:10, color:C.textDm }}>TMS Platform</div>
          </div>
        </div>
      </div>
      <nav style={{ flex:1, padding:"10px 8px", overflowY:"auto" }}>
        {nav.map(item => (
          <div key={item.id} onClick={() => setPage(item.id)}
            style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px", borderRadius:7, marginBottom:2, cursor:"pointer",
              background: page===item.id ? C.accentGl : "transparent",
              color: page===item.id ? C.accentLt : C.textMd,
              fontWeight: page===item.id ? 600 : 400, fontSize:13, transition:"all 0.12s",
              borderLeft: page===item.id ? `2px solid ${C.accentLt}` : "2px solid transparent",
            }}>
            <span style={{ fontSize:14, width:18, textAlign:"center" }}>{item.icon}</span>
            {item.label}
          </div>
        ))}
      </nav>
      <div style={{ padding:"10px 12px", borderTop:`1px solid ${C.border}` }}>
        <div style={{ fontSize:11, color:C.textDm, marginBottom:6 }}>Signed in as</div>
        <div style={{ fontSize:12, fontWeight:600, color:C.textMd }}>
          {role === "ADMIN" ? "FreightFlow Admin" : role === "SHIPPER" ? (authUser?.shipper?.companyName || authUser?.email || "My Company") : role === "BROKER" ? (authUser?.broker?.companyName || authUser?.email || "My Brokerage") : (authUser?.carrier?.companyName || authUser?.email || "My Fleet")}
        </div>
        <div style={{ fontSize:11, color:C.textDm }}>{role.toLowerCase()}</div>
      </div>
    </div>
  );
};

// ─── Load Detail Panel ────────────────────────────────────────────────────────

const BrokerBookModal = ({ load, onBook }) => {
  const { user: bbmUser } = useAuth();
  const isDemoBrokerBBM = bbmUser?.email === "broker@midwestbroker.com";
  const [open,          setOpen]         = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [carrierRate,   setCarrierRate]   = useState(load.rate);
  const [notes,         setNotes]         = useState("");
  const [booked,        setBooked]        = useState(false);

  // Filter carriers that can handle this equipment type — demo broker sees sample roster,
  // new brokers see none until they add real carrier relationships
  const suitableCarriers = (isDemoBrokerBBM ? BROKER_CARRIERS_DEMO : []).filter(c =>
    !load.equipmentType || c.types.includes(load.equipmentType) || c.types.includes("DRY_VAN")
  );

  const margin = load.rate - (parseFloat(carrierRate) || 0);
  const marginPct = load.rate > 0 ? Math.round(margin / load.rate * 100) : 0;

  const confirmBook = () => {
    if (!selectedCarrier) return;
    onBook && onBook(load, selectedCarrier.id, selectedCarrier.name, parseFloat(carrierRate));
    setBooked(true);
    setTimeout(() => { setOpen(false); setBooked(false); }, 2000);
  };

  if (!open) {
    return (
      <Btn variant="primary" style={{ width:"100%" }} onClick={() => setOpen(true)}>
        🤝 Assign Carrier & Book
      </Btn>
    );
  }

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:400,
      display:"flex", alignItems:"center", justifyContent:"center" }}
      onClick={e => e.target===e.currentTarget && setOpen(false)}>
      <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:16,
        width:520, maxHeight:"88vh", overflow:"hidden", display:"flex", flexDirection:"column" }}>

        {/* Header */}
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`,
          display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:C.text }}>Assign Carrier & Book Load</div>
            <div style={{ fontSize:12, color:C.textMd, marginTop:3 }}>
              {load.number} · {load.origin.split(",")[0]} → {load.dest.split(",")[0]} · Shipper paying {fmt$(load.rate)}
            </div>
          </div>
          <button onClick={() => setOpen(false)} style={{ background:C.surf2, border:`1px solid ${C.border}`,
            color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>✕</button>
        </div>

        <div style={{ flex:1, overflowY:"auto", padding:20 }}>
          {booked ? (
            <div style={{ textAlign:"center", padding:"24px 0" }}>
              <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
              <div style={{ fontSize:18, fontWeight:700, color:C.text, marginBottom:8 }}>Load Booked!</div>
              <div style={{ fontSize:13, color:C.textMd, lineHeight:1.7 }}>
                {selectedCarrier?.name} has been assigned to {load.number}.<br/>
                Your margin: <strong style={{color:C.success}}>{fmt$(margin)}</strong> ({marginPct}%)
              </div>
            </div>
          ) : (
            <>
              {/* Shipper rate */}
              <div style={{ background:C.accentGl, border:`1px solid ${C.accent}33`, borderRadius:8,
                padding:"11px 14px", marginBottom:16, display:"flex", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>Shipper Rate (what you collect)</div>
                  <div style={{ fontSize:22, fontWeight:800, color:C.success, fontFamily:C.mono }}>{fmt$(load.rate)}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>Your Margin</div>
                  <div style={{ fontSize:22, fontWeight:800, fontFamily:C.mono,
                    color: margin > 0 ? C.success : C.danger }}>{fmt$(margin)}</div>
                  <div style={{ fontSize:11, color:C.textDm }}>({marginPct}%)</div>
                </div>
              </div>

              {/* Carrier rate input */}
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:11, color:C.textMd, display:"block", marginBottom:6 }}>
                  Carrier Rate (what you pay the carrier)
                </label>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:18, color:C.textMd, fontFamily:C.mono }}>$</span>
                  <input type="number" value={carrierRate}
                    onChange={e => setCarrierRate(e.target.value)}
                    style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
                      padding:"10px 14px", color:C.text, fontSize:18, fontFamily:C.mono,
                      outline:"none", fontWeight:700 }} />
                </div>
                {margin < 0 && (
                  <div style={{ fontSize:11, color:C.danger, marginTop:5 }}>
                    ⚠ Carrier rate exceeds shipper rate — you'll lose money on this load.
                  </div>
                )}
                {margin > 0 && margin < load.rate * 0.05 && (
                  <div style={{ fontSize:11, color:C.warn, marginTop:5 }}>
                    Low margin — typical broker margin is 10–20%.
                  </div>
                )}
              </div>

              {/* Carrier selection */}
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:11, color:C.textMd, display:"block", marginBottom:8 }}>
                  Select Carrier
                  <span style={{ color:C.textDm, marginLeft:6 }}>({suitableCarriers.length} match {EQUIP_META[load.equipmentType]?.label || "this equipment"})</span>
                </label>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {suitableCarriers.map(c => (
                    <div key={c.id} onClick={() => setSelectedCarrier(c)}
                      style={{ background: selectedCarrier?.id===c.id ? C.accentGl : C.bg,
                        border:`1px solid ${selectedCarrier?.id===c.id ? C.accent : C.border}`,
                        borderRadius:8, padding:"11px 14px", cursor:"pointer",
                        display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{c.name}</div>
                        <div style={{ fontSize:11, color:C.textMd, marginTop:2 }}>
                          {c.city} · {c.trucks} trucks · ⭐ {c.rating}
                        </div>
                        <div style={{ fontSize:11, color:C.textDm, marginTop:1 }}>
                          {c.types.map(t => EQUIP_META[t]?.label || t).join(", ")}
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <a href={`tel:${c.phone}`} onClick={e=>e.stopPropagation()}
                          style={{ fontSize:11, color:C.accentLt, textDecoration:"none", display:"block" }}>
                          {c.phone}
                        </a>
                        {selectedCarrier?.id===c.id && (
                          <span style={{ fontSize:11, color:C.success, fontWeight:600 }}>✓ Selected</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginBottom:20 }}>
                <label style={{ fontSize:11, color:C.textMd, display:"block", marginBottom:5 }}>Notes to carrier (optional)</label>
                <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
                  placeholder="Special instructions, pickup contact, dock requirements..."
                  style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:7,
                    padding:"9px 12px", color:C.text, fontSize:12, outline:"none", resize:"none",
                    fontFamily:C.sans, boxSizing:"border-box" }} />
              </div>

              {/* Margin summary */}
              {selectedCarrier && (
                <div style={{ background:C.bg, borderRadius:8, border:`1px solid ${C.border}`,
                  padding:"12px 14px", marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:8 }}>Booking Summary</div>
                  {[
                    { label:"Shipper pays",        value:fmt$(load.rate),              color:C.success },
                    { label:"You pay carrier",      value:fmt$(parseFloat(carrierRate)||0), color:C.textMd },
                    { label:"Platform fee (3.5%)",  value:fmt$(Math.round(load.rate*0.035)), color:C.danger },
                    { label:"Your net margin",      value:fmt$(margin - Math.round(load.rate*0.035)), color:margin>0?C.success:C.danger, bold:true },
                  ].map(r => (
                    <div key={r.label} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:`1px solid ${C.border}` }}>
                      <span style={{ fontSize:12, color:C.textMd }}>{r.label}</span>
                      <span style={{ fontSize:12, fontWeight:r.bold?700:500, fontFamily:C.mono, color:r.color }}>{r.value}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display:"flex", gap:10 }}>
                <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
                <Btn variant="success" style={{flex:1}}
                  disabled={!selectedCarrier || !carrierRate || parseFloat(carrierRate) <= 0}
                  onClick={confirmBook}>
                  ✅ Confirm Booking — {selectedCarrier ? selectedCarrier.name : "Select a carrier"}
                </Btn>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const LoadDetail = ({ load, role, bids, onAcceptBid, onBidSubmit, onClose, onBook, onRejectBid, onCounterBid }) => {
  const [counterBid,  setCounterBid]  = useState(null);
  const [counterAmt,  setCounterAmt]  = useState("");
  const [counterNote, setCounterNote] = useState("");
  const [bidAmt, setBidAmt]   = useState(load.rate);
  const [bidNote, setBidNote] = useState("");
  const [bidSent, setBidSent] = useState(false);
  const [tab, setTab]         = useState("details");
  const [loadBidsFromAPI, setLoadBidsFromAPI] = useState(null);
  const { token } = useAuth();

  // Fetch bids for this specific load from API when drawer opens
  useEffect(() => {
    if (!token || !load.id) return;
    fetch(`${API_BASE}/bids/load/${load.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.bids) setLoadBidsFromAPI(data.bids.map(b => ({
          id:            b.id,
          loadId:        b.loadId,
          carrier:       b.carrier?.companyName || "Unknown Carrier",
          carrierId:     b.carrierId,
          rating:        b.carrier?.rating || 4.5,
          deliveries:    0,
          amount:        b.amount,
          note:          b.note || "",
          status:        b.status,
          counterAmount: b.counterAmount || null,
          counterNote:   b.counterNote   || "",
          submitted:     b.createdAt ? new Date(b.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : "—",
        })));
      })
      .catch(() => {});
  }, [load.id, token]); // eslint-disable-line

  // Use API bids if available, fall back to prop bids
  const loadBids = loadBidsFromAPI ?? bids.filter(b => b.loadId === load.id);
  const myBid    = loadBids.find(b => role === "CARRIER" ? b.carrierId === load.carrierId : true);

  const handleBid = () => {
    onBidSubmit(load.id, bidAmt, bidNote);
    setBidSent(true);
  };



  return (
    <div style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, display:"flex", flexDirection:"column", overflow:"hidden" }}>
      {/* Header */}
      <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontSize:10, color:C.textDm, fontFamily:C.mono }}>{load.number}</div>
          <div style={{ fontSize:16, fontWeight:700, color:C.text }}>{load.origin} → {load.dest}</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <Badge status={load.status} />
          <UrgencyDot urgency={load.urgency} />
          <button onClick={onClose} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>✕</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, padding:"0 14px" }}>
        <button onClick={() => setTab("details")} style={{ background:"transparent", border:"none", borderBottom:`2px solid ${tab==="details"?C.accentLt:"transparent"}`, color:tab==="details"?C.accentLt:C.textMd, padding:"8px 12px", fontSize:12, fontWeight:600, cursor:"pointer" }}>Details</button>
        <button onClick={() => setTab("bids")} style={{ background:"transparent", border:"none", borderBottom:`2px solid ${tab==="bids"?C.accentLt:"transparent"}`, color:tab==="bids"?C.accentLt:C.textMd, padding:"8px 12px", fontSize:12, fontWeight:600, cursor:"pointer" }}>{`Bids (${loadBids.length})`}</button>
        <button onClick={() => setTab("timeline")} style={{ background:"transparent", border:"none", borderBottom:`2px solid ${tab==="timeline"?C.accentLt:"transparent"}`, color:tab==="timeline"?C.accentLt:C.textMd, padding:"8px 12px", fontSize:12, fontWeight:600, cursor:"pointer" }}>Timeline</button>
        <button onClick={() => setTab("docs")} style={{ background:"transparent", border:"none", borderBottom:`2px solid ${tab==="docs"?C.accentLt:"transparent"}`, color:tab==="docs"?C.accentLt:C.textMd, padding:"8px 12px", fontSize:12, fontWeight:600, cursor:"pointer" }}>Documents</button>
      </div>

      {/* Content */}
      <div style={{ flex:1, overflowY:"auto", padding:18 }}>

        {tab === "details" && (
          <div>
            {/* Route */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
              {[
                { label:"ORIGIN", city:load.origin, date:`Pickup: ${load.pickupDate}` },
                { label:"DESTINATION", city:load.dest, date:`Delivery: ${load.deliveryDate}` },
              ].map(loc => (
                <div key={loc.label} style={{ background:C.bg, borderRadius:8, padding:"11px 13px", border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>{loc.label}</div>
                  <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{loc.city.split(",")[0]}</div>
                  <div style={{ fontSize:11, color:C.textMd }}>{loc.city.split(",")[1]?.trim()}</div>
                  <div style={{ fontSize:11, color:C.textDm, marginTop:3 }}>{loc.date}</div>
                </div>
              ))}
            </div>

            {/* Cargo specs */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:14 }}>
              {[
                { label:"Distance", value:fmtMi(load.miles) },
                { label:"Weight", value:fmtLb(load.weight) },
                { label:"Equipment", value:<EquipBadge type={load.equipmentType} /> },
                { label:"Commodity", value:load.commodity },
              ].map(s => (
                <div key={s.label} style={{ background:C.bg, borderRadius:7, padding:"8px 10px", border:`1px solid ${C.border}`, textAlign:"center" }}>
                  <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>{s.label}</div>
                  <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{s.value}</div>
                </div>
              ))}
            </div>

            {load.pieces && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:14 }}>
                {[
                  { label:"Pieces", value:load.pieces },
                  { label:"Pallets", value:load.pallets || "—" },
                  { label:"Temp Control", value:load.temperature ? `${load.temperature}°F` : "No" },
                ].map(s => (
                  <div key={s.label} style={{ background:C.bg, borderRadius:7, padding:"8px 10px", border:`1px solid ${C.border}`, textAlign:"center" }}>
                    <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>{s.label}</div>
                    <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{s.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Shipper info */}
            <div style={{ background:C.bg, borderRadius:8, padding:"11px 13px", border:`1px solid ${C.border}`, marginBottom:14 }}>
              <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:6 }}>Shipper</div>
              <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{load.shipper}</div>
              <div style={{ display:"flex", gap:14, marginTop:4, marginBottom: (role==="BROKER" && (load.shipperEmail||load.shipperPhone)) ? 8 : 0 }}>
                <span style={{ fontSize:11, color:C.textMd }}>⭐ 4.8 rating</span>
                <span style={{ fontSize:11, color:C.textMd }}>142 loads</span>
                <span style={{ fontSize:11, color:C.success }}>✓ Verified</span>
              </div>
              {role === "BROKER" && (
                <div style={{ marginTop:6, paddingTop:6, borderTop:`1px solid ${C.border}` }}>
                  {load.shipperCity && <div style={{ fontSize:11, color:C.textMd, marginBottom:3 }}>📍 {load.shipperCity}</div>}
                  {load.shipperEmail && (
                    <div style={{ fontSize:11, color:C.accentLt, marginBottom:3 }}>
                      ✉ <a href={`mailto:${load.shipperEmail}`} style={{ color:C.accentLt, textDecoration:"none" }}>{load.shipperEmail}</a>
                    </div>
                  )}
                  {load.shipperPhone && <div style={{ fontSize:11, color:C.textMd }}>📞 {load.shipperPhone}</div>}
                  {!load.shipperEmail && !load.shipperPhone && (
                    <div style={{ fontSize:11, color:C.textDm }}>Contact info visible after booking</div>
                  )}
                </div>
              )}
            </div>

            {/* Rate */}
            <div style={{ background:C.succGl, borderRadius:8, padding:"13px 15px", border:`1px solid ${C.success}33`, marginBottom:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:9, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:3 }}>Rate</div>
                  <div style={{ fontSize:28, fontWeight:800, color:C.success, fontFamily:C.mono }}>{fmt$(load.rate)}</div>
                  <div style={{ fontSize:11, color:C.textMd, marginTop:2 }}>{cpm(load.rate, load.miles)} · flat rate</div>
                </div>
                <div style={{ textAlign:"right", fontSize:12, color:C.textMd }}>
                  <div>Posted {load.postedAt}</div>
                  {load.bids > 0 && <div style={{ color:C.warn, marginTop:2 }}>{load.bids} competing bids</div>}
                </div>
              </div>
            </div>

            {/* Carrier Bid Form */}
            {role === "CARRIER" && ["POSTED","BIDDING"].includes(load.status) && !bidSent && !myBid && (
              <div style={{ background:C.bg, borderRadius:8, padding:14, border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:12 }}>Submit Your Bid</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                  <div>
                    <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 }}>Your Bid ($)</label>
                    <input type="number" value={bidAmt} onChange={e=>setBidAmt(Number(e.target.value))}
                      style={{ width:"100%", background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", color:C.text, fontSize:15, fontFamily:C.mono, outline:"none", boxSizing:"border-box" }} />
                  </div>
                  <div style={{ display:"flex", alignItems:"flex-end" }}>
                    <div style={{ fontSize:12, color:C.textMd, padding:"9px 0" }}>
                      {cpm(bidAmt, load.miles)}
                      <div style={{ fontSize:11, color: bidAmt < load.rate ? C.success : C.warn }}>
                        {bidAmt < load.rate ? `$${load.rate - bidAmt} below ask` : bidAmt === load.rate ? "At asking rate" : `$${bidAmt - load.rate} above ask`}
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 }}>Note (optional)</label>
                  <textarea value={bidNote} onChange={e=>setBidNote(e.target.value)} rows={2} placeholder="e.g. Available for early pickup, GPS tracking..."
                    style={{ width:"100%", background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", color:C.text, fontSize:12, outline:"none", resize:"none", fontFamily:C.sans, boxSizing:"border-box" }} />
                </div>
                <Btn onClick={handleBid} variant="primary" style={{ width:"100%" }}>Submit Bid — {fmt$(bidAmt)}</Btn>
              </div>
            )}

            {(bidSent || myBid) && role === "CARRIER" && (
              myBid?.status === "COUNTERED" ? (
                <div style={{ background:`${C.warn}11`, border:`1px solid ${C.warn}44`, borderRadius:8, padding:14 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.warn, marginBottom:6 }}>↩ Counter Offer Received</div>
                  <div style={{ fontSize:13, color:C.text, marginBottom:4 }}>
                    Your bid: <span style={{fontFamily:C.mono, color:C.textMd}}>{fmt$(myBid.amount)}</span>
                    <span style={{margin:"0 8px", color:C.textDm}}>→</span>
                    Shipper offers: <span style={{fontFamily:C.mono, color:C.success, fontWeight:700}}>{fmt$(myBid.counterAmount || myBid.amount * 0.95)}</span>
                  </div>
                  {myBid.counterNote && <div style={{ fontSize:12, color:C.textMd, fontStyle:"italic", marginBottom:10 }}>"{myBid.counterNote}"</div>}
                  <div style={{ display:"flex", gap:8, marginTop:10 }}>
                    <Btn variant="success" size="sm" style={{flex:1}} onClick={() => onAcceptBid({...myBid, amount:myBid.counterAmount||myBid.amount})}>
                      ✓ Accept {fmt$(myBid.counterAmount || myBid.amount * 0.95)}
                    </Btn>
                    <Btn variant="outline" size="sm" onClick={() => {
                      const newAmt = window.prompt(`Re-bid on ${load.number}\nShipper counter: ${fmt$(myBid.counterAmount)}\n\nYour new bid amount ($):`);
                      if (newAmt && !isNaN(parseFloat(newAmt))) {
                        onBidSubmit(load.id, parseFloat(newAmt), "Re-bid after counter offer");
                      }
                    }}>Re-bid</Btn>
                  </div>
                </div>
              ) : (
                <div style={{ background:C.succGl, borderRadius:8, padding:14, border:`1px solid ${C.success}55`, textAlign:"center" }}>
                  <div style={{ fontSize:24, marginBottom:4 }}>✅</div>
                  <div style={{ fontSize:14, fontWeight:600, color:C.success }}>Bid submitted!</div>
                  <div style={{ fontSize:12, color:C.textMd, marginTop:2 }}>
                    {myBid?.status === "ACCEPTED" ? "🎉 Your bid was accepted!" :
                     myBid?.status === "REJECTED" ? "Your bid was not selected." :
                     `Your bid of ${fmt$(bidAmt || myBid?.amount)} is pending review`}
                  </div>
                </div>
              )
            )}

            {/* Book button — shipper books directly, broker assigns carrier */}
            {load.status === "POSTED" && role === "SHIPPER" && (
              <Btn variant="success" style={{ width:"100%" }} onClick={() => onBook && onBook(load)}>
                Book at {fmt$(load.rate)}
              </Btn>
            )}
            {load.status === "POSTED" && role === "BROKER" && (
              <BrokerBookModal load={load} onBook={onBook} />
            )}

            {/* In transit info */}
            {load.status === "IN_TRANSIT" && (
              <div style={{ background:C.surf2, borderRadius:8, padding:13, border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:11, color:C.textMd, marginBottom:8 }}>Shipment Progress</div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.textMd, marginBottom:5 }}>
                  <span>{load.origin.split(",")[0]}</span>
                  <span>{load.dest.split(",")[0]}</span>
                </div>
                <ProgressBar pct={load.progress} height={6} />
                <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
                  <span style={{ fontSize:11, color:C.textMd }}>{load.progress}% complete</span>
                  <span style={{ fontSize:11, color:C.textMd }}>Carrier: {load.carrier}</span>
                </div>
                {load.driver && <div style={{ fontSize:11, color:C.textMd, marginTop:3 }}>Driver: {load.driver}</div>}
              </div>
            )}
          </div>
        )}

        {tab === "bids" && (
            <div>
              {loadBids.length === 0 && (
                <div style={{ textAlign:"center", color:C.textDm, padding:32, fontSize:13 }}>No bids yet</div>
              )}
              {loadBids.map(bid => (
                <div key={bid.id} style={{ background:C.bg, borderRadius:8, padding:"12px 14px", border:`1px solid ${C.border}`, marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{bid.carrier}</div>
                      <div style={{ fontSize:12, color:C.textMd, marginTop:2 }}>⭐ {bid.rating} · {bid.deliveries} deliveries</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:20, fontWeight:700, color:C.success, fontFamily:C.mono }}>{fmt$(bid.amount)}</div>
                      <div style={{ fontSize:10, color:C.textDm }}>{bid.submitted}</div>
                    </div>
                  </div>
                  {bid.note && <div style={{ fontSize:12, color:C.textMd, background:C.surface, borderRadius:6, padding:"6px 10px", marginBottom:10 }}>{bid.note}</div>}
                  {bid.status === "ACCEPTED"  && <div style={{ fontSize:12, color:C.success, fontWeight:600 }}>✓ Accepted</div>}
                  {bid.status === "REJECTED"  && <div style={{ fontSize:12, color:C.danger  }}>✗ Rejected</div>}
                  {bid.status === "COUNTERED" && (
                    <div style={{ fontSize:12, color:C.warn, fontWeight:600 }}>
                      ↩ Counter sent: {fmt$(bid.counterAmount || 0)}
                      {bid.counterNote && <span style={{color:C.textMd, fontWeight:400}}> — "{bid.counterNote}"</span>}
                    </div>
                  )}
                  {(role === "SHIPPER" || role === "BROKER") && bid.status === "PENDING" && (
                    counterBid?.id === bid.id ? (
                      <div style={{ marginTop:8, background:C.surface, borderRadius:8, padding:12, border:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:8 }}>Counter Offer</div>
                        <div style={{ display:"flex", gap:8, marginBottom:8 }}>
                          <div style={{ flex:1 }}>
                            <label style={{ fontSize:10, color:C.textMd, display:"block", marginBottom:3 }}>Your Counter Rate ($)</label>
                            <input type="number" value={counterAmt} onChange={e=>setCounterAmt(e.target.value)}
                              placeholder={String(Math.round(bid.amount * 0.95))}
                              style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"7px 10px", color:C.text, fontSize:14, fontFamily:C.mono, outline:"none", boxSizing:"border-box" }} />
                          </div>
                        </div>
                        <div style={{ marginBottom:8 }}>
                          <label style={{ fontSize:10, color:C.textMd, display:"block", marginBottom:3 }}>Note to carrier (optional)</label>
                          <textarea value={counterNote} onChange={e=>setCounterNote(e.target.value)} rows={2}
                            placeholder="Budget constraints, timeline requirements..."
                            style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"7px 10px", color:C.text, fontSize:12, outline:"none", resize:"none", fontFamily:C.sans, boxSizing:"border-box" }} />
                        </div>
                        <div style={{ display:"flex", gap:8 }}>
                          <Btn variant="ghost" size="sm" onClick={() => { setCounterBid(null); setCounterAmt(""); setCounterNote(""); }}>Cancel</Btn>
                          <Btn variant="primary" size="sm" style={{flex:1}} disabled={!counterAmt}
                            onClick={() => { onCounterBid && onCounterBid(bid, parseFloat(counterAmt), counterNote); setCounterBid(null); setCounterAmt(""); setCounterNote(""); }}>
                            Send Counter Offer
                          </Btn>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display:"flex", gap:8, marginTop:8 }}>
                        <Btn variant="success" size="sm" onClick={() => onAcceptBid(bid)}>✓ Accept {fmt$(bid.amount)}</Btn>
                        <Btn variant="outline" size="sm" onClick={() => { setCounterBid(bid); setCounterAmt(String(Math.round(bid.amount * 0.95))); }}>↩ Counter</Btn>
                        <Btn variant="ghost"   size="sm" onClick={() => onRejectBid && onRejectBid(bid)}>✕ Reject</Btn>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
        )}

        {tab === "timeline" && (
          <div>
            {[
              { type:"POSTED", message:`Load posted by ${load.shipper}`, time:load.postedAt, icon:"📋" },
              ...(load.status !== "POSTED" ? [{ type:"BIDDING", message:"Receiving carrier bids", time:"Recently", icon:"🏷" }] : []),
              ...(["IN_TRANSIT","DELIVERED"].includes(load.status) ? TRACKING_EVENTS.filter(e=>e.loadId===load.id).map(e=>({...e,icon:"📍"})) : []),
            ].reverse().map((ev, i) => (
              <div key={i} style={{ display:"flex", gap:12, marginBottom:14 }}>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:C.surf2, border:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12 }}>{ev.icon}</div>
                  {i < 3 && <div style={{ width:1, flex:1, background:C.border, margin:"4px 0" }} />}
                </div>
                <div style={{ paddingTop:4 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:C.text }}>{ev.message}</div>
                  {ev.location && <div style={{ fontSize:11, color:C.textMd, marginTop:2 }}>📍 {ev.location}</div>}
                  <div style={{ fontSize:11, color:C.textDm, marginTop:2 }}>{ev.time}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "docs" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <div style={{ fontSize:13, color:C.textMd }}>Load documents</div>
              <Btn variant="outline" size="sm" onClick={() => alert("Document upload will be available when connected to backend")}>+ Upload</Btn>
            </div>
            {["Rate Confirmation", "Bill of Lading"].map((docType, i) => (
              <div key={i} style={{ background:C.bg, borderRadius:8, padding:"11px 13px", border:`1px solid ${C.border}`, marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                  <div style={{ fontSize:22 }}>📄</div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500, color:C.text }}>{docType}</div>
                    <div style={{ fontSize:11, color:C.textMd }}>{load.number}_{docType.replace(/ /g,"_")}.pdf</div>
                  </div>
                </div>
                <Btn variant="ghost" size="sm">View</Btn>
              </div>
            ))}
            {load.status === "DELIVERED" && (
              <div style={{ background:C.bg, borderRadius:8, padding:"11px 13px", border:`2px dashed ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                  <div style={{ fontSize:22, opacity:0.4 }}>📄</div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500, color:C.textMd }}>Proof of Delivery</div>
                    <div style={{ fontSize:11, color:C.danger }}>Required for invoicing</div>
                  </div>
                </div>
                <Btn variant="outline" size="sm" onClick={() => alert("POD upload will be available when connected to backend")}>Upload POD</Btn>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Pages ────────────────────────────────────────────────────────────────────

const ShipperDashboard = ({ loads, setPage }) => {
  const { user: sdUser } = useAuth();
  const active = loads.filter(l => ["POSTED","BIDDING","BOOKED"].includes(l.status)).length;
  const transit = loads.filter(l => l.status === "IN_TRANSIT").length;
  const delivered = loads.filter(l => l.status === "DELIVERED").length;
  const totalSpend = loads.reduce((a,l) => a + (["DELIVERED","PAID"].includes(l.status) ? l.rate : 0), 0);
  const pendingBids = loads.reduce((a,l) => a + l.bids, 0);

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:20, fontWeight:700, color:C.text, margin:0 }}>Shipper Dashboard</h2>
        <p style={{ fontSize:13, color:C.textMd, marginTop:4 }}>{sdUser?.shipper?.companyName || sdUser?.email || "My Company"} — Overview</p>
      </div>
      <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap" }}>
        <StatCard label="Active Loads" value={active + transit} sub={`${active} open, ${transit} in transit`} color={C.accentLt} icon="📦" />
        <StatCard label="Delivered" value={delivered} sub="this period" color={C.success} icon="✅" />
        <StatCard label="Pending Bids" value={pendingBids} sub="awaiting review" color={C.warn} icon="🏷" />
        <StatCard label="Total Spend" value={`$${(totalSpend/1000).toFixed(1)}k`} sub="delivered loads" color={C.success} icon="💰" />
      </div>

      {/* Quick actions */}
      <div style={{ display:"flex", gap:10, marginBottom:20 }}>
        <Btn onClick={() => setPage("post")} variant="primary">+ Post New Load</Btn>
        <Btn onClick={() => setPage("tracking")} variant="ghost">📡 Live Tracking</Btn>
        <Btn onClick={() => setPage("invoices")} variant="ghost">🧾 View Invoices</Btn>
      </div>

      {/* Recent loads preview */}
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:13, fontWeight:600, color:C.text }}>Recent Loads</span>
          <Btn onClick={() => setPage("loads")} variant="ghost" size="sm">View All</Btn>
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:C.surf2 }}>
              {["Load #","Route","Equipment","Rate","Status","Bids","Pickup"].map(h => (
                <th key={h} style={{ padding:"8px 14px", fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em", textAlign:"left", fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loads.slice(0,6).map((l,i) => (
              <tr key={l.id} style={{ borderBottom:`1px solid ${C.border}`, background: i%2===0?"transparent":C.surf2 }}>
                <td style={{ padding:"10px 14px", fontSize:11, fontFamily:C.mono, color:C.textMd }}>{l.number}</td>
                <td style={{ padding:"10px 14px", fontSize:12, color:C.text, whiteSpace:"nowrap" }}>{l.origin.split(",")[0]} → {l.dest.split(",")[0]}</td>
                <td style={{ padding:"10px 14px" }}><EquipBadge type={l.equipmentType} /></td>
                <td style={{ padding:"10px 14px", fontSize:12, fontWeight:600, color:C.success, fontFamily:C.mono }}>{fmt$(l.rate)}</td>
                <td style={{ padding:"10px 14px" }}><Badge status={l.status} /></td>
                <td style={{ padding:"10px 14px", fontSize:12, color: l.bids>0?C.warn:C.textDm }}>{l.bids || "—"}</td>
                <td style={{ padding:"10px 14px", fontSize:12, color:C.textMd }}>{l.pickupDate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ─── Carrier Dispatch Page ────────────────────────────────────────────────────
const CarrierDispatchPage = ({ loads, trucks, drivers, savedState, onSaveState, onStatusChange }) => {
  const carrierTrucks  = trucks  || INIT_TRUCKS;
  const carrierDrivers = drivers || INIT_DRIVERS;
  const { token } = useAuth();

  // Active loads assigned to this carrier
  const activeLoads = loads
    .filter(l => ["BOOKED","AT_PICKUP","PICKED_UP","IN_TRANSIT","AT_DELIVERY","DELIVERED"].includes(l.status))
    .map(l => ({
      ...l,
      driver:      l.driver      || "",
      driverPhone: l.driverPhone || "",
      truck:       l.truck       || "",
      checkIns:    l.checkIns    || [],
      alerts:      l.alerts      || [],
      progress:    l.progress    || 0,
    }));

  const [dispLoads, setDispLoads] = useState(() => {
    const base = savedState && savedState.length > 0 ? savedState : activeLoads;
    // Always exclude cancelled loads from dispatch board regardless of saved state
    return base.filter(l => l.status !== "CANCELLED");
  });
  const [selected,  setSelected]    = useState(null);
  const [noteText,  setNoteText]    = useState("");
  const [showNote,  setShowNote]    = useState(false);
  const [filter,    setFilter]      = useState("ALL");
  const [detentionTimers, setDetentionTimers] = useState({});
  const FREE_TIME_MINUTES = 120;

  // Save to parent (localStorage) whenever dispLoads changes
  useEffect(() => { if (onSaveState) onSaveState(dispLoads); }, [dispLoads]); // eslint-disable-line

  // Sync when loads prop changes
  const loadsKey = loads.filter(l => ["BOOKED","AT_PICKUP","PICKED_UP","IN_TRANSIT","AT_DELIVERY","DELIVERED"].includes(l.status)).map(l=>l.id+l.status).join(",");
  useEffect(() => {
    setDispLoads(prev => {
      const prevMap = Object.fromEntries(prev.map(l => [l.id, l]));
      // Only add genuinely new loads — preserve all existing dispatch state
      const updatedExisting = prev.map(p => {
        const fresh = activeLoads.find(a => a.id === p.id);
        if (!fresh) return p.status === "CANCELLED" ? null : p; // drop cancelled, keep others
        return { ...fresh, driver:p.driver||"", driverPhone:p.driverPhone||"", truck:p.truck||"", checkIns:p.checkIns, alerts:p.alerts, status:p.status };
      }).filter(Boolean);
      const existingIds = new Set(prev.map(l => l.id));
      const newLoads = activeLoads.filter(l => !existingIds.has(l.id));
      return [...updatedExisting, ...newLoads];
    });
  }, [loadsKey]); // eslint-disable-line

  const displayed = filter === "ACTIVE"
    ? dispLoads.filter(l => l.status !== "DELIVERED")
    : dispLoads;

  const advanceStatus = async (loadId) => {
    const order = DISPATCH_STAGES.map(s => s.id);
    let nextStatus = null;
    setDispLoads(prev => prev.map(l => {
      if (l.id !== loadId) return l;
      const idx = order.indexOf(l.status);
      if (idx >= order.length - 1) return l;
      nextStatus = order[idx + 1];
      const stage = DISPATCH_STAGES.find(s => s.id === nextStatus);
      const newCheck = { time:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}), note:`Status updated to ${stage.label}`, type:nextStatus };
      if (nextStatus==="AT_PICKUP"||nextStatus==="AT_DELIVERY") setDetentionTimers(p=>({...p,[loadId]:{arrivedAt:Date.now(),stage:nextStatus,alerted:false}}));
      if (nextStatus==="PICKED_UP"||nextStatus==="DELIVERED") setDetentionTimers(p=>{const n={...p};delete n[loadId];return n;});
      return { ...l, status:nextStatus, checkIns:[...l.checkIns, newCheck], alerts:[] };
    }));
    setSelected(prev => {
      if (!prev||prev.id!==loadId) return prev;
      const idx2 = order.indexOf(prev.status);
      const ns = order[Math.min(idx2+1, order.length-1)];
      if (onStatusChange) onStatusChange(loadId, ns);
      return { ...prev, status:ns, alerts:[] };
    });
    if (token && nextStatus) {
      try {
        await fetch(`${API_BASE}/tracking/dispatch/${loadId}/status`, {
          method:"POST",
          headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
          body:JSON.stringify({ status:nextStatus, note:`Carrier advanced to ${nextStatus}` }),
        });
      } catch(e) { console.warn("Status sync failed:", e.message); }
    }
  };

  const assign = async (loadId, field, value, extra={}) => {
    const note = { time:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}), note:`${field} assigned: ${value}`, type:"NOTE" };
    setDispLoads(prev => prev.map(l => l.id===loadId ? {...l, [field.toLowerCase()]:value, ...extra, checkIns:[...l.checkIns, note]} : l));
    setSelected(prev => prev?.id===loadId ? {...prev, [field.toLowerCase()]:value, ...extra} : prev);

    // Persist driver assignment to backend so driver sees their load on login
    if (field === "Driver" && token && value) {
      try {
        // Find driver in fleet to get their email/id
        const driver = carrierDrivers.find(d => `${d.firstName} ${d.lastName}` === value);
        await fetch(`${API_BASE}/tracking/dispatch/${loadId}/assign-driver`, {
          method: "POST",
          headers: { "Content-Type":"application/json", Authorization:`Bearer ${token}` },
          body: JSON.stringify({ driverName: value, driverId: driver?.id, driverPhone: extra.driverPhone }),
        });
      } catch(e) { console.warn("Driver assignment sync failed:", e.message); }
    }
  };

  const addNote = (loadId) => {
    if (!noteText.trim()) return;
    const newCheck = { time:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}), note:noteText.trim(), type:"NOTE" };
    setDispLoads(prev => prev.map(l => l.id===loadId ? {...l, checkIns:[...l.checkIns, newCheck]} : l));
    setSelected(prev => prev?.id===loadId ? {...prev, checkIns:[...prev.checkIns, newCheck]} : prev);
    setNoteText(""); setShowNote(false);
  };

  const stepBack = async (loadId) => {
    const order = DISPATCH_STAGES.map(s => s.id);
    let prevStatus = null;
    setDispLoads(prev => prev.map(l => {
      if (l.id !== loadId) return l;
      const idx = order.indexOf(l.status);
      if (idx <= 0) return l;
      prevStatus = order[idx - 1];
      const stage = DISPATCH_STAGES.find(s => s.id === prevStatus);
      const newCheck = { time:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}), note:`⬅ Status moved back to ${stage.label}`, type:"NOTE" };
      return { ...l, status:prevStatus, checkIns:[...l.checkIns, newCheck] };
    }));
    setSelected(prev => {
      if (!prev||prev.id!==loadId) return prev;
      const idx2 = order.indexOf(prev.status);
      const ps = order[Math.max(0, idx2-1)];
      if (onStatusChange) onStatusChange(loadId, ps);
      return { ...prev, status:ps };
    });
    if (token && prevStatus) {
      try {
        await fetch(`${API_BASE}/tracking/dispatch/${loadId}/status`, {
          method:"POST",
          headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
          body:JSON.stringify({ status:prevStatus, note:"Status moved back by carrier" }),
        });
      } catch(e) { console.warn("Step back sync failed:", e.message); }
    }
  };

  const [showReBook, setShowReBook]   = useState(false);
  const [reBookReason, setReBookReason] = useState("");

  const confirmReBook = () => {
    if (!reBookReason) return;
    const note1 = { time:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}), note:`⚠ Re-booked by carrier: ${reBookReason}`, type:"NOTE" };
    const updated = { ...selected, status:"BOOKED", driver:"", driverPhone:"", truck:"", alerts:[], checkIns:[...selected.checkIns, note1] };
    setDispLoads(prev => prev.map(l => l.id===selected.id ? updated : l));
    setSelected(updated);
    if (onStatusChange) onStatusChange(selected.id, "BOOKED");
    setShowReBook(false);
    setReBookReason("");
  };

  const [showCancelCDP, setShowCancelCDP] = useState(false);
  const [cancelReasonCDP, setCancelReasonCDP] = useState("");
  const [cancellingCDP, setCancellingCDP] = useState(false);

  const cancelLoadCDP = async () => {
    if (!selected) return;
    setCancellingCDP(true);
    const isSampleLoad = selected.id.startsWith("DL") || INIT_DISPATCH_LOADS.some(l=>l.id===selected.id);
    try {
      if (token && !isSampleLoad) {
        await fetch(`${API_BASE}/tracking/dispatch/${selected.id}/cancel`, {
          method: "POST",
          headers: { "Content-Type":"application/json", Authorization:`Bearer ${token}` },
          body: JSON.stringify({ reason: cancelReasonCDP || "Cancelled by carrier" }),
        });
      }
      setDispLoads(prev => prev.filter(l => l.id !== selected.id));
      if (onStatusChange) onStatusChange(selected.id, "CANCELLED");
      setSelected(null);
      setShowCancelCDP(false);
      setCancelReasonCDP("");
    } catch(e) {
      console.warn("Cancel load failed:", e.message);
    } finally {
      setCancellingCDP(false);
    }
  };

  const stageColor = (s) => DISPATCH_STAGES.find(d=>d.id===s)?.color || C.textMd;
  const stageLabel = (s) => DISPATCH_STAGES.find(d=>d.id===s)?.label || s;
  const stageIcon  = (s) => DISPATCH_STAGES.find(d=>d.id===s)?.icon  || "●";
  const nextStage  = (s) => { const idx = DISPATCH_STAGES.findIndex(d=>d.id===s); return idx < DISPATCH_STAGES.length-1 ? DISPATCH_STAGES[idx+1] : null; };
  const prevStage  = (s) => { const idx = DISPATCH_STAGES.findIndex(d=>d.id===s); return idx > 0 ? DISPATCH_STAGES[idx-1] : null; };
  const checkColor = (type) => ({AT_PICKUP:"#f59e0b",IN_TRANSIT:"#3b82f6",PICKED_UP:"#8b5cf6",AT_DELIVERY:"#f97316",DELIVERED:"#10b981",NOTE:"#94a3b8",BOOKED:"#3b82f6"}[type]||"#94a3b8");

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:0 }}>Carrier Dispatch</h2>
          <p style={{ fontSize:13, color:C.textMd, margin:"3px 0 0" }}>
            {dispLoads.filter(l=>l.status!=="DELIVERED").length} active · {dispLoads.filter(l=>l.status==="DELIVERED").length} delivered
          </p>
        </div>
        <div style={{ display:"flex", gap:0, background:C.bg, borderRadius:8, padding:3, border:`1px solid ${C.border}` }}>
          {[["ALL","All Loads"],["ACTIVE","Active Only"]].map(([id,label]) => (
            <button key={id} onClick={()=>setFilter(id)} style={{
              background:filter===id?C.accent:"transparent", color:filter===id?"#fff":C.textMd,
              border:"none", borderRadius:5, padding:"4px 12px", fontSize:12, fontWeight:600, cursor:"pointer",
            }}>{label}</button>
          ))}
        </div>
      </div>

      {dispLoads.length === 0 && (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:"48px 24px", textAlign:"center" }}>
          <div style={{ fontSize:48, opacity:0.15, marginBottom:12 }}>🎯</div>
          <div style={{ fontSize:15, fontWeight:600, color:C.text, marginBottom:6 }}>No active loads</div>
          <div style={{ fontSize:13, color:C.textMd }}>Accepted loads will appear here for dispatching.</div>
        </div>
      )}

      {dispLoads.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 380px", gap:14, alignItems:"start" }}>
          {/* Kanban */}
          <div style={{ overflowX:"auto" }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:8, minWidth:700 }}>
              {DISPATCH_STAGES.map(stage => {
                const stagLoads = displayed.filter(l => l.status === stage.id);
                return (
                  <div key={stage.id}>
                    <div style={{ background:C.surf2, borderRadius:"8px 8px 0 0", padding:"8px 10px", borderBottom:`2px solid ${stage.color}`, marginBottom:8 }}>
                      <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:stage.color }}>{stage.icon} {stage.label}</div>
                      <div style={{ fontSize:10, color:C.textDm, marginTop:2 }}>{stagLoads.length} load{stagLoads.length!==1?"s":""}</div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      {stagLoads.map(l => (
                        <div key={l.id} onClick={() => setSelected(l)} style={{
                          background:selected?.id===l.id?C.surf3:C.surface,
                          border:`1px solid ${selected?.id===l.id?stage.color:C.border}`,
                          borderLeft:`3px solid ${stage.color}`,
                          borderRadius:7, padding:"9px 10px", cursor:"pointer",
                        }}>
                          <div style={{ fontSize:11, fontFamily:C.mono, color:C.textDm, marginBottom:3 }}>{l.number}</div>
                          <div style={{ fontSize:11, fontWeight:600, color:C.text, marginBottom:3, lineHeight:1.3 }}>
                            {(l.origin||"").split(",")[0]} →<br/>{(l.dest||"").split(",")[0]}
                          </div>
                          <div style={{ fontSize:10, color:l.driver?C.textMd:C.warn }}>
                            {l.driver || "⚠ No driver"}
                          </div>
                          <div style={{ fontSize:10, color:C.textDm, marginTop:3 }}>{l.pickupDate}</div>
                        </div>
                      ))}
                      {stagLoads.length===0 && (
                        <div style={{ background:C.bg, borderRadius:7, padding:"16px 8px", textAlign:"center", fontSize:10, color:C.textDm, border:`1px dashed ${C.border}` }}>Empty</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          {selected ? (
            <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:12, overflow:"hidden", display:"flex", flexDirection:"column", maxHeight:"80vh" }}>
              <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.border}`, background:`${stageColor(selected.status)}11` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                  <div>
                    <div style={{ fontSize:10, fontFamily:C.mono, color:C.textDm }}>{selected.number}</div>
                    <div style={{ fontSize:15, fontWeight:700, color:C.text }}>{(selected.origin||"").split(",")[0]} → {(selected.dest||"").split(",")[0]}</div>
                  </div>
                  <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                    <span style={{ fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:5, textTransform:"uppercase",
                      background:`${stageColor(selected.status)}22`, color:stageColor(selected.status), border:`1px solid ${stageColor(selected.status)}44` }}>
                      {stageIcon(selected.status)} {stageLabel(selected.status)}
                    </span>
                    <button onClick={()=>setSelected(null)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"3px 8px", cursor:"pointer", fontSize:11 }}>✕</button>
                  </div>
                </div>
              </div>

              <div style={{ flex:1, overflowY:"auto", padding:16 }}>
                {/* Shipper info */}
                <div style={{ background:C.bg, borderRadius:7, padding:"9px 12px", border:`1px solid ${C.border}`, marginBottom:10 }}>
                  <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>📦 Shipper</div>
                  <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{selected.shipper || "—"}</div>
                  {selected.shipperPhone && <a href={`tel:${selected.shipperPhone}`} style={{ fontSize:11, color:C.accentLt, textDecoration:"none" }}>{selected.shipperPhone}</a>}
                </div>

                {/* Driver & Truck assignment */}
                {(() => {
                  const missingDriver = !selected.driver;
                  const missingTruck  = !selected.truck;
                  const allAssigned   = !missingDriver && !missingTruck;
                  return (
                    <div style={{ background:C.bg, borderRadius:8, padding:"11px 13px", border:`1px solid ${allAssigned?C.success:C.warn}44`, marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                        <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em" }}>Assignment</div>
                        {allAssigned
                          ? <span style={{ fontSize:10, color:C.success, fontWeight:700 }}>✓ Ready</span>
                          : <span style={{ fontSize:10, color:C.warn, fontWeight:700 }}>⚠ {[missingDriver&&"Driver",missingTruck&&"Truck"].filter(Boolean).join(", ")} needed</span>}
                      </div>
                      <div style={{ marginBottom:8 }}>
                        <label style={{ fontSize:10, color:C.textMd, display:"block", marginBottom:4 }}>👤 Driver</label>
                        <select value={selected.driver||""} onChange={e => {
                          const d = carrierDrivers.find(dr=>`${dr.firstName} ${dr.lastName}`===e.target.value);
                          assign(selected.id, "Driver", e.target.value, { driverPhone:d?.phone||"" });
                        }} style={{ width:"100%", background:C.surface, border:`1px solid ${missingDriver?C.warn:C.border}`, borderRadius:6, padding:"7px 10px", color:C.text, fontSize:12, outline:"none" }}>
                          <option value="">— Select driver —</option>
                          {carrierDrivers.filter(d=>d.status!=="OFF_DUTY").map(d=>(
                            <option key={d.id} value={`${d.firstName} ${d.lastName}`}>
                              {d.firstName} {d.lastName} · {d.status==="AVAILABLE"?"✓ Available":"● On Duty"} · CDL-{d.cdlClass}
                            </option>
                          ))}
                        </select>
                        {selected.driver && (
                          <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
                            <span style={{ fontSize:11, color:C.success }}>✓ {selected.driver}</span>
                            {selected.driverPhone && <a href={`tel:${selected.driverPhone}`} style={{ fontSize:11, color:C.accentLt, textDecoration:"none" }}>{selected.driverPhone}</a>}
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={{ fontSize:10, color:C.textMd, display:"block", marginBottom:4 }}>🚛 Truck</label>
                        <select value={selected.truck||""} onChange={e => assign(selected.id, "Truck", e.target.value)}
                          style={{ width:"100%", background:C.surface, border:`1px solid ${missingTruck?C.warn:C.border}`, borderRadius:6, padding:"7px 10px", color:C.text, fontSize:12, outline:"none" }}>
                          <option value="">— Select truck —</option>
                          {carrierTrucks.filter(t=>t.status==="AVAILABLE").map(t=>(
                            <option key={t.id} value={t.number}>{t.number} · {t.year} {t.make} {t.model}</option>
                          ))}
                        </select>
                        {selected.truck && <span style={{ fontSize:11, color:C.success, marginTop:4, display:"block" }}>✓ {selected.truck}</span>}
                      </div>
                    </div>
                  );
                })()}

                {/* Load info */}
                <div style={{ background:C.bg, borderRadius:7, padding:"9px 12px", border:`1px solid ${C.border}`, marginBottom:10 }}>
                  <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
                    {[
                      { label:"Pickup",   value:`${selected.pickupDate||""} ${selected.pickupTime||""}`.trim() },
                      { label:"Delivery", value:`${selected.deliveryDate||""} ${selected.deliveryTime||""}`.trim() },
                      { label:"Weight",   value:selected.weight ? fmtLb(selected.weight) : "—" },
                      { label:"Miles",    value:selected.miles  ? fmtMi(selected.miles)  : "—" },
                      { label:"Rate",     value:fmt$(selected.rate||0) },
                    ].map(r => (
                      <div key={r.label}>
                        <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em" }}>{r.label}</div>
                        <div style={{ fontSize:11, fontWeight:600, color:C.text }}>{r.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Advance / Step Back / Re-book */}
                {selected.status !== "DELIVERED" && (() => {
                  const next = nextStage(selected.status);
                  const prev = prevStage(selected.status);
                  const canAdvance = !!selected.driver && !!selected.truck;
                  return (
                    <div style={{ marginBottom:12 }}>
                      {/* Advance */}
                      {next && (
                        <button onClick={() => canAdvance && advanceStatus(selected.id)} disabled={!canAdvance}
                          style={{
                            width:"100%", background:canAdvance?`${next.color}22`:C.surf2,
                            border:`1px solid ${canAdvance?next.color+"44":C.border}`,
                            color:canAdvance?next.color:C.textDm,
                            borderRadius:8, padding:"10px", fontSize:13, fontWeight:700,
                            cursor:canAdvance?"pointer":"not-allowed", fontFamily:C.sans,
                            opacity:canAdvance?1:0.6, marginBottom:8,
                          }}>
                          {next.icon} Advance to {next.label} →
                        </button>
                      )}
                      {!canAdvance && next && (
                        <div style={{ fontSize:11, color:C.warn, marginBottom:8, textAlign:"center" }}>Assign driver and truck before advancing</div>
                      )}
                      {/* Step Back + Re-book */}
                      <div style={{ display:"flex", gap:8 }}>
                        {prev && selected.status !== "BOOKED" && (
                          <button onClick={() => stepBack(selected.id)} style={{
                            flex:1, background:"transparent",
                            border:`1px solid ${C.border}`, color:C.textMd,
                            borderRadius:7, padding:"7px", fontSize:12, fontWeight:600,
                            cursor:"pointer", fontFamily:C.sans,
                          }}>
                            ← Back to {prev.label}
                          </button>
                        )}
                        <button onClick={() => setShowReBook(true)} style={{
                          flex:1, background:"rgba(239,68,68,0.1)",
                          border:"1px solid rgba(239,68,68,0.3)", color:C.danger,
                          borderRadius:7, padding:"7px", fontSize:12, fontWeight:600,
                          cursor:"pointer", fontFamily:C.sans,
                        }}>
                          🔄 Re-book
                        </button>
                        <button onClick={() => setShowCancelCDP(true)} style={{
                          flex:1, background:"transparent",
                          border:`1px solid ${C.border}`, color:C.textDm,
                          borderRadius:7, padding:"7px", fontSize:12, fontWeight:600,
                          cursor:"pointer", fontFamily:C.sans,
                        }}>
                          🗑 Cancel
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Activity log */}
                <div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:C.text }}>Activity Log</div>
                    <button onClick={()=>setShowNote(n=>!n)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:5, padding:"3px 10px", fontSize:11, cursor:"pointer" }}>+ Note</button>
                  </div>
                  {showNote && (
                    <div style={{ marginBottom:10 }}>
                      <textarea value={noteText} onChange={e=>setNoteText(e.target.value)} rows={2}
                        placeholder="Check-in note, exception, update..."
                        style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", color:C.text, fontSize:12, outline:"none", resize:"none", fontFamily:C.sans, boxSizing:"border-box", marginBottom:6 }} />
                      <div style={{ display:"flex", gap:6 }}>
                        <button onClick={()=>{setShowNote(false);setNoteText("");}} style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.textMd, borderRadius:5, padding:"4px 10px", fontSize:11, cursor:"pointer" }}>Cancel</button>
                        <button onClick={()=>addNote(selected.id)} disabled={!noteText.trim()} style={{ flex:1, background:C.accent, border:"none", color:"#fff", borderRadius:5, padding:"4px 10px", fontSize:11, fontWeight:600, cursor:"pointer", opacity:noteText.trim()?1:0.5 }}>Log</button>
                      </div>
                    </div>
                  )}
                  <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                    {[...selected.checkIns].reverse().map((c,i) => (
                      <div key={i} style={{ display:"flex", gap:10, paddingBottom:10 }}>
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flexShrink:0 }}>
                          <div style={{ width:8, height:8, borderRadius:"50%", background:checkColor(c.type), marginTop:3, flexShrink:0 }} />
                          {i < selected.checkIns.length-1 && <div style={{ width:1, flex:1, background:C.border, margin:"3px 0" }} />}
                        </div>
                        <div>
                          <div style={{ fontSize:11, color:i===0?C.text:C.textMd }}>{c.note}</div>
                          <div style={{ fontSize:10, color:C.textDm, marginTop:1 }}>{c.time}</div>
                        </div>
                      </div>
                    ))}
                    {selected.checkIns.length===0 && <div style={{ fontSize:12, color:C.textDm }}>No activity yet.</div>}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:"40px 20px", textAlign:"center" }}>
              <div style={{ fontSize:40, opacity:0.12, marginBottom:12 }}>🎯</div>
              <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:6 }}>Select a load to dispatch</div>
              <div style={{ fontSize:13, color:C.textMd }}>Click any load card to assign driver, truck and advance status.</div>
            </div>
          )}
        </div>
      )}

      {/* Re-book modal */}
      {showReBook && selected && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={e=>e.target===e.currentTarget&&setShowReBook(false)}>
          <div style={{background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:16,width:440,overflow:"hidden"}}>
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,background:"rgba(239,68,68,0.08)"}}>
              <div style={{fontSize:16,fontWeight:700,color:C.danger}}>🔄 Re-book Load</div>
              <div style={{fontSize:12,color:C.textMd,marginTop:3}}>{selected.number} · {(selected.origin||"").split(",")[0]} → {(selected.dest||"").split(",")[0]}</div>
            </div>
            <div style={{padding:20}}>
              <div style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"10px 14px",marginBottom:16}}>
                <div style={{fontSize:12,color:C.danger,fontWeight:600,marginBottom:3}}>Load will return to Booked status</div>
                <div style={{fontSize:12,color:C.textMd}}>Driver and truck assignment will be cleared.</div>
              </div>
              <div style={{marginBottom:16}}>
                <label style={{fontSize:11,color:C.textMd,display:"block",marginBottom:8}}>Reason for re-booking</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                  {["Driver unavailable","Truck breakdown","Wrong truck assigned","Schedule change","Customer request"].map(r=>(
                    <button key={r} onClick={()=>setReBookReason(r)} style={{background:reBookReason===r?"rgba(239,68,68,0.2)":C.bg,border:`1px solid ${reBookReason===r?C.danger:C.border}`,color:reBookReason===r?C.danger:C.textMd,borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer"}}>{r}</button>
                  ))}
                </div>
                <input value={reBookReason} onChange={e=>setReBookReason(e.target.value)} placeholder="Or type a custom reason..."
                  style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",color:C.text,fontSize:12,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{display:"flex",gap:10}}>
                <Btn variant="ghost" onClick={()=>{setShowReBook(false);setReBookReason("");}}>Cancel</Btn>
                <button disabled={!reBookReason} onClick={confirmReBook} style={{
                  flex:1, background:reBookReason?C.danger:"transparent",
                  border:`1px solid ${reBookReason?C.danger:C.border}`, color:reBookReason?"#fff":C.textDm,
                  borderRadius:8, padding:"9px", fontSize:13, fontWeight:700,
                  cursor:reBookReason?"pointer":"not-allowed", fontFamily:C.sans, opacity:reBookReason?1:0.5,
                }}>Confirm Re-book</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel load modal */}
      {showCancelCDP && selected && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={e=>e.target===e.currentTarget&&setShowCancelCDP(false)}>
          <div style={{background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:16,width:440,overflow:"hidden"}}>
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,background:"rgba(239,68,68,0.08)"}}>
              <div style={{fontSize:16,fontWeight:700,color:C.danger}}>🗑 Cancel Load</div>
              <div style={{fontSize:12,color:C.textMd,marginTop:3}}>{selected.number} · {(selected.origin||"").split(",")[0]} → {(selected.dest||"").split(",")[0]}</div>
            </div>
            <div style={{padding:20}}>
              <div style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"10px 14px",marginBottom:16}}>
                <div style={{fontSize:12,color:C.danger,fontWeight:600,marginBottom:3}}>This load will be removed from dispatch</div>
                <div style={{fontSize:12,color:C.textMd}}>Status will be set to Cancelled. This cannot be undone from here.</div>
              </div>
              <div style={{marginBottom:16}}>
                <label style={{fontSize:11,color:C.textMd,display:"block",marginBottom:8}}>Reason for cancellation</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                  {["Shipper cancelled","Equipment issue","Duplicate load","Test data","Other"].map(r=>(
                    <button key={r} onClick={()=>setCancelReasonCDP(r)} style={{background:cancelReasonCDP===r?"rgba(239,68,68,0.2)":C.bg,border:`1px solid ${cancelReasonCDP===r?C.danger:C.border}`,color:cancelReasonCDP===r?C.danger:C.textMd,borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer"}}>{r}</button>
                  ))}
                </div>
                <input value={cancelReasonCDP} onChange={e=>setCancelReasonCDP(e.target.value)} placeholder="Or type a custom reason..."
                  style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",color:C.text,fontSize:12,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{display:"flex",gap:10}}>
                <Btn variant="ghost" onClick={()=>{setShowCancelCDP(false);setCancelReasonCDP("");}}>Keep Load</Btn>
                <button disabled={cancellingCDP} onClick={cancelLoadCDP} style={{
                  flex:1, background:C.danger, border:"none", color:"#fff",
                  borderRadius:8, padding:"9px", fontSize:13, fontWeight:700,
                  cursor:cancellingCDP?"not-allowed":"pointer", fontFamily:C.sans, opacity:cancellingCDP?0.6:1,
                }}>{cancellingCDP?"Cancelling…":"Confirm Cancellation"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CarrierDashboard = ({ loads, earnings, setPage, carrierId, dismissedLoads, dismissLoad }) => {
  const { user: cdUser } = useAuth();
  const myCarrierId = carrierId || "C1";
  const wonLoads = loads.filter(l => l.carrierId === myCarrierId && !(dismissedLoads||[]).includes(l.id));
  const active = wonLoads.filter(l => l.status === "IN_TRANSIT").length;
  const totalEarned = earnings.reduce((a,e) => a + e.net, 0);

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:20, fontWeight:700, color:C.text, margin:0 }}>Carrier Dashboard</h2>
        <p style={{ fontSize:13, color:C.textMd, marginTop:4 }}>{cdUser?.carrier?.companyName || cdUser?.email || "My Fleet"} — Overview</p>
      </div>
      <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap" }}>
        <StatCard label="Active Loads" value={active} sub="in transit now" color={C.success} icon="🚛" />
        <StatCard label="Total Earned" value={`$${(totalEarned/1000).toFixed(1)}k`} sub="net after fees" color={C.success} icon="💰" />
        <StatCard label="Rating" value="4.9" sub="312 deliveries" color={C.warn} icon="⭐" />
        <StatCard label="Bid Win Rate" value="68%" sub="last 30 days" color={C.accentLt} icon="🏆" />
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:600, color:C.text }}>Active Shipments</div>
            {wonLoads.filter(l=>["IN_TRANSIT","BOOKED","AT_PICKUP","PICKED_UP","AT_DELIVERY"].includes(l.status)).length > 0 && (
              <button onClick={() => setPage("tracking")} style={{ background:"transparent", border:"none", color:C.accentLt, fontSize:12, cursor:"pointer", padding:0 }}>
                Track on map →
              </button>
            )}
          </div>
          {wonLoads.filter(l=>["IN_TRANSIT","BOOKED","AT_PICKUP","PICKED_UP","AT_DELIVERY"].includes(l.status)).map(l => (
            <div key={l.id} style={{
              marginBottom:10, paddingBottom:10, borderBottom:`1px solid ${C.border}`,
              borderRadius:6, padding:"8px",
            }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <div style={{ cursor:"pointer", flex:1 }} onClick={() => setPage("dispatch")}>
                  <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{(l.origin||"").split(",")[0]} → {(l.dest||"").split(",")[0]}</div>
                  <div style={{ fontSize:10, color:C.textDm, fontFamily:C.mono }}>{l.number}</div>
                </div>
                <div style={{ display:"flex", alignItems:"flex-start", gap:6 }}>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:12, fontFamily:C.mono, color:C.success }}>{fmt$(l.rate)}</div>
                    <Badge status={l.status} />
                  </div>
                  <button onClick={e => { e.stopPropagation(); if(dismissLoad) dismissLoad(l.id); }}
                    title="Remove from dashboard"
                    style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.textDm, borderRadius:5, padding:"2px 6px", fontSize:10, cursor:"pointer", marginTop:2, lineHeight:1 }}>
                    ✕
                  </button>
                </div>
              </div>
              <ProgressBar pct={l.progress || 0} />
              <div style={{ fontSize:11, color:C.textMd, marginTop:3, display:"flex", justifyContent:"space-between" }}>
                <span>{l.progress || 0}% complete</span>
                <span>ETA {l.deliveryDate}</span>
              </div>
              {l.driver && <div style={{ fontSize:11, color:C.textDm, marginTop:2 }}>👤 {l.driver}</div>}
            </div>
          ))}
          {wonLoads.filter(l=>["IN_TRANSIT","BOOKED","AT_PICKUP","PICKED_UP","AT_DELIVERY"].includes(l.status)).length === 0 && (
            <div style={{ fontSize:13, color:C.textDm, padding:"12px 0", textAlign:"center" }}>
              No active shipments yet.<br/>
              <span style={{ fontSize:12 }}>Win bids on the <button onClick={()=>setPage("loadboard")} style={{background:"none",border:"none",color:C.accentLt,cursor:"pointer",fontSize:12}}>Load Board</button> to get started.</span>
            </div>
          )}
        </div>
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:16 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:12 }}>Recent Earnings</div>
          {earnings.map(e => (
            <div key={e.id} style={{ display:"flex", justifyContent:"space-between", marginBottom:10, paddingBottom:10, borderBottom:`1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize:12, color:C.text }}>{e.route}</div>
                <div style={{ fontSize:11, color:C.textDm }}>{e.date}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.success, fontFamily:C.mono }}>{fmt$(e.net)}</div>
                <div style={{ fontSize:10, color:C.textDm }}>fee: {fmt$(e.fee)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const LoadBoardPage = ({ loads, bids, role, onBidSubmit, onAcceptBid, onBook, onRejectBid, onCounterBid }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  // Derive from loads so status changes (booking, etc.) reflect immediately
  const selected = loads.find(l => l.id === selectedId) ?? null;

  const EQUIP_FILTERS = ["ALL","DRY_VAN","FLATBED","REEFER","TANKER"];
  const available = role === "CARRIER"
    ? loads.filter(l => ["POSTED","BIDDING"].includes(l.status))
    : loads;

  const filtered = available.filter(l => {
    const matchEquip = filter === "ALL" || l.equipmentType === filter;
    const matchSearch = !search || l.origin.toLowerCase().includes(search.toLowerCase()) || l.dest.toLowerCase().includes(search.toLowerCase()) || l.commodity?.toLowerCase().includes(search.toLowerCase());
    return matchEquip && matchSearch;
  });

  return (
    <div style={{ display:"flex", gap:14, height:"100%" }}>
      {/* Load List */}
      <div style={{ width:340, flexShrink:0, display:"flex", flexDirection:"column", gap:10 }}>
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"8px 10px" }}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search loads, routes, commodities..."
            style={{ width:"100%", background:"transparent", border:"none", color:C.text, fontSize:12, outline:"none", fontFamily:C.sans }} />
        </div>
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
          {EQUIP_FILTERS.map(f => (
            <button key={f} onClick={()=>setFilter(f)} style={{
              background:filter===f?C.accent:"transparent", border:`1px solid ${filter===f?C.accent:C.border}`,
              color:filter===f?"#fff":C.textMd, borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:500, cursor:"pointer",
            }}>{f === "ALL" ? "All Types" : EQUIP_META[f]?.label}</button>
          ))}
        </div>
        <div style={{ fontSize:11, color:C.textDm }}>{filtered.length} loads</div>
        <div style={{ display:"flex", flexDirection:"column", gap:8, overflowY:"auto", flex:1 }}>
          {filtered.map(l => (
            <LoadCard key={l.id} load={l} selected={selectedId===l.id} onClick={()=>setSelectedId(l.id)} role={role} />
          ))}
          {filtered.length === 0 && <div style={{ textAlign:"center", color:C.textDm, padding:32, fontSize:13 }}>No loads match your filters</div>}
        </div>
      </div>

      {/* Detail */}
      {selected
        ? <LoadDetail load={selected} role={role} bids={bids} onAcceptBid={onAcceptBid} onBidSubmit={onBidSubmit} onClose={()=>setSelectedId(null)} onBook={onBook} onRejectBid={onRejectBid} onCounterBid={onCounterBid} />
        : <div style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:8 }}>
            <div style={{ fontSize:36, opacity:0.15 }}>📋</div>
            <div style={{ fontSize:13, color:C.textDm }}>Select a load to view details</div>
          </div>
      }
    </div>
  );
};

// ─── Post Load helpers (module level — must NOT be inside component) ──────────
const PLField = ({ label, children, span }) => (
  <div style={{ gridColumn: span ? `span ${span}` : "auto" }}>
    <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 }}>{label}</label>
    {children}
  </div>
);

const PLInp = ({ name, value, onChange, placeholder, type="text" }) => (
  <input
    name={name} type={type} value={value} onChange={onChange}
    placeholder={placeholder} autoComplete="off"
    style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6,
      padding:"8px 10px", color:C.text, fontSize:13, outline:"none",
      boxSizing:"border-box", fontFamily:type==="number"?C.mono:C.sans }} />
);

const PLSel = ({ name, value, onChange, options }) => (
  <select name={name} value={value} onChange={onChange}
    style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6,
      padding:"8px 10px", color:C.text, fontSize:13, outline:"none" }}>
    {options.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
  </select>
);

const PLSection = ({ title, sub }) => (
  <div style={{ marginBottom:16 }}>
    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{title}</div>
    {sub && <div style={{ fontSize:12, color:C.textMd, marginTop:2 }}>{sub}</div>}
  </div>
);

const PLTextarea = ({ name, value, onChange, placeholder, rows=2 }) => (
  <textarea name={name} value={value} onChange={onChange} rows={rows}
    placeholder={placeholder}
    style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6,
      padding:"8px 10px", color:C.text, fontSize:13, outline:"none",
      resize:"none", fontFamily:C.sans, boxSizing:"border-box" }} />
);

const PostLoadPage = ({ onPost, role }) => {
  const isBroker = role === "BROKER";

  const BLANK = {
    shipperId:"", shipperName:"", shipperContact:"", shipperEmail:"", shipperPhone:"",
    shipperRefNum:"", shipperPoNum:"",
    originAddress:"", originCity:"", originState:"", originZip:"",
    pickupDate:"", pickupTimeStart:"", pickupTimeEnd:"",
    pickupContactName:"", pickupContactPhone:"", pickupFacilityName:"",
    pickupInstructions:"",
    destAddress:"", destCity:"", destState:"", destZip:"",
    deliveryDate:"", deliveryTimeStart:"", deliveryTimeEnd:"",
    deliveryContactName:"", deliveryContactPhone:"", deliveryFacilityName:"",
    deliveryInstructions:"",
    commodity:"", weight:"", pallets:"", length:"", hazmat:false,
    tempMin:"", tempMax:"",
    equipmentType:"DRY_VAN",
    shipperRate:"", targetCarrierRate:"", rate:"", urgency:"STANDARD",
    paymentTerms:"NET30", notes:"", specialInstructions:"",
  };

  const [form, setForm] = useState(BLANK);
  const [step, setStep] = useState(1);
  const [posted, setPosted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onPickShipper = (s) => setForm(f => ({
    ...f, shipperId:s.id, shipperName:s.company,
    shipperContact:s.contact, shipperEmail:s.email, shipperPhone:s.phone,
  }));

  const margin = form.shipperRate && form.targetCarrierRate
    ? parseFloat(form.shipperRate) - parseFloat(form.targetCarrierRate) : null;
  const marginPct = margin !== null && parseFloat(form.shipperRate) > 0
    ? Math.round(margin / parseFloat(form.shipperRate) * 100) : null;

  const STEPS = isBroker
    ? ["Shipper Customer","Route & Schedule","Cargo Details","Rates & Terms"]
    : ["Route & Schedule","Cargo Details","Rate & Priority"];

  // ── Validation helpers ────────────────────────────────────────────────────
  const isValidZip  = v => /^\d{5}(-\d{4})?$/.test(v.trim());
  const isValidDate = v => {
    if (!v) return false;
    const d = new Date(v);
    return !isNaN(d.getTime()) && d >= new Date(new Date().setHours(0,0,0,0));
  };
  const isDeliveryAfterPickup = () => {
    if (!form.pickupDate || !form.deliveryDate) return true;
    return new Date(form.deliveryDate) >= new Date(form.pickupDate);
  };
  const isValidEmail = v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const isValidPhone = v => !v || /^[\d\s\(\)\-\+]{7,}$/.test(v);
  const isValidRate  = v => !v || (parseFloat(v) > 0 && parseFloat(v) < 100000);
  const isValidWeight= v => !v || (parseInt(v) > 0 && parseInt(v) <= 80000);

  // Per-field errors shown inline
  const fieldErrors = {
    originZip:     form.originZip     && !isValidZip(form.originZip)     ? "Enter a valid 5-digit ZIP" : null,
    destZip:       form.destZip       && !isValidZip(form.destZip)        ? "Enter a valid 5-digit ZIP" : null,
    pickupDate:    form.pickupDate    && !isValidDate(form.pickupDate)    ? "Date must be today or later" : null,
    deliveryDate:  form.deliveryDate  && !isValidDate(form.deliveryDate)  ? "Date must be today or later"
                 : form.deliveryDate  && !isDeliveryAfterPickup()         ? "Delivery must be on or after pickup" : null,
    shipperEmail:  form.shipperEmail  && !isValidEmail(form.shipperEmail) ? "Enter a valid email address" : null,
    shipperPhone:  form.shipperPhone  && !isValidPhone(form.shipperPhone) ? "Enter a valid phone number" : null,
    shipperRate:   form.shipperRate   && !isValidRate(form.shipperRate)   ? "Enter a valid rate between $1 and $99,999" : null,
    targetCarrierRate: form.targetCarrierRate && !isValidRate(form.targetCarrierRate) ? "Enter a valid rate" : null,
    weight:        form.weight        && !isValidWeight(form.weight)      ? "Weight must be between 1 and 80,000 lbs" : null,
    rate:          form.rate          && !isValidRate(form.rate)          ? "Enter a valid rate" : null,
  };
  const hasErrors = Object.values(fieldErrors).some(Boolean);

  const canAdvance = () => {
    if (hasErrors) return false;
    if (isBroker && step === 1) return !!(form.shipperName && (!form.shipperEmail || isValidEmail(form.shipperEmail)));
    const routeStep = isBroker ? 2 : 1;
    if (step === routeStep) {
      if (!form.originCity || !form.destCity) return false;
      if (!form.pickupDate || !isValidDate(form.pickupDate)) return false;
      if (form.deliveryDate && (!isValidDate(form.deliveryDate) || !isDeliveryAfterPickup())) return false;
      if (form.originZip && !isValidZip(form.originZip)) return false;
      if (form.destZip   && !isValidZip(form.destZip))   return false;
      return true;
    }
    const cargoStep = isBroker ? 3 : 2;
    if (step === cargoStep) {
      if (!form.commodity || !form.weight) return false;
      if (form.weight && !isValidWeight(form.weight)) return false;
      return true;
    }
    const rateStep = isBroker ? 4 : 3;
    if (step === rateStep) {
      const r = form.shipperRate || form.rate;
      if (!r || !isValidRate(r)) return false;
      return true;
    }
    return true;
  };

  // Helper to show inline field error
  const FieldError = ({ field }) => fieldErrors[field]
    ? <div style={{ fontSize:11, color:C.danger, marginTop:4 }}>⚠ {fieldErrors[field]}</div>
    : null;

  const handlePost = () => {
    if (hasErrors || !canAdvance()) return;
    onPost({ ...form, shipper:form.shipperName||"Direct",
      rate:parseFloat(form.shipperRate||form.rate||0) });
    setPosted(true);
    setTimeout(() => { setPosted(false); setForm(BLANK); setStep(1); }, 2500);
  };

  if (posted) return (
    <div style={{ maxWidth:560, margin:"60px auto", textAlign:"center" }}>
      <div style={{ fontSize:64, marginBottom:20 }}>✅</div>
      <div style={{ fontSize:22, fontWeight:700, color:C.text, marginBottom:8 }}>Load Posted!</div>
      <div style={{ fontSize:14, color:C.textMd, lineHeight:1.7 }}>
        {form.originCity} → {form.destCity} posted to the board.
        {isBroker && form.shipperName && <><br/>Shipper: <strong style={{color:C.text}}>{form.shipperName}</strong></>}
        {isBroker && margin !== null && <><br/>Margin: <strong style={{color:C.success}}>{fmt$(margin)} ({marginPct}%)</strong></>}
      </div>
    </div>
  );

  const equip_options = Object.entries(EQUIP_META).map(([v,m]) => ({ value:v, label:m.label }));

  return (
    <div style={{ maxWidth:720 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:0 }}>
            {isBroker ? "Post Load for Shipper" : "Post New Shipment"}
          </h2>
          <p style={{ fontSize:13, color:C.textMd, margin:"3px 0 0" }}>
            {isBroker ? "Create a load on behalf of your shipper customer." : "Fill in the details and carriers will start bidding."}
          </p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:4 }}>
              <div onClick={() => i < step-1 && setStep(i+1)} style={{
                width:28, height:28, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:11, fontWeight:700,
                background: step===i+1 ? C.accent : step>i+1 ? C.success : C.surf2,
                color: step>=i+1 ? "#fff" : C.textDm,
                cursor: i < step-1 ? "pointer" : "default",
                border: step===i+1 ? `2px solid ${C.accentLt}` : "2px solid transparent",
              }}>{step>i+1 ? "✓" : i+1}</div>
              {i < STEPS.length-1 && <div style={{ width:20, height:2, background:step>i+1?C.success:C.border }} />}
            </div>
          ))}
        </div>
      </div>

      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:24 }}>

        {/* STEP 1 BROKER: Shipper Customer */}
        {isBroker && step===1 && (
          <div>
            <PLSection title="Shipper Customer" sub="Who is this load being moved for?" />
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:8 }}>Select from your shipper accounts</label>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {BROKER_SHIPPERS.map(s => (
                  <button key={s.id} onClick={() => onPickShipper(s)} style={{
                    background:form.shipperId===s.id?C.accentGl:C.bg,
                    border:`1px solid ${form.shipperId===s.id?C.accent:C.border}`,
                    color:form.shipperId===s.id?C.accentLt:C.textMd,
                    borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:500, cursor:"pointer", textAlign:"left",
                  }}>
                    <div style={{ fontWeight:600, color:form.shipperId===s.id?C.text:C.textMd }}>{s.company}</div>
                    <div style={{ fontSize:10, color:C.textDm, marginTop:1 }}>{s.city}</div>
                  </button>
                ))}
                <button onClick={() => setForm(f=>({...f,shipperId:"NEW",shipperName:"",shipperContact:"",shipperEmail:"",shipperPhone:""}))} style={{
                  background:form.shipperId==="NEW"?C.accentGl:C.bg,
                  border:`1px solid ${form.shipperId==="NEW"?C.accent:C.border}`,
                  color:C.textMd, borderRadius:8, padding:"8px 14px", fontSize:12, cursor:"pointer",
                }}>+ New shipper</button>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <PLField label="Company Name" span={2}><PLInp name="shipperName" value={form.shipperName} onChange={handleChange} placeholder="Acme Freight LLC" /></PLField>
              <PLField label="Contact Name"><PLInp name="shipperContact" value={form.shipperContact} onChange={handleChange} placeholder="John Smith" /></PLField>
              <PLField label="Contact Email"><PLInp name="shipperEmail" value={form.shipperEmail} onChange={handleChange} placeholder="dispatch@company.com" type="email" /><FieldError field="shipperEmail" /></PLField>
              <PLField label="Contact Phone"><PLInp name="shipperPhone" value={form.shipperPhone} onChange={handleChange} placeholder="(312) 555-0100" type="tel" /><FieldError field="shipperPhone" /></PLField>
              <PLField label="Payment Terms"><PLSel name="paymentTerms" value={form.paymentTerms} onChange={handleChange} options={[{value:"NET30",label:"Net 30"},{value:"NET15",label:"Net 15"},{value:"QUICK",label:"Quick Pay (2%)"},{value:"PREPAID",label:"Prepaid"}]} /></PLField>
              <PLField label="Shipper Ref #"><PLInp name="shipperRefNum" value={form.shipperRefNum} onChange={handleChange} placeholder="SHP-001234" /></PLField>
              <PLField label="PO Number"><PLInp name="shipperPoNum" value={form.shipperPoNum} onChange={handleChange} placeholder="PO-56789" /></PLField>
            </div>
          </div>
        )}

        {/* STEP 2 BROKER / STEP 1 SHIPPER: Route */}
        {step===(isBroker?2:1) && (
          <div>
            <PLSection title="Pickup" />
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:10, marginBottom:10 }}>
              <PLField label="Street Address (optional)" style={{gridColumn:"1/-1"}}><PLInp name="originAddress" value={form.originAddress} onChange={handleChange} placeholder="e.g. 4000 E Slauson Ave" /></PLField>
              <PLField label="City"><PLInp name="originCity" value={form.originCity} onChange={handleChange} placeholder="Chicago" /></PLField>
              <PLField label="State"><PLInp name="originState" value={form.originState} onChange={handleChange} placeholder="IL" /></PLField>
              <PLField label="ZIP"><PLInp name="originZip" value={form.originZip} onChange={handleChange} placeholder="60601" /><FieldError field="originZip" /></PLField>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
              <PLField label="Pickup Date"><PLInp name="pickupDate" value={form.pickupDate} onChange={handleChange} placeholder="Jun 15, 2026" type="date" /><FieldError field="pickupDate" /></PLField>
              <PLField label="Open Time"><PLInp name="pickupTimeStart" value={form.pickupTimeStart} onChange={handleChange} placeholder="08:00" /></PLField>
              <PLField label="Close Time"><PLInp name="pickupTimeEnd" value={form.pickupTimeEnd} onChange={handleChange} placeholder="16:00" /></PLField>
            </div>
            {isBroker && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                <PLField label="Facility Name"><PLInp name="pickupFacilityName" value={form.pickupFacilityName} onChange={handleChange} placeholder="Warehouse A" /></PLField>
                <PLField label="Contact Name"><PLInp name="pickupContactName" value={form.pickupContactName} onChange={handleChange} placeholder="Mike Jones" /></PLField>
                <PLField label="Contact Phone"><PLInp name="pickupContactPhone" value={form.pickupContactPhone} onChange={handleChange} placeholder="(312) 555-0200" /></PLField>
              </div>
            )}
            <PLField label="Pickup Instructions"><PLTextarea name="pickupInstructions" value={form.pickupInstructions} onChange={handleChange} placeholder="Dock number, check-in procedure..." /></PLField>

            <PLSection title="Delivery" />
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:10, marginBottom:10 }}>
              <PLField label="Street Address (optional)" style={{gridColumn:"1/-1"}}><PLInp name="destAddress" value={form.destAddress} onChange={handleChange} placeholder="e.g. 3800 N Central Ave" /></PLField>
              <PLField label="City"><PLInp name="destCity" value={form.destCity} onChange={handleChange} placeholder="Dallas" /></PLField>
              <PLField label="State"><PLInp name="destState" value={form.destState} onChange={handleChange} placeholder="TX" /></PLField>
              <PLField label="ZIP"><PLInp name="destZip" value={form.destZip} onChange={handleChange} placeholder="75201" /><FieldError field="destZip" /></PLField>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
              <PLField label="Delivery Date"><PLInp name="deliveryDate" value={form.deliveryDate} onChange={handleChange} placeholder="Jun 17, 2026" type="date" /><FieldError field="deliveryDate" /></PLField>
              <PLField label="Open Time"><PLInp name="deliveryTimeStart" value={form.deliveryTimeStart} onChange={handleChange} placeholder="08:00" /></PLField>
              <PLField label="Close Time"><PLInp name="deliveryTimeEnd" value={form.deliveryTimeEnd} onChange={handleChange} placeholder="16:00" /></PLField>
            </div>
            {isBroker && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                <PLField label="Facility Name"><PLInp name="deliveryFacilityName" value={form.deliveryFacilityName} onChange={handleChange} placeholder="Distribution Center" /></PLField>
                <PLField label="Contact Name"><PLInp name="deliveryContactName" value={form.deliveryContactName} onChange={handleChange} placeholder="Lisa Chen" /></PLField>
                <PLField label="Contact Phone"><PLInp name="deliveryContactPhone" value={form.deliveryContactPhone} onChange={handleChange} placeholder="(214) 555-0300" /></PLField>
              </div>
            )}
            <PLField label="Delivery Instructions"><PLTextarea name="deliveryInstructions" value={form.deliveryInstructions} onChange={handleChange} placeholder="Appointment required, dock hours..." /></PLField>
          </div>
        )}

        {/* STEP 3 BROKER / STEP 2 SHIPPER: Cargo */}
        {step===(isBroker?3:2) && (
          <div>
            <PLSection title="Cargo Details" />
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
              <PLField label="Commodity" span={2}><PLInp name="commodity" value={form.commodity} onChange={handleChange} placeholder="General Merchandise" /></PLField>
              <PLField label="Weight (lbs)"><PLInp name="weight" value={form.weight} onChange={handleChange} placeholder="38000" type="number" /><FieldError field="weight" /></PLField>
              <PLField label="Pallets"><PLInp name="pallets" value={form.pallets} onChange={handleChange} placeholder="12" type="number" /></PLField>
              <PLField label="Length (ft)"><PLInp name="length" value={form.length} onChange={handleChange} placeholder="48" type="number" /></PLField>
              <PLField label="Equipment Type"><PLSel name="equipmentType" value={form.equipmentType} onChange={handleChange} options={equip_options} /></PLField>
            </div>
            {form.equipmentType==="REEFER" && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                <PLField label="Temp Min (°F)"><PLInp name="tempMin" value={form.tempMin} onChange={handleChange} placeholder="34" type="number" /></PLField>
                <PLField label="Temp Max (°F)"><PLInp name="tempMax" value={form.tempMax} onChange={handleChange} placeholder="38" type="number" /></PLField>
              </div>
            )}
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
              <input type="checkbox" id="hazmat" name="hazmat" checked={form.hazmat} onChange={handleChange} style={{ width:16, height:16 }} />
              <label htmlFor="hazmat" style={{ fontSize:13, color:C.text, cursor:"pointer" }}>⚠ Hazardous materials (HAZMAT)</label>
            </div>
            <PLField label="Notes"><PLTextarea name="notes" value={form.notes} onChange={handleChange} placeholder="Team driver required, no touch freight, liftgate..." /></PLField>
          </div>
        )}

        {/* STEP 4 BROKER / STEP 3 SHIPPER: Rate */}
        {step===(isBroker?4:3) && (
          <div>
            {isBroker ? (
              <>
                <PLSection title="Shipper Rate" sub="What your customer pays you" />
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                  <PLField label="Shipper Rate ($)" span={2}>
                    <input name="shipperRate" type="number" value={form.shipperRate} onChange={handleChange} placeholder="3200"
                      style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"10px 12px", color:C.text, fontSize:20, fontFamily:C.mono, fontWeight:700, outline:"none", boxSizing:"border-box" }} />
                  </PLField>
                  <PLField label="Urgency"><PLSel name="urgency" value={form.urgency} onChange={handleChange} options={[{value:"STANDARD",label:"Standard"},{value:"EXPEDITED",label:"Expedited"},{value:"URGENT",label:"Urgent"}]} /></PLField>
                  <PLField label="Payment Terms"><PLSel name="paymentTerms" value={form.paymentTerms} onChange={handleChange} options={[{value:"NET30",label:"Net 30"},{value:"NET15",label:"Net 15"},{value:"QUICK",label:"Quick Pay (2%)"},{value:"PREPAID",label:"Prepaid"}]} /></PLField>
                </div>
                <PLSection title="Target Carrier Rate" sub="What you plan to pay the carrier" />
                <div style={{ marginBottom:12 }}>
                  <input name="targetCarrierRate" type="number" value={form.targetCarrierRate} onChange={handleChange} placeholder="2600"
                    style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"10px 12px", color:C.text, fontSize:20, fontFamily:C.mono, fontWeight:700, outline:"none", boxSizing:"border-box" }} />
                </div>
                {form.shipperRate && form.targetCarrierRate && (
                  <div style={{ background:margin>0?C.succGl:"rgba(239,68,68,0.1)", border:`1px solid ${margin>0?C.success:C.danger}33`, borderRadius:8, padding:"12px 16px", marginBottom:16 }}>
                    {[
                      {label:"Shipper pays",       val:fmt$(parseFloat(form.shipperRate)),                  color:C.text    },
                      {label:"You pay carrier",     val:`-${fmt$(parseFloat(form.targetCarrierRate))}`,      color:C.textMd  },
                      {label:"Platform fee (3.5%)", val:`-${fmt$(Math.round(parseFloat(form.shipperRate)*0.035))}`, color:C.danger },
                    ].map(r=>(
                      <div key={r.label} style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                        <span style={{ fontSize:12, color:C.textMd }}>{r.label}</span>
                        <span style={{ fontSize:12, fontFamily:C.mono, color:r.color }}>{r.val}</span>
                      </div>
                    ))}
                    <div style={{ display:"flex", justifyContent:"space-between", paddingTop:8, borderTop:`1px solid ${C.border}` }}>
                      <span style={{ fontSize:14, fontWeight:700, color:C.text }}>Your Net Margin</span>
                      <span style={{ fontSize:18, fontWeight:800, fontFamily:C.mono, color:margin>0?C.success:C.danger }}>
                        {fmt$(margin-Math.round(parseFloat(form.shipperRate)*0.035))} ({marginPct}%)
                      </span>
                    </div>
                    {margin<0 && <div style={{ fontSize:11, color:C.danger, marginTop:6 }}>⚠ Negative margin</div>}
                  </div>
                )}
              </>
            ) : (
              <>
                <PLSection title="Rate & Priority" />
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                  <PLField label="Target Rate ($)"><PLInp name="rate" value={form.rate} onChange={handleChange} placeholder="2500" type="number" /></PLField>
                  <PLField label="Urgency"><PLSel name="urgency" value={form.urgency} onChange={handleChange} options={[{value:"STANDARD",label:"Standard"},{value:"EXPEDITED",label:"Expedited (+priority)"},{value:"URGENT",label:"Urgent (top of board)"}]} /></PLField>
                </div>
                <PLField label="Special Instructions"><PLTextarea name="specialInstructions" value={form.specialInstructions} onChange={handleChange} placeholder="Any special handling, access requirements, or notes..." rows={3} /></PLField>
              </>
            )}
          </div>
        )}

        {/* Navigation */}
        <div style={{ display:"flex", gap:10, marginTop:20, paddingTop:16, borderTop:`1px solid ${C.border}` }}>
          {step > 1 && <Btn variant="ghost" onClick={() => setStep(s=>s-1)}>← Back</Btn>}
          {step < STEPS.length
            ? <Btn variant="primary" style={{flex:1}} disabled={!canAdvance()} onClick={() => setStep(s=>s+1)}>
                Next: {STEPS[step]} →
              </Btn>
            : <Btn variant="success" style={{flex:1}} onClick={handlePost}>
                ✅ Post Load{isBroker && form.shipperName ? ` for ${form.shipperName}` : ""}
              </Btn>
          }
        </div>
      </div>
    </div>
  );
};


const LiveTrackingPage = ({ loads, role, carrierId = "C1" }) => {
  const { user: trackingUser } = useAuth();
  // All active shipment statuses — not just IN_TRANSIT
  const ACTIVE_STATUSES = ["IN_TRANSIT", "AT_PICKUP", "PICKED_UP", "AT_DELIVERY", "BOOKED"];
  const transit = loads.filter(l => {
    if (!ACTIVE_STATUSES.includes(l.status)) return false;
    if (role === "CARRIER") {
      return l.carrierId === carrierId ||
             l.carrierId === trackingUser?.carrier?.id ||
             carrierId === "C1";
    }
    // Brokers and admins see all active loads; shippers see their own
    if (role === "SHIPPER") return l.shipperId === trackingUser?.shipper?.id;
    return true;
  });

  const [selectedId, setSelectedId] = useState(() => transit[0]?.id ?? null);
  const selectedLoad = transit.find(l => l.id === selectedId) ?? transit[0] ?? null;

  const loadIds = transit.map(l => l.id).join(",");

  const [positions, setPositions] = useState(() => {
    const m = {};
    transit.forEach(l => { m[l.id] = l.progress ?? 0; });
    return m;
  });

  // Reset selection when transit list changes (role switch)
  useEffect(() => {
    if (transit.length > 0 && !transit.find(l => l.id === selectedId)) {
      setSelectedId(transit[0].id);
    }
  }, [loadIds]); // eslint-disable-line

  // Progress ticker
  useEffect(() => {
    if (!transit.length) return;
    const timer = setInterval(() => {
      setPositions(prev => {
        const next = { ...prev };
        transit.forEach(l => {
          next[l.id] = Math.min((next[l.id] ?? l.progress ?? 0) + 0.8 + Math.random() * 0.7, 99.5);
        });
        return next;
      });
    }, 2500);
    return () => clearInterval(timer);
  }, [loadIds]); // eslint-disable-line

  // Interpolate lat/lng along waypoints at pct%
  const getPos = (load, pct) => {
    const evts = TRACKING_EVENTS.filter(e => e.loadId === load.id);
    const pts = [];
    if (load.originLat) pts.push({ lat: load.originLat, lng: load.originLng });
    evts.forEach(e => pts.push({ lat: e.lat, lng: e.lng }));
    if (load.destLat) pts.push({ lat: load.destLat, lng: load.destLng });
    if (pts.length < 2) return pts[0] ?? null;
    const seg = (pct / 100) * (pts.length - 1);
    const i   = Math.min(Math.floor(seg), pts.length - 2);
    const t   = seg - i;
    return { lat: pts[i].lat + (pts[i+1].lat - pts[i].lat) * t,
             lng: pts[i].lng + (pts[i+1].lng - pts[i].lng) * t };
  };

  // ── Leaflet map ───────────────────────────────────────────────────────────
  const mapDiv     = useRef(null);
  const mapInst    = useRef(null);
  const truckMkr   = useRef(null);
  const routeLine  = useRef(null);
  const initialized = useRef(false);

  const renderMap = (load, pct) => {
    const L = window.L;
    if (!L || !mapInst.current || !load) return;
    const map = mapInst.current;

    // Clear previous truck + route
    if (routeLine.current)  { try { map.removeLayer(routeLine.current); } catch(_){} routeLine.current = null; }
    if (truckMkr.current)   { try { map.removeLayer(truckMkr.current);  } catch(_){} truckMkr.current  = null; }

    const evts = TRACKING_EVENTS.filter(e => e.loadId === load.id);

    // Build full waypoint list
    const waypts = [];
    if (load.originLat) waypts.push([load.originLat, load.originLng]);
    evts.forEach(e => waypts.push([e.lat, e.lng]));
    if (load.destLat)   waypts.push([load.destLat, load.destLng]);

    if (waypts.length >= 2) {
      routeLine.current = L.polyline(waypts, { color:"#2563eb", weight:4, opacity:0.8 }).addTo(map);
    }
    // Pins
    if (load.originLat) L.circleMarker([load.originLat, load.originLng], { radius:7, color:"#10b981", fillColor:"#10b981", fillOpacity:1 }).bindPopup(`<b>Origin</b><br>${load.origin}`).addTo(map);
    if (load.destLat)   L.circleMarker([load.destLat,   load.destLng],   { radius:7, color:"#ef4444", fillColor:"#ef4444", fillOpacity:1 }).bindPopup(`<b>Destination</b><br>${load.dest}`).addTo(map);
    evts.forEach(e => L.circleMarker([e.lat, e.lng], { radius:4, color:"#94a3b8", fillColor:"#94a3b8", fillOpacity:0.7 }).bindPopup(`${e.location}<br>${e.time}`).addTo(map));

    // Truck
    const pos = getPos(load, pct);
    if (pos) {
      const icon = L.divIcon({ html:`<div style="font-size:22px;line-height:1;filter:drop-shadow(0 2px 4px rgba(0,0,0,.5))">🚛</div>`, iconSize:[28,28], iconAnchor:[14,14], className:"" });
      truckMkr.current = L.marker([pos.lat, pos.lng], { icon }).bindPopup(`<b>${load.carrier || "Carrier"}</b><br>${Math.round(pct)}% complete`).addTo(map);
    }

    // Fit
    if (routeLine.current) {
      try { map.fitBounds(routeLine.current.getBounds(), { padding:[40,40] }); } catch(_){}
    } else if (pos) {
      map.setView([pos.lat, pos.lng], 8);
    }
  };

  const mountMap = () => {
    const L = window.L;
    if (!L || !mapDiv.current || mapInst.current) return;
    const map = L.map(mapDiv.current, { zoomControl:true });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:'© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom:18,
    }).addTo(map);
    map.setView([37.09, -95.71], 5);
    mapInst.current   = map;
    initialized.current = true;
    // Draw immediately
    const load = transit.find(l => l.id === selectedId) ?? transit[0];
    if (load) renderMap(load, positions[load.id] ?? load.progress ?? 65);
  };

  // Load Leaflet once, init map when ready
  useEffect(() => {
    const loadLeaflet = () => {
      if (window.L) { mountMap(); return; }
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css"; link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }
      if (!document.getElementById("leaflet-js")) {
        const script = document.createElement("script");
        script.id  = "leaflet-js";
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.onload = mountMap;
        document.head.appendChild(script);
      } else {
        // Script tag exists but still loading — poll
        const poll = setInterval(() => { if (window.L) { clearInterval(poll); mountMap(); } }, 100);
      }
    };
    loadLeaflet();
    return () => {
      // Destroy map on unmount so re-mounting the page works cleanly
      if (mapInst.current) {
        try { mapInst.current.remove(); } catch(_){}
        mapInst.current    = null;
        truckMkr.current   = null;
        routeLine.current  = null;
        initialized.current = false;
      }
    };
  }, []); // eslint-disable-line

  // Re-render map when selected load or progress changes
  useEffect(() => {
    if (!initialized.current || !selectedLoad) return;
    const pct = positions[selectedLoad.id] ?? selectedLoad.progress ?? 65;
    renderMap(selectedLoad, pct);
  }, [selectedId, positions[selectedLoad?.id ?? ""]]); // eslint-disable-line

  const pct = selectedLoad ? (positions[selectedLoad.id] ?? selectedLoad.progress ?? 0) : 0;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:0 }}>Live Tracking</h2>
          <p style={{ fontSize:13, color:C.textMd, marginTop:3 }}>{transit.length} shipment{transit.length!==1?"s":""} in transit</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:C.success }}>
          <span style={{ width:7, height:7, borderRadius:"50%", background:C.success, display:"inline-block", animation:"pulse 1.5s infinite" }} />
          Updating every 2.5s
        </div>
      </div>

      {transit.length === 0 && (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, textAlign:"center", padding:"48px 32px" }}>
          <div style={{ fontSize:36, opacity:0.15, marginBottom:12 }}>🚛</div>
          <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:6 }}>No active shipments</div>
          <div style={{ fontSize:13, color:C.textDm }}>
            {role === "CARRIER" ? "No loads assigned to your fleet." : "No shipments currently in transit."}
          </div>
        </div>
      )}

      {transit.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:14, alignItems:"start" }}>
          {/* Map */}
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
            <div ref={mapDiv} style={{ height:460, width:"100%" }} />
            {selectedLoad && (
              <div style={{ padding:"12px 16px", borderTop:`1px solid ${C.border}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.textMd, marginBottom:5 }}>
                  <span>📍 {selectedLoad.origin.split(",")[0]}</span>
                  <span style={{ color:C.warn, fontWeight:600 }}>ETA {selectedLoad.deliveryDate}</span>
                  <span>🏁 {selectedLoad.dest.split(",")[0]}</span>
                </div>
                <div style={{ position:"relative", paddingTop:20, paddingBottom:4 }}>
                  <div style={{ position:"absolute", top:0, left:`${Math.min(pct,96)}%`, transform:"translateX(-50%)", fontSize:18, transition:"left 2.5s ease", lineHeight:1 }}>🚛</div>
                  <div style={{ height:6, background:C.border, borderRadius:3, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,${C.accent},${C.success})`, borderRadius:3, transition:"width 2.5s ease" }} />
                  </div>
                </div>
                <div style={{ fontSize:11, color:C.textMd, marginTop:4, display:"flex", justifyContent:"space-between" }}>
                  <span>{Math.round(pct)}% · {fmtMi(Math.round(selectedLoad.miles * pct / 100))} of {fmtMi(selectedLoad.miles)}</span>
                  <span style={{ color:C.success }}>● On schedule</span>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {transit.map(load => {
              const p    = positions[load.id] ?? load.progress ?? 0;
              const isSel = load.id === selectedId;
              const evts  = TRACKING_EVENTS.filter(e => e.loadId === load.id);
              const latest = evts[evts.length - 1];
              return (
                <div key={load.id} onClick={() => setSelectedId(load.id)} style={{
                  background: isSel ? C.surf3 : C.surface,
                  border:`1px solid ${isSel ? C.accentLt : C.border}`,
                  borderRadius:10, padding:"13px 14px", cursor:"pointer", transition:"all 0.14s",
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                    <div>
                      <div style={{ fontSize:10, color:C.textDm, fontFamily:C.mono }}>{load.number}</div>
                      <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{load.origin.split(",")[0]} → {load.dest.split(",")[0]}</div>
                    </div>
                    <Badge status="IN_TRANSIT" />
                  </div>
                  <ProgressBar pct={p} height={4} />
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.textMd, marginTop:5 }}>
                    <span>{Math.round(p)}%</span>
                    <span>ETA {load.deliveryDate}</span>
                  </div>
                  <div style={{ fontSize:11, color:C.textMd, marginTop:4 }}>
                    🚛 {load.carrier || "—"}{load.driver && <span> · 👤 {load.driver}</span>}
                  </div>
                  {latest && <div style={{ fontSize:11, color:C.textDm, marginTop:2 }}>📍 {latest.location}</div>}
                </div>
              );
            })}

            {selectedLoad && (() => {
              const events = TRACKING_EVENTS.filter(e => e.loadId === selectedLoad.id);
              return events.length > 0 ? (
                <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"13px 14px" }}>
                  <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Location History</div>
                  {events.map((ev, i) => (
                    <div key={ev.id} style={{ display:"flex", gap:10, marginBottom: i < events.length-1 ? 10 : 0 }}>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                        <div style={{ width:8, height:8, borderRadius:"50%", flexShrink:0,
                          background: i===events.length-1 ? C.success : C.accentLt,
                          boxShadow: i===events.length-1 ? `0 0 6px ${C.success}` : "none" }} />
                        {i < events.length-1 && <div style={{ width:1, flex:1, background:C.border, margin:"3px 0" }} />}
                      </div>
                      <div style={{ paddingBottom: i < events.length-1 ? 10 : 0 }}>
                        <div style={{ fontSize:12, fontWeight:500, color: i===events.length-1 ? C.success : C.text }}>{ev.location}</div>
                        <div style={{ fontSize:10, color:C.textDm }}>{ev.time}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ display:"flex", gap:10, marginTop:8 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:C.success, animation:"pulse 1.5s infinite", flexShrink:0, marginTop:2 }} />
                    <div style={{ fontSize:11, color:C.success, fontWeight:500 }}>En route — live</div>
                  </div>
                </div>
              ) : null;
            })()}

            {selectedLoad && (
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"13px 14px" }}>
                <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>Shipment Details</div>
                {[
                  { label:"Load #",    value:selectedLoad.number },
                  { label:"Shipper",   value:selectedLoad.shipper },
                  { label:"Commodity", value:selectedLoad.commodity },
                  { label:"Weight",    value:fmtLb(selectedLoad.weight) },
                  { label:"Equipment", value:EQUIP_META[selectedLoad.equipmentType]?.label },
                  { label:"Rate",      value:fmt$(selectedLoad.rate) },
                ].map(r => (
                  <div key={r.label} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:`1px solid ${C.border}` }}>
                    <span style={{ fontSize:11, color:C.textMd }}>{r.label}</span>
                    <span style={{ fontSize:11, color:C.text, fontWeight:500 }}>{r.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const SHIPPER_INV_STATUS = {
  DRAFT:   { label:"Draft",   color:"#94a3b8" },
  SENT:    { label:"Sent",    color:"#f59e0b" },
  PAID:    { label:"Paid",    color:"#10b981" },
  OVERDUE: { label:"Overdue", color:"#ef4444" },
};

const InvoicesPage = () => {
  const { user: shipperInvUser } = useAuth();
  const isDemoShipper = ["shipper@midwestgoods.com","shipping@pacificfreight.com"].includes(shipperInvUser?.email);
  const [invoices, setInvoices] = useState(isDemoShipper ? [
    { id:"INV-001", number:"INV-C-000001", load:"FF-000006", route:"Nashville → Charlotte", carrier:"Arrow Logistics Inc.",  amount:1425.60, platformFee:49.90, dueDate:"Jul 9",  status:"UNPAID",  issued:"Jun 9",  paidDate:null },
    { id:"INV-002", number:"INV-C-000002", load:"FF-000003", route:"Atlanta → Miami",       carrier:"Arrow Logistics Inc.",  amount:2418.00, platformFee:84.63, dueDate:"Jul 10", status:"UNPAID",  issued:"Jun 10", paidDate:null },
    { id:"INV-004", number:"INV-C-000004", load:"FF-000005", route:"Chicago → St Louis",    carrier:"Arrow Logistics Inc.",  amount:756.00,  platformFee:26.46, dueDate:"Jun 15", status:"PAID",    issued:"May 15", paidDate:"Jun 10" },
  ] : []);

  const pay = (id) => {
    setInvoices(prev => prev.map(i => i.id===id
      ? {...i, status:"PAID", paidDate:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
      : i
    ));
    setSelected(prev => prev?.id===id ? {...prev, status:"PAID"} : prev);
  };

  const dispute = (id) => {
    setInvoices(prev => prev.map(i => i.id===id ? {...i, status:"DISPUTED"} : i));
    setSelected(prev => prev?.id===id ? {...prev, status:"DISPUTED"} : prev);
  };

  const INV_STATUS = {
    UNPAID:   { label:"Awaiting Payment", color:C.warn    },
    PAID:     { label:"Paid",             color:C.success  },
    OVERDUE:  { label:"Overdue",          color:C.danger   },
    DISPUTED: { label:"Disputed",         color:C.danger   },
  };

  const totalOwed = invoices.filter(i=>i.status==="UNPAID"||i.status==="OVERDUE").reduce((a,i)=>a+i.amount,0);
  const totalPaid = invoices.filter(i=>i.status==="PAID").reduce((a,i)=>a+i.amount,0);
  const overdue   = invoices.filter(i=>i.status==="OVERDUE").length;

  const filtered = filterStatus==="ALL" ? invoices : invoices.filter(i=>i.status===filterStatus);

  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:"0 0 4px" }}>Invoices</h2>
        <p style={{ fontSize:13, color:C.textMd, margin:0 }}>Invoices received from carriers for completed shipments.</p>
      </div>

      <div style={{ display:"flex", gap:10, marginBottom:16 }}>
        <StatCard label="Awaiting Payment" value={fmt$(totalOwed)} sub={`${invoices.filter(i=>["UNPAID","OVERDUE"].includes(i.status)).length} invoice(s)`} color={C.warn} icon="⏳" />
        <StatCard label="Paid (30d)"       value={fmt$(totalPaid)} sub={`${invoices.filter(i=>i.status==="PAID").length} invoice(s)`}                     color={C.success} icon="✅" />
        {overdue > 0 && <StatCard label="Overdue" value={overdue} sub="past due date" color={C.danger} icon="⚠" />}
      </div>

      {/* Filter */}
      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        {["ALL","UNPAID","PAID","OVERDUE","DISPUTED"].map(s => (
          <button key={s} onClick={()=>setFilterStatus(s)} style={{
            background:filterStatus===s?C.accent:"transparent",
            border:`1px solid ${filterStatus===s?C.accent:C.border}`,
            color:filterStatus===s?"#fff":C.textMd,
            borderRadius:6, padding:"4px 12px", fontSize:11, fontWeight:500, cursor:"pointer",
          }}>{s==="ALL"?"All":INV_STATUS[s]?.label||s}</button>
        ))}
      </div>

      <div style={{ display:"flex", gap:14 }}>
        {/* Table */}
        <div style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:C.surf2 }}>
                {["Invoice #","Load","Carrier","Amount","Issued","Due","Status","Actions"].map(h => (
                  <th key={h} style={{ padding:"9px 14px", fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em", textAlign:"left", fontWeight:600, whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv,i) => {
                const s = INV_STATUS[inv.status] || INV_STATUS.UNPAID;
                return (
                  <tr key={inv.id} onClick={()=>setSelected(inv)}
                    style={{ borderBottom:`1px solid ${C.border}`, cursor:"pointer",
                      background:selected?.id===inv.id?C.surf3:i%2===0?"transparent":C.surf2+"44" }}>
                    <td style={{ padding:"10px 14px", fontSize:11, fontFamily:C.mono, color:C.accentLt }}>{inv.number}</td>
                    <td style={{ padding:"10px 14px", fontSize:11, fontFamily:C.mono, color:C.textDm }}>{inv.load}</td>
                    <td style={{ padding:"10px 14px", fontSize:12, color:C.text }}>{inv.carrier}</td>
                    <td style={{ padding:"10px 14px", fontSize:13, fontWeight:700, color:C.text, fontFamily:C.mono }}>{fmt$(inv.amount)}</td>
                    <td style={{ padding:"10px 14px", fontSize:12, color:C.textMd }}>{inv.issued}</td>
                    <td style={{ padding:"10px 14px", fontSize:12, color:inv.status==="OVERDUE"?C.danger:C.textMd }}>{inv.dueDate}</td>
                    <td style={{ padding:"10px 14px" }}>
                      <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:4, textTransform:"uppercase",
                        background:`${s.color}18`, border:`1px solid ${s.color}44`, color:s.color }}>{s.label}</span>
                    </td>
                    <td style={{ padding:"10px 14px" }} onClick={e=>e.stopPropagation()}>
                      <div style={{ display:"flex", gap:5 }}>
                        {(inv.status==="UNPAID"||inv.status==="OVERDUE") && (
                          <button onClick={()=>pay(inv.id)} style={{ background:C.success, border:"none", color:"#fff", borderRadius:5, padding:"3px 10px", fontSize:10, fontWeight:600, cursor:"pointer" }}>
                            Pay Now
                          </button>
                        )}
                        {inv.status==="UNPAID" && (
                          <button onClick={()=>dispute(inv.id)} style={{ background:"transparent", border:`1px solid ${C.danger}`, color:C.danger, borderRadius:5, padding:"3px 8px", fontSize:10, cursor:"pointer" }}>
                            Dispute
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length===0 && <tr><td colSpan={8} style={{ padding:"32px", textAlign:"center", color:C.textDm }}>No invoices</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{ width:320, flexShrink:0, background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:10, overflow:"hidden", display:"flex", flexDirection:"column" }}>
            <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:10, fontFamily:C.mono, color:C.textDm }}>{selected.number}</div>
                <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{selected.carrier}</div>
              </div>
              <button onClick={()=>setSelected(null)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"3px 8px", cursor:"pointer", fontSize:11 }}>✕</button>
            </div>
            <div style={{ flex:1, overflowY:"auto", padding:16 }}>
              <div style={{ background:C.bg, borderRadius:8, padding:"12px 14px", border:`1px solid ${C.border}`, marginBottom:14 }}>
                <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>Shipment</div>
                <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{selected.route}</div>
                <div style={{ fontSize:11, color:C.textMd, marginTop:3 }}>{selected.load}</div>
              </div>

              <div style={{ background:C.bg, borderRadius:8, border:`1px solid ${C.border}`, overflow:"hidden", marginBottom:14 }}>
                <div style={{ padding:"8px 12px", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}>
                    <span style={{ fontSize:12, color:C.textMd }}>Freight charge</span>
                    <span style={{ fontSize:12, fontFamily:C.mono, color:C.text }}>{fmt$(selected.amount - (selected.platformFee||0))}</span>
                  </div>
                </div>
                <div style={{ padding:"8px 12px", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}>
                    <span style={{ fontSize:11, color:C.textDm }}>Platform fee (3.5%)</span>
                    <span style={{ fontSize:11, fontFamily:C.mono, color:C.textDm }}>{fmt$(selected.platformFee||0)}</span>
                  </div>
                </div>
                <div style={{ padding:"10px 12px", background: selected.status==="PAID"?C.succGl:"transparent" }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}>
                    <span style={{ fontSize:13, fontWeight:700, color:C.text }}>Total Due</span>
                    <span style={{ fontSize:16, fontWeight:800, fontFamily:C.mono, color:selected.status==="PAID"?C.success:C.text }}>{fmt$(selected.amount)}</span>
                  </div>
                </div>
              </div>

              {[
                { label:"Issued",   value:selected.issued },
                { label:"Due",      value:selected.dueDate },
                { label:"Paid",     value:selected.paidDate||"—" },
              ].map(r => (
                <div key={r.label} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${C.border}` }}>
                  <span style={{ fontSize:11, color:C.textMd }}>{r.label}</span>
                  <span style={{ fontSize:11, color:C.text }}>{r.value}</span>
                </div>
              ))}

              <div style={{ display:"flex", gap:8, marginTop:16 }}>
                {(selected.status==="UNPAID"||selected.status==="OVERDUE") && <>
                  <Btn variant="success" style={{flex:1}} onClick={()=>pay(selected.id)}>Pay Now</Btn>
                  <Btn variant="ghost" onClick={()=>dispute(selected.id)}>Dispute</Btn>
                </>}
                {selected.status==="PAID" && (
                  <div style={{ width:"100%", textAlign:"center", padding:"8px", background:C.succGl, borderRadius:8, fontSize:13, color:C.success, fontWeight:600 }}>
                    ✓ Paid on {selected.paidDate}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AnalyticsPage = ({ loads, role }) => {
  const byStatus = {};
  loads.forEach(l => { byStatus[l.status] = (byStatus[l.status]||0)+1; });

  const spendData = [
    { month:"Jan", spend:18400 }, { month:"Feb", spend:22100 }, { month:"Mar", spend:19800 },
    { month:"Apr", spend:25600 }, { month:"May", spend:21300 }, { month:"Jun", spend:8200 },
  ];
  const maxSpend = Math.max(...spendData.map(d => d.spend));

  return (
    <div>
      <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:"0 0 18px" }}>Analytics</h2>
      <div style={{ display:"flex", gap:10, marginBottom:20 }}>
        <StatCard label="Total Loads" value={loads.length} sub="all time" color={C.accentLt} />
        <StatCard label="Avg Rate" value={fmt$(Math.round(loads.reduce((a,l)=>a+l.rate,0)/loads.length))} sub="per shipment" color={C.success} />
        <StatCard label="Total Miles" value={fmtMi(loads.reduce((a,l)=>a+l.miles,0))} sub="all loads" color={C.warn} />
        <StatCard label="Avg $/mi" value={`$${(loads.reduce((a,l)=>a+l.rate,0)/loads.reduce((a,l)=>a+l.miles,0)).toFixed(2)}`} sub="blended rate" color={C.teal} />
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:14 }}>
        {/* Spend by month */}
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:18 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:16 }}>Monthly Spend</div>
          <div style={{ display:"flex", alignItems:"flex-end", gap:10, height:120 }}>
            {spendData.map(d => (
              <div key={d.month} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                <div style={{ fontSize:9, color:C.textDm, fontFamily:C.mono }}>{fmt$(d.spend/1000)}k</div>
                <div style={{ width:"100%", background:`linear-gradient(to top,${C.accent},${C.accentLt})`, borderRadius:"3px 3px 0 0", height:`${(d.spend/maxSpend)*90}px`, minHeight:4 }} />
                <div style={{ fontSize:10, color:C.textMd }}>{d.month}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Status breakdown */}
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:18 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:16 }}>Load Statuses</div>
          {Object.entries(byStatus).map(([status, count]) => {
            const m = STATUS_META[status];
            return (
              <div key={status} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:m?.color||C.textMd }} />
                  <span style={{ fontSize:12, color:C.text }}>{m?.label||status}</span>
                </div>
                <span style={{ fontSize:13, fontWeight:700, color:C.text, fontFamily:C.mono }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const INV_STATUS_META = {
  DRAFT:    { label:"Draft",    color:"#94a3b8" },
  SENT:     { label:"Sent",     color:"#f59e0b" },
  VIEWED:   { label:"Viewed",   color:"#8b5cf6" },
  PAID:     { label:"Paid",     color:"#10b981" },
  OVERDUE:  { label:"Overdue",  color:"#ef4444" },
  DISPUTED: { label:"Disputed", color:"#ef4444" },
  VOID:     { label:"Void",     color:"#475569" },
};

const CARRIERS_DATA = [
  { id:"C1", name:"Arrow Logistics Inc.",   dot:"MC-456789", city:"Chicago",     state:"IL", rating:4.9, deliveries:312, types:["DRY_VAN","REEFER"],          trucks:3, onTime:97, avgRate:2.65, verified:true,  bio:"Specializing in temperature-controlled and dry freight across the Midwest and Southeast." },
  { id:"C2", name:"Sunbelt Trucking Co.",   dot:"MC-112233", city:"Atlanta",     state:"GA", rating:4.7, deliveries:198, types:["DRY_VAN","FLATBED"],          trucks:2, onTime:94, avgRate:2.80, verified:true,  bio:"Full truckload and flatbed specialists serving the Southeast and Mid-Atlantic." },
  { id:"C3", name:"Eagle Transport LLC",    dot:"MC-334455", city:"Dallas",      state:"TX", rating:4.5, deliveries:94,  types:["FLATBED","STEP_DECK"],         trucks:4, onTime:91, avgRate:2.55, verified:true,  bio:"Heavy haul and oversized load experts. Licensed in 48 states." },
  { id:"C4", name:"Pacific Haul Co.",       dot:"MC-667788", city:"Los Angeles", state:"CA", rating:4.6, deliveries:143, types:["DRY_VAN","REEFER","TANKER"],   trucks:5, onTime:95, avgRate:2.70, verified:true,  bio:"West Coast specialists covering CA, AZ, NV, OR and WA with dedicated lanes." },
  { id:"C5", name:"Iron Road Freight",      dot:"MC-990011", city:"Denver",      state:"CO", rating:4.3, deliveries:61,  types:["FLATBED","LOWBOY"],            trucks:2, onTime:88, avgRate:2.45, verified:false, bio:"Mountain and high-desert flatbed haulers. Oversize permit experience." },
  { id:"C6", name:"Gulf Coast Carriers",    dot:"MC-223344", city:"Houston",     state:"TX", rating:4.8, deliveries:229, types:["TANKER","DRY_VAN"],            trucks:6, onTime:96, avgRate:2.90, verified:true,  bio:"Petrochemical and hazmat tanker specialists. Full TWIC and HAZMAT certified fleet." },
  { id:"C7", name:"Blue Ridge Logistics",   dot:"MC-556677", city:"Charlotte",   state:"NC", rating:4.4, deliveries:88,  types:["DRY_VAN","BOX_TRUCK"],         trucks:3, onTime:92, avgRate:2.35, verified:true,  bio:"Regional carrier covering the Carolinas and Virginia with next-day capability." },
  { id:"C8", name:"Cascade Freight Co.",    dot:"MC-889900", city:"Seattle",     state:"WA", rating:4.7, deliveries:176, types:["REEFER","DRY_VAN"],            trucks:4, onTime:93, avgRate:2.60, verified:true,  bio:"Pacific Northwest reefer specialists — produce, seafood, and pharma freight." },
];

const StarRating = ({ rating }) => {
  const full = Math.floor(rating);
  const dec  = rating - full;
  return (
    <span style={{ fontSize:12, color:C.warn }}>
      {"★".repeat(full)}{dec >= 0.5 ? "½" : ""}
      <span style={{ color:C.textDm }}>{"★".repeat(5 - full - (dec>=0.5?1:0))}</span>
      <span style={{ color:C.textMd, marginLeft:4 }}>{rating.toFixed(1)}</span>
    </span>
  );
};


// ─── FMCSA Verification Panel ─────────────────────────────────────────────────

const RATING_COLOR = {
  "Satisfactory":   C.success,
  "Conditional":    C.warn,
  "Unsatisfactory": C.danger,
  "Unrated":        C.textMd,
};

// ── FMCSA panel shared components (module-level — avoids remount on re-render) ─
const FMCSA_RATING_COLOR = {
  "Satisfactory":   C.success,
  "Conditional":    C.warn,
  "Unsatisfactory": C.danger,
  "Unrated":        C.textMd,
};

const FMCSARatingBadge = ({ rating }) => {
  const color = FMCSA_RATING_COLOR[rating] || C.textMd;
  return (
    <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:4,
      background:`${color}18`, border:`1px solid ${color}44`, color }}>
      {rating}
    </span>
  );
};

const FMCSAOOSBadge = ({ oos, operatingStatus }) => {
  const isRevoked = /revoked|not authorized|inactive/i.test(operatingStatus ?? "");
  const notGood = oos || isRevoked;
  const label = oos ? "⛔ OUT OF SERVICE" : isRevoked ? "⛔ NOT AUTHORIZED" : "✅ AUTHORIZED";
  return <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:4,
    background: notGood ? C.dangGl : C.succGl,
    border:`1px solid ${notGood ? C.danger : C.success}44`,
    color: notGood ? C.danger : C.success }}>
    {label}
  </span>;
};

const FMCSARow = ({ label, value, color }) => value == null ? null : (
  <div style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${C.border}` }}>
    <span style={{ fontSize:12, color:C.textMd }}>{label}</span>
    <span style={{ fontSize:12, fontWeight:600, color: color || C.text }}>{value}</span>
  </div>
);

const FMCSAPanel = ({ carrier, onClose }) => {
  const [status, setStatus]   = useState("idle"); // idle | loading | done | error
  const [profile, setProfile] = useState(null);
  const [error, setError]     = useState(null);
  const [dotInput, setDotInput] = useState(carrier?.dot?.replace(/\D/g, "") || "");

  const runLookup = async () => {
    if (!dotInput.trim()) return;
    setStatus("loading"); setError(null);
    try {
      const p = await fmcsaLookup(dotInput.trim());
      setProfile(p);
      setStatus("done");
    } catch (e) {
      setError(e.message);
      setStatus("error");
    }
  };



  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200 }}
      onClick={e => { if(e.target===e.currentTarget) onClose(); }}>
      <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:14, width:520, maxHeight:"85vh", overflow:"hidden", display:"flex", flexDirection:"column" }}>

        {/* Header — updates to show FMCSA legal name once results come back */}
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ minWidth:0, flex:1 }}>
            <div style={{ fontSize:11, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>FMCSA Carrier Verification</div>
            {profile?.legalName ? (
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:C.text }}>{profile.legalName}</div>
                {profile.dbaName && (
                  <div style={{ fontSize:11, color:C.textMd, marginTop:1 }}>DBA: {profile.dbaName}</div>
                )}
                {/* Show if FMCSA name differs from platform-registered name */}
                {carrier?.name && carrier.name.toUpperCase() !== profile.legalName.toUpperCase() && (
                  <div style={{ fontSize:11, marginTop:4, display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ color:C.warn }}>⚠</span>
                    <span style={{ color:C.warn }}>Registered as "{carrier.name}" — FMCSA name differs</span>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize:14, fontWeight:600, color:C.text }}>
                {carrier?.name || "Look up a carrier"}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12, flexShrink:0, marginLeft:12 }}>✕</button>
        </div>

        <div style={{ flex:1, overflowY:"auto", padding:20 }}>
          {/* DOT lookup input */}
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:6 }}>
              USDOT Number
            </label>
            <div style={{ display:"flex", gap:8 }}>
              <input value={dotInput} onChange={e=>setDotInput(e.target.value.replace(/\D/g,""))}
                placeholder="e.g. 1234567"
                onKeyDown={e => e.key==="Enter" && runLookup()}
                style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 12px", color:C.text, fontSize:14, fontFamily:C.mono, outline:"none" }} />
              <Btn onClick={runLookup} disabled={status==="loading" || !dotInput.trim()} variant="primary">
                {status==="loading" ? "Looking up…" : "Look Up"}
              </Btn>
            </div>
            <div style={{ fontSize:11, color:C.textDm, marginTop:5 }}>
              Data sourced from FMCSA SAFER system via SaferWebAPI · Updated daily
            </div>
          </div>

          {/* Error */}
          {status==="error" && (
            <div style={{ background:C.dangGl, border:`1px solid ${C.danger}33`, borderRadius:8, padding:"12px 14px", marginBottom:14 }}>
              <div style={{ color:C.danger, fontSize:12, fontWeight:600, marginBottom:6 }}>⚠ Lookup failed</div>
              {error.split("\n").map((line, i) => line.trim() ? (
                <div key={i} style={{ color: line.startsWith("SAFER_WEB") || line.startsWith("cd ") ? C.accentLt : C.textMd,
                  fontSize:11, marginBottom:3, fontFamily: line.startsWith("SAFER_WEB") || line.startsWith("cd ") ? C.mono : C.sans }}>
                  {line}
                </div>
              ) : <div key={i} style={{ height:4 }} />)}
            </div>
          )}

          {/* Results */}
          {status==="done" && profile && (
            <div>
              {/* Status banner */}
              <div style={{ background: profile.outOfService ? C.dangGl : C.succGl,
                border:`1px solid ${profile.outOfService ? C.danger : C.success}33`,
                borderRadius:10, padding:"12px 16px", marginBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:12, color:C.textMd, marginBottom:2 }}>
                    DOT {profile.dotNumber}{profile.mcNumber ? ` · ${profile.mcNumber}` : ""}
                  </div>
                  {profile.address && (
                    <div style={{ fontSize:11, color:C.textMd }}>{profile.address}</div>
                  )}
                  {profile.phone && (
                    <div style={{ fontSize:11, color:C.textMd }}>{profile.phone}</div>
                  )}
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                  <FMCSAOOSBadge oos={profile.outOfService} operatingStatus={profile.operatingStatus} />
                  <FMCSARatingBadge rating={profile.safetyRating} />
                </div>
              </div>

              {/* Key details */}
              <div style={{ background:C.bg, borderRadius:8, padding:"10px 14px", border:`1px solid ${C.border}`, marginBottom:12 }}>
                <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>Company Info</div>
                <FMCSARow label="Legal Name"        value={profile.legalName} />
                {profile.dbaName && <FMCSARow label="DBA / Trade Name" value={profile.dbaName} />}
                <FMCSARow label="Entity Type"       value={profile.entityType} />
                <FMCSARow label="Operating Status"  value={profile.operatingStatus} color={profile.outOfService?C.danger:C.success} />
                <FMCSARow label="USDOT Status"      value={profile.usdotStatus} />
                <FMCSARow label="Safety Rating"     value={profile.safetyRating} color={FMCSA_RATING_COLOR[profile.safetyRating]} />
                <FMCSARow label="Rating Date"       value={profile.ratingDate} />
                <FMCSARow label="Address"           value={profile.address} />
                <FMCSARow label="Phone"             value={profile.phone} />
                <FMCSARow label="Power Units"       value={profile.powerUnits} />
                <FMCSARow label="Drivers"           value={profile.drivers} />
                <FMCSARow label="MCS-150 Date"      value={profile.mcs150Date} />
                <FMCSARow label="MCS-150 Mileage"   value={profile.mcs150Mileage} />
                {profile.stateCarrierId && <FMCSARow label="State Carrier ID" value={profile.stateCarrierId} />}
              </div>

              {/* Authority */}
              <div style={{ background:C.bg, borderRadius:8, padding:"10px 14px", border:`1px solid ${C.border}`, marginBottom:12 }}>
                <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>Operating Authority</div>
                <FMCSARow label="Common Authority"   value={profile.commonAuthority} />
                <FMCSARow label="Contract Authority" value={profile.contractAuthority} />
                <FMCSARow label="Broker Authority"   value={profile.brokerAuthority} />
                <FMCSARow label="Insurance Required" value={profile.insuranceRequired} />
                <FMCSARow label="Insurance on File"  value={profile.insuranceOnFile} />
              </div>

              {/* Crashes */}
              {(profile.crashes?.total != null) && (
                <div style={{ background:C.bg, borderRadius:8, padding:"10px 14px", border:`1px solid ${C.border}`, marginBottom:12 }}>
                  <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>Crashes (24 months)</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
                    {[
                      { label:"Fatal",  value:profile.crashes.fatal,  color:profile.crashes.fatal  > 0 ? C.danger  : C.success },
                      { label:"Injury", value:profile.crashes.injury, color:profile.crashes.injury > 0 ? C.warn    : C.success },
                      { label:"Tow",    value:profile.crashes.tow,    color:profile.crashes.tow    > 0 ? C.warn    : C.success },
                      { label:"Total",  value:profile.crashes.total,  color:profile.crashes.total  > 0 ? C.danger  : C.success },
                    ].map(s => s.value == null ? null : (
                      <div key={s.label} style={{ background:C.surface, borderRadius:7, padding:"8px", textAlign:"center", border:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", marginBottom:4 }}>{s.label}</div>
                        <div style={{ fontSize:18, fontWeight:700, color:s.color, fontFamily:C.mono }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Inspections */}
              {(profile.inspections?.vehicle != null) && (
                <div style={{ background:C.bg, borderRadius:8, padding:"10px 14px", border:`1px solid ${C.border}`, marginBottom:12 }}>
                  <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>Inspections (24 months)</div>
                  {[
                    { label:"Vehicle inspections", total:profile.inspections.vehicle, oos:profile.inspections.vehicleOOS },
                    { label:"Driver inspections",  total:profile.inspections.driver,  oos:profile.inspections.driverOOS  },
                    { label:"Hazmat inspections",  total:profile.inspections.hazmat,  oos:profile.inspections.hazmatOOS  },
                  ].filter(r => r.total != null).map(r => {
                    const oosPct = r.total > 0 ? Math.round((r.oos/r.total)*100) : 0;
                    return (
                      <div key={r.label} style={{ marginBottom:10 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.textMd, marginBottom:3 }}>
                          <span>{r.label}</span>
                          <span>{r.oos} OOS / {r.total} total
                            <span style={{ color: oosPct>20?C.danger:oosPct>10?C.warn:C.success, marginLeft:6 }}>({oosPct}%)</span>
                          </span>
                        </div>
                        <ProgressBar pct={r.total>0?(r.oos/r.total)*100:0} color={oosPct>20?C.danger:oosPct>10?C.warn:C.success} height={4} />
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ fontSize:11, textAlign:"center", padding:"6px 10px", borderRadius:6,
                background: profile.source.includes("Demo") ? C.warnGl : C.succGl,
                border: `1px solid ${profile.source.includes("Demo") ? C.warn : C.success}33`,
                color: profile.source.includes("Demo") ? C.warn : C.textMd }}>
                {profile.source.includes("Demo") ? "⚠ " : "✓ "}
                {profile.source} · {new Date(profile.verifiedAt).toLocaleString()}
              </div>
            </div>
          )}

          {/* Idle state */}
          {status==="idle" && (
            <div style={{ textAlign:"center", padding:"32px 0", color:C.textDm }}>
              <div style={{ fontSize:32, marginBottom:8, opacity:0.3 }}>🛡</div>
              <div style={{ fontSize:13 }}>Enter a USDOT number to check safety record, authority status, crash history, and inspection scores from FMCSA.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── Sample shipper prospect directory (used as fallback alongside real shippers) ──

const InfoCard = ({ label, value }) => (
  <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 12px" }}>
    <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:3 }}>{label}</div>
    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{value}</div>
  </div>
);

const ShipperDirectoryPage = () => {
  const { token } = useAuth();
  const [city, setCity]         = useState("");
  const [state, setState]       = useState("");
  const [industry, setIndustry] = useState("");
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [selected, setSelected] = useState(null);
  const [searched, setSearched] = useState(false);

  const runSearch = async () => {
    if (!city && !state) {
      setError("Enter a city or state to search");
      return;
    }
    setError(null);
    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams();
      if (city)     params.set("city", city);
      if (state)    params.set("state", state);
      if (industry) params.set("industry", industry);
      const res = await fetch(`${API_BASE}/shippers/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setResults(data.results || []);
      } else {
        setError(data.error || "Search failed");
        setResults([]);
      }
    } catch(e) {
      setError("Network error — try again");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display:"flex", gap:16, height:"100%" }}>
      {/* Left: search form + list */}
      <div style={{ width:400, flexShrink:0, display:"flex", flexDirection:"column", gap:10 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:0 }}>Find Shippers</h2>
          <p style={{ fontSize:12, color:C.textMd, margin:"3px 0 0" }}>Search the web for shippers and manufacturers in a region to grow your book of business</p>
        </div>

        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:14 }}>
          <div style={{ display:"flex", gap:8, marginBottom:8 }}>
            <input value={city} onChange={e=>setCity(e.target.value)} placeholder="City (e.g. Dallas)"
              onKeyDown={e => e.key==="Enter" && runSearch()}
              style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", color:C.text, fontSize:13, outline:"none" }} />
            <input value={state} onChange={e=>setState(e.target.value.toUpperCase().slice(0,2))} placeholder="State"
              onKeyDown={e => e.key==="Enter" && runSearch()}
              style={{ width:70, background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", color:C.text, fontSize:13, outline:"none" }} />
          </div>
          <input value={industry} onChange={e=>setIndustry(e.target.value)} placeholder="Industry or commodity (optional) — e.g. food, steel, electronics"
            onKeyDown={e => e.key==="Enter" && runSearch()}
            style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", color:C.text, fontSize:13, outline:"none", boxSizing:"border-box", marginBottom:10 }} />
          <button onClick={runSearch} disabled={loading} style={{
            width:"100%", background:loading?C.surf2:C.accent, color:"#fff", border:"none",
            borderRadius:8, padding:"10px", fontSize:13, fontWeight:700, cursor:loading?"not-allowed":"pointer", fontFamily:C.sans,
          }}>
            {loading ? "Searching & finding contacts…" : "🔎 Search"}
          </button>
          {error && <div style={{ fontSize:12, color:C.danger, marginTop:8 }}>{error}</div>}
        </div>

        {searched && !loading && (
          <div style={{ fontSize:11, color:C.textDm }}>
            {results.length} result{results.length!==1?"s":""} found
          </div>
        )}

        <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:8 }}>
          {!searched && !loading && (
            <div style={{ textAlign:"center", padding:"40px 20px", color:C.textDm, fontSize:13 }}>
              Enter a city/state and click Search to find shippers and manufacturers in that area.
            </div>
          )}
          {loading && (
            <div style={{ textAlign:"center", padding:"40px 20px", color:C.textDm, fontSize:13 }}>
              🔎 Searching the web and checking company sites for contact info…
              <div style={{ fontSize:11, marginTop:6, opacity:0.7 }}>This can take 10-20 seconds</div>
            </div>
          )}
          {results.map((r, i) => (
            <div key={i} onClick={() => setSelected(r)} style={{
              background:selected===r?C.surf3:C.surface,
              border:`1px solid ${selected===r?C.accent:C.border}`,
              borderRadius:10, padding:"12px 14px", cursor:"pointer",
            }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{r.title}</div>
              <div style={{ fontSize:11, color:C.textMd, marginTop:3, lineHeight:1.4 }}>{r.snippet}</div>
              <div style={{ fontSize:10, color:C.accentLt, marginTop:5 }}>{r.domain || r.url}</div>
              {(r.phone || r.email) && (
                <div style={{ display:"flex", gap:6, marginTop:6, flexWrap:"wrap" }}>
                  {r.phone && <span style={{ fontSize:10, fontWeight:600, color:C.success, background:C.succGl, borderRadius:4, padding:"2px 6px" }}>📞 {r.phone}</span>}
                  {r.email && <span style={{ fontSize:10, fontWeight:600, color:C.accentLt, background:C.accentGl, borderRadius:4, padding:"2px 6px" }}>✉️ {r.email}</span>}
                </div>
              )}
            </div>
          ))}
          {searched && !loading && results.length===0 && !error && (
            <div style={{ textAlign:"center", padding:32, color:C.textDm, fontSize:13 }}>No results — try a broader search.</div>
          )}
        </div>
      </div>

      {/* Right: detail */}
      <div style={{ flex:1 }}>
        {selected ? (
          <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:12, padding:24 }}>
            <div style={{ fontSize:20, fontWeight:700, color:C.text, marginBottom:8 }}>{selected.title}</div>
            <a href={selected.url} target="_blank" rel="noreferrer" style={{ fontSize:13, color:C.accentLt, textDecoration:"none", display:"block", marginBottom:16 }}>
              {selected.domain || selected.url} ↗
            </a>

            {/* Contact info card */}
            <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:16, marginBottom:16 }}>
              <div style={{ fontSize:11, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10 }}>Contact Info</div>
              {selected.phone || selected.email ? (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`} style={{ display:"flex", alignItems:"center", gap:8, color:C.text, textDecoration:"none", fontSize:14 }}>
                      <span style={{ background:C.succGl, color:C.success, borderRadius:6, padding:"4px 8px", fontSize:12 }}>📞</span>
                      {selected.phone}
                    </a>
                  )}
                  {selected.email && (
                    <a href={`mailto:${selected.email}`} style={{ display:"flex", alignItems:"center", gap:8, color:C.text, textDecoration:"none", fontSize:14 }}>
                      <span style={{ background:C.accentGl, color:C.accentLt, borderRadius:6, padding:"4px 8px", fontSize:12 }}>✉️</span>
                      {selected.email}
                    </a>
                  )}
                  <div style={{ fontSize:10, color:C.textDm, marginTop:2 }}>Found on their website — verify before relying on it.</div>
                </div>
              ) : (
                <div style={{ fontSize:13, color:C.textMd }}>
                  No phone or email found automatically on their homepage.
                  <a href={selected.url} target="_blank" rel="noreferrer" style={{ color:C.accentLt, textDecoration:"none", display:"block", marginTop:6 }}>
                    Check their Contact page ↗
                  </a>
                </div>
              )}
            </div>

            <div style={{ fontSize:14, color:C.textMd, lineHeight:1.7, marginBottom:20 }}>
              {selected.snippet}
            </div>
            <div style={{ background:`${C.accent}11`, border:`1px solid ${C.accent}33`, borderRadius:8, padding:"12px 14px", fontSize:12, color:C.textMd, marginBottom:16 }}>
              💡 This is a web search result, not a verified FreightFlow shipper. Contact info is auto-extracted from their site and may be outdated — confirm before relying on it.
            </div>
            <a href={selected.url} target="_blank" rel="noreferrer" style={{
              display:"block", textAlign:"center", background:C.accent, color:"#fff", textDecoration:"none",
              borderRadius:8, padding:"11px", fontSize:13, fontWeight:700, fontFamily:C.sans,
            }}>
              Visit Website ↗
            </a>
          </div>
        ) : (
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:"60px 24px", textAlign:"center" }}>
            <div style={{ fontSize:48, opacity:0.15, marginBottom:12 }}>🔎</div>
            <div style={{ fontSize:15, fontWeight:600, color:C.text, marginBottom:6 }}>Select a result to view details</div>
            <div style={{ fontSize:13, color:C.textMd }}>Search by city, state, or industry to find shippers in your target region.</div>
          </div>
        )}
      </div>
    </div>
  );
};

const CarrierDirectoryPage = ({ loads }) => {
  const [search, setSearch]         = useState("");
  const [fmcsaCarrier, setFmcsaCarrier] = useState(null);
  const [filterType, setFilterType] = useState("ALL");
  const [filterState, setFilterState] = useState("ALL");
  const [sortBy, setSortBy]         = useState("rating");
  const [selected, setSelected]     = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [inviteLoad, setInviteLoad] = useState("");
  const [inviteSent, setInviteSent] = useState(false);
  const [contactMsg, setContactMsg] = useState("");
  const [contactSent, setContactSent] = useState(false);

  const types  = ["ALL", "DRY_VAN", "FLATBED", "REEFER", "TANKER", "STEP_DECK", "LOWBOY", "BOX_TRUCK"];
  const states = ["ALL", "IL", "GA", "TX", "CA", "CO", "NC", "WA"];

  const filtered = CARRIERS_DATA
    .filter(c => {
      const matchSearch = !search ||
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.city.toLowerCase().includes(search.toLowerCase()) ||
        c.dot.toLowerCase().includes(search.toLowerCase());
      const matchType  = filterType  === "ALL" || c.types.includes(filterType);
      const matchState = filterState === "ALL" || c.state === filterState;
      return matchSearch && matchType && matchState;
    })
    .sort((a, b) => {
      if (sortBy === "rating")     return b.rating - a.rating;
      if (sortBy === "deliveries") return b.deliveries - a.deliveries;
      if (sortBy === "onTime")     return b.onTime - a.onTime;
      if (sortBy === "rate")       return a.avgRate - b.avgRate;
      return 0;
    });

  // How many loads has this carrier handled for us (from the loads data)
  const loadsWithCarrier = (carrierId) =>
    loads.filter(l => l.carrierId === carrierId).length;

  return (
    <>
    <div style={{ display:"flex", gap:16, height:"100%" }}>

      {/* Left: list */}
      <div style={{ width:380, flexShrink:0, display:"flex", flexDirection:"column", gap:10 }}>
        {/* Search */}
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"7px 12px", display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ color:C.textDm }}>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search carriers, city, DOT/MC..."
            style={{ flex:1, background:"transparent", border:"none", color:C.text, fontSize:13, outline:"none", fontFamily:C.sans }} />
        </div>

        {/* Filters row */}
        <div style={{ display:"flex", gap:8 }}>
          <select value={filterType} onChange={e=>setFilterType(e.target.value)}
            style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"6px 8px", color:C.text, fontSize:12, outline:"none" }}>
            {types.map(t => <option key={t} value={t}>{t === "ALL" ? "All equipment" : EQUIP_META[t]?.label || t}</option>)}
          </select>
          <select value={filterState} onChange={e=>setFilterState(e.target.value)}
            style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"6px 8px", color:C.text, fontSize:12, outline:"none" }}>
            {states.map(s => <option key={s} value={s}>{s === "ALL" ? "All states" : s}</option>)}
          </select>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"6px 8px", color:C.text, fontSize:12, outline:"none" }}>
            <option value="rating">Top rated</option>
            <option value="deliveries">Most loads</option>
            <option value="onTime">On-time %</option>
            <option value="rate">Lowest rate</option>
          </select>
        </div>

        <div style={{ fontSize:11, color:C.textDm }}>{filtered.length} carriers</div>

        {/* Cards */}
        <div style={{ display:"flex", flexDirection:"column", gap:8, overflowY:"auto", flex:1 }}>
          {filtered.map(c => {
            const history = loadsWithCarrier(c.id);
            const isSelected = selected?.id === c.id;
            return (
              <div key={c.id} onClick={() => setSelected(c)} style={{
                background: isSelected ? C.surf3 : C.surface,
                border:`1px solid ${isSelected ? C.accentLt : C.border}`,
                borderRadius:10, padding:"13px 14px", cursor:"pointer",
                boxShadow: isSelected ? `0 0 0 1px ${C.accentLt}22` : "none",
                transition:"all 0.14s",
              }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                      <span style={{ fontSize:14, fontWeight:600, color:C.text }}>{c.name}</span>
                      {c.verified && <span style={{ fontSize:9, fontWeight:700, color:C.success, background:C.succGl, border:`1px solid ${C.success}44`, padding:"1px 5px", borderRadius:3 }}>✓ VERIFIED</span>}
                    </div>
                    <div style={{ fontSize:11, color:C.textMd }}>{c.city}, {c.state} · {c.dot}</div>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <StarRating rating={c.rating} />
                    <div style={{ fontSize:10, color:C.textDm, marginTop:2 }}>{c.deliveries} deliveries</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:8 }}>
                  {c.types.map(t => <EquipBadge key={t} type={t} />)}
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.textMd }}>
                  <span>🚛 {c.trucks} truck{c.trucks>1?"s":""}</span>
                  <span>⏱ {c.onTime}% on-time</span>
                  <span style={{ color: history > 0 ? C.success : C.textDm }}>
                    {history > 0 ? `✓ ${history} load${history>1?"s":""} with you` : "New to you"}
                  </span>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ textAlign:"center", color:C.textDm, padding:40, fontSize:13 }}>No carriers match your filters</div>
          )}
        </div>
      </div>

      {/* Right: detail */}
      {selected ? (
        <div style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden", display:"flex", flexDirection:"column" }}>
          {/* Header */}
          <div style={{ padding:"18px 22px", borderBottom:`1px solid ${C.border}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <div style={{ width:40, height:40, borderRadius:10, background:`linear-gradient(135deg,${C.accent},${C.teal})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>🚛</div>
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:18, fontWeight:700, color:C.text }}>{selected.name}</span>
                      {selected.verified && <span style={{ fontSize:10, fontWeight:700, color:C.success, background:C.succGl, border:`1px solid ${C.success}44`, padding:"2px 7px", borderRadius:4 }}>✓ VERIFIED</span>}
                    </div>
                    <div style={{ fontSize:12, color:C.textMd }}>{selected.city}, {selected.state} · DOT/MC: {selected.dot}</div>
                  </div>
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <Btn variant="ghost" size="sm" onClick={() => setFmcsaCarrier(selected)}>🛡 Verify FMCSA</Btn>
                <Btn variant="outline" size="sm" onClick={() => { setContactMsg(""); setContactSent(false); setShowContact(true); }}>✉ Contact</Btn>
                <Btn variant="primary" size="sm" onClick={() => { setInviteLoad(""); setInviteSent(false); setShowInvite(true); }}>+ Invite to Load</Btn>
                <button onClick={() => setSelected(null)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>✕</button>
              </div>
            </div>
          </div>

          <div style={{ flex:1, overflowY:"auto", padding:22 }}>
            {/* Stats row */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:20 }}>
              {[
                { label:"Rating",       value:<StarRating rating={selected.rating} />, raw:null },
                { label:"Deliveries",   value:selected.deliveries,    color:C.text },
                { label:"On-time",      value:`${selected.onTime}%`,  color:selected.onTime>=95?C.success:selected.onTime>=90?C.warn:C.danger },
                { label:"Avg $/mi",     value:`$${selected.avgRate}`, color:C.success },
              ].map(s => (
                <div key={s.label} style={{ background:C.bg, borderRadius:8, padding:"11px 13px", border:`1px solid ${C.border}`, textAlign:"center" }}>
                  <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:5 }}>{s.label}</div>
                  <div style={{ fontSize:s.label==="Rating"?13:20, fontWeight:700, color:s.color||C.text, fontFamily:s.label==="Rating"?"inherit":C.mono }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Bio */}
            <div style={{ background:C.bg, borderRadius:8, padding:"13px 15px", border:`1px solid ${C.border}`, marginBottom:16 }}>
              <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:6 }}>About</div>
              <div style={{ fontSize:13, color:C.textMd, lineHeight:1.6 }}>{selected.bio}</div>
            </div>

            {/* Equipment */}
            <div style={{ background:C.bg, borderRadius:8, padding:"13px 15px", border:`1px solid ${C.border}`, marginBottom:16 }}>
              <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>Equipment & Fleet</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
                {selected.types.map(t => <EquipBadge key={t} type={t} />)}
              </div>
              <div style={{ fontSize:12, color:C.textMd }}>{selected.trucks} active truck{selected.trucks>1?"s":""} in fleet</div>
            </div>

            {/* Load history with this shipper */}
            {(() => {
              const history = loads.filter(l => l.carrierId === selected.id);
              return history.length > 0 ? (
                <div style={{ background:C.bg, borderRadius:8, padding:"13px 15px", border:`1px solid ${C.border}`, marginBottom:16 }}>
                  <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>
                    Your History with {selected.name}
                  </div>
                  {history.map(l => (
                    <div key={l.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize:12, color:C.text }}>{l.origin.split(",")[0]} → {l.dest.split(",")[0]}</div>
                        <div style={{ fontSize:11, color:C.textDm }}>{l.number} · {l.pickupDate}</div>
                      </div>
                      <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                        <Badge status={l.status} />
                        <span style={{ fontSize:12, fontWeight:600, color:C.success, fontFamily:C.mono }}>{fmt$(l.rate)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ background:C.bg, borderRadius:8, padding:"13px 15px", border:`1px solid ${C.border}`, marginBottom:16 }}>
                  <div style={{ fontSize:12, color:C.textDm }}>No prior loads with this carrier. Invite them to bid on your next shipment.</div>
                </div>
              );
            })()}

            {/* Performance bars */}
            <div style={{ background:C.bg, borderRadius:8, padding:"13px 15px", border:`1px solid ${C.border}` }}>
              <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:12 }}>Performance</div>
              {[
                { label:"On-time delivery", pct:selected.onTime,              color: selected.onTime>=95?C.success:C.warn },
                { label:"Rating",           pct:selected.rating/5*100,        color:C.warn    },
                { label:"Network coverage", pct:Math.min(selected.deliveries/400*100,100), color:C.accentLt },
              ].map(bar => (
                <div key={bar.label} style={{ marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.textMd, marginBottom:4 }}>
                    <span>{bar.label}</span>
                    <span style={{ color:bar.color, fontFamily:C.mono }}>{Math.round(bar.pct)}%</span>
                  </div>
                  <ProgressBar pct={bar.pct} color={bar.color} height={5} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:10 }}>
          <div style={{ fontSize:40, opacity:0.12 }}>🚚</div>
          <div style={{ fontSize:14, color:C.textDm }}>Select a carrier to view their profile</div>
          <div style={{ fontSize:12, color:C.textDm }}>Search by name, city, DOT/MC number, or equipment type</div>
        </div>
      )}
    </div>

      {fmcsaCarrier && (
        <FMCSAPanel carrier={fmcsaCarrier} onClose={() => setFmcsaCarrier(null)} />
      )}

        {/* Invite to Load modal */}
        {showInvite && selected && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center" }}
            onClick={e=>e.target===e.currentTarget&&setShowInvite(false)}>
            <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:14, width:460, overflow:"hidden" }}>
              <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:C.text }}>Invite to Load</div>
                  <div style={{ fontSize:12, color:C.textMd, marginTop:2 }}>Inviting: <strong style={{color:C.text}}>{selected.name}</strong></div>
                </div>
                <button onClick={()=>setShowInvite(false)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>✕</button>
              </div>
              <div style={{ padding:20 }}>
                {inviteSent ? (
                  <div style={{ textAlign:"center", padding:"16px 0" }}>
                    <div style={{ fontSize:40, marginBottom:12 }}>📨</div>
                    <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:8 }}>Invitation sent!</div>
                    <div style={{ fontSize:13, color:C.textMd, lineHeight:1.7 }}>
                      {selected.name} has been invited to bid on {inviteLoad || "your load"}.<br />
                      They can submit a bid from the Load Board.
                    </div>
                    <Btn variant="primary" style={{marginTop:16}} onClick={()=>setShowInvite(false)}>Done</Btn>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom:16 }}>
                      <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:6 }}>Select a Posted Load</label>
                      <select value={inviteLoad} onChange={e=>setInviteLoad(e.target.value)}
                        style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:7, padding:"9px 12px", color:C.text, fontSize:13, outline:"none" }}>
                        <option value="">— Choose a load —</option>
                        {loads.filter(l=>["POSTED","BIDDING"].includes(l.status)).map(l => (
                          <option key={l.id} value={l.number}>
                            {l.number} · {l.origin.split(",")[0]} → {l.dest.split(",")[0]} · {fmt$(l.rate)} · {l.pickupDate}
                          </option>
                        ))}
                      </select>
                      {loads.filter(l=>["POSTED","BIDDING"].includes(l.status)).length===0 && (
                        <div style={{ fontSize:11, color:C.textDm, marginTop:4 }}>No active posted loads — post a load first.</div>
                      )}
                    </div>
                    <div style={{ background:C.accentGl, border:`1px solid ${C.accent}33`, borderRadius:8, padding:"10px 14px", fontSize:12, color:C.textMd, marginBottom:16 }}>
                      ℹ {selected.name} will be notified and can submit a bid. You are not committed to accepting it.
                    </div>
                    <div style={{ display:"flex", gap:10 }}>
                      <Btn variant="ghost" onClick={()=>setShowInvite(false)}>Cancel</Btn>
                      <Btn variant="primary" style={{flex:1}} disabled={!inviteLoad} onClick={()=>setInviteSent(true)}>Send Invitation</Btn>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contact carrier modal */}
        {showContact && selected && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center" }}
            onClick={e=>e.target===e.currentTarget&&setShowContact(false)}>
            <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:14, width:460, overflow:"hidden" }}>
              <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:C.text }}>Contact Carrier</div>
                  <div style={{ fontSize:12, color:C.textMd, marginTop:2 }}>{selected.name}</div>
                </div>
                <button onClick={()=>setShowContact(false)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>✕</button>
              </div>
              <div style={{ padding:20 }}>
                {contactSent ? (
                  <div style={{ textAlign:"center", padding:"16px 0" }}>
                    <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
                    <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:8 }}>Message sent!</div>
                    <div style={{ fontSize:13, color:C.textMd }}>Your message has been sent to {selected.name}.</div>
                    <Btn variant="primary" style={{marginTop:16}} onClick={()=>setShowContact(false)}>Done</Btn>
                  </div>
                ) : (
                  <>
                    <div style={{ background:C.bg, borderRadius:8, padding:"10px 14px", border:`1px solid ${C.border}`, marginBottom:14, fontSize:12, color:C.textMd }}>
                      <div style={{ fontWeight:600, color:C.text, marginBottom:2 }}>{selected.name}</div>
                      <div>DOT: {selected.dot} · {selected.city}, {selected.state}</div>
                    </div>
                    <div style={{ marginBottom:14 }}>
                      <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:6 }}>Message</label>
                      <textarea value={contactMsg} onChange={e=>setContactMsg(e.target.value)} rows={5}
                        placeholder={`Hi, I have freight lanes in your coverage area and would like to discuss working together...`}
                        style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:7, padding:"9px 12px", color:C.text, fontSize:13, outline:"none", resize:"none", fontFamily:C.sans, boxSizing:"border-box" }} />
                    </div>
                    <div style={{ display:"flex", gap:10 }}>
                      <Btn variant="ghost" onClick={()=>setShowContact(false)}>Cancel</Btn>
                      <Btn variant="primary" style={{flex:1}} disabled={!contactMsg.trim()} onClick={()=>setContactSent(true)}>Send Message</Btn>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
    </>
  );
};


// ─── Standalone FMCSA Lookup Page ────────────────────────────────────────────

const FMCSALookupPage = () => {
  const [dotInput,  setDotInput]  = useState("");
  const [mcInput,   setMcInput]   = useState("");
  const [nameInput, setNameInput] = useState("");
  const [mode,      setMode]      = useState("dot");
  const [status,    setStatus]    = useState("idle");
  const [profile,   setProfile]   = useState(null);
  const [nameResults, setNameResults] = useState([]);
  const [error,     setError]     = useState(null);
  const [history,   setHistory]   = useState([]);
  const { token: fmcsaToken } = useAuth();

  const run = async () => {
    const q = mode === "dot" ? dotInput.trim() : mode === "mc" ? mcInput.trim() : nameInput.trim();
    if (!q) return;
    setStatus("loading"); setError(null); setProfile(null); setNameResults([]);
    try {
      if (mode === "name") {
        const res = await fetch(`${API_BASE}/fmcsa/search/name/${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${fmcsaToken}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Search failed");
        if (data.type === "single") {
          setProfile(data.profile);
          setHistory(h => [{ q, mode, name: data.profile.legalName, rating: data.profile.safetyRating, ts: new Date().toLocaleTimeString() }, ...h].slice(0, 10));
        } else {
          setNameResults(data.results || []);
        }
        setStatus("done");
      } else {
        const p = await fmcsaLookup(q, mode);
        setProfile(p);
        setStatus("done");
        setHistory(h => [{ q, mode, name: p.legalName, rating: p.safetyRating, oos: p.outOfService, ts: new Date().toLocaleTimeString() }, ...h].slice(0, 10));
      }
    } catch(e) {
      setError(e.message); setStatus("error");
    }
  };

  const lookupFromName = async (dot) => {
    setMode("dot"); setDotInput(dot);
    setStatus("loading"); setError(null); setProfile(null); setNameResults([]);
    try {
      const p = await fmcsaLookup(dot, "dot");
      setProfile(p); setStatus("done");
    } catch(e) { setError(e.message); setStatus("error"); }
  };

  const RATING_COLOR = FMCSA_RATING_COLOR;

  const Pill = ({ label, value, color }) => (
    <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:"11px 14px", textAlign:"center" }}>
      <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:13, fontWeight:700, color: color || C.text }}>{value ?? "—"}</div>
    </div>
  );


  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:"0 0 4px" }}>FMCSA Carrier Verification</h2>
        <p style={{ fontSize:13, color:C.textMd, margin:0 }}>Look up any carrier's safety rating, authority, insurance, and inspection history from the FMCSA SAFER database.</p>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:16 }}>
        {/* Main lookup panel */}
        <div>
          {/* Mode toggle */}
          <div style={{ display:"flex", gap:0, background:C.bg, borderRadius:8, padding:3, border:`1px solid ${C.border}`, width:"fit-content", marginBottom:14 }}>
            {["dot","mc","name"].map(m => (
              <button key={m} onClick={() => { setMode(m); setStatus("idle"); setProfile(null); setNameResults([]); setError(null); }} style={{
                background: mode===m ? C.accent : "transparent",
                color: mode===m ? "#fff" : C.textMd,
                border:"none", borderRadius:6, padding:"5px 18px", fontSize:12, fontWeight:600, cursor:"pointer",
              }}>{m === "dot" ? "DOT Number" : m === "mc" ? "MC Number" : "Company Name"}</button>
            ))}
          </div>

          {/* Input row */}
          <div style={{ display:"flex", gap:10, marginBottom:8 }}>
            {mode === "name" ? (
              <input
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                placeholder="Enter company name — e.g. Arrow Logistics"
                onKeyDown={e => e.key==="Enter" && run()}
                style={{ flex:1, background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:8, padding:"10px 14px", color:C.text, fontSize:15, fontFamily:C.sans, outline:"none" }} />
            ) : (
              <input
                value={mode==="dot" ? dotInput : mcInput}
                onChange={e => mode==="dot" ? setDotInput(e.target.value.replace(/\D/g,"")) : setMcInput(e.target.value.replace(/[^\d]/g,""))}
                placeholder={mode==="dot" ? "Enter USDOT number — e.g. 3791729" : "Enter MC number — e.g. 456789"}
                onKeyDown={e => e.key==="Enter" && run()}
                style={{ flex:1, background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:8, padding:"10px 14px", color:C.text, fontSize:15, fontFamily:C.mono, outline:"none" }} />
            )}
            <Btn onClick={run} disabled={status==="loading"} variant="primary" size="lg">
              {status==="loading" ? "Searching…" : mode === "name" ? "Search" : "Look Up"}
            </Btn>
          </div>
          <div style={{ fontSize:11, color:C.textDm, marginBottom:20 }}>
            {mode === "name"
              ? "Search by company name — returns matching carriers from FMCSA SAFER. Click a result to view full profile."
              : "Data from FMCSA SAFER system — updated daily. No API key required for basic lookups."}
          </div>

          {/* Name search results list */}
          {status==="done" && nameResults.length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:10 }}>{nameResults.length} carriers found — click to view full profile</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {nameResults.map((r, i) => (
                  <div key={i} onClick={() => lookupFromName(r.dot)}
                    style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 14px", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{r.name}</div>
                      <div style={{ fontSize:11, color:C.textMd, marginTop:2 }}>DOT {r.dot}{r.state ? ` · ${r.state}` : ""}</div>
                    </div>
                    <div style={{ fontSize:11, color:C.accentLt }}>View Profile →</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {status==="error" && (
            <div style={{ background:C.dangGl, border:`1px solid ${C.danger}33`, borderRadius:8, padding:"14px 16px", marginBottom:16 }}>
              <div style={{ color:C.danger, fontSize:13, fontWeight:600, marginBottom:8 }}>⚠ Lookup failed</div>
              {error.split("\n").map((line, i) => line.trim() ? (
                <div key={i} style={{ color: line.startsWith("SAFER_WEB") || line.startsWith("cd ") || line.startsWith("Start") ? C.accentLt : C.textMd,
                  fontSize:12, marginBottom:4, fontFamily: line.startsWith("SAFER_WEB") || line.startsWith("cd ") ? C.mono : C.sans }}>
                  {line}
                </div>
              ) : <div key={i} style={{ height:6 }} />)}
            </div>
          )}

          {/* Results */}
          {status==="done" && profile && (() => {
            const rc = RATING_COLOR[profile.safetyRating] || C.textMd;
            return (
              <div>
                {/* Header card */}
                <div style={{ background: profile.outOfService ? C.dangGl : C.succGl, border:`1px solid ${profile.outOfService?C.danger:C.success}33`, borderRadius:12, padding:"16px 20px", marginBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <div style={{ fontSize:20, fontWeight:700, color:C.text }}>{profile.legalName}</div>
                    {profile.dbaName && (
                      <div style={{ fontSize:12, color:C.textMd, marginTop:2 }}>
                        DBA: <strong style={{ color:C.text }}>{profile.dbaName}</strong>
                      </div>
                    )}
                    <div style={{ fontSize:12, color:C.textMd, marginTop:6, display:"flex", gap:12, flexWrap:"wrap" }}>
                      <span>DOT {profile.dotNumber}</span>
                      {profile.mcNumber && <span>{profile.mcNumber}</span>}
                      {profile.address && <span>📍 {profile.address}</span>}
                      {profile.phone   && <span>📞 {profile.phone}</span>}
                    </div>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:8 }}>
                    <span style={{ fontSize:12, fontWeight:700, padding:"3px 10px", borderRadius:5, background: (profile.outOfService||/revoked|not authorized|inactive/i.test(profile.operatingStatus??"")?C.dangGl:C.succGl), border:`1px solid ${(profile.outOfService||/revoked|not authorized|inactive/i.test(profile.operatingStatus??""))?C.danger:C.success}44`, color: (profile.outOfService||/revoked|not authorized|inactive/i.test(profile.operatingStatus??""))?C.danger:C.success }}>
                      {profile.outOfService ? "⛔ OUT OF SERVICE" : /revoked|not authorized|inactive/i.test(profile.operatingStatus ?? "") ? "⛔ NOT AUTHORIZED" : "✅ AUTHORIZED"}
                    </span>
                    <span style={{ fontSize:12, fontWeight:700, padding:"3px 10px", borderRadius:5, background:`${rc}18`, border:`1px solid ${rc}44`, color:rc }}>
                      {profile.safetyRating}
                    </span>
                  </div>
                </div>

                {/* Stats grid */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:8, marginBottom:16 }}>
                  <Pill label="USDOT Status"   value={profile.usdotStatus}    color={profile.outOfService?C.danger:C.success} />
                  <Pill label="Safety Rating"  value={profile.safetyRating}   color={rc} />
                  <Pill label="Power Units"    value={profile.powerUnits} />
                  <Pill label="Drivers"        value={profile.drivers} />
                  <Pill label="Entity Type"    value={profile.entityType} />
                  <Pill label="MCS-150"        value={profile.mcs150Date} />
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
                  {/* Authority & Insurance */}
                  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"13px 16px" }}>
                    <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Company Details</div>
                    <FMCSARow label="Entity Type"       value={profile.entityType} />
                    <FMCSARow label="USDOT Status"      value={profile.usdotStatus} />
                    <FMCSARow label="Phone"             value={profile.phone} />
                    <FMCSARow label="Power Units"       value={profile.powerUnits} />
                    <FMCSARow label="Drivers"           value={profile.drivers} />
                    <FMCSARow label="MCS-150 Date"      value={profile.mcs150Date} />
                    <FMCSARow label="MCS-150 Mileage"   value={profile.mcs150Mileage} />
                    {profile.stateCarrierId && <FMCSARow label="State Carrier ID" value={profile.stateCarrierId} />}
                    <FMCSARow label="Rating Date"       value={profile.ratingDate} />
                    <FMCSARow label="Common Authority"   value={profile.commonAuthority} />
                    <FMCSARow label="Contract Authority" value={profile.contractAuthority} />
                    <FMCSARow label="Broker Authority"   value={profile.brokerAuthority} />
                    <FMCSARow label="Insurance Required" value={profile.insuranceRequired} />
                    <FMCSARow label="Insurance on File"  value={profile.insuranceOnFile} />
                  </div>

                  {/* Crashes */}
                  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"13px 16px" }}>
                    <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Crashes (24 months)</div>
                    {[
                      { label:"Fatal",  value:profile.crashes?.fatal,  color: profile.crashes?.fatal  > 0 ? C.danger : C.success },
                      { label:"Injury", value:profile.crashes?.injury, color: profile.crashes?.injury > 0 ? C.warn   : C.success },
                      { label:"Tow",    value:profile.crashes?.tow,    color: profile.crashes?.tow    > 0 ? C.warn   : C.success },
                      { label:"Total",  value:profile.crashes?.total,  color: profile.crashes?.total  > 0 ? C.warn   : C.success },
                    ].map(r => <FMCSARow key={r.label} {...r} />)}
                    <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", margin:"12px 0 8px" }}>Inspections (24 months)</div>
                    {[
                      { label:"Vehicle OOS %", value: profile.inspections?.vehicle > 0 ? `${Math.round(profile.inspections.vehicleOOS/profile.inspections.vehicle*100)}% (${profile.inspections.vehicleOOS}/${profile.inspections.vehicle})` : null, color: profile.inspections?.vehicleOOS > 0 ? C.warn : C.success },
                      { label:"Driver OOS %",  value: profile.inspections?.driver  > 0 ? `${Math.round(profile.inspections.driverOOS /profile.inspections.driver *100)}% (${profile.inspections.driverOOS }/${profile.inspections.driver })` : null, color: profile.inspections?.driverOOS  > 0 ? C.warn : C.success },
                    ].map(r => r.value && <FMCSARow key={r.label} {...r} />)}
                  </div>
                </div>

                <div style={{ fontSize:11, color:C.textDm, textAlign:"center", padding:"6px 0", borderTop:`1px solid ${C.border}` }}>
                  Source: {profile.source} · Verified {new Date(profile.verifiedAt).toLocaleString()}
                </div>
              </div>
            );
          })()}

          {status==="idle" && (
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:"32px", textAlign:"center" }}>
              <div style={{ fontSize:44, opacity:0.12, marginBottom:12 }}>🛡</div>
              <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:16 }}>Verify any US motor carrier</div>
              <div style={{ fontSize:11, color:C.textDm, marginBottom:10 }}>Try these example DOT numbers:</div>
              <div style={{ display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap" }}>
                {[
                  { dot:"3791729", name:"ACME FREIGHT LLC",              rating:"Satisfactory",   rc:C.success },
                  { dot:"2529862", name:"SUNBELT CARRIERS INC",          rating:"Satisfactory",   rc:C.success },
                  { dot:"1234567", name:"EAGLE TRANSPORT SOLUTIONS LLC", rating:"Conditional",    rc:C.warn    },
                  { dot:"9999999", name:"OUT OF SERVICE HAULING INC",    rating:"Unsatisfactory", rc:C.danger  },
                ].map(d => (
                  <button key={d.dot} onClick={() => { setDotInput(d.dot); setMode("dot"); }} style={{
                    background:C.surf2, border:`1px solid ${C.border}`, borderRadius:7,
                    padding:"8px 12px", fontSize:11, cursor:"pointer", textAlign:"left", fontFamily:C.sans,
                  }}>
                    <div style={{ fontFamily:C.mono, color:C.accentLt, marginBottom:2, fontSize:10 }}>DOT {d.dot}</div>
                    <div style={{ color:C.text, fontWeight:600, marginBottom:2 }}>{d.name}</div>
                    <div style={{ color:d.rc, fontSize:10 }}>{d.rating}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lookup history sidebar */}
        <div>
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"11px 14px", borderBottom:`1px solid ${C.border}`, fontSize:12, fontWeight:600, color:C.text }}>Recent Lookups</div>
            {history.length === 0 && (
              <div style={{ padding:"24px 14px", fontSize:12, color:C.textDm, textAlign:"center" }}>No lookups yet</div>
            )}
            {history.map((h, i) => {
              const rc = { "Satisfactory":C.success, "Conditional":C.warn, "Unsatisfactory":C.danger, "Unrated":C.textMd }[h.rating] || C.textMd;
              return (
                <div key={i} onClick={() => { setMode(h.mode); if(h.mode==="dot") setDotInput(h.q); else setMcInput(h.q); run(); }}
                  style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, cursor:"pointer" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div style={{ fontSize:12, fontWeight:500, color:C.text, maxWidth:180 }}>{h.name}</div>
                    <span style={{ fontSize:10, fontWeight:700, color:h.oos?C.danger:rc }}>{h.oos?"OOS":h.rating}</span>
                  </div>
                  <div style={{ fontSize:11, color:C.textDm, marginTop:2 }}>{h.mode.toUpperCase()} {h.q} · {h.ts}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};


// ─── Signup / Registration Page (public-facing) ───────────────────────────────

const SIGNUP_PLANS = [
  { id:"starter",  label:"Starter",  price:99,  period:"mo", features:["Up to 50 loads/mo","Load board access","Basic tracking","Email support"] },
  { id:"growth",   label:"Growth",   price:249, period:"mo", features:["Unlimited loads","Priority listing","Real-time GPS","Phone support","Analytics dashboard"], popular:true },
  { id:"enterprise",label:"Enterprise",price:null,period:null, features:["Everything in Growth","Dedicated account manager","Custom integrations","SLA guarantee","Volume discounts"] },
];

const SignupPage = ({ onClose, defaultRole }) => {
  const [step,    setStep]    = useState(defaultRole ? 2 : 1); // skip role step if pre-set
  const [role,    setRole]    = useState(defaultRole || null);
  const [plan,    setPlan]    = useState(null);
  const [form,    setForm]    = useState({ companyName:"", email:"", phone:"", password:"", dot:"", mc:"", address:"", city:"", state:"", zip:"" });
  const [submitting, setSub]  = useState(false);

  const set = k => e => setForm(f => ({...f, [k]: e.target.value}));

  const submit = async () => {
    setSub(true);
    await new Promise(r => setTimeout(r, 1200)); // simulate API call
    setSub(false);
    setStep(4);
  };

  const STEPS = ["Role", "Plan", "Details", "Done"];

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:300 }}
      onClick={e => e.target===e.currentTarget && onClose?.()}>
      <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:16, width:600, maxHeight:"90vh", overflow:"hidden", display:"flex", flexDirection:"column" }}>

        {/* Header */}
        <div style={{ padding:"18px 24px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:C.text }}>Join FreightFlow</div>
            <div style={{ display:"flex", gap:0, marginTop:8 }}>
              {STEPS.map((s,i) => (
                <div key={s} style={{ display:"flex", alignItems:"center" }}>
                  <div style={{ width:20, height:20, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700,
                    background: step>i+1?C.success:step===i+1?C.accent:C.border,
                    color: step>=i+1?"#fff":C.textDm }}>
                    {step>i+1?"✓":i+1}
                  </div>
                  <div style={{ fontSize:11, color:step===i+1?C.text:C.textDm, marginLeft:4, marginRight: i<3?16:0 }}>{s}</div>
                  {i < 3 && <div style={{ width:24, height:1, background:C.border, marginRight:4 }} />}
                </div>
              ))}
            </div>
          </div>
          {onClose && <button onClick={onClose} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>✕</button>}
        </div>

        <div style={{ flex:1, overflowY:"auto", padding:24 }}>

          {/* Step 1: Role */}
          {step===1 && (
            <div>
              <div style={{ fontSize:15, fontWeight:600, color:C.text, marginBottom:6 }}>What best describes you?</div>
              <div style={{ fontSize:13, color:C.textMd, marginBottom:20 }}>Choose your account type to get started.</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                {[
                  { r:"SHIPPER", icon:"📦", title:"Shipper", desc:"I have freight that needs to be moved. I want to post loads and find reliable carriers." },
                  { r:"CARRIER", icon:"🚛", title:"Carrier", desc:"I operate trucks and want to find loads, bid on freight, and grow my business." },
                  { r:"BROKER",  icon:"🤝", title:"Broker",  desc:"I arrange freight between shippers and carriers, manage loads on behalf of clients, and earn a margin." },
                  { r:"BROKER",  icon:"🤝", title:"Broker",  desc:"I connect shippers with carriers, manage freight on behalf of clients, and earn a margin." },
                ].map(opt => (
                  <div key={opt.r} onClick={() => { setRole(opt.r); setStep(2); }} style={{
                    background: role===opt.r ? C.accentGl : C.bg,
                    border:`2px solid ${role===opt.r ? C.accent : C.border}`,
                    borderRadius:12, padding:"20px 18px", cursor:"pointer", transition:"all 0.15s",
                  }}>
                    <div style={{ fontSize:32, marginBottom:10 }}>{opt.icon}</div>
                    <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:6 }}>{opt.title}</div>
                    <div style={{ fontSize:12, color:C.textMd, lineHeight:1.6 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Plan */}
          {step===2 && (
            <div>
              <div style={{ fontSize:15, fontWeight:600, color:C.text, marginBottom:4 }}>Choose a plan</div>
              <div style={{ fontSize:13, color:C.textMd, marginBottom:20 }}>All plans include a 14-day free trial. No credit card required to start.</div>
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {SIGNUP_PLANS.map(p => (
                  <div key={p.id} onClick={() => setPlan(p.id)} style={{
                    background: plan===p.id ? C.accentGl : C.bg,
                    border:`2px solid ${plan===p.id?C.accent:p.popular?C.purple:C.border}`,
                    borderRadius:10, padding:"14px 16px", cursor:"pointer", position:"relative",
                  }}>
                    {p.popular && <span style={{ position:"absolute", top:-10, right:14, fontSize:10, fontWeight:700, background:C.purple, color:"#fff", padding:"2px 10px", borderRadius:10 }}>MOST POPULAR</span>}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{p.label}</div>
                      <div style={{ fontSize:16, fontWeight:700, color:C.success, fontFamily:C.mono }}>
                        {p.price ? `$${p.price}/${p.period}` : "Contact us"}
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                      {p.features.map(f => <span key={f} style={{ fontSize:11, color:C.textMd }}>✓ {f}</span>)}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", justifyContent:"flex-end", marginTop:16 }}>
                <Btn onClick={() => setStep(3)} disabled={!plan} variant="primary">Continue →</Btn>
              </div>
            </div>
          )}

          {/* Step 3: Details form */}
          {step===3 && (
            <div>
              <div style={{ fontSize:15, fontWeight:600, color:C.text, marginBottom:4 }}>
                {role === "CARRIER" ? "Carrier" : "Shipper"} Details
              </div>
              <div style={{ fontSize:13, color:C.textMd, marginBottom:20 }}>
                Your account will be reviewed by our team within 24 hours. You'll get an email when access is granted.
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div style={{ gridColumn:"1/-1" }}>
                  <Input label="Company Name" value={form.companyName} onChange={set("companyName")} placeholder="Acme Freight LLC" />
                </div>
                <Input label="Email" value={form.email} onChange={set("email")} placeholder="you@company.com" type="email" />
                <Input label="Phone" value={form.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" />
                <Input label="Password" value={form.password} onChange={set("password")} placeholder="Min 8 characters" type="password" />
                <Input label="Confirm Password" value={form.confirm} onChange={set("confirm")} placeholder="Repeat password" type="password" />
                {role === "CARRIER" && <>
                  <Input label="USDOT Number" value={form.dot} onChange={set("dot")} placeholder="e.g. 3791729" />
                  <Input label="MC Number" value={form.mc} onChange={set("mc")} placeholder="e.g. 456789" />
                </>}
                {role === "SHIPPER" && <>
                  <Input label="DOT Number (optional)" value={form.dot} onChange={set("dot")} placeholder="e.g. 3791729" />
                  <Input label="Industry" value={form.industry} onChange={set("industry")} placeholder="e.g. Manufacturing" />
                </>}
                <div style={{ gridColumn:"1/-1" }}>
                  <Input label="Address" value={form.address} onChange={set("address")} placeholder="123 Main St" />
                </div>
                <Input label="City" value={form.city} onChange={set("city")} placeholder="Chicago" />
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  <Input label="State" value={form.state} onChange={set("state")} placeholder="IL" small />
                  <Input label="ZIP" value={form.zip} onChange={set("zip")} placeholder="60601" small />
                </div>
              </div>
              {role === "CARRIER" && (
                <div style={{ background:C.accentGl, border:`1px solid ${C.accent}33`, borderRadius:8, padding:"10px 14px", marginTop:8, fontSize:12, color:C.textMd }}>
                  ℹ Your DOT number will be automatically verified against the FMCSA SAFER database during review.
                </div>
              )}
              <div style={{ display:"flex", justifyContent:"space-between", marginTop:20 }}>
                <Btn onClick={() => setStep(2)} variant="ghost">← Back</Btn>
                <Btn onClick={submit} disabled={submitting || !form.companyName || !form.email || !form.password} variant="primary">
                  {submitting ? "Submitting…" : "Submit Application"}
                </Btn>
              </div>
            </div>
          )}

          {/* Step 4: Done */}
          {step===4 && (
            <div style={{ textAlign:"center", padding:"24px 0" }}>
              <div style={{ fontSize:56, marginBottom:16 }}>🎉</div>
              <div style={{ fontSize:20, fontWeight:700, color:C.text, marginBottom:8 }}>Application Submitted!</div>
              <div style={{ fontSize:14, color:C.textMd, marginBottom:24, lineHeight:1.7, maxWidth:380, margin:"0 auto 24px" }}>
                Thank you for applying to FreightFlow. Our team will review your application and send you an access confirmation email within 24 hours.
                {role==="CARRIER" && " We'll automatically verify your FMCSA credentials during this process."}
              </div>
              <div style={{ background:C.bg, borderRadius:10, padding:"14px 18px", border:`1px solid ${C.border}`, textAlign:"left", marginBottom:20, maxWidth:360, margin:"0 auto 20px" }}>
                <div style={{ fontSize:11, color:C.textDm, marginBottom:8, textTransform:"uppercase", letterSpacing:"0.08em" }}>What happens next</div>
                {["Application under review (up to 24h)","FMCSA verification (carriers only)","Admin approves access","You receive login credentials by email","Start posting or bidding on loads"].map((s,i) => (
                  <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:6 }}>
                    <div style={{ width:18, height:18, borderRadius:"50%", background:C.surf3, border:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:C.textMd, flexShrink:0, marginTop:1 }}>{i+1}</div>
                    <span style={{ fontSize:12, color:C.textMd }}>{s}</span>
                  </div>
                ))}
              </div>
              {onClose && <Btn onClick={onClose} variant="primary">Close</Btn>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── Admin Console ────────────────────────────────────────────────────────────

const ADMIN_USERS = [
  { id:"U1", email:"shipper@midwestgoods.com",  company:"Midwest Goods Co.",    role:"SHIPPER", status:"ACTIVE",   plan:"growth",   joinedAt:"May 12",  loads:28, revenue:74200, verified:true,  dotVerified:null,   lastActive:"2h ago"  },
  { id:"U2", email:"shipping@pacificfreight.com",company:"Pacific Freight LLC",  role:"SHIPPER", status:"ACTIVE",   plan:"starter",  joinedAt:"Apr 3",   loads:11, revenue:18400, verified:true,  dotVerified:null,   lastActive:"5h ago"  },
  { id:"U3", email:"ops@arrowlogistics.com",     company:"Arrow Logistics Inc.", role:"CARRIER", status:"ACTIVE",   plan:"growth",   joinedAt:"Mar 20",  loads:44, revenue:112600,verified:true,  dotVerified:"Satisfactory", lastActive:"1h ago"  },
  { id:"U4", email:"dispatch@sunbelttrucking.com",company:"Sunbelt Trucking Co.",role:"CARRIER", status:"ACTIVE",   plan:"starter",  joinedAt:"May 28",  loads:12, revenue:31800, verified:true,  dotVerified:"Satisfactory", lastActive:"3d ago"  },
  { id:"U5", email:"apply@eagletransport.com",   company:"Eagle Transport LLC",  role:"CARRIER", status:"PENDING",  plan:"starter",  joinedAt:"Jun 5",   loads:0,  revenue:0,     verified:false, dotVerified:null,   lastActive:"Never"   },
  { id:"U6", email:"freight@mountainsteel.com",  company:"Mountain Steel Corp.", role:"SHIPPER", status:"PENDING",  plan:"growth",   joinedAt:"Jun 6",   loads:0,  revenue:0,     verified:false, dotVerified:null,   lastActive:"Never"   },
  { id:"U7", email:"ceo@blueridgelogistics.com", company:"Blue Ridge Logistics", role:"CARRIER", status:"PENDING",  plan:"enterprise",joinedAt:"Jun 7",  loads:0,  revenue:0,     verified:false, dotVerified:null,   lastActive:"Never"   },
  { id:"U8", email:"ops@gulfcoastcarriers.com",  company:"Gulf Coast Carriers",  role:"CARRIER", status:"SUSPENDED",plan:"growth",   joinedAt:"Jan 15",  loads:22, revenue:58400, verified:true,  dotVerified:"Conditional", lastActive:"14d ago" },
  { id:"U9", email:"broker@midwestbroker.com",   company:"Midwest Broker LLC",   role:"BROKER",  status:"ACTIVE",   plan:"growth",   joinedAt:"Jun 1",   loads:18, revenue:42600, verified:true,  dotVerified:null,   lastActive:"1h ago"  },
];

const AdminLoadsTab = () => {

        const [loadSearch,  setLoadSearch]  = useState("");
        const [loadStatus,  setLoadStatus]  = useState("ALL");
        const [loadEquip,   setLoadEquip]   = useState("ALL");
        const [loadDetail,  setLoadDetail]  = useState(null);
        const [loadPage,    setLoadPage]    = useState(1);
        const PAGE_SIZE = 8;

        const filtered = LOADS.filter(l => {
          const matchSearch = !loadSearch ||
            l.number.toLowerCase().includes(loadSearch.toLowerCase()) ||
            l.origin.toLowerCase().includes(loadSearch.toLowerCase()) ||
            l.dest.toLowerCase().includes(loadSearch.toLowerCase()) ||
            l.shipper.toLowerCase().includes(loadSearch.toLowerCase()) ||
            (l.carrier||"").toLowerCase().includes(loadSearch.toLowerCase());
          const matchStatus = loadStatus === "ALL" || l.status === loadStatus;
          const matchEquip  = loadEquip  === "ALL" || l.equipmentType === loadEquip;
          return matchSearch && matchStatus && matchEquip;
        });

        const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
        const paginated  = filtered.slice((loadPage-1)*PAGE_SIZE, loadPage*PAGE_SIZE);

        const totalValue = filtered.reduce((a,l) => a + l.rate, 0);
        const inTransit  = filtered.filter(l => l.status==="IN_TRANSIT").length;
        const posted     = filtered.filter(l => l.status==="POSTED").length;

        return (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:0 }}>All Platform Loads</h2>
              <div style={{ fontSize:12, color:C.textMd }}>{filtered.length} loads · ${(totalValue/1000).toFixed(0)}k total value</div>
            </div>

            {/* Summary stats */}
            <div style={{ display:"flex", gap:10, marginBottom:16 }}>
              {[
                { label:"Total Loads",   value:LOADS.length,  color:C.text    },
                { label:"Posted",        value:posted,         color:C.accentLt },
                { label:"In Transit",    value:inTransit,      color:C.success  },
                { label:"Total Value",   value:`$${(LOADS.reduce((a,l)=>a+l.rate,0)/1000).toFixed(0)}k`, color:C.success },
              ].map(s => (
                <div key={s.label} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 14px", flex:1, textAlign:"center" }}>
                  <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>{s.label}</div>
                  <div style={{ fontSize:18, fontWeight:700, color:s.color, fontFamily:C.mono }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
              <div style={{ flex:2, background:C.surface, border:`1px solid ${C.border}`, borderRadius:7, padding:"6px 12px", display:"flex", gap:8, alignItems:"center", minWidth:200 }}>
                <span style={{ color:C.textDm }}>🔍</span>
                <input value={loadSearch} onChange={e=>{setLoadSearch(e.target.value);setLoadPage(1);}}
                  placeholder="Search load #, route, shipper, carrier..."
                  style={{ flex:1, background:"transparent", border:"none", color:C.text, fontSize:13, outline:"none", fontFamily:C.sans }} />
                {loadSearch && <button onClick={()=>setLoadSearch("")} style={{ background:"none", border:"none", color:C.textDm, cursor:"pointer", fontSize:14, padding:0 }}>×</button>}
              </div>
              <select value={loadStatus} onChange={e=>{setLoadStatus(e.target.value);setLoadPage(1);}}
                style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:7, padding:"6px 12px", color:C.text, fontSize:12, outline:"none" }}>
                <option value="ALL">All Statuses</option>
                {["POSTED","BIDDING","BOOKED","IN_TRANSIT","DELIVERED","CANCELLED"].map(s =>
                  <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>
                )}
              </select>
              <select value={loadEquip} onChange={e=>{setLoadEquip(e.target.value);setLoadPage(1);}}
                style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:7, padding:"6px 12px", color:C.text, fontSize:12, outline:"none" }}>
                <option value="ALL">All Equipment</option>
                {Object.entries(EQUIP_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>

            {/* Table */}
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden", marginBottom:12 }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ background:C.surf2 }}>
                    {["Load #","Route","Shipper","Carrier","Equipment","Rate","Status","Pickup","Urgency"].map(h => (
                      <th key={h} style={{ padding:"8px 12px", fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em", textAlign:"left", fontWeight:600, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((l, i) => (
                    <tr key={l.id}
                      onClick={() => setLoadDetail(l)}
                      style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?"transparent":C.surf2+"55", cursor:"pointer" }}>
                      <td style={{ padding:"10px 12px", fontSize:11, fontFamily:C.mono, color:C.textMd, whiteSpace:"nowrap" }}>{l.number}</td>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ fontSize:12, fontWeight:600, color:C.text, whiteSpace:"nowrap" }}>
                          {l.origin.split(",")[0]} → {l.dest.split(",")[0]}
                        </div>
                        <div style={{ fontSize:10, color:C.textDm }}>{l.miles} mi · {fmtLb(l.weight)}</div>
                      </td>
                      <td style={{ padding:"10px 12px", fontSize:12, color:C.textMd, whiteSpace:"nowrap" }}>{l.shipper}</td>
                      <td style={{ padding:"10px 12px", fontSize:12, color: l.carrier ? C.text : C.textDm, whiteSpace:"nowrap" }}>{l.carrier || "—"}</td>
                      <td style={{ padding:"10px 12px" }}><EquipBadge type={l.equipmentType} /></td>
                      <td style={{ padding:"10px 12px", fontSize:12, fontWeight:700, color:C.success, fontFamily:C.mono, whiteSpace:"nowrap" }}>{fmt$(l.rate)}</td>
                      <td style={{ padding:"10px 12px" }}><Badge status={l.status} /></td>
                      <td style={{ padding:"10px 12px", fontSize:12, color:C.textMd, whiteSpace:"nowrap" }}>{l.pickupDate}</td>
                      <td style={{ padding:"10px 12px" }}><UrgencyDot urgency={l.urgency} /></td>
                    </tr>
                  ))}
                  {paginated.length === 0 && (
                    <tr><td colSpan={9} style={{ padding:"32px", textAlign:"center", color:C.textDm, fontSize:13 }}>No loads match your filters</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:12, color:C.textMd }}>
                  Showing {(loadPage-1)*PAGE_SIZE+1}–{Math.min(loadPage*PAGE_SIZE, filtered.length)} of {filtered.length}
                </div>
                <div style={{ display:"flex", gap:4 }}>
                  <button onClick={()=>setLoadPage(p=>Math.max(1,p-1))} disabled={loadPage===1}
                    style={{ background:C.surface, border:`1px solid ${C.border}`, color:loadPage===1?C.textDm:C.text, borderRadius:6, padding:"4px 12px", cursor:loadPage===1?"default":"pointer", fontSize:12 }}>← Prev</button>
                  {Array.from({length:totalPages},(_,i)=>i+1).map(p => (
                    <button key={p} onClick={()=>setLoadPage(p)}
                      style={{ background:loadPage===p?C.accent:C.surface, border:`1px solid ${loadPage===p?C.accent:C.border}`, color:loadPage===p?"#fff":C.text, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>{p}</button>
                  ))}
                  <button onClick={()=>setLoadPage(p=>Math.min(totalPages,p+1))} disabled={loadPage===totalPages}
                    style={{ background:C.surface, border:`1px solid ${C.border}`, color:loadPage===totalPages?C.textDm:C.text, borderRadius:6, padding:"4px 12px", cursor:loadPage===totalPages?"default":"pointer", fontSize:12 }}>Next →</button>
                </div>
              </div>
            )}

            {/* Load detail drawer */}
            {loadDetail && (
              <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:200 }}
                onClick={e => e.target===e.currentTarget && setLoadDetail(null)}>
                <div style={{ position:"absolute", right:0, top:0, bottom:0, width:460, background:C.surface, borderLeft:`1px solid ${C.borderHi}`, overflow:"auto", padding:24 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                    <div>
                      <div style={{ fontSize:11, color:C.textDm, fontFamily:C.mono }}>{loadDetail.number}</div>
                      <div style={{ fontSize:16, fontWeight:700, color:C.text }}>{loadDetail.origin} → {loadDetail.dest}</div>
                    </div>
                    <button onClick={()=>setLoadDetail(null)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>✕</button>
                  </div>

                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:16 }}>
                    <Badge status={loadDetail.status} />
                    <EquipBadge type={loadDetail.equipmentType} />
                    <UrgencyDot urgency={loadDetail.urgency} />
                  </div>

                  {/* Route */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                    {[
                      { label:"ORIGIN",      value:loadDetail.origin,      sub:`Pickup: ${loadDetail.pickupDate}` },
                      { label:"DESTINATION", value:loadDetail.dest,        sub:`Delivery: ${loadDetail.deliveryDate}` },
                    ].map(loc => (
                      <div key={loc.label} style={{ background:C.bg, borderRadius:8, padding:"11px 13px", border:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>{loc.label}</div>
                        <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{loc.value.split(",")[0]}</div>
                        <div style={{ fontSize:11, color:C.textDm, marginTop:2 }}>{loc.sub}</div>
                      </div>
                    ))}
                  </div>

                  {/* Cargo */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:14 }}>
                    {[
                      { label:"Miles",   value:fmtMi(loadDetail.miles)   },
                      { label:"Weight",  value:fmtLb(loadDetail.weight)  },
                      { label:"Commodity", value:loadDetail.commodity     },
                    ].map(s => (
                      <div key={s.label} style={{ background:C.bg, borderRadius:7, padding:"9px 10px", border:`1px solid ${C.border}`, textAlign:"center" }}>
                        <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>{s.label}</div>
                        <div style={{ fontSize:11, fontWeight:600, color:C.text }}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Shipper / Carrier */}
                  <div style={{ background:C.bg, borderRadius:8, padding:"12px 14px", border:`1px solid ${C.border}`, marginBottom:14 }}>
                    <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>Parties</div>
                    <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${C.border}` }}>
                      <span style={{ fontSize:12, color:C.textMd }}>Shipper</span>
                      <span style={{ fontSize:12, color:C.text, fontWeight:500 }}>{loadDetail.shipper}</span>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0" }}>
                      <span style={{ fontSize:12, color:C.textMd }}>Carrier</span>
                      <span style={{ fontSize:12, color:loadDetail.carrier?C.text:C.textDm }}>{loadDetail.carrier || "Not assigned"}</span>
                    </div>
                  </div>

                  {/* Rate */}
                  <div style={{ background:C.succGl, borderRadius:8, padding:"12px 14px", border:`1px solid ${C.success}33`, marginBottom:14 }}>
                    <div style={{ fontSize:9, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>Rate</div>
                    <div style={{ fontSize:26, fontWeight:800, color:C.success, fontFamily:C.mono }}>{fmt$(loadDetail.rate)}</div>
                    <div style={{ fontSize:11, color:C.textMd, marginTop:2 }}>{cpm(loadDetail.rate, loadDetail.miles)}</div>
                  </div>

                  {/* In-transit progress */}
                  {loadDetail.status === "IN_TRANSIT" && (
                    <div style={{ background:C.bg, borderRadius:8, padding:"12px 14px", border:`1px solid ${C.border}`, marginBottom:14 }}>
                      <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>Progress</div>
                      <ProgressBar pct={loadDetail.progress||0} height={6} />
                      <div style={{ fontSize:11, color:C.textMd, marginTop:6 }}>{loadDetail.progress||0}% complete</div>
                    </div>
                  )}

                  {/* Admin actions */}
                  <div style={{ background:C.bg, borderRadius:8, padding:"12px 14px", border:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:9, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>Admin Actions</div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      <Btn variant="ghost" size="sm">View Timeline</Btn>
                      <Btn variant="ghost" size="sm">View Documents</Btn>
                      {loadDetail.status === "POSTED" && <Btn variant="danger" size="sm">Cancel Load</Btn>}
                      {loadDetail.status === "POSTED" && <Btn variant="outline" size="sm">Force Book</Btn>}
                      {["IN_TRANSIT","BOOKED"].includes(loadDetail.status) && <Btn variant="ghost" size="sm">Flag Dispute</Btn>}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
};

const BILLING_PLANS = {
  FREE:       { label:"Free",       price:0,   color:C.textMd  },
  STARTER:    { label:"Starter",    price:99,  color:C.accentLt },
  GROWTH:     { label:"Growth",     price:249, color:C.purple   },
  ENTERPRISE: { label:"Enterprise", price:999, color:C.warn     },
};
const SUB_STATUS_META = {
  TRIALING: { label:"Trial",    color:"#8b5cf6" },
  ACTIVE:   { label:"Active",   color:"#10b981" },
  PAST_DUE: { label:"Past Due", color:"#ef4444" },
  CANCELED: { label:"Canceled", color:"#475569" },
  FREE:     { label:"Free",     color:"#94a3b8" },
};
const AdminBillingTab = () => {
  const [billingTab, setBillingTab] = useState("overview");
  const [selectedSub, setSelectedSub] = useState(null);
  const [overridePlan, setOverridePlan] = useState("");
  const [subs, setSubs] = useState([
    { id:"S1", userId:"U1", plan:"GROWTH",  status:"ACTIVE",   stripeCustomerId:"cus_1", currentPeriodEnd:new Date("2026-07-10"), user:{ email:"shipper@midwestgoods.com",    role:"SHIPPER" } },
    { id:"S2", userId:"U2", plan:"STARTER", status:"TRIALING", stripeCustomerId:"cus_2", currentPeriodEnd:new Date("2026-06-24"), user:{ email:"ops@arrowlogistics.com",      role:"CARRIER" } },
    { id:"S3", userId:"U3", plan:"GROWTH",  status:"PAST_DUE", stripeCustomerId:"cus_3", currentPeriodEnd:new Date("2026-06-05"), user:{ email:"dispatch@sunbelttrucking.com",role:"CARRIER" } },
    { id:"S4", userId:"U4", plan:"FREE",    status:"FREE",     stripeCustomerId:null,     currentPeriodEnd:null,                  user:{ email:"apply@eagletransport.com",    role:"CARRIER" } },
    { id:"S5", userId:"U5", plan:"STARTER", status:"TRIALING", stripeCustomerId:"cus_5", currentPeriodEnd:new Date("2026-06-28"), user:{ email:"broker@midwestbroker.com",    role:"BROKER"  } },
  ]);
  const fees = [
    { id:"F1", loadId:"L006", gross:1320, feeAmount:46.20, createdAt:new Date("2026-06-09") },
    { id:"F2", loadId:"L003", gross:2418, feeAmount:84.63, createdAt:new Date("2026-06-10") },
    { id:"F3", loadId:"L009", gross:2240, feeAmount:78.40, createdAt:new Date("2026-06-06") },
  ];
  const mrr = subs.filter(s=>["ACTIVE","TRIALING"].includes(s.status)).reduce((a,s)=>a+(BILLING_PLANS[s.plan]?.price||0),0);
  const totalFees = fees.reduce((a,f)=>a+f.feeAmount,0);
  const applyOverride = (sub) => {
    if (!overridePlan) return;
    setSubs(prev=>prev.map(s=>s.id===sub.id?{...s,plan:overridePlan,status:overridePlan==="FREE"?"FREE":"ACTIVE"}:s));
    setSelectedSub(prev=>prev?.id===sub.id?{...prev,plan:overridePlan}:prev);
    setOverridePlan("");
  };
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <h2 style={{fontSize:18,fontWeight:700,color:C.text,margin:0}}>Billing & Revenue</h2>
        <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" style={{background:C.surf2,border:`1px solid ${C.border}`,color:C.textMd,borderRadius:6,padding:"5px 12px",fontSize:12,textDecoration:"none"}}>Open Stripe ↗</a>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <StatCard label="MRR"           value={`$${mrr}`}          sub="monthly recurring" color={C.success}  icon="💰"/>
        <StatCard label="ARR"           value={`$${mrr*12}`}       sub="annualized"        color={C.success}  icon="📈"/>
        <StatCard label="Platform Fees" value={fmt$(totalFees)}    sub="3.5% of loads"     color={C.teal}     icon="⚙"/>
        <StatCard label="Active Subs"   value={subs.filter(s=>["ACTIVE","TRIALING"].includes(s.status)).length} color={C.accentLt} icon="✅"/>
        {subs.filter(s=>s.status==="PAST_DUE").length>0&&<StatCard label="Past Due" value={subs.filter(s=>s.status==="PAST_DUE").length} color={C.danger} icon="⚠"/>}
      </div>
      <div style={{display:"flex",gap:0,borderBottom:`1px solid ${C.border}`,marginBottom:16}}>
        {[["overview","Subscriptions"],["fees","Fees"],["stripe","Stripe Config"]].map(([id,label])=>(
          <button key={id} onClick={()=>setBillingTab(id)} style={{background:"transparent",border:"none",borderBottom:`2px solid ${billingTab===id?C.accentLt:"transparent"}`,color:billingTab===id?C.accentLt:C.textMd,padding:"8px 18px",fontSize:13,fontWeight:billingTab===id?600:400,cursor:"pointer"}}>{label}</button>
        ))}
      </div>
      {billingTab==="overview"&&(
        <div style={{display:"flex",gap:14}}>
          <div style={{flex:1,background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:C.surf2}}>{["Account","Role","Plan","$/mo","Status","Renews"].map(h=><th key={h} style={{padding:"8px 14px",fontSize:10,color:C.textDm,textTransform:"uppercase",letterSpacing:"0.07em",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
              <tbody>
                {subs.map((s,i)=>{
                  const plan=BILLING_PLANS[s.plan]||BILLING_PLANS.FREE;
                  const stat=SUB_STATUS_META[s.status]||SUB_STATUS_META.FREE;
                  const rc=s.user.role==="SHIPPER"?C.accentLt:s.user.role==="BROKER"?C.purple:C.success;
                  return(
                    <tr key={s.id} onClick={()=>setSelectedSub(s)} style={{borderBottom:`1px solid ${C.border}`,cursor:"pointer",background:selectedSub?.id===s.id?C.surf3:i%2===0?"transparent":C.surf2+"44"}}>
                      <td style={{padding:"10px 14px",fontSize:12,color:C.text}}>{s.user.email}</td>
                      <td style={{padding:"10px 14px"}}><span style={{fontSize:10,fontWeight:600,padding:"2px 6px",borderRadius:4,background:`${rc}22`,color:rc}}>{s.user.role}</span></td>
                      <td style={{padding:"10px 14px",fontSize:12,fontWeight:600,color:plan.color}}>{plan.label}</td>
                      <td style={{padding:"10px 14px",fontSize:12,fontFamily:C.mono,color:plan.price>0?C.success:C.textDm}}>{plan.price>0?`$${plan.price}`:"Free"}</td>
                      <td style={{padding:"10px 14px"}}><span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,textTransform:"uppercase",background:`${stat.color}18`,border:`1px solid ${stat.color}44`,color:stat.color}}>{stat.label}</span></td>
                      <td style={{padding:"10px 14px",fontSize:11,color:C.textMd}}>{s.currentPeriodEnd?new Date(s.currentPeriodEnd).toLocaleDateString("en-US",{month:"short",day:"numeric"}):"—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {selectedSub&&(
            <div style={{width:260,flexShrink:0,background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:10,padding:16}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:700,color:C.text}}>{selectedSub.user.email.split("@")[0]}</div>
                <button onClick={()=>setSelectedSub(null)} style={{background:C.surf2,border:`1px solid ${C.border}`,color:C.textMd,borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:11}}>✕</button>
              </div>
              <div style={{fontSize:10,color:C.textDm,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Override Plan</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                {Object.entries(BILLING_PLANS).map(([k,p])=>(
                  <button key={k} onClick={()=>setOverridePlan(k)} style={{background:overridePlan===k?`${p.color}22`:"transparent",border:`1px solid ${overridePlan===k?p.color:C.border}`,color:overridePlan===k?p.color:C.textDm,borderRadius:5,padding:"3px 9px",fontSize:11,cursor:"pointer"}}>{p.label}</button>
                ))}
              </div>
              <Btn variant="primary" style={{width:"100%"}} disabled={!overridePlan} onClick={()=>applyOverride(selectedSub)}>Apply Override</Btn>
            </div>
          )}
        </div>
      )}
      {billingTab==="fees"&&(
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:C.surf2}}>{["Load","Gross","Fee","Date"].map(h=><th key={h} style={{padding:"8px 14px",fontSize:10,color:C.textDm,textTransform:"uppercase",textAlign:"left",fontWeight:600}}>{h}</th>)}</tr></thead>
            <tbody>{fees.map((f,i)=>(
              <tr key={f.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"transparent":C.surf2+"44"}}>
                <td style={{padding:"10px 14px",fontSize:11,fontFamily:C.mono,color:C.textDm}}>{f.loadId}</td>
                <td style={{padding:"10px 14px",fontSize:12,fontFamily:C.mono,color:C.text}}>{fmt$(f.gross)}</td>
                <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,fontFamily:C.mono,color:C.teal}}>{fmt$(f.feeAmount)}</td>
                <td style={{padding:"10px 14px",fontSize:12,color:C.textMd}}>{new Date(f.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric"})}</td>
              </tr>
            ))}</tbody>
          </table>
          <div style={{padding:"10px 14px",background:C.surf2,borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between"}}>
            <span style={{fontSize:12,fontWeight:600,color:C.text}}>Total</span>
            <span style={{fontSize:14,fontWeight:700,fontFamily:C.mono,color:C.teal}}>{fmt$(totalFees)}</span>
          </div>
        </div>
      )}
      {billingTab==="stripe"&&(
        <div style={{maxWidth:500}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:20}}>
            <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:12}}>Required env vars in backend/.env</div>
            {["STRIPE_SECRET_KEY","STRIPE_WEBHOOK_SECRET","STRIPE_PRICE_STARTER","STRIPE_PRICE_GROWTH","STRIPE_PRICE_ENTERPRISE"].map(v=>(
              <div key={v} style={{marginBottom:10}}>
                <code style={{fontSize:12,color:C.accentLt,fontFamily:C.mono}}>{v}</code>
              </div>
            ))}
            <div style={{fontSize:12,color:C.textMd,marginTop:12}}>Revenue: <strong>$99/$249/$999/mo subscriptions</strong> + <strong>3.5% platform fee</strong> per carrier invoice</div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminConsole = ({ adminPage, setAdminPage }) => {
  const { token } = useAuth();
  const [users, setUsers]           = useState(ADMIN_USERS);
  const [search, setSearch]         = useState("");
  const [filterRole, setFilterRole] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSignup, setShowSignup] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showGrantModal, setShowGrantModal] = useState(null); // userId
  const [tempPassword, setTempPassword] = useState("");
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantDone, setGrantDone] = useState(null); // { email, password }

  const generateTempPassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$";
    return Array.from({length:10}, () => chars[Math.floor(Math.random()*chars.length)]).join("");
  };

  const [trialDays, setTrialDays] = useState(14);

  const handleGrantAccess = async (userId, password, trial) => {
    setGrantLoading(true);
    try {
      const user = users.find(u => u.id===userId);
      const res = await fetch(`${API_BASE}/admin/users/${userId}/grant-access`, {
        method: "POST",
        headers: { Authorization:`Bearer ${token}`, "Content-Type":"application/json" },
        body: JSON.stringify({ password, trialDays: trial || null }),
      });
      const data = await res.json();
      if (res.ok) {
        setUsers(prev => prev.map(u => u.id===userId ? {...u, status:"ACTIVE"} : u));
        setGrantDone({ email: user?.email, password, trialDays: trial || null, emailSent: data.emailSent });
        setShowGrantModal(null);
        setTempPassword("");
      } else {
        alert(`Failed to grant access: ${data.error || "Unknown error"}`);
      }
    } catch(e) { console.error("Grant access failed:", e); alert("Network error granting access"); }
    finally { setGrantLoading(false); }
  };

  // Fetch real users from API
  const fetchUsers = async () => {
    if (!token) return;
    setLoadingUsers(true);
    try {
      const res = await fetch(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const realUsers = data.users.map(u => ({
          id: u.id,
          email: u.email,
          company: u.broker?.companyName || u.shipper?.companyName || u.carrier?.companyName || u.email.split("@")[0],
          role: u.role,
          status: u.status,
          plan: "starter",
          joinedAt: new Date(u.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),
          trialEndsAt: u.trialEndsAt || null,
          loads: 0, revenue: 0, verified: false, dotVerified: null, lastActive: "—",
        }));
        setUsers(realUsers.length > 0 ? realUsers : ADMIN_USERS);
      } else {
        console.log("[Admin] fetchUsers failed:", res.status, await res.text());
      }
    } catch(_) {
      // Fall back to sample data
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch on mount and when adminPage changes
  useEffect(() => { fetchUsers(); }, [token, adminPage]); // eslint-disable-line

  const changeStatus = async (userId, newStatus) => {
    // Optimistic update
    setUsers(prev => prev.map(u => u.id===userId ? {...u, status:newStatus} : u));
    setSelectedUser(prev => prev?.id===userId ? {...prev, status:newStatus} : prev);
    // Sync to backend
    try {
      await fetch(`${API_BASE}/admin/users/${userId}/status`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
    } catch(_) {}
  };

  const pendingCount = users.filter(u => u.status==="PENDING").length;
  const activeCount  = users.filter(u => u.status==="ACTIVE").length;
  const totalRevenue = users.filter(u=>u.status==="ACTIVE").reduce((a,u) => a + (u.plan==="growth"?249:u.plan==="enterprise"?999:99), 0);

  const filteredUsers = users.filter(u => {
    const matchSearch = !search || u.email.toLowerCase().includes(search.toLowerCase()) || (u.company||"").toLowerCase().includes(search.toLowerCase());
    const matchRole   = filterRole   === "ALL" || u.role   === filterRole;
    const matchStatus = filterStatus === "ALL" || u.status === filterStatus;
    return matchSearch && matchRole && matchStatus;
  });

  const STATUS_ACTIONS = {
    PENDING:   [{ label:"✓ Grant Access", action:"grant",    variant:"success" }, { label:"✕ Reject", status:"REJECTED", variant:"danger" }],
    ACTIVE:    [{ label:"⏸ Suspend",      status:"SUSPENDED", variant:"danger"  }],
    SUSPENDED: [{ label:"↩ Reinstate",    status:"ACTIVE",    variant:"success" }],
    REJECTED:  [{ label:"↩ Re-review",    status:"PENDING",   variant:"ghost"   }],
  };

  const STATUS_COLORS = { ACTIVE:"success", PENDING:"warn", SUSPENDED:"danger", REJECTED:"textDm" };

  const UserTable = ({ data }) => (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr style={{ background:C.surf2 }}>
            {["Company","Role","Plan","Status","FMCSA","Loads","Joined","Last Active","Actions"].map(h => (
              <th key={h} style={{ padding:"8px 12px", fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em", textAlign:"left", fontWeight:600, whiteSpace:"nowrap" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((u,i) => {
            const sc = STATUS_COLORS[u.status];
            const actions = STATUS_ACTIONS[u.status] || [];
            return (
              <tr key={u.id} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?"transparent":C.surf2+"66", cursor:"pointer" }}
                onClick={() => setSelectedUser(u)}>
                <td style={{ padding:"10px 12px" }}>
                  <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{u.company}</div>
                  <div style={{ fontSize:11, color:C.textDm }}>{u.email}</div>
                </td>
                <td style={{ padding:"10px 12px" }}>
                  <span style={{ fontSize:11, fontWeight:600, padding:"2px 7px", borderRadius:4,
                    background: u.role==="SHIPPER" ? C.accentGl : C.succGl,
                    color: u.role==="SHIPPER" ? C.accentLt : C.success }}>
                    {u.role}
                  </span>
                </td>
                <td style={{ padding:"10px 12px" }}>
                  <div style={{ fontSize:12, color:C.textMd, textTransform:"capitalize" }}>{u.plan||"free"}</div>
                  {u.status==="ACTIVE" && (
                    <select onChange={e => {
                      if (!e.target.value) return;
                      changeStatus(u.id, u.status); // keep status
                      alert(`Plan change to ${e.target.value} for ${u.email} — integrate with Stripe to process payment.`);
                      e.target.value = "";
                    }} defaultValue=""
                      style={{ fontSize:10, background:C.bg, border:`1px solid ${C.border}`, borderRadius:4, color:C.textMd, marginTop:3, padding:"2px 4px", cursor:"pointer", outline:"none" }}>
                      <option value="">Change plan…</option>
                      <option value="starter">Starter $99/mo</option>
                      <option value="growth">Growth $249/mo</option>
                      <option value="enterprise">Enterprise $999/mo</option>
                    </select>
                  )}
                </td>
                <td style={{ padding:"10px 12px" }}>
                  <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:4, textTransform:"uppercase",
                    background:`${C[sc] || C.textMd}18`, border:`1px solid ${C[sc] || C.textMd}44`,
                    color: C[sc] || C.textMd }}>
                    {u.status}
                  </span>
                </td>
                <td style={{ padding:"10px 12px", fontSize:11 }}>
                  {u.role==="CARRIER"
                    ? u.dotVerified
                      ? <span style={{ color: u.dotVerified==="Satisfactory"?C.success:C.warn }}>✓ {u.dotVerified}</span>
                      : <span style={{ color:C.textDm }}>—</span>
                    : <span style={{ color:C.textDm }}>N/A</span>}
                </td>
                <td style={{ padding:"10px 12px", fontSize:12, fontFamily:C.mono, color:C.text }}>{u.loads}</td>
                <td style={{ padding:"10px 12px", fontSize:12, color:C.textMd }}>{u.joinedAt}</td>
                <td style={{ padding:"10px 12px", fontSize:12, color:C.textMd }}>{u.lastActive}</td>
                <td style={{ padding:"10px 12px" }} onClick={e => e.stopPropagation()}>
                  <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                    <button onClick={() => { setShowGrantModal(u.id); setTempPassword(generateTempPassword()); }}
                      style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.textMd, borderRadius:5, padding:"3px 8px", fontSize:10, cursor:"pointer", whiteSpace:"nowrap" }}>
                      🔑 Password
                    </button>
                    {actions.map(a => (
                      <button key={a.action||a.status}
                        onClick={() => {
                          if (a.action === "grant") {
                            setShowGrantModal(u.id);
                            setTempPassword(generateTempPassword());
                          } else {
                            changeStatus(u.id, a.status);
                          }
                        }}
                        style={{
                          background:"transparent", border:`1px solid ${a.variant==="success"?C.success:a.variant==="danger"?C.danger:C.border}`,
                          color: a.variant==="success"?C.success:a.variant==="danger"?C.danger:C.textMd,
                          borderRadius:5, padding:"3px 8px", fontSize:10, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap",
                        }}>{a.label}</button>
                    ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {data.length===0 && <div style={{ padding:"32px", textAlign:"center", color:C.textDm, fontSize:13 }}>No users match filters</div>}
    </div>
  );

  return (
    <>
    <div>
      {/* Grant Access Modal */}
      {showGrantModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:400, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={e => e.target===e.currentTarget && setShowGrantModal(null)}>
          <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:16, width:440, overflow:"hidden" }}>
            <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, background:C.succGl }}>
              <div style={{ fontSize:16, fontWeight:700, color:C.success }}>
                {users.find(u=>u.id===showGrantModal)?.status==="PENDING" ? "✓ Grant Access" : "🔑 Reset Password"}
              </div>
              <div style={{ fontSize:12, color:C.textMd, marginTop:3 }}>
                {users.find(u=>u.id===showGrantModal)?.email}
              </div>
            </div>
            <div style={{ padding:20 }}>
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:11, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:6 }}>
                  Temporary Password <span style={{color:C.textDm, textTransform:"none", letterSpacing:"normal"}}>(user must change on first login)</span>
                </label>
                <div style={{ display:"flex", gap:8 }}>
                  <input value={tempPassword} onChange={e=>setTempPassword(e.target.value)}
                    style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, borderRadius:7, padding:"10px 12px", color:C.text, fontSize:14, fontFamily:C.mono, outline:"none", letterSpacing:"0.1em" }} />
                  <button onClick={() => setTempPassword(generateTempPassword())} style={{
                    background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd,
                    borderRadius:7, padding:"10px 12px", fontSize:12, cursor:"pointer", whiteSpace:"nowrap",
                  }}>🔀 Generate</button>
                </div>
                <div style={{ fontSize:11, color:C.textDm, marginTop:5 }}>
                  Share this password with the user via email or phone. Min 8 characters.
                </div>
              </div>
              <div style={{ background:C.accentGl, borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:12, color:C.textMd }}>
                This will activate their account and set their password. They can log in immediately.
              </div>

              {/* Trial period selector — only relevant when granting (not plain password reset) */}
              {users.find(u=>u.id===showGrantModal)?.status==="PENDING" && (
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:11, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:6 }}>
                    Free Trial Period
                  </label>
                  <div style={{ display:"flex", gap:6 }}>
                    {[
                      { label:"No trial", value:0 },
                      { label:"14 days",  value:14 },
                      { label:"30 days",  value:30 },
                      { label:"60 days",  value:60 },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setTrialDays(opt.value)} style={{
                        flex:1, background:trialDays===opt.value?C.accent:C.bg,
                        border:`1px solid ${trialDays===opt.value?C.accent:C.border}`,
                        color:trialDays===opt.value?"#fff":C.textMd,
                        borderRadius:7, padding:"8px 4px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:C.sans,
                      }}>{opt.label}</button>
                    ))}
                  </div>
                  {trialDays > 0 && (
                    <div style={{ fontSize:11, color:C.textDm, marginTop:6 }}>
                      Account will be billed after {trialDays} days unless cancelled. Trial ends {new Date(Date.now()+trialDays*86400000).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}.
                    </div>
                  )}
                </div>
              )}

              <div style={{ display:"flex", gap:10 }}>
                <Btn variant="ghost" onClick={()=>setShowGrantModal(null)}>Cancel</Btn>
                <Btn variant="success" style={{flex:1}} disabled={!tempPassword||tempPassword.length<8||grantLoading}
                  onClick={() => handleGrantAccess(showGrantModal, tempPassword, users.find(u=>u.id===showGrantModal)?.status==="PENDING" ? trialDays : null)}>
                  {grantLoading ? "Processing…" : users.find(u=>u.id===showGrantModal)?.status==="PENDING" ? "✓ Grant Access & Set Password" : "🔑 Reset Password"}
                </Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success notification */}
      {grantDone && (
        <div style={{ position:"fixed", bottom:20, right:20, zIndex:500, background:C.surface, border:`1px solid ${C.success}`, borderRadius:12, padding:16, maxWidth:340, boxShadow:"0 8px 32px rgba(0,0,0,0.4)" }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.success, marginBottom:6 }}>✅ Access Granted</div>
          <div style={{ fontSize:12, color:C.textMd, marginBottom:8 }}>
            <strong>{grantDone.email}</strong> can now log in with:
          </div>
          <div style={{ background:C.bg, borderRadius:6, padding:"8px 12px", fontFamily:C.mono, fontSize:13, color:C.text, marginBottom:10, letterSpacing:"0.05em" }}>
            {grantDone.password}
          </div>
          {grantDone.trialDays > 0 && (
            <div style={{ fontSize:11, color:C.warn, marginBottom:8 }}>⏱ {grantDone.trialDays}-day free trial started</div>
          )}
          <div style={{ fontSize:11, color: grantDone.emailSent ? C.success : C.textDm, marginBottom:10 }}>
            {grantDone.emailSent ? "✓ Credentials emailed to the user" : "⚠ Email not sent — share these credentials manually"}
          </div>
          <button onClick={() => setGrantDone(null)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"5px 12px", fontSize:12, cursor:"pointer", width:"100%" }}>Dismiss</button>
        </div>
      )}

      {/* Admin sub-nav */}
      <div style={{ display:"flex", gap:4, marginBottom:20, flexWrap:"wrap" }}>
        {NAV_ADMIN.map(n => (
          <button key={n.id} onClick={() => setAdminPage(n.id)} style={{
            background: adminPage===n.id ? C.accent : C.surface,
            color: adminPage===n.id ? "#fff" : C.textMd,
            border:`1px solid ${adminPage===n.id?C.accent:C.border}`,
            borderRadius:7, padding:"6px 14px", fontSize:12, fontWeight:500, cursor:"pointer",
          }}>{n.icon} {n.label}</button>
        ))}
        <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
          <Btn onClick={() => setShowSignup("SHIPPER")} variant="ghost" size="sm">+ Add Shipper</Btn>
          <Btn onClick={() => setShowSignup("CARRIER")} variant="outline" size="sm">+ Add Carrier</Btn>
        </div>
      </div>

      {/* Dashboard */}
      {adminPage==="admin_dashboard" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:0 }}>Platform Overview</h2>
            <button onClick={fetchUsers} disabled={loadingUsers}
              style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"5px 12px", fontSize:12, cursor:"pointer" }}>
              {loadingUsers ? "Refreshing..." : "↻ Refresh"}
            </button>
          </div>
          <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap" }}>
            <StatCard label="Pending Approval" value={pendingCount} sub="awaiting review" color={pendingCount>0?C.warn:C.textMd} icon="⏳" />
            <StatCard label="Active Users" value={activeCount}  sub="shippers + carriers" color={C.success} icon="✅" />
            <StatCard label="MRR" value={`$${totalRevenue.toLocaleString()}`} sub="from active plans" color={C.success} icon="💰" />
            <StatCard label="Total Loads" value={users.reduce((a,u)=>a+u.loads,0)} sub="platform-wide" color={C.accentLt} icon="📦" />
          </div>

          {/* Pending queue */}
          {pendingCount > 0 && (
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:13, fontWeight:600, color:C.warn, marginBottom:10 }}>⏳ Pending Applications ({pendingCount})</div>
              <UserTable data={users.filter(u=>u.status==="PENDING")} />
            </div>
          )}

          <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:10 }}>Recent Activity</div>
          <UserTable data={users.filter(u=>u.status!=="PENDING").slice(0,5)} />
        </div>
      )}

      {/* Access Control — pending requests focused */}
      {adminPage==="admin_access" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div>
              <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:0 }}>Access Control</h2>
              <p style={{ fontSize:13, color:C.textMd, margin:"3px 0 0" }}>Review and approve access requests. Grant access with a temporary password.</p>
            </div>
            {pendingCount > 0 && (
              <div style={{ background:`${C.warn}18`, border:`1px solid ${C.warn}44`, borderRadius:8, padding:"6px 14px", fontSize:13, fontWeight:600, color:C.warn }}>
                ⚠ {pendingCount} pending request{pendingCount!==1?"s":""}
              </div>
            )}
          </div>

          {/* Pending requests first */}
          {users.filter(u=>u.status==="PENDING").length > 0 && (
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:12, fontWeight:600, color:C.warn, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>
                ⏳ Pending Requests ({users.filter(u=>u.status==="PENDING").length})
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {users.filter(u=>u.status==="PENDING").map(u => (
                  <div key={u.id} style={{ background:C.surface, border:`1px solid ${C.warn}44`, borderRadius:10, padding:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                        <span style={{ fontSize:14, fontWeight:700, color:C.text }}>{u.company}</span>
                        <span style={{ fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:4, textTransform:"uppercase",
                          background: u.role==="SHIPPER"?C.accentGl:u.role==="BROKER"?`${C.purple}18`:C.succGl,
                          color: u.role==="SHIPPER"?C.accentLt:u.role==="BROKER"?C.purple:C.success }}>
                          {u.role}
                        </span>
                      </div>
                      <div style={{ fontSize:12, color:C.textMd }}>{u.email}</div>
                      <div style={{ fontSize:11, color:C.textDm, marginTop:2 }}>Requested {u.joinedAt}</div>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={() => changeStatus(u.id, "REJECTED")}
                        style={{ background:"transparent", border:`1px solid ${C.danger}`, color:C.danger, borderRadius:7, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                        ✕ Reject
                      </button>
                      <button onClick={() => { setShowGrantModal(u.id); setTempPassword(generateTempPassword()); }}
                        style={{ background:C.success, border:"none", color:"#fff", borderRadius:7, padding:"6px 16px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                        ✓ Grant Access
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {users.filter(u=>u.status==="PENDING").length === 0 && (
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"40px 24px", textAlign:"center", marginBottom:20 }}>
              <div style={{ fontSize:40, opacity:0.15, marginBottom:10 }}>✅</div>
              <div style={{ fontSize:14, fontWeight:600, color:C.text }}>No pending requests</div>
              <div style={{ fontSize:13, color:C.textMd, marginTop:4 }}>All access requests have been reviewed.</div>
            </div>
          )}

          {/* Active users */}
          <div style={{ fontSize:12, fontWeight:600, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>
            Active Users ({users.filter(u=>u.status==="ACTIVE").length})
          </div>
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ background:C.surf2 }}>
                {["Company","Email","Role","Joined","Trial","Actions"].map(h=>(
                  <th key={h} style={{ padding:"8px 14px", fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em", textAlign:"left", fontWeight:600 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {users.filter(u=>u.status==="ACTIVE").map((u,i)=>{
                  const trialDaysLeft = u.trialEndsAt ? Math.ceil((new Date(u.trialEndsAt) - new Date()) / 86400000) : null;
                  return (
                  <tr key={u.id} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?"transparent":C.surf2+"44" }}>
                    <td style={{ padding:"10px 14px", fontSize:13, fontWeight:600, color:C.text }}>{u.company}</td>
                    <td style={{ padding:"10px 14px", fontSize:12, color:C.textMd }}>{u.email}</td>
                    <td style={{ padding:"10px 14px" }}>
                      <span style={{ fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:4, textTransform:"uppercase",
                        background:u.role==="SHIPPER"?C.accentGl:u.role==="BROKER"?`${C.purple}18`:C.succGl,
                        color:u.role==="SHIPPER"?C.accentLt:u.role==="BROKER"?C.purple:C.success }}>
                        {u.role}
                      </span>
                    </td>
                    <td style={{ padding:"10px 14px", fontSize:12, color:C.textMd }}>{u.joinedAt}</td>
                    <td style={{ padding:"10px 14px" }}>
                      {trialDaysLeft === null ? (
                        <span style={{ fontSize:11, color:C.textDm }}>—</span>
                      ) : trialDaysLeft < 0 ? (
                        <span style={{ fontSize:10, fontWeight:600, color:C.danger, background:"rgba(239,68,68,0.1)", borderRadius:4, padding:"2px 7px" }}>Trial ended</span>
                      ) : (
                        <span style={{ fontSize:10, fontWeight:600, color:C.warn, background:`${C.warn}18`, borderRadius:4, padding:"2px 7px" }}>{trialDaysLeft}d left</span>
                      )}
                    </td>
                    <td style={{ padding:"10px 14px" }}>
                      <div style={{ display:"flex", gap:6 }}>
                        <button onClick={() => { setShowGrantModal(u.id); setTempPassword(generateTempPassword()); }}
                          style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.textMd, borderRadius:5, padding:"3px 8px", fontSize:10, cursor:"pointer" }}>
                          🔑 Reset Password
                        </button>
                        <button onClick={() => changeStatus(u.id, "SUSPENDED")}
                          style={{ background:"transparent", border:`1px solid ${C.danger}`, color:C.danger, borderRadius:5, padding:"3px 8px", fontSize:10, cursor:"pointer" }}>
                          Suspend
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {users.filter(u=>u.status==="ACTIVE").length===0 && (
              <div style={{ padding:"24px", textAlign:"center", color:C.textDm, fontSize:13 }}>No active users</div>
            )}
          </div>
        </div>
      )}

      {/* All Users — full management view */}
      {(adminPage==="admin_users" || adminPage==="admin_shippers" || adminPage==="admin_carriers" || adminPage==="admin_brokers") && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:0 }}>
              {adminPage==="admin_shippers"?"Shippers":adminPage==="admin_carriers"?"Carriers":adminPage==="admin_brokers"?"Brokers":"All Users"}
            </h2>
            <div style={{ display:"flex", gap:8 }}>
              <Btn onClick={() => setShowSignup("SHIPPER")} variant="ghost" size="sm">+ Add Shipper</Btn>
              <Btn onClick={() => setShowSignup("CARRIER")} variant="outline" size="sm">+ Add Carrier</Btn>
            </div>
          </div>

          {/* Filters */}
          <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:7, padding:"6px 12px", display:"flex", gap:8, alignItems:"center", flex:1 }}>
              <span style={{ color:C.textDm }}>🔍</span>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by company or email..."
                style={{ flex:1, background:"transparent", border:"none", color:C.text, fontSize:13, outline:"none" }} />
            </div>
            {adminPage==="admin_users" && (
              <select value={filterRole} onChange={e=>setFilterRole(e.target.value)}
                style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:7, padding:"6px 12px", color:C.text, fontSize:12, outline:"none" }}>
                <option value="ALL">All Roles</option>
                <option value="SHIPPER">Shippers</option>
                <option value="CARRIER">Carriers</option>
                <option value="BROKER">Brokers</option>
              </select>
            )}
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
              style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:7, padding:"6px 12px", color:C.text, fontSize:12, outline:"none" }}>
              <option value="ALL">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="PENDING">Pending</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          <UserTable data={filteredUsers.filter(u =>
            adminPage==="admin_shippers" ? u.role==="SHIPPER" :
            adminPage==="admin_carriers" ? u.role==="CARRIER" :
            adminPage==="admin_brokers"  ? u.role==="BROKER"  : true
          )} />
        </div>
      )}

      {/* All loads (admin) */}
      {adminPage==="admin_loads" && <AdminLoadsTab />}

      {/* FMCSA Lookup */}
      {adminPage==="fmcsa" && <FMCSALookupPage />}
    </div>

    {/* User detail side-drawer */}
    {selectedUser && (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:200 }}
        onClick={e => e.target===e.currentTarget && setSelectedUser(null)}>
        <div style={{ position:"absolute", right:0, top:0, bottom:0, width:420, background:C.surface, borderLeft:`1px solid ${C.borderHi}`, overflow:"auto", padding:24 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
            <div style={{ fontSize:15, fontWeight:700, color:C.text }}>{selectedUser.company}</div>
            <button onClick={() => setSelectedUser(null)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>✕</button>
          </div>

          {/* Status & actions */}
          <div style={{ background:C.bg, borderRadius:10, padding:14, border:`1px solid ${C.border}`, marginBottom:16 }}>
            <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Access Status</div>
            <div style={{ display:"flex", gap:8, marginBottom:12 }}>
              {(STATUS_ACTIONS[selectedUser.status]||[]).map(a => (
                <Btn key={a.status} onClick={() => changeStatus(selectedUser.id, a.status)} variant={a.variant} size="sm">{a.label}</Btn>
              ))}
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {["ACTIVE","PENDING","SUSPENDED"].map(s => (
                <button key={s} onClick={() => changeStatus(selectedUser.id, s)} style={{
                  background: selectedUser.status===s ? `${C[STATUS_COLORS[s]]}22` : "transparent",
                  border:`1px solid ${selectedUser.status===s?C[STATUS_COLORS[s]]:C.border}`,
                  color: selectedUser.status===s ? C[STATUS_COLORS[s]] : C.textDm,
                  borderRadius:5, padding:"3px 10px", fontSize:11, cursor:"pointer",
                }}>{s}</button>
              ))}
            </div>
          </div>

          {/* Details */}
          <div style={{ background:C.bg, borderRadius:10, padding:14, border:`1px solid ${C.border}`, marginBottom:16 }}>
            <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Account Details</div>
            {[
              { label:"Email",      value:selectedUser.email },
              { label:"Role",       value:selectedUser.role },
              { label:"Plan",       value:selectedUser.plan, extra:selectedUser.plan==="growth"?"$249/mo":selectedUser.plan==="enterprise"?"Custom":"$99/mo" },
              { label:"Joined",     value:selectedUser.joinedAt },
              { label:"Last Active",value:selectedUser.lastActive },
              { label:"Loads",      value:selectedUser.loads },
            ].map(r => (
              <div key={r.label} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontSize:12, color:C.textMd }}>{r.label}</span>
                <span style={{ fontSize:12, color:C.text }}>{r.value}{r.extra ? <span style={{ color:C.textDm, marginLeft:6 }}>({r.extra})</span> : ""}</span>
              </div>
            ))}
          </div>

          {/* FMCSA for carriers */}
          {selectedUser.role==="CARRIER" && (
            <div style={{ background:C.bg, borderRadius:10, padding:14, border:`1px solid ${C.border}`, marginBottom:16 }}>
              <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>FMCSA Verification</div>
              {selectedUser.dotVerified
                ? <div style={{ fontSize:13, color: selectedUser.dotVerified==="Satisfactory"?C.success:C.warn }}>✓ {selectedUser.dotVerified}</div>
                : <div>
                    <div style={{ fontSize:12, color:C.textDm, marginBottom:8 }}>Not yet verified</div>
                    <Btn variant="outline" size="sm">🛡 Run FMCSA Check</Btn>
                  </div>}
            </div>
          )}

          {/* Plan override */}
          <div style={{ background:C.bg, borderRadius:10, padding:14, border:`1px solid ${C.border}` }}>
            <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Plan Override</div>
            <div style={{ display:"flex", gap:8 }}>
              {["starter","growth","enterprise","free"].map(p => (
                <button key={p} onClick={() => setUsers(prev => prev.map(u => u.id===selectedUser.id?{...u,plan:p}:u)) || setSelectedUser(prev=>({...prev,plan:p}))}
                  style={{
                    background: selectedUser.plan===p ? C.accentGl : "transparent",
                    border:`1px solid ${selectedUser.plan===p?C.accent:C.border}`,
                    color: selectedUser.plan===p ? C.accentLt : C.textMd,
                    borderRadius:5, padding:"4px 10px", fontSize:11, cursor:"pointer", textTransform:"capitalize",
                  }}>{p}</button>
              ))}
            </div>
            <div style={{ fontSize:11, color:C.textDm, marginTop:8 }}>Admins can assign any plan including free access for partners or pilots.</div>
          </div>
        </div>
      </div>
    )}

    {/* Add user modal */}
    {showSignup && <SignupPage defaultRole={showSignup} onClose={() => setShowSignup(null)} />}
    </>
  );
};

const FleetPage = ({ trucks: propTrucks, setTrucks: setPropTrucks, drivers: propDrivers, setDrivers: setPropDrivers }) => {
  const [trucks,  setTrucksLocal]  = useState(propTrucks  || INIT_TRUCKS);
  const [drivers, setDriversLocal] = useState(propDrivers || INIT_DRIVERS);
  const [tab, setTab]        = useState("trucks");
  const [showAddTruck,  setShowAddTruck]  = useState(false);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [truckForm,  setTruckForm]  = useState({ number:"", make:"", model:"", year:"", plate:"", unitType:"TRACTOR", trailerTypes:["DRY_VAN"], status:"AVAILABLE" });
  const [driverForm, setDriverForm] = useState({ firstName:"", lastName:"", phone:"", email:"", cdlClass:"A", cdlState:"", cdlNumber:"", cdlExpiry:"", yearsExp:"", status:"AVAILABLE" });
  const [driverCreated, setDriverCreated] = useState(null); // { name, email, password }
  const [addingDriver, setAddingDriver]   = useState(false);
  const { token: fleetToken } = useAuth();

  const genPassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#";
    return Array.from({length:10}, () => chars[Math.floor(Math.random()*chars.length)]).join("");
  };

  const setTrucks = (v) => { setTrucksLocal(v); if (setPropTrucks) setPropTrucks(v); };
  const setDrivers= (v) => { setDriversLocal(v); if (setPropDrivers) setPropDrivers(v); };

  const setTF = k => e => setTruckForm(f => ({...f, [k]: e.target.value}));
  const setDF = k => e => setDriverForm(f => ({...f, [k]: e.target.value}));

  const addTruck = () => {
    if (!truckForm.number || !truckForm.make) return;
    setTrucks([...trucks, { id:`T${Date.now()}`, ...truckForm, year:parseInt(truckForm.year)||2024, trailerTypes:[truckForm.trailerTypes?.[0]||"DRY_VAN"] }]);
    setShowAddTruck(false);
    setTruckForm({ number:"", make:"", model:"", year:"", plate:"", unitType:"TRACTOR", trailerTypes:["DRY_VAN"], status:"AVAILABLE" });
  };

  const addDriver = async () => {
    if (!driverForm.firstName || !driverForm.lastName || !driverForm.email) return;
    setAddingDriver(true);
    const tempPassword = genPassword();
    let driverId = `D${Date.now()}`;
    let accountCreated = false;

    // Create backend account if we have a token
    if (fleetToken) {
      try {
        const res = await fetch(`${API_BASE}/auth/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email:       driverForm.email,
            password:    tempPassword,
            role:        "DRIVER",
            companyName: `${driverForm.firstName} ${driverForm.lastName}`,
            phone:       driverForm.phone,
            status:      "ACTIVE",
          }),
        });
        if (res.ok) {
          const data = await res.json();
          driverId = data.user?.id || driverId;
          accountCreated = true;
        }
      } catch(e) { console.warn("Driver account creation failed:", e.message); }
    }

    const newDriver = { id:driverId, ...driverForm, yearsExp:parseInt(driverForm.yearsExp)||0, accountCreated };
    setDrivers([...drivers, newDriver]);
    setShowAddDriver(false);
    setDriverForm({ firstName:"", lastName:"", phone:"", email:"", cdlClass:"A", cdlState:"", cdlNumber:"", cdlExpiry:"", yearsExp:"", status:"AVAILABLE" });
    setAddingDriver(false);

    // Show credentials modal
    setDriverCreated({ name:`${driverForm.firstName} ${driverForm.lastName}`, email:driverForm.email, password:tempPassword, accountCreated });
  };

  const setTruckStatus = (id, status) => setTrucks(trucks.map(t => t.id===id ? {...t, status} : t));
  const setDriverStatus= (id, status) => setDrivers(drivers.map(d => d.id===id ? {...d, status} : d));

  const TRUCK_STATUS  = { AVAILABLE:{label:"Available",color:C.success}, IN_USE:{label:"In Use",color:C.warn}, MAINTENANCE:{label:"Maintenance",color:C.danger} };
  const DRIVER_STATUS = { AVAILABLE:{label:"Available",color:C.success}, ON_DUTY:{label:"On Duty",color:C.warn}, OFF_DUTY:{label:"Off Duty",color:C.textMd} };

  const inp = (val, onChange, placeholder, type="text") => (
    <input type={type} value={val} onChange={onChange} placeholder={placeholder}
      style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", color:C.text, fontSize:13, outline:"none", boxSizing:"border-box" }} />
  );
  const sel = (val, onChange, options) => (
    <select value={val} onChange={onChange} style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", color:C.text, fontSize:13, outline:"none" }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:0 }}>Fleet Management</h2>
        <div style={{ display:"flex", gap:8 }}>
          {tab === "drivers" && <Btn variant="primary" size="sm" onClick={() => setShowAddDriver(true)}>+ Add Driver</Btn>}
          {tab === "trucks"  && <Btn variant="primary" size="sm" onClick={() => setShowAddTruck(true)}>+ Add Truck</Btn>}
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <StatCard label="Trucks"        value={trucks.length}                                   icon="🚛" color={C.text} />
        <StatCard label="Available"     value={trucks.filter(t=>t.status==="AVAILABLE").length}  icon="✅" color={C.success} />
        <StatCard label="In Use"        value={trucks.filter(t=>t.status==="IN_USE").length}     icon="📦" color={C.warn} />
        <StatCard label="Drivers"       value={drivers.length}                                  icon="👤" color={C.text} />
        <StatCard label="Available"     value={drivers.filter(d=>d.status==="AVAILABLE").length} icon="✅" color={C.success} />
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:0, borderBottom:`1px solid ${C.border}`, marginBottom:16 }}>
        {[["trucks","🚛 Trucks"],["drivers","👤 Drivers"]].map(([id,label]) => (
          <button key={id} onClick={()=>setTab(id)} style={{
            background:"transparent", border:"none",
            borderBottom:`2px solid ${tab===id?C.accentLt:"transparent"}`,
            color:tab===id?C.accentLt:C.textMd,
            padding:"10px 20px", fontSize:13, fontWeight:tab===id?600:400, cursor:"pointer",
          }}>{label}</button>
        ))}
      </div>

      {/* Trucks tab */}
      {tab === "trucks" && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:12 }}>
          {trucks.map(t => {
            const s = TRUCK_STATUS[t.status] || TRUCK_STATUS.AVAILABLE;
            return (
              <div key={t.id} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                  <div>
                    <div style={{ fontSize:15, fontWeight:700, color:C.text }}>{t.number}</div>
                    <div style={{ fontSize:12, color:C.textMd }}>{t.year} {t.make} {t.model}</div>
                    {t.plate && <div style={{ fontSize:11, color:C.textDm }}>Plate: {t.plate}</div>}
                  </div>
                  <span style={{ fontSize:10, fontWeight:700, color:s.color, background:`${s.color}18`, border:`1px solid ${s.color}44`, padding:"2px 8px", borderRadius:4, textTransform:"uppercase" }}>{s.label}</span>
                </div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
                  {(t.trailerTypes||[t.unitType||"DRY_VAN"]).map(tp => <EquipBadge key={tp} type={tp} />)}
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  {["AVAILABLE","IN_USE","MAINTENANCE"].map(st => (
                    <button key={st} onClick={() => setTruckStatus(t.id, st)}
                      style={{ fontSize:10, padding:"3px 8px", borderRadius:5, cursor:"pointer", fontFamily:C.sans,
                        background:t.status===st?`${TRUCK_STATUS[st].color}22`:"transparent",
                        border:`1px solid ${t.status===st?TRUCK_STATUS[st].color:C.border}`,
                        color:t.status===st?TRUCK_STATUS[st].color:C.textDm,
                        fontWeight:t.status===st?700:400 }}>
                      {TRUCK_STATUS[st].label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {trucks.length === 0 && (
            <div style={{ gridColumn:"1/-1", textAlign:"center", padding:40, color:C.textDm }}>
              No trucks yet — <button onClick={()=>setShowAddTruck(true)} style={{background:"none",border:"none",color:C.accentLt,cursor:"pointer"}}>add your first truck →</button>
            </div>
          )}
        </div>
      )}

      {/* Drivers tab */}
      {tab === "drivers" && (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:C.surf2 }}>
                {["Name","Phone","CDL","Experience","Status","Actions"].map(h => (
                  <th key={h} style={{ padding:"9px 14px", fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em", textAlign:"left", fontWeight:600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drivers.map((d,i) => {
                const s = DRIVER_STATUS[d.status] || DRIVER_STATUS.AVAILABLE;
                return (
                  <tr key={d.id} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?"transparent":C.surf2+"44" }}>
                    <td style={{ padding:"11px 14px" }}>
                      <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{d.firstName} {d.lastName}</div>
                    </td>
                    <td style={{ padding:"11px 14px" }}>
                      {d.phone ? <a href={`tel:${d.phone}`} style={{ fontSize:12, color:C.accentLt, textDecoration:"none" }}>{d.phone}</a> : <span style={{color:C.textDm}}>—</span>}
                    </td>
                    <td style={{ padding:"11px 14px" }}>
                      <div style={{ fontSize:12, color:C.text }}>CDL-{d.cdlClass} {d.cdlState} {d.cdlNumber && `· ${d.cdlNumber}`}</div>
                      {d.cdlExpiry && (() => {
                        const daysLeft = Math.ceil((new Date(d.cdlExpiry) - new Date()) / 86400000);
                        if (daysLeft < 0) return <div style={{ fontSize:10, color:C.danger, fontWeight:600, marginTop:2 }}>⚠ Expired</div>;
                        if (daysLeft < 60) return <div style={{ fontSize:10, color:C.warn, fontWeight:600, marginTop:2 }}>⚠ Expires in {daysLeft}d</div>;
                        return <div style={{ fontSize:10, color:C.textDm, marginTop:2 }}>Exp {new Date(d.cdlExpiry).toLocaleDateString("en-US",{month:"short",year:"numeric"})}</div>;
                      })()}
                    </td>
                    <td style={{ padding:"11px 14px", fontSize:12, color:C.textMd }}>{d.yearsExp} yr{d.yearsExp!==1?"s":""}</td>
                    <td style={{ padding:"11px 14px" }}>
                      <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, textTransform:"uppercase",
                        background:`${s.color}18`, border:`1px solid ${s.color}44`, color:s.color }}>{s.label}</span>
                    </td>
                    <td style={{ padding:"11px 14px" }}>
                      <div style={{ display:"flex", gap:4 }}>
                        {["AVAILABLE","ON_DUTY","OFF_DUTY"].map(st => (
                          <button key={st} onClick={() => setDriverStatus(d.id, st)}
                            style={{ fontSize:10, padding:"3px 7px", borderRadius:4, cursor:"pointer", fontFamily:C.sans,
                              background:d.status===st?`${DRIVER_STATUS[st].color}22`:"transparent",
                              border:`1px solid ${d.status===st?DRIVER_STATUS[st].color:C.border}`,
                              color:d.status===st?DRIVER_STATUS[st].color:C.textDm }}>
                            {DRIVER_STATUS[st].label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {drivers.length === 0 && (
                <tr><td colSpan={6} style={{ padding:"32px", textAlign:"center", color:C.textDm, fontSize:13 }}>
                  No drivers yet — <button onClick={()=>setShowAddDriver(true)} style={{background:"none",border:"none",color:C.accentLt,cursor:"pointer"}}>add your first driver →</button>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Truck Modal */}
      {showAddTruck && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={e => e.target===e.currentTarget && setShowAddTruck(false)}>
          <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:14, width:460, overflow:"hidden" }}>
            <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between" }}>
              <span style={{ fontSize:15, fontWeight:700, color:C.text }}>Add Truck</span>
              <button onClick={()=>setShowAddTruck(false)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>✕</button>
            </div>
            <div style={{ padding:20 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                {[{label:"Unit Number",key:"number",placeholder:"ARR-005"},{label:"Make",key:"make",placeholder:"Freightliner"},{label:"Model",key:"model",placeholder:"Cascadia"},{label:"Year",key:"year",placeholder:"2024",type:"number"},{label:"License Plate",key:"plate",placeholder:"IL-12345"}].map(f => (
                  <div key={f.key} style={{ gridColumn:f.key==="number"?"1/-1":"auto" }}>
                    <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 }}>{f.label}</label>
                    {inp(truckForm[f.key], setTF(f.key), f.placeholder, f.type)}
                  </div>
                ))}
                <div>
                  <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 }}>Equipment Type</label>
                  {sel(truckForm.trailerTypes?.[0]||"DRY_VAN", e=>setTruckForm(f=>({...f,trailerTypes:[e.target.value]})),
                    Object.entries(EQUIP_META).map(([v,m])=>({value:v,label:m.label})))}
                </div>
                <div>
                  <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 }}>Status</label>
                  {sel(truckForm.status, setTF("status"), [{value:"AVAILABLE",label:"Available"},{value:"MAINTENANCE",label:"Maintenance"}])}
                </div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <Btn variant="ghost" onClick={()=>setShowAddTruck(false)}>Cancel</Btn>
                <Btn variant="primary" style={{flex:1}} disabled={!truckForm.number||!truckForm.make} onClick={addTruck}>Add Truck</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Driver Modal */}
      {showAddDriver && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={e => e.target===e.currentTarget && setShowAddDriver(false)}>
          <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:14, width:460, overflow:"hidden" }}>
            <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between" }}>
              <span style={{ fontSize:15, fontWeight:700, color:C.text }}>Add Driver</span>
              <button onClick={()=>setShowAddDriver(false)} style={{ background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12 }}>✕</button>
            </div>
            <div style={{ padding:20 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                {[{label:"First Name",key:"firstName",placeholder:"Marcus"},{label:"Last Name",key:"lastName",placeholder:"Johnson"},{label:"Email (for login)",key:"email",placeholder:"driver@company.com",col:"1/-1",type:"email"},{label:"Phone",key:"phone",placeholder:"(312) 555-0100",col:"1/-1"},{label:"CDL Number",key:"cdlNumber",placeholder:"D1234567"},{label:"CDL State",key:"cdlState",placeholder:"IL"},{label:"CDL Expiration",key:"cdlExpiry",placeholder:"",type:"date"},{label:"Years Experience",key:"yearsExp",placeholder:"5",type:"number"}].map(f => (
                  <div key={f.key} style={{ gridColumn:f.col||"auto" }}>
                    <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 }}>{f.label}</label>
                    {inp(driverForm[f.key], setDF(f.key), f.placeholder, f.type)}
                  </div>
                ))}
                <div>
                  <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 }}>CDL Class</label>
                  {sel(driverForm.cdlClass, setDF("cdlClass"), [{value:"A",label:"Class A"},{value:"B",label:"Class B"},{value:"C",label:"Class C"}])}
                </div>
                <div>
                  <label style={{ fontSize:10, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 }}>Status</label>
                  {sel(driverForm.status, setDF("status"), [{value:"AVAILABLE",label:"Available"},{value:"OFF_DUTY",label:"Off Duty"}])}
                </div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <Btn variant="ghost" onClick={()=>setShowAddDriver(false)}>Cancel</Btn>
                <Btn variant="primary" style={{flex:1}} disabled={!driverForm.firstName||!driverForm.lastName||!driverForm.email||!driverForm.cdlNumber||!driverForm.cdlExpiry||addingDriver} onClick={addDriver}>
                  {addingDriver ? "Creating account…" : "Add Driver & Create Login"}
                </Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Driver credentials success modal */}
      {driverCreated && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:400, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:16, width:440, overflow:"hidden" }}>
            <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, background:C.succGl }}>
              <div style={{ fontSize:16, fontWeight:700, color:C.success }}>✅ Driver Added</div>
              <div style={{ fontSize:12, color:C.textMd, marginTop:3 }}>{driverCreated.name} has been added to your fleet</div>
            </div>
            <div style={{ padding:20 }}>
              {driverCreated.accountCreated ? (
                <>
                  <div style={{ fontSize:13, color:C.text, marginBottom:14 }}>
                    Share these login credentials with <strong>{driverCreated.name}</strong>. They can log in at{" "}
                    <a href="https://freightflow-app.onrender.com" target="_blank" rel="noreferrer" style={{ color:C.accentLt }}>freightflow-app.onrender.com</a>
                  </div>
                  <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:16, marginBottom:16 }}>
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>Email</div>
                      <div style={{ fontSize:14, fontFamily:C.mono, color:C.text, letterSpacing:"0.03em" }}>{driverCreated.email}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>Temporary Password</div>
                      <div style={{ fontSize:18, fontFamily:C.mono, color:C.success, fontWeight:700, letterSpacing:"0.15em" }}>{driverCreated.password}</div>
                    </div>
                  </div>
                  <div style={{ background:`${C.warn}11`, border:`1px solid ${C.warn}33`, borderRadius:8, padding:"10px 14px", fontSize:12, color:C.textMd, marginBottom:16 }}>
                    ⚠ Save this password now — it won't be shown again. The driver should change it after first login.
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={() => navigator.clipboard?.writeText(`Email: ${driverCreated.email}
Password: ${driverCreated.password}`)}
                      style={{ flex:1, background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd, borderRadius:8, padding:"9px", fontSize:13, cursor:"pointer", fontFamily:C.sans }}>
                      📋 Copy Credentials
                    </button>
                    <Btn variant="primary" style={{flex:1}} onClick={() => setDriverCreated(null)}>Done</Btn>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize:13, color:C.textMd, marginBottom:16 }}>
                    Driver added to fleet. Account creation failed — you can manually add them via Admin → Access Control, or try again.
                  </div>
                  <Btn variant="primary" onClick={() => setDriverCreated(null)}>OK</Btn>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main App ─────────────────────────────────────────────────────────────────



// ─── Broker Dashboard ────────────────────────────────────────────────────────

const BrokerDashboard = ({ loads, bids, setPage }) => {
  const brokeredLoads = loads.filter(l => l.status !== "POSTED" || l.shipper);
  const delivered    = loads.filter(l => l.status === "DELIVERED");
  const inTransit    = loads.filter(l => ["IN_TRANSIT","AT_PICKUP","PICKED_UP","AT_DELIVERY"].includes(l.status));
  const needsCarrier = loads.filter(l => l.status === "BOOKED" && (!l.carrier || l.carrier === "Unassigned" || l.carrier === "Direct Booking"));
  const grossRevenue = brokeredLoads.reduce((a,l) => a + (l.rate||0), 0);
  const carrierCosts = brokeredLoads.reduce((a,l) => a + (l.brokerRate||l.rate||0), 0);
  const platformFee  = Math.round(grossRevenue * 0.035);
  const netMargin    = grossRevenue - carrierCosts - platformFee;

  const shipperMap = {};
  brokeredLoads.forEach(l => {
    if (!l.shipper) return;
    if (!shipperMap[l.shipper]) shipperMap[l.shipper] = { loads:0, revenue:0, email:l.shipperEmail||"" };
    shipperMap[l.shipper].loads++;
    shipperMap[l.shipper].revenue += l.rate||0;
  });
  const shipperAccounts = Object.entries(shipperMap).map(([name,d]) => ({ name, ...d }));

  const SAMPLE_LOADS = [
    { id:"S1", number:"FF-000001", origin:"Chicago, IL",     dest:"Dallas, TX",   shipper:"Midwest Goods Co.",  shipperEmail:"billing@midwestgoods.com", carrier:"Arrow Logistics",  rate:3200, brokerRate:2650, status:"IN_TRANSIT", pickupDate:"Jun 10", deliveryDate:"Jun 12", driver:"Marcus Johnson" },
    { id:"S2", number:"FF-000002", origin:"Atlanta, GA",     dest:"Miami, FL",    shipper:"Pacific Freight LLC", shipperEmail:"ap@pacificfreight.com",    carrier:"Sunbelt Trucking", rate:2800, brokerRate:2100, status:"DELIVERED",  pickupDate:"Jun 7",  deliveryDate:"Jun 8",  driver:"Sandra Torres" },
    { id:"S3", number:"FF-000003", origin:"Los Angeles, CA", dest:"Phoenix, AZ",  shipper:"Northwest Retail",   shipperEmail:"freight@nwretail.com",     carrier:"",                 rate:1800, brokerRate:0,    status:"BOOKED",     pickupDate:"Jun 14", deliveryDate:"Jun 15", driver:"" },
    { id:"S4", number:"FF-000004", origin:"Seattle, WA",     dest:"Portland, OR", shipper:"Midwest Goods Co.",  shipperEmail:"billing@midwestgoods.com", carrier:"Eagle Transport",  rate:680,  brokerRate:520,  status:"DELIVERED",  pickupDate:"Jun 5",  deliveryDate:"Jun 5",  driver:"Kevin Park" },
  ];
  const usingSample  = brokeredLoads.length === 0;
  const displayLoads = usingSample ? SAMPLE_LOADS : brokeredLoads;
  const dispGross    = usingSample ? SAMPLE_LOADS.reduce((a,l)=>a+l.rate,0) : grossRevenue;
  const dispCosts    = usingSample ? SAMPLE_LOADS.reduce((a,l)=>a+l.brokerRate,0) : carrierCosts;
  const dispFee      = Math.round(dispGross * 0.035);
  const dispMargin   = dispGross - dispCosts - dispFee;
  const dispTransit  = usingSample ? SAMPLE_LOADS.filter(l=>l.status==="IN_TRANSIT") : inTransit;
  const dispNC       = usingSample ? SAMPLE_LOADS.filter(l=>l.status==="BOOKED"&&!l.carrier) : needsCarrier;
  const dispShippers = usingSample
    ? [{name:"Midwest Goods Co.",loads:2,revenue:3880,email:"billing@midwestgoods.com"},{name:"Pacific Freight LLC",loads:1,revenue:2800,email:"ap@pacificfreight.com"},{name:"Northwest Retail",loads:1,revenue:1800,email:"freight@nwretail.com"}]
    : shipperAccounts;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:700,color:C.text,margin:0}}>Broker Dashboard</h2>
          {usingSample && <div style={{fontSize:11,color:C.textDm,marginTop:3}}>Showing sample data — post your first load to see real activity</div>}
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn variant="ghost"   size="sm" onClick={()=>setPage("dispatch")}>🎯 Dispatch</Btn>
          <Btn variant="ghost"   size="sm" onClick={()=>setPage("carriers")}>Find Carriers</Btn>
          <Btn variant="primary" size="sm" onClick={()=>setPage("post")}>+ Post Load</Btn>
        </div>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        <StatCard label="Net Margin"    value={fmt$(dispMargin)}    sub={`${usingSample?2:delivered.length} delivered`} color={dispMargin>=0?C.success:C.danger} icon="💰"/>
        <StatCard label="Gross Revenue" value={fmt$(dispGross)}     sub="from shippers"    color={C.text}     icon="📈"/>
        <StatCard label="In Transit"    value={dispTransit.length}  sub="active"           color={C.accentLt} icon="🚛"/>
        <StatCard label="Needs Carrier" value={dispNC.length}       sub="action required"  color={dispNC.length>0?C.warn:C.textMd} icon="⚠"/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:14}}>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:13,fontWeight:600,color:C.text}}>Load Pipeline</div>
            <Btn variant="ghost" size="sm" onClick={()=>setPage("mybids")}>View all →</Btn>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:C.surf2}}>{["Load","Shipper","Route","Carrier","Shipper $","Carrier $","Margin","Status"].map(h=><th key={h} style={{padding:"8px 12px",fontSize:10,color:C.textDm,textTransform:"uppercase",letterSpacing:"0.07em",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
            <tbody>
              {displayLoads.slice(0,8).map((l,i)=>{
                const margin=(l.rate||0)-(l.brokerRate||l.rate||0);
                const marginPct=l.rate>0&&l.brokerRate>0?Math.round(margin/l.rate*100):null;
                return(
                  <tr key={l.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"transparent":C.surf2+"44"}}>
                    <td style={{padding:"10px 12px",fontSize:11,fontFamily:C.mono,color:C.textDm}}>{l.number}</td>
                    <td style={{padding:"10px 12px"}}><div style={{fontSize:12,fontWeight:500,color:C.text}}>{l.shipper}</div>{l.shipperEmail&&<div style={{fontSize:10,color:C.textDm}}>{l.shipperEmail}</div>}</td>
                    <td style={{padding:"10px 12px"}}><div style={{fontSize:12,color:C.text}}>{(l.origin||"").split(",")[0]} → {(l.dest||"").split(",")[0]}</div>{l.driver&&<div style={{fontSize:10,color:C.textDm}}>👤 {l.driver}</div>}</td>
                    <td style={{padding:"10px 12px",fontSize:12,color:l.carrier&&l.carrier!=="Unassigned"?C.text:C.warn}}>{l.carrier&&l.carrier!=="Unassigned"&&l.carrier!=="Direct Booking"?l.carrier:<span style={{color:C.warn}}>⚠ Unassigned</span>}</td>
                    <td style={{padding:"10px 12px",fontSize:12,fontFamily:C.mono,color:C.success,fontWeight:600}}>{fmt$(l.rate||0)}</td>
                    <td style={{padding:"10px 12px",fontSize:12,fontFamily:C.mono,color:C.textMd}}>{l.brokerRate?fmt$(l.brokerRate):"—"}</td>
                    <td style={{padding:"10px 12px"}}>{marginPct!==null?<div><span style={{fontSize:12,fontWeight:700,fontFamily:C.mono,color:margin>0?C.success:C.danger}}>{fmt$(margin)}</span><span style={{fontSize:10,color:C.textDm,marginLeft:3}}>({marginPct}%)</span></div>:<span style={{color:C.textDm}}>—</span>}</td>
                    <td style={{padding:"10px 12px"}}><Badge status={l.status}/></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
            <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:12}}>P&L Summary</div>
            {[
              {label:"Shipper Revenue",     value:fmt$(dispGross),  color:C.success},
              {label:"Carrier Costs",       value:`-${fmt$(dispCosts)}`, color:C.textMd},
              {label:"Platform Fee (3.5%)", value:`-${fmt$(dispFee)}`,  color:C.danger},
              {label:"Net Margin",          value:fmt$(dispMargin), color:dispMargin>=0?C.success:C.danger, bold:true},
            ].map(r=>(
              <div key={r.label} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>
                <span style={{fontSize:12,color:C.textMd}}>{r.label}</span>
                <span style={{fontSize:12,fontWeight:r.bold?700:600,color:r.color,fontFamily:C.mono}}>{r.value}</span>
              </div>
            ))}
            <Btn variant="ghost" size="sm" style={{width:"100%",marginTop:10}} onClick={()=>setPage("invoices")}>View Invoices →</Btn>
          </div>
          {dispNC.length>0&&(
            <div style={{background:`${C.warn}11`,border:`1px solid ${C.warn}44`,borderRadius:10,padding:14}}>
              <div style={{fontSize:12,fontWeight:700,color:C.warn,marginBottom:8}}>⚠ Needs Carrier ({dispNC.length})</div>
              {dispNC.slice(0,3).map(l=>(
                <div key={l.id} style={{padding:"6px 0",borderBottom:`1px solid ${C.warn}22`}}>
                  <div style={{fontSize:12,color:C.text}}>{(l.origin||"").split(",")[0]} → {(l.dest||"").split(",")[0]}</div>
                  <div style={{fontSize:11,color:C.textMd}}>Shipper: {fmt$(l.rate||0)} · Pickup {l.pickupDate}</div>
                </div>
              ))}
              <Btn variant="outline" size="sm" style={{width:"100%",marginTop:8}} onClick={()=>setPage("dispatch")}>Go to Dispatch →</Btn>
            </div>
          )}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>Shipper Accounts</div>
              <Btn variant="ghost" size="sm" onClick={()=>setPage("shippers")}>View all →</Btn>
            </div>
            {dispShippers.slice(0,4).map(s=>(
              <div key={s.name} style={{padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <div style={{fontSize:12,fontWeight:600,color:C.text}}>{s.name}</div>
                  <div style={{fontSize:12,fontFamily:C.mono,color:C.success}}>{fmt$(s.revenue)}</div>
                </div>
                <div style={{fontSize:11,color:C.textMd,marginTop:2}}>{s.loads} load{s.loads!==1?"s":""}{s.email&&<span style={{color:C.textDm}}> · {s.email}</span>}</div>
              </div>
            ))}
            {dispShippers.length===0&&<div style={{fontSize:12,color:C.textDm,padding:"8px 0"}}>No shipper accounts yet — <button onClick={()=>setPage("shippers")} style={{background:"none",border:"none",color:C.accentLt,cursor:"pointer",fontSize:12}}>add one →</button></div>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Broker Dispatch Board ────────────────────────────────────────────────────

const DISPATCH_STAGES = [
  { id:"BOOKED",      label:"Booked",      color:"#3b82f6", icon:"📋" },
  { id:"AT_PICKUP",   label:"At Pickup",   color:"#f59e0b", icon:"🏭" },
  { id:"PICKED_UP",   label:"Picked Up",   color:"#8b5cf6", icon:"📦" },
  { id:"IN_TRANSIT",  label:"In Transit",  color:"#06b6d4", icon:"🚛" },
  { id:"AT_DELIVERY", label:"At Delivery", color:"#f97316", icon:"🏪" },
  { id:"DELIVERED",   label:"Delivered",   color:"#10b981", icon:"✅" },
];

// Sample carrier roster — shown only to the demo broker account; real brokers
// build their network via carrier relationships (see BrokerCarriersPage)
const BROKER_CARRIERS_DEMO = [
  { id:"C1", name:"Arrow Logistics Inc.",  dot:"MC-442211", rating:4.9, trucks:12, phone:"(773) 555-0200", city:"Chicago, IL",   types:["DRY_VAN","FLATBED"] },
  { id:"C2", name:"Sunbelt Trucking Co.",  dot:"MC-558833", rating:4.7, trucks:8,  phone:"(404) 555-0300", city:"Atlanta, GA",    types:["REEFER","DRY_VAN"] },
  { id:"C3", name:"Eagle Transport LLC",   dot:"MC-661144", rating:4.8, trucks:6,  phone:"(206) 555-0400", city:"Seattle, WA",    types:["DRY_VAN"] },
  { id:"C4", name:"Gulf Coast Carriers",   dot:"MC-774455", rating:4.6, trucks:15, phone:"(713) 555-0500", city:"Houston, TX",    types:["TANKER","FLATBED"] },
  { id:"C5", name:"Mountain West Freight", dot:"MC-887766", rating:4.9, trucks:9,  phone:"(303) 555-0600", city:"Denver, CO",     types:["DRY_VAN","REEFER"] },
];

const INIT_DISPATCH_LOADS = [
  {
    id:"L002", number:"FF-000002",
    origin:"Atlanta, GA", dest:"Miami, FL",
    shipper:"Midwest Goods Co.", shipperPhone:"(312) 555-0101",
    carrier:"Arrow Logistics Inc.", carrierPhone:"(773) 555-0200",
    driver:"", driverPhone:"", truck:"",
    commodity:"Fresh Produce", weight:18000, miles:662, rate:2800, brokerRate:2100,
    pickupDate:"Jun 17", deliveryDate:"Jun 18", pickupTime:"08:00", deliveryTime:"17:00",
    status:"BOOKED", progress:0, checkIns:[], alerts:[],
  },
  {
    id:"L003", number:"FF-000003",
    origin:"Atlanta, GA", dest:"Miami, FL",
    shipper:"Pacific Freight LLC", shipperPhone:"(415) 555-0202",
    carrier:"Arrow Logistics Inc.", carrierPhone:"(773) 555-0200",
    driver:"Marcus Johnson", driverPhone:"(312) 555-9001", truck:"ARR-002",
    commodity:"Retail Goods", weight:25000, miles:662, rate:2100, brokerRate:1650,
    pickupDate:"Jun 14", deliveryDate:"Jun 15", pickupTime:"07:00", deliveryTime:"16:00",
    status:"IN_TRANSIT", progress:55,
    checkIns:[
      { time:"Jun 14, 7:02 AM",  note:"Driver at shipper — loading dock 3",      type:"AT_PICKUP"  },
      { time:"Jun 14, 9:15 AM",  note:"Load secured, departed Atlanta",           type:"IN_TRANSIT" },
      { time:"Jun 14, 12:30 PM", note:"Checked in — Gainesville, FL",             type:"CHECKIN"    },
    ],
    alerts:[],
  },
  {
    id:"L004", number:"FF-000004",
    origin:"Seattle, WA", dest:"Portland, OR",
    shipper:"Midwest Goods Co.", shipperPhone:"(312) 555-0101",
    carrier:"Arrow Logistics Inc.", carrierPhone:"(773) 555-0200",
    driver:"Kevin Park", driverPhone:"(206) 555-9003", truck:"ARR-001",
    commodity:"Electronics", weight:12000, miles:175, rate:1800, brokerRate:1400,
    pickupDate:"Jun 12", deliveryDate:"Jun 12", pickupTime:"09:00", deliveryTime:"13:00",
    status:"DELIVERED", progress:100,
    checkIns:[
      { time:"Jun 12, 8:55 AM",  note:"At shipper — loading complete",            type:"AT_PICKUP"  },
      { time:"Jun 12, 9:40 AM",  note:"Departed Seattle",                          type:"IN_TRANSIT" },
      { time:"Jun 12, 12:50 PM", note:"Delivered — POD signed by T. Williams",     type:"DELIVERED"  },
    ],
    alerts:[],
  },
  {
    id:"L006", number:"FF-000006",
    origin:"Kansas City, MO", dest:"Memphis, TN",
    shipper:"Midwest Goods Co.", shipperPhone:"(312) 555-0101",
    carrier:"Arrow Logistics Inc.", carrierPhone:"(773) 555-0200",
    driver:"Sandra Torres", driverPhone:"(404) 555-9002", truck:"ARR-003",
    commodity:"Steel Coils", weight:38000, miles:441, rate:2400, brokerRate:1900,
    pickupDate:"Jun 14", deliveryDate:"Jun 15", pickupTime:"07:00", deliveryTime:"15:00",
    status:"AT_PICKUP", progress:10,
    checkIns:[
      { time:"Jun 14, 6:58 AM",  note:"Driver arrived at shipper — dock 2",       type:"AT_PICKUP"  },
    ],
    alerts:[{ type:"DETENTION", msg:"Driver waiting 35min+ at pickup — detention clock running" }],
  },
];

const INIT_TRUCKS = [
  { id:"T1", number:"ARR-001", year:2022, make:"Freightliner", model:"Cascadia", unitType:"TRACTOR", trailerTypes:["DRY_VAN","FLATBED"], status:"AVAILABLE", odometer:187420, plate:"IL-4829TK" },
  { id:"T2", number:"ARR-002", year:2021, make:"Kenworth",     model:"T680",     unitType:"TRACTOR", trailerTypes:["DRY_VAN","REEFER"],   status:"AVAILABLE", odometer:243100, plate:"IL-9201RX" },
  { id:"T3", number:"ARR-003", year:2023, make:"Peterbilt",    model:"579",       unitType:"TRACTOR", trailerTypes:["REEFER"],             status:"IN_USE",    odometer:98300,  plate:"IL-3344PT" },
  { id:"T4", number:"ARR-004", year:2020, make:"Volvo",        model:"VNL 760",   unitType:"TRACTOR", trailerTypes:["DRY_VAN"],            status:"AVAILABLE", odometer:312800, plate:"IL-7755VL" },
];

const INIT_DRIVERS = [
  { id:"D1", firstName:"Marcus",  lastName:"Johnson", status:"AVAILABLE", cdlClass:"A", cdlState:"IL", phone:"(312) 555-9001", yearsExp:8  },
  { id:"D2", firstName:"Sandra",  lastName:"Torres",  status:"ON_DUTY",   cdlClass:"A", cdlState:"GA", phone:"(404) 555-9002", yearsExp:5  },
  { id:"D3", firstName:"Kevin",   lastName:"Park",    status:"AVAILABLE", cdlClass:"A", cdlState:"WA", phone:"(206) 555-9003", yearsExp:12 },
  { id:"D4", firstName:"Darnell", lastName:"Wright",  status:"OFF_DUTY",  cdlClass:"A", cdlState:"IL", phone:"(312) 555-9004", yearsExp:3  },
];

const BrokerDispatchPage = ({ loads, onStatusChange, dispatchState, setDispatchState, onCarrierAssign }) => {
  const { token, user: bdpUser } = useAuth();
  const isDemoBrokerBDP = bdpUser?.email === "broker@midwestbroker.com";
  // Demo broker sees the sample carrier roster; new brokers see none until they
  // build real carrier relationships (type carrier name/phone manually for now)
  const availableCarriers = isDemoBrokerBDP ? BROKER_CARRIERS_DEMO : [];

  // State restored from parent dispatchState prop
  const realActiveLoads = loads
    .filter(l => ["POSTED","BIDDING","BOOKED","IN_TRANSIT","AT_PICKUP","PICKED_UP","AT_DELIVERY","DELIVERED"].includes(l.status))
    .filter(l => l.status !== "CANCELLED")
    .map(l => ({ id:l.id, number:l.number, origin:l.origin, dest:l.dest, shipper:l.shipper||"", shipperPhone:l.shipperPhone||"", carrier:l.carrier||"Unassigned", carrierPhone:l.carrierPhone||"", driver:l.driver||"", driverPhone:l.driverPhone||"", truck:l.truck||"", commodity:l.commodity||"", weight:l.weight||0, miles:l.miles||0, rate:l.rate||0, brokerRate:l.brokerRate||l.rate||0, pickupDate:l.pickupDate||"", deliveryDate:l.deliveryDate||"", pickupTime:l.pickupTime||"08:00", deliveryTime:l.deliveryTime||"17:00", status:l.status, progress:l.progress||0, checkIns:[], alerts:(l.status==="POSTED"||!l.carrier||l.carrier==="Unassigned")?[{type:"CARRIER",msg:"No carrier assigned — select a carrier to dispatch"}]:[] }));

  const realIds = new Set(realActiveLoads.map(l => l.id));
  const sampleLoads = isDemoBrokerBDP ? INIT_DISPATCH_LOADS.filter(l => !realIds.has(l.id)) : [];
  const [dispLoads, setDispLoads] = useState(() => {
    // Restore from parent state, always excluding cancelled loads
    const base = dispatchState && dispatchState.length > 0
      ? dispatchState
      : [...realActiveLoads, ...sampleLoads];
    return base.filter(l => l.status !== "CANCELLED");
  });
  const [selected,  setSelected]  = useState(null);
  const [noteText,  setNoteText]  = useState("");
  const [showNote,  setShowNote]  = useState(false);
  const [filter,    setFilter]    = useState("ALL");
  const [detentionTimers, setDetentionTimers] = useState({});
  const FREE_TIME_MINUTES = 120;
  const [showRebook, setShowRebook] = useState(false);
  const [rebookReason, setRebookReason] = useState("");
  const [rebookCarrier, setRebookCarrier] = useState("");
  const [rebookRate, setRebookRate] = useState("");

  const removedSamplesRef = useRef(new Set());
  // Save dispatch state to parent on every change so it survives navigation
  useEffect(() => {
    if (setDispatchState) setDispatchState(dispLoads);
  }, [dispLoads]); // eslint-disable-line

  const loadsKey = loads.filter(l=>["BOOKED","IN_TRANSIT","AT_PICKUP","PICKED_UP","AT_DELIVERY","DELIVERED"].includes(l.status)).map(l=>l.id+l.status).join(",");

  useEffect(() => {
    setDispLoads(prev => {
      // If we have saved dispatch state, only merge in NEW real loads not already tracked
      const prevIds = new Set(prev.map(l => l.id));
      const newRealLoads = realActiveLoads.filter(rl => !prevIds.has(rl.id));
      if (newRealLoads.length === 0) {
        // No new loads — just update status of existing real loads from main loads state
        return prev.map(p => {
          const realLoad = realActiveLoads.find(r => r.id === p.id);
          if (!realLoad) return p; // keep sample/saved loads as-is
          // Preserve dispatch-specific fields (driver, truck, checkIns, alerts)
          // but always use the DB status (realLoad.status) to reflect backend truth
          return { ...realLoad, driver:p.driver||"", driverPhone:p.driverPhone||"", truck:p.truck||"", checkIns:p.checkIns, alerts:p.alerts };
        });
      }
      // New loads added — append them
      const keepSample = prev.filter(l => INIT_DISPATCH_LOADS.some(s=>s.id===l.id) && !removedSamplesRef.current.has(l.id));
      return [...prev.filter(p => !INIT_DISPATCH_LOADS.some(s=>s.id===p.id)), ...newRealLoads, ...keepSample];
    });
  }, [loadsKey]); // eslint-disable-line
  useEffect(() => {
    const interval = setInterval(() => {
      setDetentionTimers(prev => {
        const now = Date.now();
        const updated = { ...prev };
        let changed = false;
        Object.entries(prev).forEach(([loadId, timer]) => {
          const waitMinutes = (now - timer.arrivedAt) / 60000;
          if (waitMinutes >= FREE_TIME_MINUTES && !timer.alerted) {
            updated[loadId] = { ...timer, alerted: true };
            changed = true;
            const stageName = timer.stage==="AT_PICKUP"?"pickup":"delivery";
            const waitStr = Math.round(waitMinutes);
            const alert = { type:"DETENTION", msg:`Driver waiting ${waitStr}min+ at ${stageName} — detention clock running ($65/hr after 2hr free time)` };
            setDispLoads(loads => loads.map(l => l.id===loadId ? { ...l, alerts:[...l.alerts.filter(a=>a.type!=="DETENTION"), alert] } : l));
            setSelected(sel => sel?.id===loadId ? { ...sel, alerts:[...(sel.alerts||[]).filter(a=>a.type!=="DETENTION"), alert] } : sel);
          }
        });
        return changed ? updated : prev;
      });
    }, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  const advanceStatus = async (loadId) => {
    const order = DISPATCH_STAGES.map(s => s.id);
    let nextStatus = null;
    setDispLoads(prev => prev.map(l => {
      if (l.id !== loadId) return l;
      const idx = order.indexOf(l.status);
      if (idx >= order.length - 1) return l;
      nextStatus = order[idx + 1];
      const stage = DISPATCH_STAGES.find(s => s.id === nextStatus);
      const newCheck = { time:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}), note:`Status updated to ${stage.label}`, type:nextStatus };
      if (nextStatus==="AT_PICKUP"||nextStatus==="AT_DELIVERY") setDetentionTimers(p=>({...p,[loadId]:{arrivedAt:Date.now(),stage:nextStatus,alerted:false}}));
      if (nextStatus==="PICKED_UP"||nextStatus==="DELIVERED") setDetentionTimers(p=>{const n={...p};delete n[loadId];return n;});
      return { ...l, status:nextStatus, checkIns:[...l.checkIns, newCheck], alerts:[] };
    }));
    setSelected(prev => {
      if (!prev||prev.id!==loadId) return prev;
      const idx2 = order.indexOf(prev.status);
      const ns = order[Math.min(idx2+1, order.length-1)];
      if (onStatusChange) onStatusChange(loadId, ns);
      return { ...prev, status:ns, alerts:[] };
    });
    // Persist to backend via broker dispatch endpoint (skip sample loads)
    const isSampleLoad = loadId.startsWith("DL") || INIT_DISPATCH_LOADS.some(l => l.id === loadId);
    if (token && nextStatus && !isSampleLoad) {
      try {
        await fetch(`${API_BASE}/tracking/dispatch/${loadId}/status`, {
          method:"POST",
          headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
          body:JSON.stringify({ status:nextStatus, note:`Dispatcher advanced to ${nextStatus}` }),
        });
      } catch(e) { console.warn("Status sync failed:", e.message); }
    }
  };

  const addNote = async (loadId) => {
    if (!noteText.trim()) return;
    const note = noteText.trim();
    const newCheck = { time:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}), note, type:"NOTE" };
    setDispLoads(prev=>prev.map(l=>l.id===loadId?{...l,checkIns:[...l.checkIns,newCheck]}:l));
    setSelected(prev=>prev?.id===loadId?{...prev,checkIns:[...prev.checkIns,newCheck]}:prev);
    setNoteText(""); setShowNote(false);
    // Persist note to backend
    if (token) {
      try {
        await fetch(`${API_BASE}/tracking/update`, {
          method:"POST",
          headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
          body:JSON.stringify({ loadId, note, status:selected?.status }),
        });
      } catch(e) { console.warn("Note sync failed:", e.message); }
    }
  };
  const dismissDetention = (loadId) => {
    setDetentionTimers(prev=>{const n={...prev};delete n[loadId];return n;});
    const note={time:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}),note:"Detention dismissed — no charge",type:"NOTE"};
    setDispLoads(prev=>prev.map(l=>l.id===loadId?{...l,alerts:l.alerts.filter(a=>a.type!=="DETENTION"),checkIns:[...l.checkIns,note]}:l));
    setSelected(prev=>prev?.id===loadId?{...prev,alerts:prev.alerts.filter(a=>a.type!=="DETENTION")}:prev);
  };

  const billDetention = (loadId) => {
    const timer = detentionTimers[loadId];
    const waitMinutes = timer?Math.round((Date.now()-timer.arrivedAt)/60000):0;
    const hours = Math.max(0,(waitMinutes-FREE_TIME_MINUTES)/60);
    const charge = Math.round(hours*65);
    setDetentionTimers(prev=>{const n={...prev};delete n[loadId];return n;});
    const note={time:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}),note:`Detention billed: ${waitMinutes}min wait · $${charge} charge added to invoice`,type:"NOTE"};
    setDispLoads(prev=>prev.map(l=>l.id===loadId?{...l,alerts:l.alerts.filter(a=>a.type!=="DETENTION"),detentionCharge:charge,checkIns:[...l.checkIns,note]}:l));
    setSelected(prev=>prev?.id===loadId?{...prev,alerts:prev.alerts.filter(a=>a.type!=="DETENTION"),detentionCharge:charge}:prev);
  };

  const handleRebook = () => {
    if (!rebookCarrier) return;
    const c = availableCarriers.find(c=>c.name===rebookCarrier);
    const now = new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"});
    const notes = [{time:now,note:`⚠ Re-booked: ${rebookReason||"Carrier change"}. Previous carrier: ${selected.carrier||"—"}`,type:"NOTE"},{time:now,note:`New carrier assigned: ${rebookCarrier}${rebookRate?` at ${fmt$(parseFloat(rebookRate))}`:""}`,type:"NOTE"}];
    const updated = {...selected,carrier:rebookCarrier,carrierPhone:c?.phone||"",brokerRate:rebookRate?parseFloat(rebookRate):selected.brokerRate,status:"BOOKED",driver:"",driverPhone:"",truck:"",alerts:[],checkIns:[...selected.checkIns,...notes]};
    setDispLoads(prev=>prev.map(l=>l.id===selected.id?updated:l));
    setSelected(updated);
    setShowRebook(false); setRebookReason(""); setRebookCarrier(""); setRebookRate("");
  };

  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const cancelLoad = async () => {
    if (!selected) return;
    setCancelling(true);
    const isSample = selected.id.startsWith("DL") || INIT_DISPATCH_LOADS.some(l=>l.id===selected.id);
    try {
      if (token && !isSample) {
        await fetch(`${API_BASE}/tracking/dispatch/${selected.id}/cancel`, {
          method: "POST",
          headers: { "Content-Type":"application/json", Authorization:`Bearer ${token}` },
          body: JSON.stringify({ reason: cancelReason || "Cancelled by dispatcher" }),
        });
      }
      // Remove from dispatch board entirely — it's no longer active
      setDispLoads(prev => prev.filter(l => l.id !== selected.id));
      if (onStatusChange) onStatusChange(selected.id, "CANCELLED");
      setSelected(null);
      setShowCancel(false);
      setCancelReason("");
    } catch(e) {
      console.warn("Cancel load failed:", e.message);
    } finally {
      setCancelling(false);
    }
  };

  const stageColor = s => DISPATCH_STAGES.find(d=>d.id===s)?.color||C.textMd;
  const stageLabel = s => DISPATCH_STAGES.find(d=>d.id===s)?.label||s;
  const stageIcon  = s => DISPATCH_STAGES.find(d=>d.id===s)?.icon||"●";
  const nextStage  = s => { const order=DISPATCH_STAGES.map(d=>d.id); const idx=order.indexOf(s); return idx<order.length-1?DISPATCH_STAGES[idx+1]:null; };
  const checkColor = t => ({PICKUP:"#10b981",TRANSIT:"#3b82f6",CHECKIN:"#94a3b8",DELIVERY:"#f97316",NOTE:"#8b5cf6",AT_DELIVERY:"#f97316",AT_PICKUP:"#f59e0b",IN_TRANSIT:"#06b6d4",DELIVERED:"#10b981",BOOKED:"#3b82f6"}[t]||"#94a3b8");

  const displayed = filter==="ACTIVE" ? dispLoads.filter(l=>l.status!=="DELIVERED") : dispLoads;
  const hasAlerts = dispLoads.some(l=>l.alerts?.length>0);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:700,color:C.text,margin:0}}>Dispatch Board</h2>
          <p style={{fontSize:13,color:C.textMd,margin:"3px 0 0"}}>{dispLoads.filter(l=>l.status!=="DELIVERED").length} active · {dispLoads.filter(l=>l.alerts?.length>0).length} alert{dispLoads.filter(l=>l.alerts?.length>0).length!==1?"s":""}</p>
        </div>
        <div style={{display:"flex",gap:0,background:C.bg,borderRadius:8,padding:3,border:`1px solid ${C.border}`}}>
          {[["ALL","All Loads"],["ACTIVE","Active Only"]].map(([id,label])=>(
            <button key={id} onClick={()=>setFilter(id)} style={{background:filter===id?C.accent:"transparent",color:filter===id?"#fff":C.textMd,border:"none",borderRadius:5,padding:"4px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>{label}</button>
          ))}
        </div>
      </div>

      {hasAlerts && (
        <div style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
          {dispLoads.filter(l=>l.alerts?.length>0).map(l=>l.alerts.map((a,i)=>(
            <div key={`${l.id}-${i}`} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.danger}}>
              <span>⚠</span><strong>{l.number}</strong><span style={{color:C.textMd}}>—</span><span>{a.msg}</span>
            </div>
          )))}
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 380px",gap:14,alignItems:"start"}}>
        {/* Kanban */}
        <div style={{overflowX:"auto"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8,minWidth:700}}>
            {DISPATCH_STAGES.map(stage=>{
              const stagLoads=displayed.filter(l=>l.status===stage.id);
              return(
                <div key={stage.id}>
                  <div style={{background:C.surf2,borderRadius:"8px 8px 0 0",padding:"8px 10px",borderBottom:`2px solid ${stage.color}`,marginBottom:8}}>
                    <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",color:stage.color}}>{stage.icon} {stage.label}</div>
                    <div style={{fontSize:10,color:C.textDm,marginTop:2}}>{stagLoads.length} load{stagLoads.length!==1?"s":""}</div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {stagLoads.map(l=>{
                      const isSel=selected?.id===l.id;
                      const hasAlert=l.alerts?.length>0;
                      return(
                        <div key={l.id} onClick={()=>setSelected(l)} style={{background:isSel?C.surf3:C.surface,border:`1px solid ${hasAlert?C.danger:isSel?stage.color:C.border}`,borderLeft:`3px solid ${stage.color}`,borderRadius:7,padding:"9px 10px",cursor:"pointer"}}>
                          {hasAlert&&<div style={{fontSize:9,color:C.danger,fontWeight:700,marginBottom:3}}>⚠ ALERT</div>}
                          <div style={{fontSize:11,fontFamily:C.mono,color:C.textDm,marginBottom:3}}>{l.number}</div>
                          <div style={{fontSize:11,fontWeight:600,color:C.text,marginBottom:3,lineHeight:1.3}}>{l.origin.split(",")[0]} →<br/>{l.dest.split(",")[0]}</div>
                          <div style={{fontSize:10,color:C.textMd}}>{l.driver||"No driver"}</div>
                          <div style={{fontSize:10,color:C.textDm,marginTop:4}}>{l.pickupDate}</div>
                          {l.status!=="BOOKED"&&l.status!=="DELIVERED"&&(
                            <div style={{marginTop:6}}>
                              <div style={{height:3,background:C.border,borderRadius:2}}><div style={{height:"100%",width:`${l.progress}%`,background:stage.color,borderRadius:2}}/></div>
                              <div style={{fontSize:9,color:C.textDm,marginTop:2}}>{l.progress}%</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {stagLoads.length===0&&<div style={{background:C.bg,borderRadius:7,padding:"16px 8px",textAlign:"center",fontSize:10,color:C.textDm,border:`1px dashed ${C.border}`}}>Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        {selected ? (
          <div style={{background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:12,overflow:"hidden",display:"flex",flexDirection:"column",maxHeight:"80vh"}}>
            <div style={{padding:"14px 18px",borderBottom:`1px solid ${C.border}`,background:`${stageColor(selected.status)}11`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontSize:10,fontFamily:C.mono,color:C.textDm}}>{selected.number}</div>
                  <div style={{fontSize:15,fontWeight:700,color:C.text}}>{selected.origin.split(",")[0]} → {selected.dest.split(",")[0]}</div>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:5,textTransform:"uppercase",background:`${stageColor(selected.status)}22`,color:stageColor(selected.status),border:`1px solid ${stageColor(selected.status)}44`}}>{stageIcon(selected.status)} {stageLabel(selected.status)}</span>
                  {["BOOKED","AT_PICKUP","PICKED_UP","IN_TRANSIT"].includes(selected.status)&&<button onClick={()=>setShowRebook(true)} style={{background:"rgba(239,68,68,0.15)",border:"1px solid rgba(239,68,68,0.4)",color:C.danger,borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>🔄 Re-book</button>}
                  {selected.status!=="DELIVERED"&&<button onClick={()=>setShowCancel(true)} style={{background:"transparent",border:`1px solid ${C.border}`,color:C.textDm,borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>🗑 Cancel</button>}
                  <button onClick={()=>setSelected(null)} style={{background:C.surf2,border:`1px solid ${C.border}`,color:C.textMd,borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:11}}>✕</button>
                </div>
              </div>
            </div>

            <div style={{flex:1,overflowY:"auto",padding:16}}>
              {/* Alerts */}
              {(selected.alerts||[]).map((a,i)=>(
                <div key={i} style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"10px 12px",marginBottom:12}}>
                  <div style={{fontSize:12,color:C.danger,fontWeight:600,marginBottom:a.type==="DETENTION"?6:0}}>⚠ {a.msg}</div>
                  {a.type==="DETENTION"&&(
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>billDetention(selected.id)} style={{flex:1,background:C.danger,border:"none",color:"#fff",borderRadius:6,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>💰 Bill Detention ($65/hr)</button>
                      <button onClick={()=>dismissDetention(selected.id)} style={{background:"transparent",border:`1px solid ${C.border}`,color:C.textMd,borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer"}}>Dismiss</button>
                    </div>
                  )}
                </div>
              ))}
              {selected.detentionCharge>0&&<div style={{background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:7,padding:"8px 12px",marginBottom:12,fontSize:12,color:C.warn}}>💰 Detention charge: <strong>${selected.detentionCharge}</strong> added to invoice</div>}

              {/* Progress */}
              {selected.status!=="BOOKED"&&(
                <div style={{marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.textMd,marginBottom:4}}><span>{selected.origin.split(",")[0]}</span><span style={{color:stageColor(selected.status),fontWeight:600}}>{selected.progress}%</span><span>{selected.dest.split(",")[0]}</span></div>
                  <div style={{height:6,background:C.border,borderRadius:3}}><div style={{height:"100%",width:`${selected.progress}%`,borderRadius:3,background:`linear-gradient(90deg,${stageColor(selected.status)},${C.success})`}}/></div>
                </div>
              )}

              {/* Shipper */}
              <div style={{background:C.bg,borderRadius:7,padding:"9px 11px",border:`1px solid ${C.border}`,marginBottom:8}}>
                <div style={{fontSize:9,color:C.textDm,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>📦 Shipper</div>
                <div style={{fontSize:12,fontWeight:600,color:C.text}}>{selected.shipper||"—"}</div>
                {selected.shipperPhone&&<a href={`tel:${selected.shipperPhone}`} style={{fontSize:11,color:C.accentLt,textDecoration:"none",display:"block",marginTop:2}}>{selected.shipperPhone}</a>}
              </div>

              {/* Carrier + Driver + Truck assignment */}
              {(()=>{
                const missingCarrier=!selected.carrier||selected.carrier==="Unassigned";
                const missingDriver=!selected.driver;
                const missingTruck=!selected.truck;
                const allAssigned=!missingCarrier&&!missingDriver&&!missingTruck;
                const assign=async(field,value,extra={})=>{
                  const note={time:new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}),note:`${field} assigned: ${value}`,type:"NOTE"};
                  setDispLoads(prev=>prev.map(l=>l.id===selected.id?{...l,[field.toLowerCase()]:value,...extra,checkIns:[...l.checkIns,note],alerts:[]}:l));
                  setSelected(prev=>({...prev,[field.toLowerCase()]:value,...extra,alerts:[]}));

                  // Sync carrier assignment to backend so the carrier sees this load
                  if (field === "Carrier" && token) {
                    const isSample = selected.id.startsWith("DL") || INIT_DISPATCH_LOADS.some(l=>l.id===selected.id);
                    if (!isSample) {
                      try {
                        const c = availableCarriers.find(c=>c.name===value);
                        await fetch(`${API_BASE}/tracking/dispatch/${selected.id}/assign-carrier`, {
                          method:"POST",
                          headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
                          body:JSON.stringify({ carrierName:value, carrierId:c?.id, carrierPhone:extra.carrierPhone }),
                        });
                      } catch(e) { console.warn("Carrier assignment sync failed:", e.message); }
                    }
                  }
                  // Notify parent loads state so other views (My Loads, Tracking) reflect the carrier
                  if (field === "Carrier" && onCarrierAssign) {
                    onCarrierAssign(selected.id, value, extra.carrierId, extra.carrierPhone);
                  }
                };
                return(
                  <div style={{background:C.bg,borderRadius:8,padding:"11px 13px",border:`1px solid ${allAssigned?C.success:C.warn}44`,marginBottom:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                      <div style={{fontSize:10,color:C.textDm,textTransform:"uppercase",letterSpacing:"0.08em"}}>Dispatch Assignment</div>
                      {allAssigned?<span style={{fontSize:10,color:C.success,fontWeight:700}}>✓ Ready to dispatch</span>:<span style={{fontSize:10,color:C.warn,fontWeight:700}}>⚠ {[missingCarrier&&"Carrier",missingDriver&&"Driver",missingTruck&&"Truck"].filter(Boolean).join(", ")} needed</span>}
                    </div>
                    <div style={{marginBottom:8}}>
                      <label style={{fontSize:10,color:C.textMd,display:"block",marginBottom:4}}>🏢 Carrier</label>
                      {availableCarriers.length > 0 ? (
                        <select value={selected.carrier||""} onChange={e=>{const c=availableCarriers.find(c=>c.name===e.target.value);assign("Carrier",e.target.value,{carrierPhone:c?.phone||"",carrierId:c?.id||""});}} style={{width:"100%",background:C.surface,border:`1px solid ${missingCarrier?C.warn:C.border}`,borderRadius:6,padding:"7px 10px",color:C.text,fontSize:12,outline:"none"}}>
                          <option value="">— Select carrier —</option>
                          {availableCarriers.map(c=><option key={c.id} value={c.name}>{c.name} · ⭐{c.rating} · {c.trucks} trucks · {c.city}</option>)}
                        </select>
                      ) : (
                        <input value={selected.carrier&&selected.carrier!=="Unassigned"?selected.carrier:""} onChange={e=>assign("Carrier",e.target.value,{})}
                          placeholder="Carrier company name"
                          style={{width:"100%",background:C.surface,border:`1px solid ${missingCarrier?C.warn:C.border}`,borderRadius:6,padding:"7px 10px",color:C.text,fontSize:12,outline:"none"}} />
                      )}
                      {selected.carrier&&selected.carrier!=="Unassigned"&&<div style={{display:"flex",justifyContent:"space-between",marginTop:4}}><span style={{fontSize:11,color:C.success}}>✓ {selected.carrier}</span>{selected.carrierPhone&&<a href={`tel:${selected.carrierPhone}`} style={{fontSize:11,color:C.accentLt,textDecoration:"none"}}>{selected.carrierPhone}</a>}</div>}
                    </div>
                    <div style={{marginBottom:8}}>
                      <label style={{fontSize:10,color:C.textMd,display:"block",marginBottom:4}}>👤 Driver Name</label>
                      <input value={selected.driver||""} onChange={e=>assign("Driver",e.target.value,{})}
                        placeholder="Driver name (assigned by carrier)"
                        style={{width:"100%",background:C.surface,border:`1px solid ${missingDriver?C.warn:C.border}`,borderRadius:6,padding:"7px 10px",color:C.text,fontSize:12,outline:"none"}} />
                      {selected.driver&&<div style={{display:"flex",justifyContent:"space-between",marginTop:4}}><span style={{fontSize:11,color:C.success}}>✓ {selected.driver}</span></div>}
                    </div>
                    <div>
                      <label style={{fontSize:10,color:C.textMd,display:"block",marginBottom:4}}>🚛 Truck / Unit #</label>
                      <input value={selected.truck||""} onChange={e=>assign("Truck",e.target.value)}
                        placeholder="Truck unit number"
                        style={{width:"100%",background:C.surface,border:`1px solid ${missingTruck?C.warn:C.border}`,borderRadius:6,padding:"7px 10px",color:C.text,fontSize:12,outline:"none"}} />
                      {selected.truck&&<span style={{fontSize:11,color:C.success,marginTop:4,display:"block"}}>✓ {selected.truck}</span>}
                    </div>
                  </div>
                );
              })()}

              {/* Load info */}
              <div style={{background:C.bg,borderRadius:7,padding:"9px 12px",border:`1px solid ${C.border}`,marginBottom:12}}>
                <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
                  {[{label:"Pickup",value:selected.pickupDate+" "+selected.pickupTime},{label:"Delivery",value:selected.deliveryDate+" "+selected.deliveryTime},{label:"Weight",value:fmtLb(selected.weight)},{label:"Miles",value:fmtMi(selected.miles)},{label:"Shipper Rate",value:fmt$(selected.rate)},{label:"Carrier Rate",value:selected.brokerRate?fmt$(selected.brokerRate):"—"},{label:"Margin",value:selected.brokerRate?fmt$(selected.rate-selected.brokerRate):"—"}].map(r=>(
                    <div key={r.label}><div style={{fontSize:9,color:C.textDm,textTransform:"uppercase",letterSpacing:"0.08em"}}>{r.label}</div><div style={{fontSize:11,fontWeight:600,color:C.text}}>{r.value}</div></div>
                  ))}
                </div>
              </div>

              {/* Advance */}
              {selected.status!=="DELIVERED"&&(()=>{
                const next=nextStage(selected.status);
                const hasCarrier=selected.carrier&&selected.carrier!=="Unassigned";
                const canAdvance=hasCarrier&&!!selected.driver&&!!selected.truck;
                if(!next) return null;
                return(
                  <div style={{marginBottom:12}}>
                    <button onClick={()=>canAdvance&&advanceStatus(selected.id)} disabled={!canAdvance} style={{width:"100%",background:canAdvance?`${next.color}22`:C.surf2,border:`1px solid ${canAdvance?next.color+"44":C.border}`,color:canAdvance?next.color:C.textDm,borderRadius:8,padding:"10px",fontSize:13,fontWeight:700,cursor:canAdvance?"pointer":"not-allowed",fontFamily:C.sans,opacity:canAdvance?1:0.6}}>
                      {next.icon} Advance to {next.label} →
                    </button>
                    {!canAdvance&&<div style={{fontSize:11,color:C.warn,marginTop:5,textAlign:"center"}}>Assign {[!hasCarrier&&"carrier",!selected.driver&&"driver",!selected.truck&&"truck"].filter(Boolean).join(", ")} before advancing</div>}
                  </div>
                );
              })()}

              {/* Activity log */}
              <div style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{fontSize:11,fontWeight:600,color:C.text}}>Activity Log</div>
                  <button onClick={()=>setShowNote(n=>!n)} style={{background:C.surf2,border:`1px solid ${C.border}`,color:C.textMd,borderRadius:5,padding:"3px 10px",fontSize:11,cursor:"pointer"}}>+ Add Note</button>
                </div>
                {showNote&&(
                  <div style={{marginBottom:10}}>
                    <textarea value={noteText} onChange={e=>setNoteText(e.target.value)} rows={2} placeholder="Check-in note, exception, or update..." style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",color:C.text,fontSize:12,outline:"none",resize:"none",fontFamily:C.sans,boxSizing:"border-box",marginBottom:6}}/>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>{setShowNote(false);setNoteText("");}} style={{background:"transparent",border:`1px solid ${C.border}`,color:C.textMd,borderRadius:5,padding:"4px 10px",fontSize:11,cursor:"pointer"}}>Cancel</button>
                      <button onClick={()=>addNote(selected.id)} disabled={!noteText.trim()} style={{flex:1,background:C.accent,border:"none",color:"#fff",borderRadius:5,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer",opacity:noteText.trim()?1:0.5}}>Log Note</button>
                    </div>
                  </div>
                )}
                <div style={{display:"flex",flexDirection:"column",gap:0}}>
                  {[...selected.checkIns].reverse().map((c,i)=>(
                    <div key={i} style={{display:"flex",gap:10,paddingBottom:10}}>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"center",flexShrink:0}}>
                        <div style={{width:8,height:8,borderRadius:"50%",background:checkColor(c.type),marginTop:3,flexShrink:0,boxShadow:i===0?`0 0 6px ${checkColor(c.type)}`:"none"}}/>
                        {i<selected.checkIns.length-1&&<div style={{width:1,flex:1,background:C.border,margin:"3px 0"}}/>}
                      </div>
                      <div>
                        <div style={{fontSize:11,fontWeight:500,color:i===0?C.text:C.textMd}}>{c.note}</div>
                        <div style={{fontSize:10,color:C.textDm,marginTop:1}}>{c.time}</div>
                      </div>
                    </div>
                  ))}
                  {selected.checkIns.length===0&&<div style={{fontSize:12,color:C.textDm,padding:"8px 0"}}>No activity yet — advance status or add a note.</div>}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:"40px 20px",textAlign:"center"}}>
            <div style={{fontSize:40,opacity:0.12,marginBottom:12}}>🎯</div>
            <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:6}}>Click a load to dispatch</div>
            <div style={{fontSize:13,color:C.textMd}}>Select any load from the pipeline to view details, advance status, and log activity.</div>
          </div>
        )}
      </div>

      {/* Re-book modal */}
      {showRebook && selected && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>e.target===e.currentTarget&&setShowRebook(false)}>
          <div style={{background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:16,width:500,overflow:"hidden"}}>
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,background:"rgba(239,68,68,0.08)",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div><div style={{fontSize:16,fontWeight:700,color:C.danger}}>🔄 Re-book Load</div><div style={{fontSize:12,color:C.textMd,marginTop:3}}>{selected.number} · {selected.origin.split(",")[0]} → {selected.dest.split(",")[0]}</div></div>
              <button onClick={()=>setShowRebook(false)} style={{background:C.surf2,border:`1px solid ${C.border}`,color:C.textMd,borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:12}}>✕</button>
            </div>
            <div style={{padding:20}}>
              <div style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"10px 14px",marginBottom:16}}>
                <div style={{fontSize:12,color:C.danger,fontWeight:600,marginBottom:3}}>Current carrier will be removed</div>
                <div style={{fontSize:12,color:C.textMd}}>{selected.carrier||"No carrier"} · Driver: {selected.driver||"None"} · Truck: {selected.truck||"None"}</div>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:11,color:C.textMd,display:"block",marginBottom:5}}>Reason for re-booking</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                  {["Carrier no-show","Carrier breakdown","Rate negotiation","Shipper request","Better rate found","Schedule change"].map(r=>(
                    <button key={r} onClick={()=>setRebookReason(r)} style={{background:rebookReason===r?"rgba(239,68,68,0.2)":C.bg,border:`1px solid ${rebookReason===r?C.danger:C.border}`,color:rebookReason===r?C.danger:C.textMd,borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer"}}>{r}</button>
                  ))}
                </div>
                <input value={rebookReason} onChange={e=>setRebookReason(e.target.value)} placeholder="Or type a custom reason..." style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",color:C.text,fontSize:12,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:11,color:C.textMd,display:"block",marginBottom:5}}>New Carrier</label>
                {availableCarriers.length > 0 ? (
                  <select value={rebookCarrier} onChange={e=>setRebookCarrier(e.target.value)} style={{width:"100%",background:C.bg,border:`1px solid ${rebookCarrier?C.border:C.warn}`,borderRadius:7,padding:"9px 11px",color:C.text,fontSize:13,outline:"none"}}>
                    <option value="">— Select replacement carrier —</option>
                    {availableCarriers.filter(c=>c.name!==selected.carrier).map(c=><option key={c.id} value={c.name}>{c.name} · ⭐{c.rating} · {c.trucks} trucks · {c.city}</option>)}
                  </select>
                ) : (
                  <input value={rebookCarrier} onChange={e=>setRebookCarrier(e.target.value)} placeholder="Replacement carrier company name"
                    style={{width:"100%",background:C.bg,border:`1px solid ${rebookCarrier?C.border:C.warn}`,borderRadius:7,padding:"9px 11px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box"}} />
                )}
              </div>
              <div style={{marginBottom:20}}>
                <label style={{fontSize:11,color:C.textMd,display:"block",marginBottom:5}}>New Carrier Rate (current: {fmt$(selected.brokerRate||selected.rate)})</label>
                <input type="number" value={rebookRate} onChange={e=>setRebookRate(e.target.value)} placeholder={String(selected.brokerRate||selected.rate)} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 11px",color:C.text,fontSize:15,fontFamily:C.mono,outline:"none",fontWeight:600,boxSizing:"border-box"}}/>
                {rebookRate&&selected.rate&&<div style={{fontSize:11,marginTop:5,color:(selected.rate-parseFloat(rebookRate))>0?C.success:C.danger}}>New margin: {fmt$(selected.rate-parseFloat(rebookRate))}</div>}
              </div>
              <div style={{display:"flex",gap:10}}>
                <Btn variant="ghost" onClick={()=>setShowRebook(false)}>Cancel</Btn>
                <Btn variant="danger" style={{flex:1}} disabled={!rebookCarrier||!rebookReason} onClick={handleRebook}>Confirm Re-book → {rebookCarrier||"Select carrier"}</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel load modal */}
      {showCancel && selected && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={e=>e.target===e.currentTarget&&setShowCancel(false)}>
          <div style={{background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:16,width:440,overflow:"hidden"}}>
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,background:"rgba(239,68,68,0.08)"}}>
              <div style={{fontSize:16,fontWeight:700,color:C.danger}}>🗑 Cancel Load</div>
              <div style={{fontSize:12,color:C.textMd,marginTop:3}}>{selected.number} · {(selected.origin||"").split(",")[0]} → {(selected.dest||"").split(",")[0]}</div>
            </div>
            <div style={{padding:20}}>
              <div style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"10px 14px",marginBottom:16}}>
                <div style={{fontSize:12,color:C.danger,fontWeight:600,marginBottom:3}}>This load will be removed from dispatch</div>
                <div style={{fontSize:12,color:C.textMd}}>Status will be set to Cancelled. This cannot be undone from here.</div>
              </div>
              <div style={{marginBottom:16}}>
                <label style={{fontSize:11,color:C.textMd,display:"block",marginBottom:8}}>Reason for cancellation</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                  {["Shipper cancelled","No carrier available","Duplicate load","Test data","Other"].map(r=>(
                    <button key={r} onClick={()=>setCancelReason(r)} style={{background:cancelReason===r?"rgba(239,68,68,0.2)":C.bg,border:`1px solid ${cancelReason===r?C.danger:C.border}`,color:cancelReason===r?C.danger:C.textMd,borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer"}}>{r}</button>
                  ))}
                </div>
                <input value={cancelReason} onChange={e=>setCancelReason(e.target.value)} placeholder="Or type a custom reason..."
                  style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",color:C.text,fontSize:12,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{display:"flex",gap:10}}>
                <Btn variant="ghost" onClick={()=>{setShowCancel(false);setCancelReason("");}}>Keep Load</Btn>
                <button disabled={cancelling} onClick={cancelLoad} style={{
                  flex:1, background:C.danger, border:"none", color:"#fff",
                  borderRadius:8, padding:"9px", fontSize:13, fontWeight:700,
                  cursor:cancelling?"not-allowed":"pointer", fontFamily:C.sans, opacity:cancelling?0.6:1,
                }}>{cancelling?"Cancelling…":"Confirm Cancellation"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Broker Shippers Page ─────────────────────────────────────────────────────

const BROKER_SHIPPERS = [
  { id:"BS1", company:"Midwest Goods Co.",    contact:"Jim Harrington", email:"dispatch@midwestgoods.com",  phone:"(312) 555-0101", city:"Chicago, IL",       status:"ACTIVE",   loads:12, revenue:38400, since:"Jan 2026", notes:"Prefers dry van. Net 30 terms." },
  { id:"BS2", company:"Pacific Freight LLC",  contact:"Sarah Kim",      email:"loads@pacificfreight.com",   phone:"(415) 555-0202", city:"San Francisco, CA", status:"ACTIVE",   loads:7,  revenue:21800, since:"Mar 2026", notes:"Tech equipment — handle with care." },
  { id:"BS3", company:"Northwest Retail",     contact:"David Park",     email:"freight@northwestretail.com",phone:"(206) 555-0303", city:"Seattle, WA",       status:"ACTIVE",   loads:4,  revenue:9200,  since:"May 2026", notes:"Seasonal volume peaks in Q4." },
  { id:"BS4", company:"Mountain Steel Corp.", contact:"Linda Torres",   email:"ap@mountainsteel.com",       phone:"(303) 555-0404", city:"Denver, CO",        status:"PROSPECT", loads:0,  revenue:0,     since:"Jun 2026", notes:"Reached out at FreightWest conf." },
];

const BrokerShippersPage = ({ loads }) => {
  const { user: bspUser } = useAuth();
  const isDemoBrokerSP = bspUser?.email === "broker@midwestbroker.com";
  const [selected,    setSelected]    = useState(null);
  const [showAdd,     setShowAdd]     = useState(false);
  const [shippers,    setShippers]    = useState(isDemoBrokerSP ? BROKER_SHIPPERS : []);
  const [showContact, setShowContact] = useState(false);
  const [contactMsg,  setContactMsg]  = useState("");
  const [contactSent, setContactSent] = useState(false);
  const [addForm, setAddForm] = useState({ company:"", contact:"", email:"", phone:"", city:"", notes:"" });

  const addShipper = () => {
    if (!addForm.company||!addForm.email) return;
    setShippers(prev=>[...prev,{id:`BS${Date.now()}`,...addForm,status:"PROSPECT",loads:0,revenue:0,since:new Date().toLocaleDateString("en-US",{month:"short",year:"numeric"})}]);
    setShowAdd(false);
    setAddForm({company:"",contact:"",email:"",phone:"",city:"",notes:""});
  };

  const totalRevenue = shippers.reduce((a,s)=>a+s.revenue,0);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:700,color:C.text,margin:0}}>Shipper Accounts</h2>
          <p style={{fontSize:13,color:C.textMd,margin:"3px 0 0"}}>Shippers you broker freight for.</p>
        </div>
        <Btn variant="primary" size="sm" onClick={()=>setShowAdd(true)}>+ Add Shipper</Btn>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:16}}>
        <StatCard label="Active Accounts" value={shippers.filter(s=>s.status==="ACTIVE").length} color={C.success} icon="🏢"/>
        <StatCard label="Total Revenue"   value={fmt$(totalRevenue)} color={C.success} icon="💰" sub="brokered loads"/>
        <StatCard label="Prospects"       value={shippers.filter(s=>s.status==="PROSPECT").length} color={C.warn} icon="🎯"/>
        <StatCard label="Total Loads"     value={shippers.reduce((a,s)=>a+s.loads,0)} color={C.accentLt} icon="📦"/>
      </div>
      <div style={{display:"flex",gap:14}}>
        <div style={{flex:1,background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:C.surf2}}>{["Company","Contact","Location","Loads","Revenue","Since","Status","Actions"].map(h=><th key={h} style={{padding:"9px 14px",fontSize:10,color:C.textDm,textTransform:"uppercase",letterSpacing:"0.07em",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
            <tbody>
              {shippers.map((s,i)=>(
                <tr key={s.id} onClick={()=>setSelected(s)} style={{borderBottom:`1px solid ${C.border}`,cursor:"pointer",background:selected?.id===s.id?C.surf3:i%2===0?"transparent":C.surf2+"44"}}>
                  <td style={{padding:"11px 14px"}}><div style={{fontSize:13,fontWeight:600,color:C.text}}>{s.company}</div></td>
                  <td style={{padding:"11px 14px"}}><div style={{fontSize:12,color:C.text}}>{s.contact}</div><div style={{fontSize:11,color:C.textDm}}>{s.email}</div></td>
                  <td style={{padding:"11px 14px",fontSize:12,color:C.textMd}}>{s.city}</td>
                  <td style={{padding:"11px 14px",fontSize:12,fontFamily:C.mono,color:C.text}}>{s.loads}</td>
                  <td style={{padding:"11px 14px",fontSize:12,fontFamily:C.mono,color:C.success}}>{s.revenue>0?fmt$(s.revenue):"—"}</td>
                  <td style={{padding:"11px 14px",fontSize:12,color:C.textMd}}>{s.since}</td>
                  <td style={{padding:"11px 14px"}}><span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,textTransform:"uppercase",background:s.status==="ACTIVE"?C.succGl:`${C.warn}18`,border:`1px solid ${s.status==="ACTIVE"?C.success:C.warn}44`,color:s.status==="ACTIVE"?C.success:C.warn}}>{s.status}</span></td>
                  <td style={{padding:"11px 14px"}} onClick={e=>e.stopPropagation()}><button onClick={()=>{setSelected(s);setContactMsg("");setContactSent(false);setShowContact(true);}} style={{background:"transparent",border:`1px solid ${C.border}`,color:C.textMd,borderRadius:5,padding:"3px 10px",fontSize:11,cursor:"pointer"}}>✉ Contact</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {selected&&(
          <div style={{width:300,flexShrink:0,background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:10,overflow:"hidden",display:"flex",flexDirection:"column"}}>
            <div style={{padding:"14px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:14,fontWeight:700,color:C.text}}>{selected.company}</div>
              <button onClick={()=>setSelected(null)} style={{background:C.surf2,border:`1px solid ${C.border}`,color:C.textMd,borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:11}}>✕</button>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:16}}>
              {[{label:"Name",value:selected.contact},{label:"Email",value:selected.email,link:`mailto:${selected.email}`},{label:"Phone",value:selected.phone,link:`tel:${selected.phone}`},{label:"Location",value:selected.city},{label:"Since",value:selected.since}].map(r=>(
                <div key={r.label} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${C.border}`}}>
                  <span style={{fontSize:11,color:C.textMd}}>{r.label}</span>
                  {r.link?<a href={r.link} style={{fontSize:11,color:C.accentLt,textDecoration:"none"}}>{r.value}</a>:<span style={{fontSize:11,color:C.text}}>{r.value}</span>}
                </div>
              ))}
              {selected.notes&&<div style={{fontSize:12,color:C.textMd,marginTop:10,lineHeight:1.6}}>{selected.notes}</div>}
              <Btn variant="primary" style={{width:"100%",marginTop:14}} onClick={()=>{setContactMsg("");setContactSent(false);setShowContact(true);}}>✉ Contact</Btn>
            </div>
          </div>
        )}
      </div>
      {showAdd&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>e.target===e.currentTarget&&setShowAdd(false)}>
          <div style={{background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:14,width:460,overflow:"hidden"}}>
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between"}}>
              <span style={{fontSize:15,fontWeight:700,color:C.text}}>Add Shipper</span>
              <button onClick={()=>setShowAdd(false)} style={{background:C.surf2,border:`1px solid ${C.border}`,color:C.textMd,borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:12}}>✕</button>
            </div>
            <div style={{padding:20}}>
              {[{label:"Company Name",key:"company"},{label:"Contact Name",key:"contact"},{label:"Email",key:"email"},{label:"Phone",key:"phone"},{label:"City, State",key:"city"}].map(f=>(
                <div key={f.key} style={{marginBottom:10}}>
                  <label style={{fontSize:10,color:C.textMd,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:4}}>{f.label}</label>
                  <input value={addForm[f.key]} onChange={e=>setAddForm(p=>({...p,[f.key]:e.target.value}))} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
              <div style={{marginBottom:16}}>
                <label style={{fontSize:10,color:C.textMd,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:4}}>Notes</label>
                <textarea value={addForm.notes} onChange={e=>setAddForm(p=>({...p,notes:e.target.value}))} rows={2} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",color:C.text,fontSize:13,outline:"none",resize:"none",fontFamily:C.sans,boxSizing:"border-box"}}/>
              </div>
              <div style={{display:"flex",gap:10}}>
                <Btn variant="ghost" onClick={()=>setShowAdd(false)}>Cancel</Btn>
                <Btn variant="primary" style={{flex:1}} disabled={!addForm.company||!addForm.email} onClick={addShipper}>Add Shipper</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
      {showContact&&selected&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>e.target===e.currentTarget&&setShowContact(false)}>
          <div style={{background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:14,width:460,overflow:"hidden"}}>
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between"}}>
              <div><div style={{fontSize:15,fontWeight:700,color:C.text}}>Contact {selected.company}</div><div style={{fontSize:12,color:C.textMd}}>{selected.contact} · {selected.email}</div></div>
              <button onClick={()=>setShowContact(false)} style={{background:C.surf2,border:`1px solid ${C.border}`,color:C.textMd,borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:12}}>✕</button>
            </div>
            <div style={{padding:20}}>
              {contactSent?(
                <div style={{textAlign:"center",padding:"16px 0"}}><div style={{fontSize:40,marginBottom:12}}>✅</div><div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:8}}>Message sent!</div><Btn variant="primary" style={{marginTop:16}} onClick={()=>setShowContact(false)}>Done</Btn></div>
              ):(
                <>
                  <div style={{marginBottom:14}}>
                    <label style={{fontSize:10,color:C.textMd,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:6}}>Message</label>
                    <textarea value={contactMsg} onChange={e=>setContactMsg(e.target.value)} rows={5} placeholder={`Hi ${selected.contact}...`} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.text,fontSize:13,outline:"none",resize:"none",fontFamily:C.sans,boxSizing:"border-box"}}/>
                  </div>
                  <div style={{display:"flex",gap:10}}>
                    <Btn variant="ghost" onClick={()=>setShowContact(false)}>Cancel</Btn>
                    <Btn variant="primary" style={{flex:1}} disabled={!contactMsg.trim()} onClick={()=>setContactSent(true)}>Send Message</Btn>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Broker Invoicing Page ────────────────────────────────────────────────────

const BrokerInvoicingPage = () => {
  const [tab, setTab] = useState("ar");
  const { user: invoiceUser } = useAuth();
  const isDemoBroker = ["broker@midwestbroker.com"].includes(invoiceUser?.email);
  const [arInvoices, setArInvoices] = useState(isDemoBroker ? [
    { id:"AR1", number:"BRINV-0001", load:"FF-000002", route:"Atlanta \u2192 Miami",    shipper:"Pacific Freight LLC", shipperEmail:"ap@pacificfreight.com",  amount:2800, dueDate:"Jul 8",  status:"SENT",  issued:"Jun 9",  paidDate:null },
    { id:"AR2", number:"BRINV-0002", load:"FF-000004", route:"Seattle \u2192 Portland", shipper:"Midwest Goods Co.",  shipperEmail:"billing@midwestgoods.com", amount:680, dueDate:"Jul 5",  status:"PAID",  issued:"Jun 6",  paidDate:"Jun 14" },
  ] : []);
  const [apInvoices, setApInvoices] = useState(isDemoBroker ? [
    { id:"AP1", number:"INV-C-000001", load:"FF-000002", route:"Atlanta \u2192 Miami", carrier:"Sunbelt Trucking", amount:2100, dueDate:"Jul 8", status:"UNPAID", issued:"Jun 9", paidDate:null },
  ] : []);
  const [showGenAR, setShowGenAR] = useState(false);
  const [arForm, setArForm] = useState({load:"",shipper:"",shipperEmail:"",amount:""});

  const sendAR = id => setArInvoices(prev=>prev.map(i=>i.id===id?{...i,status:"SENT"}:i));
  const payAP  = id => setApInvoices(prev=>prev.map(i=>i.id===id?{...i,status:"PAID",paidDate:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"})}:i));

  const generateAR = () => {
    if (!arForm.load||!arForm.amount) return;
    const today=new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"});
    const due=new Date(Date.now()+30*86400000).toLocaleDateString("en-US",{month:"short",day:"numeric"});
    setArInvoices(prev=>[{id:`AR${Date.now()}`,number:`BRINV-${String(prev.length+1).padStart(4,"0")}`,load:arForm.load,shipper:arForm.shipper,shipperEmail:arForm.shipperEmail,amount:parseFloat(arForm.amount),dueDate:due,status:"DRAFT",issued:today,paidDate:null},...prev]);
    setShowGenAR(false);
    setArForm({load:"",shipper:"",shipperEmail:"",amount:""});
  };

  const arPaid=arInvoices.filter(i=>i.status==="PAID").reduce((a,i)=>a+i.amount,0);
  const arOut =arInvoices.filter(i=>["SENT","OVERDUE"].includes(i.status)).reduce((a,i)=>a+i.amount,0);
  const apOwed=apInvoices.filter(i=>i.status==="UNPAID").reduce((a,i)=>a+i.amount,0);

  const AR_META={DRAFT:{label:"Draft",color:C.textMd},SENT:{label:"Sent",color:C.warn},PAID:{label:"Paid",color:C.success},OVERDUE:{label:"Overdue",color:C.danger}};
  const AP_META={UNPAID:{label:"Unpaid",color:C.warn},PAID:{label:"Paid",color:C.success}};
  const Pill=({status,meta})=>{const m=meta[status]||{label:status,color:C.textMd};return<span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,textTransform:"uppercase",background:`${m.color}18`,border:`1px solid ${m.color}44`,color:m.color}}>{m.label}</span>;};

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <h2 style={{fontSize:18,fontWeight:700,color:C.text,margin:0}}>Invoicing</h2>
        {tab==="ar"&&<Btn variant="primary" size="sm" onClick={()=>setShowGenAR(true)}>+ Invoice Shipper</Btn>}
      </div>
      <div style={{display:"flex",gap:10,marginBottom:16}}>
        <StatCard label="Receivable (AR)" value={fmt$(arOut)}  color={C.success} icon="📥" sub="owed by shippers"/>
        <StatCard label="Collected"        value={fmt$(arPaid)} color={C.text}    icon="✅" sub="paid"/>
        <StatCard label="Payable (AP)"     value={fmt$(apOwed)} color={C.warn}    icon="📤" sub="owed to carriers"/>
        <StatCard label="Net Cash"         value={fmt$(arPaid-apOwed)} color={arPaid-apOwed>=0?C.success:C.danger} icon="💰"/>
      </div>
      <div style={{display:"flex",gap:0,borderBottom:`1px solid ${C.border}`,marginBottom:16}}>
        {[["ar","📥 Bill Shippers (AR)"],["ap","📤 Pay Carriers (AP)"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{background:"transparent",border:"none",borderBottom:`2px solid ${tab===id?C.accentLt:"transparent"}`,color:tab===id?C.accentLt:C.textMd,padding:"10px 20px",fontSize:13,fontWeight:tab===id?600:400,cursor:"pointer"}}>{label}</button>
        ))}
      </div>
      {tab==="ar"&&(
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:C.surf2}}>{["Invoice #","Load","Shipper","Amount","Issued","Due","Status","Actions"].map(h=><th key={h} style={{padding:"9px 14px",fontSize:10,color:C.textDm,textTransform:"uppercase",letterSpacing:"0.07em",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
            <tbody>
              {arInvoices.map((inv,i)=>(
                <tr key={inv.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"transparent":C.surf2+"44"}}>
                  <td style={{padding:"10px 14px",fontSize:11,fontFamily:C.mono,color:C.accentLt}}>{inv.number}</td>
                  <td style={{padding:"10px 14px",fontSize:11,color:C.textDm}}>{inv.load}</td>
                  <td style={{padding:"10px 14px"}}><div style={{fontSize:12,color:C.text}}>{inv.shipper}</div><div style={{fontSize:10,color:C.textDm}}>{inv.shipperEmail}</div></td>
                  <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,fontFamily:C.mono,color:C.success}}>{fmt$(inv.amount)}</td>
                  <td style={{padding:"10px 14px",fontSize:11,color:C.textMd}}>{inv.issued||"—"}</td>
                  <td style={{padding:"10px 14px",fontSize:11,color:C.textMd}}>{inv.dueDate}</td>
                  <td style={{padding:"10px 14px"}}><Pill status={inv.status} meta={AR_META}/></td>
                  <td style={{padding:"10px 14px"}}>{inv.status==="DRAFT"&&<button onClick={()=>sendAR(inv.id)} style={{background:C.accent,border:"none",color:"#fff",borderRadius:5,padding:"3px 10px",fontSize:10,fontWeight:600,cursor:"pointer"}}>Send</button>}{inv.status==="PAID"&&<span style={{fontSize:11,color:C.success}}>✓ {inv.paidDate}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tab==="ap"&&(
        <div>
          <div style={{background:C.accentGl,border:`1px solid ${C.accent}33`,borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12,color:C.textMd}}>Invoices from carriers for loads completed on your behalf. Pay after collecting from shipper.</div>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:C.surf2}}>{["Invoice #","Load","Carrier","Amount","Due","Status","Actions"].map(h=><th key={h} style={{padding:"9px 14px",fontSize:10,color:C.textDm,textTransform:"uppercase",letterSpacing:"0.07em",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
              <tbody>
                {apInvoices.map((inv,i)=>(
                  <tr key={inv.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"transparent":C.surf2+"44"}}>
                    <td style={{padding:"10px 14px",fontSize:11,fontFamily:C.mono,color:C.accentLt}}>{inv.number}</td>
                    <td style={{padding:"10px 14px",fontSize:11,color:C.textDm}}>{inv.load}</td>
                    <td style={{padding:"10px 14px",fontSize:12,color:C.text}}>{inv.carrier}</td>
                    <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,fontFamily:C.mono,color:C.text}}>{fmt$(inv.amount)}</td>
                    <td style={{padding:"10px 14px",fontSize:11,color:C.textMd}}>{inv.dueDate}</td>
                    <td style={{padding:"10px 14px"}}><Pill status={inv.status} meta={AP_META}/></td>
                    <td style={{padding:"10px 14px"}}>{inv.status==="UNPAID"&&<button onClick={()=>payAP(inv.id)} style={{background:C.success,border:"none",color:"#fff",borderRadius:5,padding:"3px 10px",fontSize:10,fontWeight:600,cursor:"pointer"}}>Pay Now</button>}{inv.status==="PAID"&&<span style={{fontSize:11,color:C.success}}>✓ {inv.paidDate}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showGenAR&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>e.target===e.currentTarget&&setShowGenAR(false)}>
          <div style={{background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:14,width:440,overflow:"hidden"}}>
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between"}}>
              <span style={{fontSize:15,fontWeight:700,color:C.text}}>Invoice Shipper</span>
              <button onClick={()=>setShowGenAR(false)} style={{background:C.surf2,border:`1px solid ${C.border}`,color:C.textMd,borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:12}}>✕</button>
            </div>
            <div style={{padding:20}}>
              {[{label:"Load Number",key:"load",placeholder:"FF-000001"},{label:"Shipper Name",key:"shipper",placeholder:"Midwest Goods Co."},{label:"Shipper Email",key:"shipperEmail",placeholder:"billing@shipper.com"},{label:"Amount ($)",key:"amount",placeholder:"3200",type:"number"}].map(f=>(
                <div key={f.key} style={{marginBottom:12}}>
                  <label style={{fontSize:10,color:C.textMd,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:4}}>{f.label}</label>
                  <input type={f.type||"text"} value={arForm[f.key]||""} onChange={e=>setArForm(p=>({...p,[f.key]:e.target.value}))} placeholder={f.placeholder} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
              <div style={{display:"flex",gap:10,marginTop:16}}>
                <Btn variant="ghost" onClick={()=>setShowGenAR(false)}>Cancel</Btn>
                <Btn variant="primary" style={{flex:1}} disabled={!arForm.load||!arForm.amount} onClick={generateAR}>Create Invoice Draft</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Carrier Invoicing Page ───────────────────────────────────────────────────

const CARRIER_INVOICES_INIT = [
  { id:"CI-001", number:"INV-C-000001", loadId:"L006", loadNumber:"FF-000006", shipper:"Midwest Goods Co.", shipperEmail:"billing@midwestgoods.com", origin:"Nashville, TN", dest:"Charlotte, NC", pickupDate:"Jun 7", deliveryDate:"Jun 8", miles:409, weight:18000, lineItems:[{desc:"Linehaul freight charge",qty:1,unit:"flat",rate:1320,total:1320},{desc:"Fuel surcharge (8%)",qty:1,unit:"flat",rate:105.6,total:105.6}], subtotal:1320, fuelSurcharge:105.6, accessorials:0, total:1425.6, platformFee:49.90, net:1375.70, status:"PAID",  issueDate:"Jun 9",  dueDate:"Jul 9",  paidDate:"Jun 15", notes:"" },
  { id:"CI-002", number:"INV-C-000002", loadId:"L003", loadNumber:"FF-000003", shipper:"Midwest Goods Co.", shipperEmail:"billing@midwestgoods.com", origin:"Atlanta, GA",   dest:"Miami, FL",     pickupDate:"Jun 8", deliveryDate:"Jun 9", miles:662, weight:25000, lineItems:[{desc:"Linehaul freight charge",qty:1,unit:"flat",rate:2100,total:2100},{desc:"Fuel surcharge (8%)",qty:1,unit:"flat",rate:168,total:168},{desc:"Reefer / temp control",qty:1,unit:"flat",rate:150,total:150}],      subtotal:2100, fuelSurcharge:168,  accessorials:150, total:2418, platformFee:84.63, net:2333.37, status:"SENT",  issueDate:"Jun 10", dueDate:"Jul 10", paidDate:null,     notes:"Reefer fuel additional." },
];

const PLATFORM_FEE_PCT = 0.035;

const CarrierInvoicingPage = ({ loads, carrierId = "C1" }) => {
  const { user: carrierInvUser } = useAuth();
  const isDemoCarrier = ["ops@arrowlogistics.com"].includes(carrierInvUser?.email);
  const [invoices, setInvoices] = useState(isDemoCarrier ? CARRIER_INVOICES_INIT : []);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("invoices");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const blankInv = { loadId:"", shipper:"", shipperEmail:"", lineItems:[{desc:"Linehaul freight charge",qty:1,unit:"flat",rate:"",total:""}], fuelSurchargePct:"8", accessorials:"0", notes:"" };
  const [form, setForm] = useState(blankInv);

  const totalEarned = invoices.filter(i=>i.status==="PAID").reduce((a,i)=>a+i.net,0);
  const outstanding = invoices.filter(i=>["SENT","VIEWED","OVERDUE"].includes(i.status)).reduce((a,i)=>a+i.total,0);
  const drafts      = invoices.filter(i=>i.status==="DRAFT").length;

  const changeStatus = (invId, status) => {
    setInvoices(prev=>prev.map(i=>i.id===invId?{...i,status,...(status==="PAID"?{paidDate:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"})}:{})}:i));
    setSelected(prev=>prev?.id===invId?{...prev,status}:prev);
  };

  const deliveredLoads = loads.filter(l=>["DELIVERED","POD_RECEIVED"].includes(l.status)&&(l.carrierId===(carrierId||"C1")||!l.carrierId));

  const generateInvoice = () => {
    const load=loads.find(l=>l.id===form.loadId);
    if (!load) return;
    const linehaul=parseFloat(form.lineItems[0]?.rate||load.rate);
    const fuelAmt=linehaul*(parseFloat(form.fuelSurchargePct||0)/100);
    const accessAmt=parseFloat(form.accessorials||0);
    const total=linehaul+fuelAmt+accessAmt;
    const fee=total*PLATFORM_FEE_PCT;
    const num=`INV-C-${String(invoices.length+1).padStart(6,"0")}`;
    const today=new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"});
    const due=new Date(Date.now()+30*86400000).toLocaleDateString("en-US",{month:"short",day:"numeric"});
    setInvoices(prev=>[{id:`CI-${Date.now()}`,number:num,loadId:load.id,loadNumber:load.number,shipper:load.shipper,shipperEmail:form.shipperEmail||"billing@shipper.com",origin:load.origin,dest:load.dest,pickupDate:load.pickupDate,deliveryDate:load.deliveryDate,miles:load.miles,weight:load.weight,lineItems:[{desc:"Linehaul freight charge",qty:1,unit:"flat",rate:linehaul,total:linehaul},...(fuelAmt>0?[{desc:`Fuel surcharge (${form.fuelSurchargePct}%)`,qty:1,unit:"flat",rate:fuelAmt,total:fuelAmt}]:[]),...(accessAmt>0?[{desc:"Accessorial charges",qty:1,unit:"flat",rate:accessAmt,total:accessAmt}]:[])],subtotal:linehaul,fuelSurcharge:fuelAmt,accessorials:accessAmt,total,platformFee:parseFloat(fee.toFixed(2)),net:parseFloat((total-fee).toFixed(2)),status:"DRAFT",issueDate:today,dueDate:due,paidDate:null,notes:form.notes},...prev]);
    setTab("invoices"); setForm(blankInv);
  };

  const INV_META={DRAFT:{label:"Draft",color:"#94a3b8"},SENT:{label:"Sent",color:"#f59e0b"},PAID:{label:"Paid",color:"#10b981"},OVERDUE:{label:"Overdue",color:"#ef4444"},DISPUTED:{label:"Disputed",color:"#ef4444"},VOID:{label:"Void",color:"#475569"}};
  const StatusBadge=({status})=>{const m=INV_META[status]||INV_META.DRAFT;return<span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,textTransform:"uppercase",background:`${m.color}18`,border:`1px solid ${m.color}44`,color:m.color}}>{m.label}</span>;};
  const filtered=filterStatus==="ALL"?invoices:invoices.filter(i=>i.status===filterStatus);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <h2 style={{fontSize:18,fontWeight:700,color:C.text,margin:0}}>Invoicing & Earnings</h2>
        <Btn variant="primary" onClick={()=>setTab("create")}>+ Create Invoice</Btn>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <StatCard label="Total Earned"  value={fmt$(totalEarned)} sub="paid invoices"    color={C.success} icon="💰"/>
        <StatCard label="Outstanding"   value={fmt$(outstanding)} sub="awaiting payment" color={C.warn}   icon="⏳"/>
        <StatCard label="Drafts"        value={drafts}            sub="not yet sent"      color={C.textMd} icon="📝"/>
      </div>
      <div style={{display:"flex",gap:0,borderBottom:`1px solid ${C.border}`,marginBottom:16}}>
        {[["invoices","📋 Invoices"],["create","+ New Invoice"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{background:"transparent",border:"none",borderBottom:`2px solid ${tab===id?C.accentLt:"transparent"}`,color:tab===id?C.accentLt:C.textMd,padding:"10px 18px",fontSize:13,fontWeight:tab===id?600:400,cursor:"pointer"}}>{label}</button>
        ))}
      </div>
      {tab==="invoices"&&(
        <div style={{display:"flex",gap:14}}>
          <div style={{flex:1}}>
            <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
              {["ALL","DRAFT","SENT","PAID","OVERDUE","DISPUTED"].map(s=>(
                <button key={s} onClick={()=>setFilterStatus(s)} style={{background:filterStatus===s?C.accent:"transparent",border:`1px solid ${filterStatus===s?C.accent:C.border}`,color:filterStatus===s?"#fff":C.textMd,borderRadius:6,padding:"4px 11px",fontSize:11,fontWeight:500,cursor:"pointer"}}>{s==="ALL"?"All":INV_META[s]?.label||s}</button>
              ))}
            </div>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{background:C.surf2}}>{["Invoice","Load","Shipper","Amount","Net","Issued","Due","Status","Actions"].map(h=><th key={h} style={{padding:"8px 12px",fontSize:10,color:C.textDm,textTransform:"uppercase",letterSpacing:"0.07em",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
                <tbody>
                  {filtered.map((inv,i)=>(
                    <tr key={inv.id} onClick={()=>setSelected(inv)} style={{borderBottom:`1px solid ${C.border}`,cursor:"pointer",background:selected?.id===inv.id?C.surf3:i%2===0?"transparent":C.surf2+"44"}}>
                      <td style={{padding:"10px 12px",fontSize:11,fontFamily:C.mono,color:C.accentLt}}>{inv.number}</td>
                      <td style={{padding:"10px 12px"}}><div style={{fontSize:11,fontFamily:C.mono,color:C.textDm}}>{inv.loadNumber}</div><div style={{fontSize:11,color:C.textMd}}>{inv.origin?.split(",")[0]} → {inv.dest?.split(",")[0]}</div></td>
                      <td style={{padding:"10px 12px",fontSize:12,color:C.text}}>{inv.shipper}</td>
                      <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:C.text,fontFamily:C.mono}}>{fmt$(inv.total)}</td>
                      <td style={{padding:"10px 12px",fontSize:12,fontWeight:600,color:C.success,fontFamily:C.mono}}>{fmt$(inv.net)}</td>
                      <td style={{padding:"10px 12px",fontSize:11,color:C.textMd,whiteSpace:"nowrap"}}>{inv.issueDate||"—"}</td>
                      <td style={{padding:"10px 12px",fontSize:11,color:C.textMd,whiteSpace:"nowrap"}}>{inv.dueDate||"—"}</td>
                      <td style={{padding:"10px 12px"}}><StatusBadge status={inv.status}/></td>
                      <td style={{padding:"10px 12px"}} onClick={e=>e.stopPropagation()}>
                        <div style={{display:"flex",gap:4}}>
                          {inv.status==="DRAFT"&&<button onClick={()=>changeStatus(inv.id,"SENT")} style={{background:C.accent,border:"none",color:"#fff",borderRadius:5,padding:"3px 9px",fontSize:10,fontWeight:600,cursor:"pointer"}}>Send</button>}
                          {inv.status==="SENT"&&<button onClick={()=>changeStatus(inv.id,"PAID")} style={{background:C.success,border:"none",color:"#fff",borderRadius:5,padding:"3px 9px",fontSize:10,fontWeight:600,cursor:"pointer"}}>Mark Paid</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length===0&&<tr><td colSpan={9} style={{padding:"32px",textAlign:"center",color:C.textDm,fontSize:13}}>No invoices</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          {selected&&(
            <div style={{width:360,flexShrink:0,background:C.surface,border:`1px solid ${C.borderHi}`,borderRadius:12,overflow:"hidden",display:"flex",flexDirection:"column"}}>
              <div style={{padding:"14px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div><div style={{fontSize:10,color:C.textDm,fontFamily:C.mono}}>{selected.number}</div><div style={{fontSize:14,fontWeight:700,color:C.text}}>{selected.shipper}</div></div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}><StatusBadge status={selected.status}/><button onClick={()=>setSelected(null)} style={{background:C.surf2,border:`1px solid ${C.border}`,color:C.textMd,borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:11}}>✕</button></div>
              </div>
              <div style={{flex:1,overflowY:"auto",padding:16}}>
                <div style={{background:C.bg,borderRadius:8,padding:"11px 13px",border:`1px solid ${C.border}`,marginBottom:12}}>
                  <div style={{fontSize:12,fontWeight:600,color:C.text}}>{selected.origin} → {selected.dest}</div>
                  <div style={{fontSize:11,color:C.textMd,marginTop:2}}>{selected.loadNumber} · Pickup {selected.pickupDate} · Delivery {selected.deliveryDate}</div>
                </div>
                <div style={{background:C.bg,borderRadius:8,border:`1px solid ${C.border}`,overflow:"hidden",marginBottom:12}}>
                  {selected.lineItems.map((li,i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 12px",borderBottom:`1px solid ${C.border}`}}><span style={{fontSize:12,color:C.text}}>{li.desc}</span><span style={{fontSize:12,fontWeight:600,color:C.text,fontFamily:C.mono}}>{fmt$(li.total)}</span></div>
                  ))}
                  <div style={{padding:"8px 12px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between"}}><span style={{fontSize:11,color:C.textDm}}>Platform fee (3.5%)</span><span style={{fontSize:11,fontFamily:C.mono,color:C.danger}}>-{fmt$(selected.platformFee)}</span></div>
                  <div style={{padding:"10px 12px",display:"flex",justifyContent:"space-between",background:C.succGl}}><span style={{fontSize:13,fontWeight:700,color:C.text}}>Your Net</span><span style={{fontSize:16,fontWeight:800,fontFamily:C.mono,color:C.success}}>{fmt$(selected.net)}</span></div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  {selected.status==="DRAFT"&&<Btn variant="primary" style={{flex:1}} onClick={()=>changeStatus(selected.id,"SENT")}>📤 Send to Shipper</Btn>}
                  {selected.status==="SENT"&&<Btn variant="success" style={{flex:1}} onClick={()=>changeStatus(selected.id,"PAID")}>✓ Mark as Paid</Btn>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {tab==="create"&&(
        <div style={{maxWidth:640}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:24}}>
            <div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:16}}>New Invoice</div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:10,color:C.textMd,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:6}}>Select Delivered Load</label>
              <select value={form.loadId} onChange={e=>{const load=loads.find(l=>l.id===e.target.value);setForm(f=>({...f,loadId:e.target.value,lineItems:[{desc:"Linehaul freight charge",qty:1,unit:"flat",rate:load?.rate||"",total:load?.rate||""}]}));}} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.text,fontSize:13,outline:"none"}}>
                <option value="">— Select a delivered load —</option>
                {deliveredLoads.map(l=><option key={l.id} value={l.id}>{l.number} · {l.origin?.split(",")[0]} → {l.dest?.split(",")[0]} · {fmt$(l.rate)} · Delivered {l.deliveryDate}</option>)}
              </select>
            </div>
            {form.loadId&&(()=>{
              const load=loads.find(l=>l.id===form.loadId);
              if(!load) return null;
              const linehaul=parseFloat(form.lineItems[0]?.rate||load.rate)||0;
              const fuelAmt=linehaul*(parseFloat(form.fuelSurchargePct||0)/100);
              const accessAmt=parseFloat(form.accessorials||0);
              const total=linehaul+fuelAmt+accessAmt;
              const fee=total*PLATFORM_FEE_PCT;
              const net=total-fee;
              return(
                <>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                    <div><label style={{fontSize:10,color:C.textMd,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:4}}>Linehaul Rate ($)</label><input type="number" value={form.lineItems[0]?.rate} onChange={e=>setForm(f=>({...f,lineItems:[{...f.lineItems[0],rate:e.target.value,total:e.target.value},...f.lineItems.slice(1)]}))} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",color:C.text,fontSize:14,fontFamily:C.mono,outline:"none",boxSizing:"border-box"}}/></div>
                    <div><label style={{fontSize:10,color:C.textMd,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:4}}>Fuel Surcharge (%)</label><input type="number" value={form.fuelSurchargePct} onChange={e=>setForm(f=>({...f,fuelSurchargePct:e.target.value}))} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",color:C.text,fontSize:14,fontFamily:C.mono,outline:"none",boxSizing:"border-box"}}/></div>
                    <div><label style={{fontSize:10,color:C.textMd,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:4}}>Accessorials ($)</label><input type="number" value={form.accessorials} onChange={e=>setForm(f=>({...f,accessorials:e.target.value}))} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",color:C.text,fontSize:14,fontFamily:C.mono,outline:"none",boxSizing:"border-box"}}/></div>
                    <div><label style={{fontSize:10,color:C.textMd,textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:4}}>Shipper Email</label><input type="email" value={form.shipperEmail} onChange={e=>setForm(f=>({...f,shipperEmail:e.target.value}))} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box"}}/></div>
                  </div>
                  <div style={{background:C.bg,borderRadius:8,border:`1px solid ${C.border}`,overflow:"hidden",marginBottom:16}}>
                    {[{label:"Linehaul",val:linehaul},{label:`Fuel (${form.fuelSurchargePct}%)`,val:fuelAmt},{label:"Accessorials",val:accessAmt},{label:"Total",val:total,bold:true},{label:"Platform fee (3.5%)",val:-fee,color:C.danger}].filter(r=>r.val!==0).map((r,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 12px",borderBottom:`1px solid ${C.border}`}}><span style={{fontSize:12,color:C.textMd,fontWeight:r.bold?600:400}}>{r.label}</span><span style={{fontSize:12,fontFamily:C.mono,color:r.color||C.text,fontWeight:r.bold?700:500}}>{r.val<0?"-":""}{fmt$(Math.abs(r.val))}</span></div>
                    ))}
                    <div style={{display:"flex",justifyContent:"space-between",padding:"10px 12px",background:C.succGl}}><span style={{fontSize:13,fontWeight:700,color:C.text}}>Your Net Pay</span><span style={{fontSize:18,fontWeight:800,fontFamily:C.mono,color:C.success}}>{fmt$(net)}</span></div>
                  </div>
                  <div style={{display:"flex",gap:10}}>
                    <Btn variant="primary" style={{flex:1}} onClick={generateInvoice} disabled={!linehaul}>Generate Invoice</Btn>
                    <Btn variant="ghost" onClick={()=>setTab("invoices")}>Cancel</Btn>
                  </div>
                </>
              );
            })()}
            {!form.loadId&&<div style={{textAlign:"center",padding:"24px 0",color:C.textDm,fontSize:13}}>Select a delivered load above to start invoicing.</div>}
          </div>
        </div>
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// DRIVER INTERFACE
// Mobile-optimized. Large tap targets, minimal chrome.
// ─────────────────────────────────────────────────────────────────────────────

const DRIVER_LOAD = {
  id:"L003", number:"FF-000003",
  origin:"Atlanta, GA",        originAddress:"1420 Peachtree St NE, Atlanta, GA 30309",
  dest:"Miami, FL",            destAddress:"100 Biscayne Blvd, Miami, FL 33132",
  shipper:"Pacific Freight LLC", shipperContact:"Sarah Kim",     shipperPhone:"(415) 555-0202",
  consignee:"Miami Distribution Center", consigneeContact:"Rosa Hernandez", consigneePhone:"(305) 555-0202",
  pickupDate:"Jun 14", pickupWindow:"07:00–09:00",
  deliveryDate:"Jun 15", deliveryWindow:"14:00–16:00",
  commodity:"Retail Goods", weight:25000, pallets:16,
  equipmentType:"DRY_VAN",
  rate:2100, miles:662,
  dispatcher:"Sarah K.", dispatcherPhone:"(773) 555-0300",
  truck:"ARR-002", trailer:"TRL-005",
  specialInstructions:"Handle with care — fragile retail merchandise. Call consignee 1hr before arrival for dock assignment.",
  bolNumber:"BOL-2026-00341",
  status:"IN_TRANSIT",
  progress:55,
};

const DISPATCH_STAGES_DRIVER = [
  { id:"ASSIGNED",    label:"Assigned",     icon:"📋", color:"#3b82f6", action:null },
  { id:"EN_ROUTE_PU", label:"En Route to Pickup", icon:"🚛", color:"#8b5cf6", action:"Start driving to pickup" },
  { id:"AT_PICKUP",   label:"At Pickup",    icon:"🏭", color:"#f59e0b", action:"Arrived at pickup" },
  { id:"LOADED",      label:"Loaded",       icon:"📦", color:"#06b6d4", action:"Load secured, ready to go" },
  { id:"IN_TRANSIT",  label:"In Transit",   icon:"🛣",  color:"#3b82f6", action:"Departed, en route to delivery" },
  { id:"AT_DELIVERY", label:"At Delivery",  icon:"🏪", color:"#f97316", action:"Arrived at delivery" },
  { id:"DELIVERED",   label:"Delivered",    icon:"✅", color:"#10b981", action:"Delivery complete, POD signed" },
];

// ── Driver Dashboard ──────────────────────────────────────────────────────────
const DriverDashboard = ({ load, status, setStatus, setPage, messages }) => {
  const stage     = DISPATCH_STAGES_DRIVER.find(s => s.id === status) || DISPATCH_STAGES_DRIVER[0];
  const stageIdx  = DISPATCH_STAGES_DRIVER.findIndex(s => s.id === status);
  const nextStage = DISPATCH_STAGES_DRIVER[stageIdx + 1];
  const pct       = Math.round((stageIdx / (DISPATCH_STAGES_DRIVER.length - 1)) * 100);
  const unread    = messages.filter(m => !m.read && m.from === "Dispatcher").length;

  return (
    <div style={{ maxWidth:480, margin:"0 auto" }}>
      {/* Status hero */}
      <div style={{ background:`linear-gradient(135deg,${stage.color}22,${stage.color}11)`, border:`1px solid ${stage.color}44`, borderRadius:16, padding:20, marginBottom:16, textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:8 }}>{stage.icon}</div>
        <div style={{ fontSize:22, fontWeight:800, color:C.text, marginBottom:4 }}>{stage.label}</div>
        <div style={{ fontSize:13, color:C.textMd }}>{load.number} · {load.origin.split(",")[0]} → {load.dest.split(",")[0]}</div>
        <div style={{ marginTop:14, height:8, background:C.border, borderRadius:4, overflow:"hidden" }}>
          <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,${stage.color},${C.success})`, borderRadius:4, transition:"width 0.5s" }} />
        </div>
        <div style={{ fontSize:11, color:C.textDm, marginTop:5 }}>{pct}% complete · {load.miles} miles</div>
      </div>

      {/* Next action CTA */}
      {nextStage && (
        <button onClick={() => setPage("checkin")} style={{
          width:"100%", background:`linear-gradient(135deg,${nextStage.color},${nextStage.color}cc)`,
          border:"none", color:"#fff", borderRadius:14, padding:"16px 20px",
          fontSize:16, fontWeight:700, cursor:"pointer", marginBottom:16,
          display:"flex", alignItems:"center", justifyContent:"center", gap:10,
          fontFamily:C.sans, boxShadow:`0 4px 20px ${nextStage.color}44`,
        }}>
          <span style={{ fontSize:22 }}>{nextStage.icon}</span>
          {nextStage.action} →
        </button>
      )}
      {status === "DELIVERED" && (
        <div style={{ background:C.succGl, border:`1px solid ${C.success}44`, borderRadius:14, padding:16, textAlign:"center", marginBottom:16 }}>
          <div style={{ fontSize:32, marginBottom:8 }}>🎉</div>
          <div style={{ fontSize:16, fontWeight:700, color:C.success }}>Delivery Complete!</div>
          <div style={{ fontSize:13, color:C.textMd, marginTop:4 }}>Great job. Upload your BOL in Documents.</div>
          <Btn variant="success" style={{marginTop:12}} onClick={()=>setPage("documents")}>Upload BOL →</Btn>
        </div>
      )}

      {/* Quick info cards */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
        {/* Pickup */}
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px" }}>
          <div style={{ fontSize:18, marginBottom:4 }}>🏭</div>
          <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em" }}>Pickup</div>
          <div style={{ fontSize:13, fontWeight:600, color:C.text, marginTop:2 }}>{load.pickupWindow}</div>
          <div style={{ fontSize:11, color:C.textMd, marginBottom:8 }}>{load.pickupDate}</div>
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(load.originAddress||load.origin)}`}
            target="_blank" rel="noreferrer"
            style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, fontWeight:600, color:"#fff", background:"#4285F4", borderRadius:6, padding:"5px 10px", textDecoration:"none", width:"fit-content" }}>
            <span style={{ fontSize:14 }}>📍</span> Maps
          </a>
        </div>

        {/* Delivery */}
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px" }}>
          <div style={{ fontSize:18, marginBottom:4 }}>🏪</div>
          <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em" }}>Delivery</div>
          <div style={{ fontSize:13, fontWeight:600, color:C.text, marginTop:2 }}>{load.deliveryWindow}</div>
          <div style={{ fontSize:11, color:C.textMd, marginBottom:8 }}>{load.deliveryDate}</div>
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(load.destAddress||load.dest)}`}
            target="_blank" rel="noreferrer"
            style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, fontWeight:600, color:"#fff", background:"#4285F4", borderRadius:6, padding:"5px 10px", textDecoration:"none", width:"fit-content" }}>
            <span style={{ fontSize:14 }}>📍</span> Maps
          </a>
        </div>

        {/* Commodity */}
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px" }}>
          <div style={{ fontSize:18, marginBottom:4 }}>📦</div>
          <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em" }}>Commodity</div>
          <div style={{ fontSize:13, fontWeight:600, color:C.text, marginTop:2 }}>{load.commodity}</div>
          <div style={{ fontSize:11, color:C.textMd }}>{load.pallets} pallets · {(load.weight/1000).toFixed(0)}k lbs</div>
        </div>

        {/* Temperature (reefer only) or Miles */}
        {load.tempMin ? (
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px" }}>
            <div style={{ fontSize:18, marginBottom:4 }}>🌡</div>
            <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em" }}>Temperature</div>
            <div style={{ fontSize:13, fontWeight:600, color:C.text, marginTop:2 }}>{load.tempMin}–{load.tempMax}°F</div>
            <div style={{ fontSize:11, color:C.warn }}>⚠ Must maintain</div>
          </div>
        ) : (
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px" }}>
            <div style={{ fontSize:18, marginBottom:4 }}>🛣</div>
            <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em" }}>Distance</div>
            <div style={{ fontSize:13, fontWeight:600, color:C.text, marginTop:2 }}>{load.miles} mi</div>
            <div style={{ fontSize:11, color:C.textMd }}>{load.equipmentType}</div>
          </div>
        )}
      </div>

      {/* Navigate route button */}
      <a href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(load.originAddress||load.origin)}&destination=${encodeURIComponent(load.destAddress||load.dest)}&travelmode=driving`}
        target="_blank" rel="noreferrer"
        style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, background:"#4285F4", color:"#fff", borderRadius:12, padding:"13px", fontSize:14, fontWeight:700, textDecoration:"none", marginBottom:16 }}>
        🗺 Navigate Full Route
      </a>

      {/* Contacts */}
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginBottom:16 }}>
        <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:12 }}>Contacts</div>
        {[
          { role:"Dispatcher",  name:load.dispatcher,       phone:load.dispatcherPhone },
          { role:"Shipper",     name:load.shipperContact,    phone:load.shipperPhone },
          { role:"Consignee",   name:load.consigneeContact,  phone:load.consigneePhone },
        ].map(c => (
          <div key={c.role} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${C.border}` }}>
            <div>
              <div style={{ fontSize:10, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em" }}>{c.role}</div>
              <div style={{ fontSize:13, fontWeight:500, color:C.text }}>{c.name}</div>
            </div>
            <a href={`tel:${c.phone}`} style={{
              background:C.success, color:"#fff", borderRadius:8, padding:"6px 14px",
              fontSize:12, fontWeight:600, textDecoration:"none", display:"flex", alignItems:"center", gap:4,
            }}>📞 Call</a>
          </div>
        ))}
      </div>

      {/* Special instructions */}
      {load.specialInstructions && (
        <div style={{ background:`${C.warn}11`, border:`1px solid ${C.warn}44`, borderRadius:12, padding:14, marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.warn, marginBottom:6 }}>⚠ Special Instructions</div>
          <div style={{ fontSize:13, color:C.textMd, lineHeight:1.7 }}>{load.specialInstructions}</div>
        </div>
      )}

      {/* Message notification */}
      {unread > 0 && (
        <button onClick={()=>setPage("messages")} style={{
          width:"100%", background:C.accentGl, border:`1px solid ${C.accent}44`,
          borderRadius:12, padding:"12px 16px", cursor:"pointer", fontFamily:C.sans,
          display:"flex", alignItems:"center", gap:10,
        }}>
          <span style={{ fontSize:20 }}>💬</span>
          <span style={{ fontSize:13, fontWeight:600, color:C.accentLt }}>{unread} new message{unread>1?"s":""} from dispatcher</span>
          <span style={{ marginLeft:"auto", fontSize:12, color:C.accentLt }}>View →</span>
        </button>
      )}
    </div>
  );
};

// ── Driver Check-In ───────────────────────────────────────────────────────────
const DriverCheckIn = ({ load, status, setStatus, addCheckIn, setPage }) => {
  const stageIdx  = DISPATCH_STAGES_DRIVER.findIndex(s => s.id === status);
  const nextStage = DISPATCH_STAGES_DRIVER[stageIdx + 1];
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Simulated GPS coords
  const [gps, setGps] = useState({ lat:"33.7490° N", lng:"84.3880° W", accuracy:"±8m", updated:"Just now" });
  useEffect(() => {
    const t = setInterval(() => {
      setGps(g => ({ ...g, updated:new Date().toLocaleTimeString() }));
    }, 10000);
    return () => clearInterval(t);
  }, []);

  const handleCheckIn = () => {
    if (!nextStage) return;
    setStatus(nextStage.id);
    addCheckIn({
      time: new Date().toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}),
      note: note.trim() || nextStage.action,
      type: nextStage.id,
      gps: `${gps.lat}, ${gps.lng}`,
    });
    setConfirmed(true);
    setTimeout(() => { setConfirmed(false); setConfirming(false); setNote(""); setPage("dashboard"); }, 2000);
  };

  if (confirmed) return (
    <div style={{ maxWidth:480, margin:"0 auto", textAlign:"center", paddingTop:60 }}>
      <div style={{ fontSize:80, marginBottom:20 }}>✅</div>
      <div style={{ fontSize:22, fontWeight:700, color:C.success, marginBottom:8 }}>Check-in logged!</div>
      <div style={{ fontSize:14, color:C.textMd }}>Dispatcher has been notified.</div>
    </div>
  );

  return (
    <div style={{ maxWidth:480, margin:"0 auto" }}>
      <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:"0 0 16px" }}>Check In</h2>

      {/* Current status */}
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginBottom:16 }}>
        <div style={{ fontSize:11, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10 }}>Status Pipeline</div>
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {DISPATCH_STAGES_DRIVER.map((s, i) => {
            const isDone    = i < stageIdx;
            const isCurrent = s.id === status;
            const isNext    = nextStage?.id === s.id;
            return (
              <div key={s.id} style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{
                  width:28, height:28, borderRadius:"50%", flexShrink:0,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:14,
                  background: isDone ? C.success : isCurrent ? s.color : isNext ? `${s.color}33` : C.surf2,
                  border: `2px solid ${isDone ? C.success : isCurrent ? s.color : isNext ? s.color : C.border}`,
                }}>
                  {isDone ? "✓" : s.icon}
                </div>
                <div style={{ fontSize:13, fontWeight:isCurrent||isNext?600:400, color:isDone?C.textMd:isCurrent?C.text:isNext?s.color:C.textDm }}>
                  {s.label}
                  {isCurrent && <span style={{ fontSize:10, color:s.color, marginLeft:6 }}>← Current</span>}
                  {isNext    && <span style={{ fontSize:10, color:s.color, marginLeft:6 }}>← Next</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* GPS */}
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:14, marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:11, color:C.textDm, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>📍 GPS Location</div>
            <div style={{ fontSize:12, color:C.text, fontFamily:C.mono }}>{gps.lat}, {gps.lng}</div>
            <div style={{ fontSize:11, color:C.textDm, marginTop:2 }}>Accuracy: {gps.accuracy} · Updated: {gps.updated}</div>
          </div>
          <div style={{ width:8, height:8, borderRadius:"50%", background:C.success, animation:"pulse 1.5s infinite" }} />
        </div>
      </div>

      {/* Note */}
      <div style={{ marginBottom:16 }}>
        <label style={{ fontSize:11, color:C.textMd, textTransform:"uppercase", letterSpacing:"0.07em", display:"block", marginBottom:6 }}>
          Add a note <span style={{ color:C.textDm }}>(optional)</span>
        </label>
        <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3}
          placeholder="Any notes for dispatcher — delays, issues, exceptions..."
          style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", color:C.text, fontSize:14, outline:"none", resize:"none", fontFamily:C.sans, boxSizing:"border-box" }} />
      </div>

      {/* Confirm button */}
      {nextStage ? (
        !confirming ? (
          <button onClick={()=>setConfirming(true)} style={{
            width:"100%", background:`linear-gradient(135deg,${nextStage.color},${nextStage.color}cc)`,
            border:"none", color:"#fff", borderRadius:14, padding:"18px",
            fontSize:17, fontWeight:700, cursor:"pointer", fontFamily:C.sans,
            boxShadow:`0 4px 20px ${nextStage.color}44`,
          }}>
            {nextStage.icon} {nextStage.action}
          </button>
        ) : (
          <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:14, padding:20, textAlign:"center" }}>
            <div style={{ fontSize:16, fontWeight:600, color:C.text, marginBottom:6 }}>Confirm check-in?</div>
            <div style={{ fontSize:13, color:C.textMd, marginBottom:16 }}>This will notify your dispatcher immediately.</div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setConfirming(false)} style={{ flex:1, background:"transparent", border:`1px solid ${C.border}`, color:C.textMd, borderRadius:10, padding:"12px", fontSize:14, cursor:"pointer", fontFamily:C.sans }}>Cancel</button>
              <button onClick={handleCheckIn} style={{ flex:2, background:nextStage.color, border:"none", color:"#fff", borderRadius:10, padding:"12px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:C.sans }}>
                ✓ Confirm
              </button>
            </div>
          </div>
        )
      ) : (
        <div style={{ textAlign:"center", color:C.success, fontSize:16, fontWeight:600, padding:20 }}>✅ All milestones complete</div>
      )}
    </div>
  );
};

// ── Driver Messages ───────────────────────────────────────────────────────────
const DriverMessages = ({ messages, setMessages }) => {
  const [text, setText] = useState("");

  const send = () => {
    if (!text.trim()) return;
    setMessages(prev => [...prev, {
      id: Date.now(), from:"Driver", text:text.trim(), read:true,
      time: new Date().toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"}),
    }]);
    setText("");
    // Simulate dispatcher reply after 3s
    setTimeout(() => {
      const replies = [
        "Got it, thanks for the update!",
        "Copy that. Keep me posted.",
        "Understood. ETA still on track?",
        "Thanks driver. Let me know when you arrive.",
      ];
      setMessages(prev => [...prev, {
        id: Date.now()+1, from:"Dispatcher",
        text: replies[Math.floor(Math.random()*replies.length)],
        read:false,
        time: new Date().toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"}),
      }]);
    }, 3000);
  };

  // Mark all dispatcher messages as read when viewing
  useEffect(() => {
    setMessages(prev => prev.map(m => m.from==="Dispatcher" ? {...m, read:true} : m));
  }, []);

  return (
    <div style={{ maxWidth:480, margin:"0 auto", display:"flex", flexDirection:"column", height:"calc(100vh - 160px)" }}>
      <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:"0 0 16px", flexShrink:0 }}>Messages</h2>
      {messages.length === 0 && (
        <div style={{ textAlign:"center", color:C.textDm, fontSize:13, padding:"40px 20px" }}>No messages yet.</div>
      )}

      {/* Message thread */}
      <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:10, paddingBottom:10 }}>
        {messages.map(m => (
          <div key={m.id} style={{ display:"flex", flexDirection:"column", alignItems:m.from==="Driver"?"flex-end":"flex-start" }}>
            <div style={{ fontSize:10, color:C.textDm, marginBottom:3 }}>{m.from} · {m.time}</div>
            <div style={{
              maxWidth:"80%", padding:"10px 14px", borderRadius:m.from==="Driver"?"16px 16px 4px 16px":"16px 16px 16px 4px",
              background: m.from==="Driver" ? C.accent : C.surface,
              border: m.from==="Driver" ? "none" : `1px solid ${C.border}`,
              color: m.from==="Driver" ? "#fff" : C.text,
              fontSize:14, lineHeight:1.5,
            }}>{m.text}</div>
          </div>
        ))}
        {messages.length === 0 && (
          <div style={{ textAlign:"center", color:C.textDm, fontSize:13, paddingTop:40 }}>No messages yet</div>
        )}
      </div>

      {/* Compose */}
      <div style={{ flexShrink:0, paddingTop:12, borderTop:`1px solid ${C.border}`, display:"flex", gap:8 }}>
        <input value={text} onChange={e=>setText(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&send()}
          placeholder="Message dispatcher..."
          style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", color:C.text, fontSize:14, outline:"none", fontFamily:C.sans }} />
        <button onClick={send} disabled={!text.trim()} style={{
          background:C.accent, border:"none", color:"#fff", borderRadius:10,
          padding:"12px 18px", fontSize:14, fontWeight:700, cursor:text.trim()?"pointer":"not-allowed",
          opacity:text.trim()?1:0.5, fontFamily:C.sans,
        }}>Send</button>
      </div>
    </div>
  );
};

// ── Driver Documents ──────────────────────────────────────────────────────────
const DriverDocuments = ({ load, status }) => {
  const [docs, setDocs] = useState([
    { id:"D1", name:"Rate Confirmation", type:"RATE_CON", url:null, status:"PROVIDED", uploadedAt:"Jun 11" },
    { id:"D2", name:"Bill of Lading",    type:"BOL",      url:null, status:status==="DELIVERED"?"REQUIRED":"PENDING", uploadedAt:null },
    { id:"D3", name:"Delivery Receipt",  type:"POD",      url:null, status:status==="DELIVERED"?"REQUIRED":"PENDING", uploadedAt:null },
  ]);
  const [uploading, setUploading]   = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const handleFile = (docId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(docId);
    const reader = new FileReader();
    reader.onload = () => {
      setTimeout(() => {
        setDocs(prev => prev.map(d => d.id===docId
          ? { ...d, url:reader.result, status:"UPLOADED", uploadedAt:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) }
          : d
        ));
        setUploading(null);
      }, 1500);
    };
    reader.readAsDataURL(file);
  };

  const DOC_META = {
    RATE_CON: { label:"Rate Confirmation", icon:"💰", color:C.accentLt },
    BOL:      { label:"Bill of Lading",    icon:"📋", color:C.purple   },
    POD:      { label:"Proof of Delivery", icon:"✍", color:C.success  },
  };
  const STATUS_META = {
    PROVIDED: { label:"Provided",  color:C.success },
    UPLOADED: { label:"Uploaded",  color:C.success },
    REQUIRED: { label:"Required",  color:C.danger  },
    PENDING:  { label:"Pending delivery", color:C.textDm },
  };

  return (
    <div style={{ maxWidth:480, margin:"0 auto" }}>
      <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:"0 0 4px" }}>Documents</h2>
      <p style={{ fontSize:13, color:C.textMd, margin:"0 0 20px" }}>Load {load.number} · BOL #{load.bolNumber}</p>

      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {docs.map(doc => {
          const meta   = DOC_META[doc.type]   || { label:doc.name, icon:"📄", color:C.textMd };
          const sMeta  = STATUS_META[doc.status] || STATUS_META.PENDING;
          const isRequired = doc.status === "REQUIRED";
          const isUploaded = doc.status === "UPLOADED";
          const isProvided = doc.status === "PROVIDED";

          return (
            <div key={doc.id} style={{
              background:C.surface, borderRadius:14,
              border:`2px solid ${isRequired?C.danger:isUploaded||isProvided?C.success:C.border}`,
              overflow:"hidden",
            }}>
              <div style={{ padding:"14px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ fontSize:28 }}>{meta.icon}</div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{meta.label}</div>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:3 }}>
                      <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:4, textTransform:"uppercase",
                        background:`${sMeta.color}18`, border:`1px solid ${sMeta.color}44`, color:sMeta.color }}>
                        {sMeta.label}
                      </span>
                      {doc.uploadedAt && <span style={{ fontSize:11, color:C.textDm }}>{doc.uploadedAt}</span>}
                    </div>
                  </div>
                </div>

                {(isRequired || (isUploaded && !isProvided)) && (
                  uploading === doc.id ? (
                    <div style={{ fontSize:12, color:C.textMd, animation:"pulse 1s infinite" }}>Uploading…</div>
                  ) : (
                    <label style={{
                      background: isUploaded ? C.surf2 : C.danger,
                      color: isUploaded ? C.textMd : "#fff",
                      borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:600,
                      cursor:"pointer", whiteSpace:"nowrap",
                    }}>
                      {isUploaded ? "Re-upload" : "📷 Upload"}
                      <input type="file" accept="image/*,application/pdf" onChange={e=>handleFile(doc.id,e)} style={{ display:"none" }} />
                    </label>
                  )
                )}
                {(isUploaded || isProvided) && doc.url && (
                  <button onClick={()=>{setPreviewUrl(doc.url);setPreviewDoc(doc);}} style={{
                    background:C.surf2, border:`1px solid ${C.border}`, color:C.textMd,
                    borderRadius:8, padding:"8px 14px", fontSize:12, cursor:"pointer",
                  }}>View</button>
                )}
              </div>

              {isRequired && (
                <div style={{ padding:"10px 16px", background:`${C.danger}11`, borderTop:`1px solid ${C.danger}22`, fontSize:12, color:C.danger }}>
                  ⚠ Required — upload before leaving delivery location
                </div>
              )}
              {isUploaded && (
                <div style={{ padding:"10px 16px", background:C.succGl, borderTop:`1px solid ${C.success}22`, fontSize:12, color:C.success }}>
                  ✓ Uploaded and sent to dispatcher
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Tips */}
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginTop:16 }}>
        <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:8 }}>📸 Photo tips</div>
        {[
          "Lay document flat on a dark surface",
          "Make sure all 4 corners are visible",
          "Check signatures are legible",
          "Both sides if there's writing on the back",
        ].map((tip,i) => (
          <div key={i} style={{ fontSize:12, color:C.textMd, padding:"4px 0" }}>• {tip}</div>
        ))}
      </div>

      {/* Image preview modal */}
      {previewUrl && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.9)", zIndex:400, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}
          onClick={()=>{setPreviewUrl(null);setPreviewDoc(null);}}>
          <div style={{ fontSize:13, color:"#fff", marginBottom:12 }}>{previewDoc?.name} — tap to close</div>
          <img src={previewUrl} alt="document" style={{ maxWidth:"90vw", maxHeight:"80vh", borderRadius:8, objectFit:"contain" }} />
        </div>
      )}
    </div>
  );
};

// ── Driver App Root ───────────────────────────────────────────────────────────
const DriverApp = ({ setPage: setMainPage }) => {
  const { user: driverAuthUser } = useAuth();
  const isDemoDriver = driverAuthUser?.email === "driver@arrowlogistics.com";
  // Only the demo driver account sees the sample load/messages — new drivers start clean
  const activeLoad = isDemoDriver ? DRIVER_LOAD : null;

  const [driverPage, setDriverPage] = useState("dashboard");
  const [status, setStatus]         = useState(activeLoad?.status === "IN_TRANSIT" ? "IN_TRANSIT" : "ASSIGNED");
  const [checkIns, setCheckIns]     = useState(isDemoDriver ? [
    { id:1, from:"Dispatcher", text:"Good morning Marcus! Load FF-000003 is ready. Pickup at Atlanta warehouse by 8am. Stay safe!", read:true, time:"6:45 AM" },
    { id:2, from:"Driver",     text:"On my way. Should be there by 7:45.", read:true, time:"6:48 AM" },
    { id:3, from:"Dispatcher", text:"Perfect. Dock 4, ask for Jim. BOL is in your documents.", read:true, time:"6:49 AM" },
  ] : []);

  const addCheckIn = (ci) => setCheckIns(prev => [...prev, { ...ci, id:Date.now(), from:"Driver" }]);

  const NAV = [
    { id:"dashboard", label:"My Load",   icon:"📋" },
    { id:"checkin",   label:"Check In",  icon:"📍" },
    { id:"messages",  label:"Messages",  icon:"💬" },
    { id:"documents", label:"Docs",      icon:"📄" },
  ];

  const unreadMessages = checkIns.filter(m => !m.read && m.from === "Dispatcher").length;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100vh", background:C.bg, fontFamily:C.sans }}>
      {/* Driver top bar */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:C.text }}>FreightFlow Driver</div>
          <div style={{ fontSize:11, color:C.textDm }}>
            {driverAuthUser?.email || "Driver"}{activeLoad?.truck ? ` · ${activeLoad.truck}` : ""}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background:C.success, animation:"pulse 1.5s infinite" }} />
          <span style={{ fontSize:11, color:C.success }}>Online</span>
          <button onClick={() => { Object.keys(localStorage).filter(k=>k.startsWith("ff_")).forEach(k=>localStorage.removeItem(k)); window.location.reload(); }}
            style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.textDm, borderRadius:6, padding:"3px 8px", fontSize:11, cursor:"pointer", marginLeft:8 }}>
            Sign out
          </button>
        </div>
      </div>

      {/* Page content */}
      <div style={{ flex:1, overflowY:"auto", padding:"16px 16px 80px" }}>
        {!activeLoad ? (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", textAlign:"center", padding:"40px 20px" }}>
            <div style={{ fontSize:48, opacity:0.15, marginBottom:16 }}>🚛</div>
            <div style={{ fontSize:16, fontWeight:600, color:C.text, marginBottom:8 }}>No load assigned yet</div>
            <div style={{ fontSize:13, color:C.textMd, lineHeight:1.6 }}>Your dispatcher will assign you a load. Check back soon or contact your carrier.</div>
          </div>
        ) : (
          <>
            {driverPage === "dashboard" && <DriverDashboard load={activeLoad} status={status} setStatus={setStatus} setPage={setDriverPage} messages={checkIns} />}
            {driverPage === "checkin"   && <DriverCheckIn   load={activeLoad} status={status} setStatus={setStatus} addCheckIn={addCheckIn} setPage={setDriverPage} />}
            {driverPage === "messages"  && <DriverMessages  messages={checkIns} setMessages={setCheckIns} />}
            {driverPage === "documents" && <DriverDocuments load={activeLoad} status={status} />}
          </>
        )}
      </div>

      {/* Bottom tab bar — mobile style */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:C.surface, borderTop:`1px solid ${C.border}`, display:"flex", zIndex:50 }}>
        {NAV.map(n => {
          const isActive = driverPage === n.id;
          const badge    = n.id==="messages" && unreadMessages > 0;
          return (
            <button key={n.id} onClick={()=>setDriverPage(n.id)} style={{
              flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
              padding:"10px 0", background:"transparent", border:"none", cursor:"pointer",
              borderTop:`2px solid ${isActive?C.accent:"transparent"}`,
              position:"relative",
            }}>
              <span style={{ fontSize:20 }}>{n.icon}</span>
              <span style={{ fontSize:10, color:isActive?C.accentLt:C.textDm, marginTop:2, fontWeight:isActive?600:400 }}>{n.label}</span>
              {badge && <span style={{ position:"absolute", top:6, right:"25%", width:16, height:16, borderRadius:"50%", background:C.danger, color:"#fff", fontSize:9, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center" }}>{unreadMessages}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight:"100vh", background:"#070c16", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"sans-serif" }}>
          <div style={{ maxWidth:600, padding:32, background:"#0e1625", borderRadius:12, border:"1px solid #ef4444" }}>
            <div style={{ fontSize:18, fontWeight:700, color:"#ef4444", marginBottom:12 }}>Runtime Error</div>
            <div style={{ fontSize:13, color:"#94a3b8", marginBottom:16 }}>{this.state.error?.message}</div>
            <pre style={{ fontSize:11, color:"#475569", background:"#070c16", padding:12, borderRadius:6, overflow:"auto", maxHeight:300 }}>
              {this.state.error?.stack}
            </pre>
            <button onClick={() => { localStorage.removeItem("ff_token"); window.location.reload(); }}
              style={{ marginTop:16, background:"#2563eb", color:"#fff", border:"none", borderRadius:8, padding:"8px 20px", cursor:"pointer" }}>
              Clear Session & Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppInner() {
  const { user: authUser, logout, token } = useAuth();
  const role      = authUser?.role || "SHIPPER";
  const carrierId = authUser?.carrier?.id || "C1";
  const shipperId = authUser?.shipper?.id || "S1";

  const [page, setPage]   = useState(role === "ADMIN" ? "admin_dashboard" : "dashboard");
  const [loads, setLoads] = useState([]); // start empty — populated from API on login
  const [bids,  setBids]  = useState([]);
  const [loadsFetched, setLoadsFetched] = useState(false);
  const [dismissedLoads, setDismissedLoads] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ff_dismissed_loads") || "[]"); } catch(_) { return []; }
  });
  const dismissLoad = (loadId) => {
    const next = [...dismissedLoads, loadId];
    setDismissedLoads(next);
    try { localStorage.setItem("ff_dismissed_loads", JSON.stringify(next)); } catch(_) {}
  };

  // Fetch real loads from backend on login
  useEffect(() => {
    if (!token || loadsFetched) return;
    const fetchLoads = async () => {
      try {
        const res = await fetch(`${API_BASE}/loads`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          console.warn("GET /loads failed:", res.status);
          return;
        }
        const data = await res.json();

        // Always replace with backend data when authenticated
        // Only fall back to sample data if backend returns nothing at all
        const mapped = (data.loads || []).map(l => ({
          id:           l.id,
          number:       l.loadNumber || l.number || `FF-${l.id.slice(0,6)}`,
          shipperId:    l.shipperId,
          shipper:      l.shipper?.companyName || "Unknown",
          shipperEmail: l.shipper?.user?.email || "",
          shipperPhone: l.shipper?.phone || "",
          carrierId:    l.carrierId,
          carrier:      l.carrier?.companyName || null,
          origin:       l.originCity && l.originState ? `${l.originCity}, ${l.originState}` : (l.origin || `${l.originCity||""}${l.originState?", "+l.originState:""}`).trim() || "—",
          dest:         l.destCity   && l.destState   ? `${l.destCity}, ${l.destState}`     : (l.dest   || `${l.destCity  ||""}${l.destState  ?", "+l.destState  :""}`).trim() || "—",
          pickupDate:   l.pickupDate   ? new Date(l.pickupDate).toLocaleDateString("en-US",{month:"short",day:"numeric"})   : "",
          deliveryDate: l.deliveryDate ? new Date(l.deliveryDate).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : "",
          miles:        l.miles        || l.distanceMiles || 0,
          weight:       l.weight       || l.weightLbs     || 0,
          commodity:    l.commodity    || "",
          equipmentType:l.equipmentType|| "DRY_VAN",
          rate:         l.rate         || 0,
          brokerRate:   l.brokerRate   || 0,
          status:       l.status,
          urgency:      l.urgency      || "STANDARD",
          bids:         l._count?.bids || 0,
          postedAt:     l.createdAt ? new Date(l.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : "—",
          progress:     l.progress    || 0,
          originLat:    l.originLat   || null,
          originLng:    l.originLng   || null,
          destLat:      l.destLat     || null,
          destLng:      l.destLng     || null,
          originAddress:l.originAddress || null,
          destAddress:  l.destAddress   || null,
          pallets:      l.pallets     || 0,
          pieces:       l.pieces      || 0,
          hazmat:       l.hazmat      || false,
          tempMin:      l.temperature ? l.temperature - 5 : null,
          tempMax:      l.temperature ? l.temperature + 5 : null,
        }));

        // Always use backend data — empty array means no loads yet
        setLoads(mapped);
        // Note: sample data (LOADS constant) is only used in local dev without a backend
      } catch(e) {
        console.warn("Failed to fetch loads:", e.message);
      }
      setLoadsFetched(true);
    };

    const fetchBids = async () => {
      try {
        const res = await fetch(`${API_BASE}/bids/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data.bids) {
          const mapped = data.bids.map(b => ({
            id:            b.id,
            loadId:        b.loadId,
            carrier:       b.carrier?.companyName || "Unknown Carrier",
            carrierId:     b.carrierId,
            rating:        b.carrier?.rating || 4.5,
            deliveries:    0,
            amount:        b.amount,
            note:          b.note || b.notes || "",
            status:        b.status,
            counterAmount: b.counterAmount || null,
            counterNote:   b.counterNote   || "",
            submitted:     b.createdAt ? new Date(b.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : "—",
          }));
          setBids(mapped);
        }
      } catch(e) { console.warn("Failed to fetch bids:", e.message); }
    };

    fetchLoads();
    // Only fetch bids for roles that need them
    if (["SHIPPER","CARRIER","BROKER"].includes(role)) fetchBids();
  }, [token]); // eslint-disable-line
  // Fleet state — scoped per carrier, loaded after auth resolves
  const [fleetTrucks,  setFleetTrucksState]  = useState([]);
  const [fleetDrivers, setFleetDriversState] = useState([]);

  // Load fleet when carrierId is known (after auth resolves)
  useEffect(() => {
    if (!authUser || role !== "CARRIER") return;
    const realCarrierId = authUser?.carrier?.id || "C1";
    const fleetKey = `ff_fleet_${realCarrierId}`;
    try {
      const saved = localStorage.getItem(fleetKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        setFleetTrucksState(parsed.trucks  || []);
        setFleetDriversState(parsed.drivers || []);
        return;
      }
    } catch(_) {}
    // No saved fleet — seed sample data only for demo carrier C1
    setFleetTrucksState(realCarrierId === "C1" ? INIT_TRUCKS  : []);
    setFleetDriversState(realCarrierId === "C1" ? INIT_DRIVERS : []);
  }, [authUser?.carrier?.id]); // eslint-disable-line

  const setFleetTrucks = (trucks) => {
    setFleetTrucksState(trucks);
    const realCarrierId = authUser?.carrier?.id || "C1";
    const fleetKey = `ff_fleet_${realCarrierId}`;
    try {
      const saved = localStorage.getItem(fleetKey);
      const existing = saved ? JSON.parse(saved) : {};
      localStorage.setItem(fleetKey, JSON.stringify({ ...existing, trucks }));
    } catch(_) {}
  };
  const setFleetDrivers = (drivers) => {
    setFleetDriversState(drivers);
    const realCarrierId = authUser?.carrier?.id || "C1";
    const fleetKey = `ff_fleet_${realCarrierId}`;
    try {
      const saved = localStorage.getItem(fleetKey);
      const existing = saved ? JSON.parse(saved) : {};
      localStorage.setItem(fleetKey, JSON.stringify({ ...existing, drivers }));
    } catch(_) {}
  };

  // Carrier dispatch state — scoped per user
  const carrierDispatchKey = `ff_carrier_dispatch_${authUser?.id || "guest"}`;
  const [carrierDispatchState, setCarrierDispatchState] = useState(() => {
    try { const s = localStorage.getItem(carrierDispatchKey); return s ? JSON.parse(s) : null; } catch(_) { return null; }
  });
  const updateCarrierDispatch = (state) => {
    setCarrierDispatchState(state);
    try { localStorage.setItem(carrierDispatchKey, JSON.stringify(state)); } catch(_) {}
  };

  // Persist dispatch state scoped per user so different users don't share it
  const dispatchKey = `ff_dispatch_${authUser?.id || "guest"}`;
  const [dispatchState, setDispatchState] = useState(() => {
    try {
      const saved = localStorage.getItem(dispatchKey);
      return saved ? JSON.parse(saved) : null;
    } catch(_) { return null; }
  });

  const updateDispatchState = (state) => {
    setDispatchState(state);
    try { localStorage.setItem(dispatchKey, JSON.stringify(state)); } catch(_) {}
  };
  const [notifOpen,  setNotifOpen]  = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Notifications scoped per user — demo seed accounts get sample notifications
  const isDemoAccount = ["admin@freightflow.com","shipper@midwestgoods.com","ops@arrowlogistics.com","broker@midwestbroker.com","driver@arrowlogistics.com"].includes(authUser?.email);
  const [notifs, setNotifs] = useState(isDemoAccount ? [
    { id:1, title:"New bid received",   message:"Arrow Logistics bid $2,750 on FF-000001", time:"2m ago", read:false },
    { id:2, title:"Load delivered",     message:"FF-000006 delivered to Charlotte, NC",     time:"1h ago", read:false },
    { id:3, title:"Payment confirmed",  message:"$1,320 payment received for INV-000001",  time:"3h ago", read:true  },
  ] : []);

  const prevUserId = useRef(authUser?.id);
  useEffect(() => {
    if (prevUserId.current !== authUser?.id) {
      setPage(role === "ADMIN" ? "admin_dashboard" : "dashboard");
      prevUserId.current = authUser?.id;
    }
  }, [authUser?.id, role]);

  const markRead    = (id) => setNotifs(prev => prev.map(n => n.id===id ? {...n, read:true} : n));
  const markAllRead = ()   => setNotifs(prev => prev.map(n => ({...n, read:true})));
  const dismissNotif= (id) => setNotifs(prev => prev.filter(n => n.id!==id));
  const unreadCount = notifs.filter(n => !n.read).length;

  const handleBidSubmit = async (loadId, amount, note) => {
    // Optimistic local update
    const tempBid = { id:`B${Date.now()}`, loadId, carrier:authUser?.carrier?.companyName||"My Company", rating:4.9, deliveries:312, amount, note, status:"PENDING", submitted:"just now" };
    setBids(prev => [...prev, tempBid]);
    setLoads(prev => prev.map(l => l.id===loadId ? {...l, status:"BIDDING", bids:(l.bids||0)+1} : l));

    // Save to backend
    if (token) {
      try {
        const res = await fetch(`${API_BASE}/bids`, {
          method: "POST",
          headers: { "Content-Type":"application/json", Authorization:`Bearer ${token}` },
          body: JSON.stringify({ loadId, amount: parseFloat(amount), note: note||"" }),
        });
        if (res.ok) {
          const data = await res.json();
          // Replace temp bid with real one
          setBids(prev => prev.map(b => b.id===tempBid.id ? { ...tempBid, id:data.bid.id } : b));
        } else {
          const err = await res.json().catch(()=>({}));
          console.error("Bid failed:", err);
          alert(`Bid could not be submitted: ${err.error||"Unknown error"}`);
          // Roll back
          setBids(prev => prev.filter(b => b.id!==tempBid.id));
          setLoads(prev => prev.map(l => l.id===loadId ? {...l, status:"POSTED", bids:Math.max(0,(l.bids||1)-1)} : l));
        }
      } catch(e) { console.warn("Bid network error:", e.message); }
    }
  };

  const handleBook = (load, assignedCarrierId, carrierName, carrierRate) => {
    setLoads(prev => prev.map(l =>
      l.id === load.id ? { ...l, status:"BOOKED", carrier:carrierName||"Direct Booking", carrierId:assignedCarrierId||"C_DIRECT", rate:load.rate, brokerRate:carrierRate||load.rate, progress:0, checkIns:[], alerts:[] } : l
    ));
  };

  const handleAcceptBid = async (bid) => {
    setBids(prev => prev.map(b => b.loadId===bid.loadId ? {...b, status:b.id===bid.id?"ACCEPTED":"REJECTED"} : b));
    setLoads(prev => prev.map(l => l.id===bid.loadId ? {...l, status:"BOOKED", carrier:bid.carrier, carrierId:bid.carrierId||"C_NEW"} : l));
    if (token) {
      try { await fetch(`${API_BASE}/bids/${bid.id}/accept`, { method:"POST", headers:{ Authorization:`Bearer ${token}` } }); }
      catch(e) { console.warn("Accept bid error:", e.message); }
    }
  };

  const handleRejectBid = async (bid) => {
    setBids(prev => prev.map(b => b.id===bid.id ? {...b, status:"REJECTED"} : b));
    if (token) {
      try { await fetch(`${API_BASE}/bids/${bid.id}/reject`, { method:"POST", headers:{ Authorization:`Bearer ${token}` } }); }
      catch(e) { console.warn("Reject bid error:", e.message); }
    }
  };

  const handleCounterBid = async (bid, counterAmount, note) => {
    // Optimistic update
    setBids(prev => prev.map(b => b.id===bid.id
      ? {...b, status:"COUNTERED", counterAmount, counterNote:note}
      : b
    ));
    if (token) {
      try {
        const res = await fetch(`${API_BASE}/bids/${bid.id}/counter`, {
          method: "POST",
          headers: { "Content-Type":"application/json", Authorization:`Bearer ${token}` },
          body: JSON.stringify({ counterAmount, note }),
        });
        if (!res.ok) {
          const err = await res.json().catch(()=>({}));
          console.warn("Counter bid failed:", err);
        }
      } catch(e) { console.warn("Counter bid error:", e.message); }
    }
  };

  const handlePostLoad = async (form) => {
    if (!token) return;

    // Build the payload matching backend schema
    const toISO = (val) => {
      if (!val) return new Date().toISOString();
      const d = new Date(val);
      return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
    };

    const payload = {
      originAddress:  form.originAddress || form.pickupFacilityName || `${form.originCity}, ${form.originState}`,
      originCity:     form.originCity    || "",
      originState:    form.originState   || "XX",
      originZip:      form.originZip     || "00000",
      destAddress:    form.destAddress || form.deliveryFacilityName || `${form.destCity}, ${form.destState}`,
      destCity:       form.destCity      || "",
      destState:      form.destState     || "XX",
      destZip:        form.destZip       || "00000",
      pickupDate:     toISO(form.pickupDate),
      deliveryDate:   toISO(form.deliveryDate),
      pickupInstructions:   form.pickupInstructions   || "",
      deliveryInstructions: form.deliveryInstructions || "",
      commodity:      form.commodity     || "General Freight",
      weight:         parseFloat(form.weight)  || 30000,
      equipmentType:  form.equipmentType || "DRY_VAN",
      rate:           parseFloat(form.shipperRate || form.rate) || 2000,
      urgency:        form.urgency       || "STANDARD",
      specialInstructions: form.specialInstructions || form.notes || "",
    };

    try {
      const res = await fetch(`${API_BASE}/loads`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        const savedLoad = {
          id:           data.load.id,
          number:       data.load.loadNumber || `FF-NEW`,
          shipperId:    data.load.shipperId,
          shipper:      form.shipperName || authUser?.shipper?.companyName || "My Company",
          shipperEmail: form.shipperEmail || "",
          shipperPhone: form.shipperPhone || "",
          carrierId:    null,
          carrier:      null,
          origin:       `${form.originCity}, ${form.originState}`,
          dest:         `${form.destCity}, ${form.destState}`,
          pickupDate:   form.pickupDate   || "",
          deliveryDate: form.deliveryDate || "",
          miles:        data.load.miles   || Math.floor(Math.random()*800+100),
          weight:       parseFloat(form.weight) || 30000,
          commodity:    form.commodity    || "General Freight",
          equipmentType:form.equipmentType|| "DRY_VAN",
          rate:         parseFloat(form.shipperRate || form.rate) || 2000,
          brokerRate:   parseFloat(form.targetCarrierRate) || 0,
          status:       "POSTED",
          urgency:      form.urgency      || "STANDARD",
          bids:         0,
          postedAt:     "just now",
          progress:     0,
        };
        // Add to state only after backend confirms
        setLoads(prev => [savedLoad, ...prev]);
        setPage(role === "BROKER" ? "mybids" : "loads");
      } else {
        const errData = await res.json().catch(() => ({}));
        console.error("Backend rejected load:", res.status, errData);
        alert(`Could not post load: ${errData.error || "Please check all fields and try again."}`);
      }
    } catch(e) {
      console.error("Network error posting load:", e.message);
      alert("Network error — check your connection and try again.");
    }
  };

  const renderPage = () => {
    if (role === "ADMIN") {
      return <AdminConsole adminPage={page} setAdminPage={setPage} />;
    }
    if (role === "SHIPPER") {
      switch(page) {
        case "dashboard": return <ShipperDashboard loads={loads} setPage={setPage} />;
        case "loads":     return <LoadBoardPage loads={loads} bids={bids} role={role} onBidSubmit={handleBidSubmit} onAcceptBid={handleAcceptBid} onBook={handleBook} onRejectBid={handleRejectBid} onCounterBid={handleCounterBid} />;
        case "post":      return <PostLoadPage onPost={handlePostLoad} role={role} />;
        case "tracking":  return <LiveTrackingPage loads={loads} role={role} carrierId={carrierId} />;
        case "invoices":  return <InvoicesPage />;
        case "analytics": return <AnalyticsPage loads={loads} role={role} />;
        case "carriers":  return <CarrierDirectoryPage loads={loads} />;
        case "fmcsa":     return <FMCSALookupPage />;
        default: setPage("dashboard"); return <ShipperDashboard loads={loads} setPage={setPage} />;
      }
    }
    if (role === "BROKER") {
      switch(page) {
        case "dashboard":  return <BrokerDashboard loads={loads} bids={bids} setPage={setPage} />;
        case "dispatch":   return <BrokerDispatchPage loads={loads} dispatchState={dispatchState} setDispatchState={updateDispatchState} onStatusChange={(id,status)=>setLoads(prev=>prev.map(l=>l.id===id?{...l,status}:l))} onCarrierAssign={(id,carrier,carrierId,carrierPhone)=>setLoads(prev=>prev.map(l=>l.id===id?{...l,carrier,carrierId,carrierPhone,status:l.status==="POSTED"?"BOOKED":l.status}:l))} />;
        case "loadboard":  return <LoadBoardPage loads={loads} bids={bids} role="SHIPPER" onBidSubmit={handleBidSubmit} onAcceptBid={handleAcceptBid} onBook={handleBook} onRejectBid={handleRejectBid} onCounterBid={handleCounterBid} />;
        case "post":       return <PostLoadPage onPost={handlePostLoad} role={role} />;
        case "mybids":     return <LoadBoardPage loads={loads.filter(l=>["BOOKED","AT_PICKUP","PICKED_UP","IN_TRANSIT","AT_DELIVERY","DELIVERED"].includes(l.status))} bids={bids} role="SHIPPER" onBidSubmit={handleBidSubmit} onAcceptBid={handleAcceptBid} onBook={handleBook} onRejectBid={handleRejectBid} onCounterBid={handleCounterBid} />;
        case "shippers":   return <BrokerShippersPage loads={loads} />;
        case "directory":  return <ShipperDirectoryPage />;
        case "carriers":   return <CarrierDirectoryPage loads={loads} />;
        case "tracking":   return <LiveTrackingPage loads={loads} role="SHIPPER" carrierId={carrierId} />;
        case "invoices":   return <BrokerInvoicingPage />;
        case "analytics":  return <AnalyticsPage loads={loads} role="SHIPPER" />;
        case "fmcsa":      return <FMCSALookupPage />;
        default: setPage("dashboard"); return <BrokerDashboard loads={loads} bids={bids} setPage={setPage} />;
      }
    }
    // CARRIER
    switch(page) {
      case "dashboard": return <CarrierDashboard loads={loads} earnings={carrierId==="C1"?EARNINGS:[]} setPage={setPage} carrierId={carrierId} dismissedLoads={dismissedLoads} dismissLoad={dismissLoad} />;
      case "dispatch":  return <CarrierDispatchPage loads={loads} trucks={fleetTrucks} drivers={fleetDrivers} savedState={carrierDispatchState} onSaveState={updateCarrierDispatch} onStatusChange={(id,status)=>setLoads(prev=>prev.map(l=>l.id===id?{...l,status}:l))} />;
      case "loadboard": return <LoadBoardPage loads={loads} bids={bids} role={role} onBidSubmit={handleBidSubmit} onAcceptBid={handleAcceptBid} onBook={handleBook} onRejectBid={handleRejectBid} onCounterBid={handleCounterBid} />;
      case "mybids":    return <LoadBoardPage loads={loads.filter(l=>bids.some(b=>b.loadId===l.id))} bids={bids} role={role} onBidSubmit={handleBidSubmit} onAcceptBid={handleAcceptBid} onBook={handleBook} onRejectBid={handleRejectBid} onCounterBid={handleCounterBid} />;
      case "tracking":  return <LiveTrackingPage loads={loads} role={role} carrierId={carrierId} />;
      case "invoicing": return <CarrierInvoicingPage loads={loads} carrierId={carrierId} />;
      case "fleet":     return <FleetPage trucks={fleetTrucks} setTrucks={setFleetTrucks} drivers={fleetDrivers} setDrivers={setFleetDrivers} />;
      case "fmcsa":     return <FMCSALookupPage />;
      default: setPage("dashboard"); return <CarrierDashboard loads={loads} earnings={carrierId==="C1"?EARNINGS:[]} setPage={setPage} carrierId={carrierId} dismissedLoads={dismissedLoads} dismissLoad={dismissLoad} />;
    }
  };

  // DRIVER: render full-screen mobile app, bypass shell entirely
  if (role === "DRIVER") {
    return <DriverApp />;
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100vh", background:C.bg, fontFamily:C.sans, color:C.text }}>
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        ::-webkit-scrollbar { width:6px; height:6px; }
        ::-webkit-scrollbar-track { background:${C.bg}; }
        ::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }
        /* Mobile responsive */
        @media (max-width: 768px) {
          .mobile-menu-btn { display: block !important; }
          .sidebar-hidden { transform: translateX(-100%); position: fixed !important; top: 52px; left: 0; height: calc(100vh - 52px); z-index: 200; transition: transform 0.25s; }
          .sidebar-visible { transform: translateX(0) !important; }
          .main-content { padding: 12px !important; }
          table { font-size: 11px !important; }
          table th, table td { padding: 6px 8px !important; white-space: nowrap; }
          .hide-mobile { display: none !important; }
        }
        /* Touch targets — prevent zoom on iOS */
        button { min-height: 36px; }
        input, select, textarea { font-size: 16px !important; }
      `}</style>

      {/* Top bar */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:"0 20px", display:"flex", alignItems:"center", justifyContent:"space-between", height:52, flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={() => setMobileNavOpen(o => !o)}
            style={{ background:"transparent", border:"none", color:C.textMd, fontSize:20, cursor:"pointer", padding:"4px 8px", display:"none" }}
            className="mobile-menu-btn">
            ☰
          </button>
          <div style={{ width:32, height:32, borderRadius:8, background:`linear-gradient(135deg,${C.accent},${C.purple})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>🚛</div>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:C.text }}>FreightFlow</div>
            <div style={{ fontSize:10, color:C.textDm }}>TMS Platform</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:12, fontWeight:600, color:C.text }}>
                {authUser ? (authUser.shipper?.companyName || authUser.carrier?.companyName || authUser.broker?.companyName || "FreightFlow Admin") : role==="SHIPPER"?"Midwest Goods Co.":role==="CARRIER"?"Arrow Logistics Inc.":role==="BROKER"?"Midwest Broker LLC":"Admin"}
              </div>
              <div style={{ fontSize:10, color:C.textDm, textTransform:"capitalize" }}>{role.toLowerCase()}</div>
            </div>
            <div style={{ width:30, height:30, borderRadius:"50%", background:`linear-gradient(135deg,${C.accent},${C.teal})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700 }}>
              {role==="ADMIN"?"AD":role==="SHIPPER"?(authUser?.shipper?.companyName?.[0]||"S"):role==="BROKER"?(authUser?.broker?.companyName?.[0]||"B"):role==="DRIVER"?"DR":(authUser?.carrier?.companyName?.[0]||"C")}
            </div>
          </div>
          <div style={{ position:"relative" }}>
            <button onClick={()=>setNotifOpen(!notifOpen)} style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:7, padding:"5px 10px", color:C.textMd, cursor:"pointer", fontSize:14 }}>🔔</button>
            {unreadCount > 0 && <span style={{ position:"absolute", top:-4, right:-4, width:16, height:16, borderRadius:"50%", background:C.danger, color:"#fff", fontSize:9, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center" }}>{unreadCount}</span>}
            {notifOpen && (
              <div style={{ position:"absolute", top:"110%", right:0, width:320, background:C.surf2, border:`1px solid ${C.border}`, borderRadius:10, zIndex:100, overflow:"hidden", boxShadow:"0 8px 32px rgba(0,0,0,.5)" }}>
                <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:12, fontWeight:600, color:C.text }}>Notifications</span>
                  {notifs.some(n=>!n.read) && <button onClick={markAllRead} style={{ background:"none", border:"none", color:C.accentLt, fontSize:11, cursor:"pointer", padding:0 }}>Mark all read</button>}
                </div>
                {notifs.length===0 && <div style={{ padding:"20px 14px", textAlign:"center", fontSize:12, color:C.textDm }}>No notifications</div>}
                {notifs.map(n => (
                  <div key={n.id} onClick={()=>markRead(n.id)} style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, background:n.read?"transparent":C.accentGl, borderLeft:n.read?"3px solid transparent":`3px solid ${C.accentLt}`, cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:n.read?400:600, color:C.text }}>{n.title}</div>
                      <div style={{ fontSize:11, color:C.textMd, marginTop:2 }}>{n.message}</div>
                      <div style={{ fontSize:10, color:C.textDm, marginTop:3 }}>{n.time}</div>
                    </div>
                    <button onClick={e=>{e.stopPropagation();dismissNotif(n.id);}} style={{ background:"none", border:"none", color:C.textDm, cursor:"pointer", fontSize:14, padding:"0 0 0 8px" }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => {
            if (window.confirm("Clear locally-saved dispatch boards, dismissed loads, and fleet data on this browser?\n\nYou'll stay logged in. This does not affect the database or other users.")) {
              // Clear everything EXCEPT the auth token — stay logged in
              Object.keys(localStorage)
                .filter(k => k.startsWith("ff_") && k !== "ff_token")
                .forEach(k => localStorage.removeItem(k));
              window.location.reload();
            }
          }} style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.textDm, borderRadius:6, padding:"5px 10px", fontSize:11, cursor:"pointer" }}>
            🧹 Clear Test Data
          </button>
          <button onClick={() => { logout(); setTimeout(() => window.location.reload(), 100); }} 
  style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.textMd, borderRadius:6, padding:"5px 12px", fontSize:12, cursor:"pointer" }}>
  Sign out
</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ display:"flex", flex:1, overflow:"hidden" }}>
        {/* Mobile overlay */}
        {mobileNavOpen && (
          <div onClick={() => setMobileNavOpen(false)}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:199, top:52 }} />
        )}
        <div className={`sidebar-hidden ${mobileNavOpen ? "sidebar-visible" : ""}`} style={{ display:"flex" }}>
          <Sidebar role={role} page={page} setPage={(p) => { setPage(p); setMobileNavOpen(false); }} />
        </div>
        <main style={{ flex:1, overflow:"auto", padding:20 }} className="main-content" onClick={()=>{notifOpen&&setNotifOpen(false);}}>
          {renderPage()}
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AccessGate>
        <ErrorBoundary>
          <AppInner />
        </ErrorBoundary>
      </AccessGate>
    </AuthProvider>
  );
}
